/**
 * Rogue Alloy fitment system.
 *
 * Framework-free modules covering:
 *   - the shared vehicle store (localStorage + cookie, `rogue.vehicle` / `rogue_vehicle`)
 *   - pure matching/formatting functions (unit tested by tests/rogue-fitment.test.js)
 *   - three custom elements that wire the pure functions to the DOM:
 *       <rogue-vehicle-selector>  sections/rogue-vehicle-selector.liquid
 *       <rogue-your-van-bar>      snippets/rogue-your-van-bar.liquid
 *       <rogue-fitment-gate>      snippets/rogue-fitment-gate.liquid
 *
 * No third-party libraries. No bundler: this file is loaded directly as an
 * ES module (`<script type="module" src="{{ 'rogue-fitment.js' | asset_url }}">`).
 *
 * The finish swatch map (colourSwatchColor/colourSwatchBackground and their
 * data, below) used to live in its own module, assets/rogue-swatch-map.js,
 * imported here with a relative specifier (`./rogue-swatch-map.js`). On
 * Shopify's asset CDN this file's own URL carries a `?v=` cache-busting
 * query, and a relative import resolves against the path, not the query -
 * so in principle the second request lands on the same, correctly-served
 * file. Rather than leave that resolution to chance for something every
 * custom element on the site depends on, the map is inlined here instead:
 * one file, one request, no second asset that could 404 silently and take
 * every <rogue-*> element down with it.
 */

export const VEHICLE_STORAGE_KEY = 'rogue.vehicle';
export const VEHICLE_COOKIE_NAME = 'rogue_vehicle';
export const VEHICLE_COOKIE_MAX_AGE_DAYS = 180;

/* -------------------------------------------------------------------------
 * Pure functions: serialization
 * ---------------------------------------------------------------------- */

/**
 * Shape stored for the shopper's chosen vehicle. Only the fields the rest of
 * the theme needs are kept (see the task brief): slug, display name,
 * collection handle, verification mode, and pcd options.
 * @typedef {object} RogueVehicleSelection
 * @property {string} slug
 * @property {string} display_name
 * @property {string} collection_handle
 * @property {string} verification_mode
 * @property {string[]} pcd_options
 * @property {string} rim_size - the rim size chosen in the selector, if any ("" when not chosen - it's optional)
 */

/**
 * Picks only the fields that get persisted, so callers can pass a full
 * vehicle record (with make/model/etc.) without leaking extra data into
 * storage.
 * @param {Record<string, unknown>} vehicle
 * @returns {RogueVehicleSelection|null}
 */
export function toStoredVehicle(vehicle) {
  if (!vehicle || typeof vehicle !== 'object' || !vehicle.slug) return null;

  return {
    slug: String(vehicle.slug),
    display_name: typeof vehicle.display_name === 'string' ? vehicle.display_name : '',
    collection_handle: typeof vehicle.collection_handle === 'string' ? vehicle.collection_handle : '',
    verification_mode: typeof vehicle.verification_mode === 'string' ? vehicle.verification_mode : 'none',
    pcd_options: Array.isArray(vehicle.pcd_options) ? vehicle.pcd_options.filter((p) => typeof p === 'string') : [],
    rim_size:
      typeof vehicle.rim_size === 'string'
        ? vehicle.rim_size
        : typeof vehicle.rim_size === 'number'
          ? String(vehicle.rim_size)
          : '',
  };
}

/**
 * @param {RogueVehicleSelection} vehicle
 * @returns {string}
 */
export function serializeVehicle(vehicle) {
  return JSON.stringify(toStoredVehicle(vehicle));
}

/**
 * Parses a stored vehicle value (from localStorage or a cookie). Never
 * throws: malformed or missing input returns null.
 * @param {string|null|undefined} raw
 * @returns {RogueVehicleSelection|null}
 */
export function deserializeVehicle(raw) {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return toStoredVehicle(parsed);
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------
 * Pure functions: cookie string helpers (operate on plain strings so they
 * are testable without a document).
 * ---------------------------------------------------------------------- */

/**
 * @param {string} name
 * @param {string} value
 * @param {number} maxAgeDays
 * @returns {string} a value suitable for assigning to `document.cookie`
 */
export function buildCookieString(name, value, maxAgeDays = VEHICLE_COOKIE_MAX_AGE_DAYS) {
  const maxAgeSeconds = Math.max(0, Math.round(maxAgeDays * 24 * 60 * 60));
  return `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
}

/**
 * @param {string} name
 * @returns {string} a value suitable for assigning to `document.cookie` to delete it
 */
export function buildCookieClearString(name) {
  return `${name}=; max-age=0; path=/; SameSite=Lax`;
}

/**
 * Reads a single cookie value out of a raw `document.cookie` string.
 * @param {string} cookieString - e.g. `document.cookie`
 * @param {string} name
 * @returns {string|null}
 */
export function readCookieValue(cookieString, name) {
  if (!cookieString) return null;

  const parts = cookieString.split(';');
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    const key = part.slice(0, eq).trim();
    if (key === name) {
      return decodeURIComponent(part.slice(eq + 1).trim());
    }
  }
  return null;
}

/* -------------------------------------------------------------------------
 * Vehicle store: a small factory so storage/cookie backends are injectable
 * (and therefore unit-testable without a DOM).
 * ---------------------------------------------------------------------- */

/**
 * @typedef {object} StorageLike
 * @property {(key: string) => string|null} getItem
 * @property {(key: string, value: string) => void} setItem
 * @property {(key: string) => void} removeItem
 */

/**
 * @typedef {object} CookieJarLike
 * @property {() => string} read - returns the raw cookie string
 * @property {(cookieString: string) => void} write - assigns a `key=value; ...` string
 */

/**
 * @param {{ storage?: StorageLike|null, cookies?: CookieJarLike|null }} deps
 */
export function createVehicleStore({ storage = null, cookies = null } = {}) {
  return {
    /** @returns {RogueVehicleSelection|null} */
    get() {
      if (storage) {
        const fromStorage = deserializeVehicle(safeGetItem(storage, VEHICLE_STORAGE_KEY));
        if (fromStorage) return fromStorage;
      }
      if (cookies) {
        const fromCookie = deserializeVehicle(readCookieValue(cookies.read(), VEHICLE_COOKIE_NAME));
        if (fromCookie) return fromCookie;
      }
      return null;
    },
    /** @param {Record<string, unknown>} vehicle */
    set(vehicle) {
      const stored = toStoredVehicle(vehicle);
      if (!stored) return null;
      const raw = JSON.stringify(stored);
      if (storage) safeSetItem(storage, VEHICLE_STORAGE_KEY, raw);
      if (cookies) cookies.write(buildCookieString(VEHICLE_COOKIE_NAME, raw));
      return stored;
    },
    clear() {
      if (storage) safeRemoveItem(storage, VEHICLE_STORAGE_KEY);
      if (cookies) cookies.write(buildCookieClearString(VEHICLE_COOKIE_NAME));
    },
  };
}

function safeGetItem(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode, quota, etc.) - degrade silently */
  }
}

function safeRemoveItem(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/* -------------------------------------------------------------------------
 * Pure functions: vehicle list handling (for the selector's cascading
 * selects and the ?vehicle=slug deep link).
 * ---------------------------------------------------------------------- */

/**
 * @param {Array<Record<string, unknown>>} vehicles
 * @returns {Array<Record<string, unknown>>} only vehicles whose `active` field is not explicitly false
 */
export function filterActiveVehicles(vehicles) {
  if (!Array.isArray(vehicles)) return [];
  return vehicles.filter((v) => v && v.active !== false);
}

/**
 * @param {Array<Record<string, unknown>>} vehicles
 * @returns {string[]} unique makes, in first-seen order
 */
export function uniqueMakes(vehicles) {
  const seen = new Set();
  const result = [];
  for (const v of vehicles) {
    const make = v && typeof v.make === 'string' ? v.make : '';
    if (!make || seen.has(make)) continue;
    seen.add(make);
    result.push(make);
  }
  return result;
}

/**
 * @param {Array<Record<string, unknown>>} vehicles
 * @param {string} make
 * @returns {string[]} unique models for that make, in first-seen order
 */
export function uniqueModelsForMake(vehicles, make) {
  const seen = new Set();
  const result = [];
  for (const v of vehicles) {
    if (!v || v.make !== make) continue;
    const model = typeof v.model === 'string' ? v.model : '';
    if (!model || seen.has(model)) continue;
    seen.add(model);
    result.push(model);
  }
  return result;
}

/**
 * @param {Array<Record<string, unknown>>} vehicles
 * @param {string} make
 * @param {string} model
 * @returns {Array<Record<string, unknown>>} vehicle variants (generation/years/rim entries) for that make+model
 */
export function variantsForModel(vehicles, make, model) {
  return vehicles.filter((v) => v && v.make === make && v.model === model);
}

/**
 * @param {Record<string, unknown>} vehicle
 * @returns {string} a label combining generation and years, e.g. "T6 (2015 on)"
 */
export function generationLabel(vehicle) {
  if (!vehicle) return '';
  const generation = typeof vehicle.generation === 'string' ? vehicle.generation : '';
  const years = typeof vehicle.years_display === 'string' ? vehicle.years_display : '';
  if (!generation) return years;
  if (!years) return generation;
  // Review N14/task item 8: the Ford Transit Custom's own generation field
  // sometimes already folds the years in ("2011 to 2023", "2024 onwards" -
  // the platform's own display-name rule), so appending years_display again
  // read "2011 to 2023 (2011 to 2023)" and "2024 onwards (2024 on)". A
  // 4-digit year anywhere in the generation is the same signal the platform
  // uses: only append years_display when the generation doesn't already
  // carry one ("Second generation (2023 on)" still gets the bracket).
  if (/\d{4}/.test(generation)) return generation;
  return `${generation} (${years})`;
}

/**
 * @param {Array<Record<string, unknown>>} vehicles
 * @param {string} slug
 * @returns {Record<string, unknown>|null}
 */
export function findVehicleBySlug(vehicles, slug) {
  if (!slug) return null;
  return vehicles.find((v) => v && v.active !== false && v.slug === slug) || null;
}

/**
 * @param {Record<string, unknown>} vehicle
 * @returns {string[]} rim sizes as strings (metaobject list fields decode as arrays already, but be defensive)
 */
export function rimSizesForVehicle(vehicle) {
  if (!vehicle || !Array.isArray(vehicle.rim_sizes)) return [];
  return vehicle.rim_sizes.map((size) => String(size));
}

/**
 * @param {Record<string, unknown>} vehicle
 * @returns {boolean} whether a rim size selector step is needed
 */
export function needsRimSizeStep(vehicle) {
  return rimSizesForVehicle(vehicle).length > 1;
}

/* -------------------------------------------------------------------------
 * Pure functions: the "My van isn't listed" vehicle (S3.2a).
 *
 * The Wix dropdown had a "can't find vehicle model" option that 62 corrected
 * orders came through on the old site; Paul's pipeline expects a
 * `Typed Vehicle` field for those. This is a special vehicle record, stored
 * in the same `rogue.vehicle` slot the real vans use (see createVehicleStore
 * above), so every piece of the storefront that already reads the stored
 * vehicle - the your-van bar, the fitment gate, cart attributes - picks it
 * up for free. It deliberately carries no PCD data (`pcd_options: []`):
 * there is nothing to check fitment against for a van we don't recognise, so
 * every product's fitment gate lands on `verification_mode:
 * 'ask_stud_pattern'`'s hold path (see needsStudPatternVerification) rather
 * than a false "fits your van" - see RogueFitmentGate.matchingVehicle in the
 * DOM section below for how the picker itself (Finish/Size) is kept fully
 * open rather than greyed out against empty PCD data.
 * ---------------------------------------------------------------------- */

/** The `make` <select> option value that reveals the "tell us" text field, never a real vehicle make or slug. */
export const UNLISTED_MAKE_VALUE = '__unlisted__';

/** The stored vehicle's slug for an unlisted van - never a real rogue_vehicle metaobject slug. */
export const UNLISTED_VEHICLE_SLUG = 'unlisted';

/** The fallback, unfiltered "browse everything" collection an unlisted van's "Show all wheels" button sends the shopper to. */
export const UNLISTED_VEHICLE_COLLECTION_HANDLE = 'all-wheels';

/** Puts an unlisted van's product pages on the same stud-pattern hold path as a multi-drilling real van. */
export const UNLISTED_VEHICLE_VERIFICATION_MODE = 'ask_stud_pattern';

/** The prefix `display_name` carries for an unlisted van, so it reads as "Unlisted: Renault Trafic 2019" everywhere `display_name` is already shown (the your-van bar, cart attributes). */
export const UNLISTED_VEHICLE_DISPLAY_PREFIX = 'Unlisted: ';

/**
 * The stored-vehicle shape for a shopper whose van isn't in the list.
 * @param {string} typedText - what the shopper typed for their make/model/year
 * @returns {RogueVehicleSelection}
 */
export function buildUnlistedVehicle(typedText) {
  const cleaned = typeof typedText === 'string' ? typedText.trim() : '';
  return {
    slug: UNLISTED_VEHICLE_SLUG,
    display_name: UNLISTED_VEHICLE_DISPLAY_PREFIX + cleaned,
    collection_handle: UNLISTED_VEHICLE_COLLECTION_HANDLE,
    verification_mode: UNLISTED_VEHICLE_VERIFICATION_MODE,
    pcd_options: [],
    rim_size: '',
  };
}

/**
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {boolean} whether the catalogue requires a human fitment decision
 */
export function requiresManualReview(vehicle) {
  return vehicle?.verification_mode === 'manual_review';
}

/**
 * Customer-facing words for a manual-review vehicle (25 Oct 2026, Daniel:
 * "They aren't 'buying a fitment check'"). The shopper buys as normal; Rogue
 * double-checks the fit after the order, at no extra cost, before anything
 * is sent. Words only: the hold markers and which vehicles need review are
 * untouched (see buildVerificationCartData and DetermineFitmentHold).
 */
export const MANUAL_REVIEW_LINE_VALUE = 'Free double-check before dispatch';

/** The muted note under the buy box fit line. Keeps the honest "the size may change" notice, positively. */
export const MANUAL_REVIEW_BADGE_NOTE = "Order as normal. Before anything is sent, our team checks the stud pattern, offset and size against your vehicle, and we'll call you if anything needs to change.";

/** A manual-review vehicle's collection summary (also server-rendered by sections/rogue-collection.liquid). */
export const MANUAL_REVIEW_COLLECTION_SUMMARY = "Order as normal. We'll double-check the fit for your vehicle after you order, at no extra cost, before anything is sent.";

/**
 * The vehicle name for the manual-review lines: the buy-box short form
 * ("Defender", "Transit Connect"), with a twin-rear-wheel body read the way
 * people say it ("twin-rear-wheel Sprinter", not "Sprinter Twin Rear Wheel").
 * @param {Record<string, unknown>|null} vehicle
 * @returns {string} "" when there is nothing to name
 */
export function manualReviewVehicleName(vehicle) {
  if (!vehicle) return '';
  const model = typeof vehicle.model === 'string' ? vehicle.model.trim() : '';
  const generation = typeof vehicle.generation === 'string' ? vehicle.generation.trim() : '';
  if (model && /^twin rear wheel$/i.test(generation)) return `twin-rear-wheel ${model}`;
  return shortVanLabel(vehicle);
}

/** "We'll double-check this fits your Defender" - the buy box fit line. */
export function manualReviewBadgeText(vehicle) {
  const name = manualReviewVehicleName(vehicle);
  return name ? `We'll double-check this fits your ${name}` : "We'll double-check this fits your vehicle";
}

/** The reassurance line under the Add to basket button. */
export function manualReviewAssuranceText(vehicle) {
  const name = manualReviewVehicleName(vehicle) || 'vehicle';
  return `We'll double-check the fit for your ${name} after you order, at no extra cost, before anything is sent.`;
}

export function isUnlistedVehicle(vehicle) {
  return Boolean(vehicle && vehicle.slug === UNLISTED_VEHICLE_SLUG);
}

/**
 * The typed text a shopper entered for an unlisted van, recovered from its
 * stored `display_name` ("Unlisted: Renault Trafic 2019" -> "Renault Trafic
 * 2019") rather than kept in a second storage field for the same string.
 * "" for anything that isn't an unlisted vehicle, so the caller can write
 * the `Typed vehicle` cart attribute only when there's something to write.
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {string}
 */
export function typedVehicleText(vehicle) {
  if (!isUnlistedVehicle(vehicle)) return '';
  const displayName = typeof vehicle.display_name === 'string' ? vehicle.display_name : '';
  return displayName.startsWith(UNLISTED_VEHICLE_DISPLAY_PREFIX)
    ? displayName.slice(UNLISTED_VEHICLE_DISPLAY_PREFIX.length).trim()
    : displayName.trim();
}

/**
 * The Vehicle/Typed vehicle cart attributes for the stored van - the one
 * place both snippets/rogue-fitment-gate.liquid's add-to-cart form
 * (updateCartAttributesFields) and a van change on any other page
 * (syncVehicleCartAttributes, below) compute this pair, so they can never
 * disagree on what a given vehicle writes.
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {Record<string, string>}
 */
export function vehicleCartAttributes(vehicle) {
  const attributes = {};
  if (vehicle && vehicle.display_name) {
    attributes.Vehicle = vehicle.display_name;
  }
  // Spec 2.8 / walk finding (section 0): once there IS a vehicle to write,
  // always send this key - even blank - rather than omitting it whenever
  // the current van isn't unlisted. /cart/update.js leaves an omitted
  // attribute untouched, so a shopper who stored an unlisted van, added a
  // wheel (writing "Typed vehicle"), then switched to a real, listed one
  // kept yesterday's typed text on screen forever ("Typed vehicle: Renault
  // Kangoo 2019" surviving on a Ducato basket) - an explicit "" is what
  // actually clears it.
  if (vehicle) {
    attributes['Typed vehicle'] = typedVehicleText(vehicle);
  }
  return attributes;
}

/**
 * The full attributes payload a real van CHANGE posts (as opposed to
 * `vehicleCartAttributes`, also read by the product page on every render of
 * the SAME stored van - see updateCartAttributesFields - where clearing
 * these fields would wipe out registration/tyre size/upplating answers the shopper had
 * already typed into the basket panel for no reason). Review finding
 * (Major): "Vehicle registration"/"Current tyre size"/"Upplating" are per-van detail
 * entered in the basket's "Your details" panel (spec 2.8), so they go
 * stale exactly like the already-fixed `Typed vehicle` did - measured
 * live: a Ducato basket's "AB12CDE"/"215/65R16" survived a switch to the
 * Transporter and an add of a different wheel. Explicit "" (not omitted),
 * same reasoning as vehicleCartAttributes' own Typed vehicle comment: a
 * blank key is what actually clears a stale `/cart/update.js` attribute,
 * an omitted one leaves it untouched.
 *
 * THEME_A finding (24 Sep 2026, Major): `fitment_verification: 'requested'`
 * (set by buildVerificationCartData's "not sure of my stud pattern" hold
 * flow) is a whole-cart attribute too, and went just as stale - measured
 * live, it survived a switch from an unlisted van to a listed one with no
 * drilling ambiguity at all. Cleared here for any LISTED van (unlisted vans
 * always land on the hold path regardless, so there is nothing stale to
 * clear for them); a listed van that itself turns out to need verification
 * gets it set fresh at its own next add-to-cart.
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {Record<string, string>}
 */
export function vehicleChangeCartAttributes(vehicle) {
  const attributes = vehicleCartAttributes(vehicle);
  if (!vehicle) return {
    Vehicle: '', 'Typed vehicle': '', 'Vehicle registration': '',
    'Current tyre size': '', Upplating: '', fitment_verification: '',
  };
  const cleared = { ...attributes, 'Vehicle registration': '', 'Current tyre size': '', Upplating: '' };
  if (requiresManualReview(vehicle)) {
    cleared.fitment_verification = 'requested';
  } else if (!isUnlistedVehicle(vehicle)) {
    cleared.fitment_verification = '';
  }
  return cleared;
}

/** Serialize cart attribute writes so an older response cannot overwrite newer details. */
export function createCartAttributeWriter(fetchImpl = (...args) => fetch(...args)) {
  let pending = Promise.resolve();
  let desired = {};
  let revision = 0;
  return {
    /**
     * `forget` (review SHOP-007): send these keys in this write only, rather
     * than resending them with every later write - for a one-off clear that
     * must not overwrite a value another tab sets afterwards.
     */
    save(attributes, { keepalive = false, forget = false } = {}) {
      desired = { ...desired, ...attributes };
      const snapshot = { ...desired };
      if (forget) for (const key of Object.keys(attributes)) delete desired[key];
      revision += 1;
      const operation = pending.catch(() => {}).then(async () => {
        const response = await fetchImpl('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          keepalive,
          body: JSON.stringify({ attributes: snapshot }),
        });
        if (!response?.ok) throw new Error('Basket details could not be saved');
        return response.json();
      });
      pending = operation;
      return operation;
    },
    async flush() {
      let observed;
      do {
        observed = revision;
        await pending;
      } while (observed !== revision);
    },
  };
}

// Asset URLs can carry different Shopify cache versions. Share one queue even
// when the browser evaluates more than one URL for this module on a page.
const cartWriterKey = Symbol.for('rogue.cartAttributeWriter');
const cartAttributeWriter = typeof window === 'undefined'
  ? createCartAttributeWriter()
  : (window[cartWriterKey] ||= createCartAttributeWriter());

// Custom additions start before Horizon emits its cart event. Track their
// full intent, including preflight reads, across cache-version module copies.
const cartMutationKey = Symbol.for('rogue.cartMutationState');
const cartMutationState = typeof window === 'undefined'
  ? { pending: new Set(), failed: false }
  : (window[cartMutationKey] ||= { pending: new Set(), failed: false });

export function beginCartMutation() {
  let settle;
  const pending = new Promise((resolve) => { settle = resolve; });
  cartMutationState.pending.add(pending);
  let finished = false;
  return (needsReview = false) => {
    if (finished) return;
    finished = true;
    if (needsReview) cartMutationState.failed = true;
    cartMutationState.pending.delete(pending);
    settle();
  };
}

/**
 * Never turn a confirmed add into a retryable add merely because refresh failed.
 * `afterRefused` (3 Nov 2026, Black Friday review F1): on a 4xx answer the
 * caller may read the basket and take this add back out; it returns
 * `{ removed, ... }` and the result is `{ state: 'refused', status, ...that }`.
 * Without it, or when it throws, any non-OK answer stays 'unknown'.
 */
export async function addBasketItemsOnce(payload, { fetchImpl = (...args) => fetch(...args), afterAccepted = async () => {}, afterRefused = null } = {}) {
  const finishIntent = beginCartMutation();
  const finish = (result) => {
    finishIntent(result.state === 'unknown' || (result.state === 'refused' ? !result.removed : !result.refreshed));
    return result;
  };
  let response;
  try {
    response = await fetchImpl('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    return finish({ state: 'unknown' });
  }
  if (!response?.ok) {
    // Shopify may add available stock before returning 422. An error status
    // alone cannot establish that the basket is unchanged. A 4xx answer is
    // Shopify's final word on this add, so the basket can be read and put
    // right; a 5xx or a lost connection may still be in flight.
    const status = Number(response?.status) || 0;
    if (typeof afterRefused === 'function' && status >= 400 && status < 500) {
      try {
        const undone = await afterRefused(response);
        if (undone) return finish({ ...undone, state: 'refused', status });
      } catch {
        // The basket could not be read: fall back to "check your basket".
      }
    }
    return finish({ state: 'unknown' });
  }
  try {
    await afterAccepted();
    return finish({ state: 'accepted', refreshed: true });
  } catch {
    return finish({ state: 'accepted', refreshed: false });
  }
}

const lineQuantity = (line) => Number(line?.quantity) || 0;
const sumLineQuantity = (lines) => lines.reduce((sum, line) => sum + lineQuantity(line), 0);

/**
 * One basket request, tried once more after a pause when Shopify is busy
 * (429) or the connection drops. Only for basket reads and changes to an
 * absolute quantity, which are safe to repeat.
 */
async function cartRequestWithRetry(fetchImpl, url, options, pause) {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetchImpl(url, options);
      if (response?.status === 429 && attempt === 0) {
        await pause(1000);
        continue;
      }
      return response;
    } catch (error) {
      if (attempt > 0) throw error;
      await pause(1000);
    }
  }
}

/**
 * Black Friday review F1 (3 Nov 2026). Shopify answers an add it cannot
 * fill in full with 422 but still puts in what stock remains: 2 of a set
 * of 4 wheels, with all 4 tyres. This reads the basket and takes this add
 * back out: every set line carries this add's own `_set_id`, and an extra
 * from the same POST (no `_set_id`) goes back to the quantity it had in the
 * basket read just before the add. Lines are changed by key to an absolute
 * quantity, so a retried request does no harm.
 * @param {{
 *   items: Array<{ id: string|number, quantity: number, properties?: Record<string, unknown> }>,
 *   before?: Array<Record<string, any>>,
 *   status?: number,
 *   fetchImpl?: (url: string, options?: Record<string, unknown>) => Promise<any>,
 *   pause?: (ms: number) => Promise<unknown>,
 * }} args - `before` is the basket's items read just before the add
 * @returns {Promise<{ removed: boolean, short: Array<{ id: string, requested: number, added: number, left: number }>, kept: Array<Record<string, any>>, cart: Record<string, any> }>}
 *   `short` (422 only, Shopify's stock answer): each set line Shopify cut
 *   short, with `left` = what the whole basket held of that variant, which
 *   is Shopify's stock. `kept`: lines from this add still in the basket
 *   when taking them out failed. Throws when the basket cannot be read.
 */
export async function undoRefusedAdd({ items, before = [], status = 422, fetchImpl = (...args) => fetch(...args), pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms)) }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const setIds = new Set(list.map((item) => item.properties?._set_id).filter(Boolean));
  const extraIds = new Set(list.filter((item) => !item.properties?._set_id).map((item) => String(item.id)));
  const beforeByKey = new Map((Array.isArray(before) ? before : []).map((line) => [line?.key, lineQuantity(line)]));
  const isSetLine = (line) => setIds.has(line?.properties?._set_id);
  const fromThisAdd = (line) => isSetLine(line) ||
    (extraIds.has(String(line?.variant_id)) && !line?.properties?._set_id && lineQuantity(line) > (beforeByKey.get(line?.key) || 0));
  const read = async () => {
    const response = await cartRequestWithRetry(fetchImpl, '/cart.js', { headers: { Accept: 'application/json' } }, pause);
    if (!response?.ok) throw new Error('Could not read the basket');
    const cart = await response.json();
    if (!Array.isArray(cart?.items)) throw new Error('Could not read the basket');
    return cart;
  };

  const cart = await read();
  const short = [];
  if (status === 422) {
    for (const item of list.filter((entry) => setIds.has(entry.properties?._set_id))) {
      const id = String(item.id);
      const requested = Number(item.quantity) || 0;
      const ofVariant = cart.items.filter((line) => String(line.variant_id) === id);
      const added = sumLineQuantity(ofVariant.filter((line) => line.properties?._set_id === item.properties._set_id));
      // Shopify fills a variant up to its stock across the whole basket, so
      // what the basket holds of it now is what there was.
      if (added < requested) short.push({ id, requested, added, left: sumLineQuantity(ofVariant) });
    }
  }

  let current = cart;
  let failed = false;
  for (const line of cart.items.filter(fromThisAdd)) {
    const quantity = isSetLine(line) ? 0 : beforeByKey.get(line.key) || 0;
    try {
      const response = await cartRequestWithRetry(fetchImpl, '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity }),
      }, pause);
      const next = response?.ok ? await response.json() : null;
      if (Array.isArray(next?.items)) current = next;
      else failed = true;
    } catch {
      failed = true;
    }
  }
  if (failed) {
    // Read again, so the shopper is told what the basket really holds.
    try { current = await read(); } catch { /* keep the last basket Shopify sent */ }
  }
  const kept = current.items.filter(fromThisAdd);
  return { removed: kept.length === 0, short, kept, cart: current };
}

/**
 * The line under the Add button after a refused add (Black Friday review
 * F1, 3 Nov 2026): how many are left, that nothing was added, and what to
 * do next. When taking the add back out failed, it says what the basket
 * holds instead.
 * @param {{
 *   status?: number,
 *   wheelShort?: { left: number }|null,
 *   tyreShort?: { left: number }|null,
 *   already?: number,
 *   removed: boolean,
 *   kept?: Array<Record<string, any>>,
 *   switched?: boolean,
 *   phone?: string,
 * }} args - `already`: wheels of this variant in the basket before the add;
 *   `switched`: the page moved to another drilling of the same size and finish
 * @returns {string}
 */
export function refusedAddMessage({ status = 422, wheelShort = null, tyreShort = null, already = 0, removed, kept = [], switched = false, phone = '' }) {
  const call = phone ? `, or call us on ${phone}` : '';
  const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  let reason;
  let next;
  if (wheelShort) {
    const left = Math.max(0, Number(wheelShort.left) || 0);
    const more = Math.max(0, left - Math.max(0, Number(already) || 0));
    const moreWord = already > 0 ? 'more ' : '';
    if (switched) {
      reason = more > 0 ? `Only ${more} ${moreWord}left in that specification` : 'That specification has just sold out';
      next = `The page now shows another specification of this size and finish that fits your van. Check it and add again${call}.`;
    } else if (left === 0) {
      reason = 'This size and finish has just sold out';
      next = `Choose another finish or size${call}.`;
    } else if (more === 0) {
      reason = `All ${left} left in this size and finish are already in your basket`;
      next = `Choose another finish or size${call}.`;
    } else {
      reason = `Only ${more} ${moreWord}left in this size and finish`;
      next = `Choose ${count(more, 'wheel', 'wheels')}, another finish or size${call}.`;
    }
  } else if (tyreShort) {
    const left = Math.max(0, Number(tyreShort.left) || 0);
    reason = left > 0 ? `Only ${count(left, 'tyre', 'tyres')} of that kind left` : 'Those tyres have just sold out';
    next = `Choose another tyre or Wheels only${call}.`;
  } else if (status === 429) {
    reason = 'The shop is very busy right now';
    next = 'Please try again in a moment.';
  } else {
    reason = 'We could not add this set';
    next = `Please try again${call}.`;
  }
  if (removed) return `${reason}, so nothing was added. ${next}`;

  const lines = Array.isArray(kept) ? kept : [];
  const wheels = sumLineQuantity(lines.filter((line) => line.product_type === 'Wheel'));
  const tyres = sumLineQuantity(lines.filter((line) => line.product_type === 'Tyre'));
  const others = sumLineQuantity(lines.filter((line) => line.product_type !== 'Wheel' && line.product_type !== 'Tyre'));
  const parts = [
    wheels ? count(wheels, 'wheel', 'wheels') : '',
    tyres ? count(tyres, 'tyre', 'tyres') : '',
    others ? count(others, 'other item', 'other items') : '',
  ].filter(Boolean);
  const held = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] || 'part of this set';
  return `${reason}. Your basket now holds ${held} from this add, not the set you chose. Change or remove them in your basket${call}.`;
}

export async function syncVehicleCartAttributes(vehicle, deps = {}) {
  const writer = deps.fetchImpl ? createCartAttributeWriter(deps.fetchImpl) : cartAttributeWriter;
  try {
    return await writer.save(vehicleChangeCartAttributes(vehicle), { keepalive: true });
  } catch {
    return null;
  }
}

/**
 * Store a confirmed van whatever the basket write does (31 Oct 2026, T1).
 * On drop night /cart/update.js can fail or return 429; the shopper must
 * still get their van saved and their next page. The basket attributes are
 * rewritten by the add itself (RogueFitmentGate.syncCartAttributes runs
 * before addBasketItemsOnce) and every line carries `_vehicle_slug`, so a
 * failed write here loses nothing. `saved` only decides the status wording.
 * @param {Record<string, unknown>} vehicle
 * @param {{ store: { set(vehicle: Record<string, unknown>): RogueVehicleSelection|null }, sync?: (vehicle: Record<string, unknown>) => Promise<unknown> }} deps
 * @returns {Promise<{ stored: RogueVehicleSelection|null, saved: boolean }>}
 */
export async function storeConfirmedVehicle(vehicle, { store, sync = syncVehicleCartAttributes }) {
  let saved = false;
  try {
    saved = Boolean(await sync(vehicle));
  } catch {
    saved = false;
  }
  return { stored: store.set(vehicle), saved };
}

/** The status line under the selector after a confirmed van: quiet when the basket write failed, never an error. */
export function vehicleSavedStatus(stored, saved, name = stored?.display_name) {
  if (!stored) return '';
  return saved ? `Saved: ${name}` : 'Saved on this device';
}

/**
 * The variant ids of the extras in an add payload: every line that is not
 * part of the wheel set (the wheel and its tyre line share a `_set_id`).
 * @param {Array<{ id: string|number, properties?: Record<string, unknown> }>|undefined} items
 * @returns {string[]}
 */
/**
 * D1 (31 Oct 2026): the add payload with the extras ahead of the set, so
 * Shopify (newest line first) lists the wheels and their tyres above the
 * extras in the basket, the drawer and at checkout.
 * @param {Array<Record<string, unknown>>} setItems - the wheel line and its tyre line
 * @param {Array<Record<string, unknown>>} extraItems
 * @returns {Array<Record<string, unknown>>}
 */
export function orderAddItemsExtrasFirst(setItems, extraItems) {
  return [...(Array.isArray(extraItems) ? extraItems : []), ...(Array.isArray(setItems) ? setItems : [])];
}

/**
 * The same items with the set (lines carrying a `_set_id`) first, the
 * wheel ahead of its tyre as the payload built them, extras after.
 * @param {Array<Record<string, unknown>>|undefined} items
 * @returns {Array<Record<string, unknown>>}
 */
export function setLinesFirst(items) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  return [...list.filter((item) => item.properties?._set_id), ...list.filter((item) => !item.properties?._set_id)];
}

export function extraVariantIdsOf(items) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => item && item.id != null && !item.properties?._set_id)
    .map((item) => String(item.id));
}

/** The event RogueVehicleSelector fires (on document) whenever the stored van changes. */
export const ROGUE_VEHICLE_CHANGE_EVENT = 'rogue:vehicle-changed';

/** Keep vehicle changes on this shop, preserve a selected variant, and drop stale vehicle links. */
export function vehicleReturnPath(raw) {
  if (typeof raw !== 'string' || !raw.startsWith('/') || /^[\/\\]{2}/.test(raw) || /[\\\u0000-\u0020]/.test(raw)) return null;
  try {
    const base = 'https://rogue.invalid';
    const url = new URL(raw, base);
    if (url.origin !== base) return null;
    url.searchParams.delete('vehicle');
    url.searchParams.delete('change_vehicle');
    url.searchParams.delete('return');
    return url.pathname + url.search + url.hash;
  } catch { return null; }
}

export function vehicleSelectorUrl(returnPath) {
  const safe = vehicleReturnPath(returnPath);
  return '/?change_vehicle=1' + (safe && safe !== '/' ? `&return=${encodeURIComponent(safe)}` : '') + '#rogue-vehicle-selector';
}

/**
 * The header's "Choose vehicle" link. On the home page itself it must keep
 * the `return` the shopper arrived with (from a product page), not drop it
 * and send them on to a collection after they choose (25 Oct 2026).
 * @param {string} pathname
 * @param {string} search
 * @param {string} hash
 * @returns {string}
 */
export function vanBarSelectorUrl(pathname, search, hash) {
  if (pathname === '/') return vehicleSelectorUrl(new URLSearchParams(search || '').get('return'));
  return vehicleSelectorUrl(`${pathname || ''}${search || ''}${hash || ''}`);
}

/**
 * Where the home selector goes back to after a van is chosen (Paul's fixes
 * list, 26 Oct 2026: on one van's wheels, "Change", pick another van, and it
 * still showed the first van's wheels). A `return` that is a listed van's own
 * wheels page belongs to the old van, so it gives way to the new van's (null
 * here); any other page, such as a product, is still returned to.
 * @param {string|null} raw
 * @param {Array<Record<string, unknown>>} vehicles
 * @returns {string|null}
 */
export function vehicleChangeReturnPath(raw, vehicles) {
  const safe = vehicleReturnPath(raw);
  const handle = safe ? /\/collections\/([^/?#]+)\/?(?:[?#]|$)/.exec(safe)?.[1] : null;
  if (!handle) return safe;
  let decoded = handle;
  try { decoded = decodeURIComponent(handle); } catch { /* keep it as written */ }
  return (Array.isArray(vehicles) ? vehicles : []).some((v) => v && v.collection_handle === decoded) ? null : safe;
}

/**
 * The header menu after the basket drawer closes (27 Oct 2026 report: "the
 * menu gets all bunched up"). From 990px Horizon's drawer squeezes the page,
 * so the header menu moves items into "More"; when the drawer closes nothing
 * asks it to measure again, because <overflow-list> only watches its own box,
 * which stays at its collapsed width. This asks every header <overflow-list>
 * to reflow whenever the header itself changes width.
 *
 * Squeezed far enough (1280 wide: an 800px header), the menu falls below its
 * minimum and Horizon switches the header to the phone layout; it only ever
 * switches back on a WINDOW resize (assets/header.js), and the drawer never
 * resizes the window. So when the header grows back on a non-touch screen,
 * a "minimum-reached" left by the squeeze is cleared and the menu shown
 * again before it reflows; if it genuinely does not fit, the reflow sets it
 * again and Horizon goes back to the drawer as before.
 * @param {Document} doc
 * @param {typeof ResizeObserver|undefined} Observer
 * @param {boolean} [touch] - Horizon's isTouchDevice(): a touch screen keeps the drawer layout
 * @returns {ResizeObserver|null}
 */
export function watchHeaderMenuWidth(doc, Observer, touch = false) {
  const header = doc.querySelector('header');
  if (!header || typeof Observer !== 'function') return null;
  let lastWidth = null;
  const observer = new Observer((entries) => {
    const width = Math.round(entries[entries.length - 1]?.contentRect?.width ?? 0);
    if (lastWidth === null || width === lastWidth) {
      lastWidth = width;
      return;
    }
    const grew = width > lastWidth;
    lastWidth = width;
    const component = doc.querySelector('#header-component');
    for (const list of doc.querySelectorAll('header overflow-list')) {
      if (grew && !touch && list.hasAttribute('minimum-reached')) {
        list.removeAttribute('minimum-reached');
        if (component?.dataset?.menuStyle === 'drawer') component.dataset.menuStyle = 'menu';
      }
      list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    }
  });
  observer.observe(header);
  return observer;
}

/**
 * A wheel card's price note (front-end sweep, 26 Oct 2026): "Exact price once
 * you pick your vehicle" stayed on the cards after a van was picked. With a
 * listed van stored, the exact price is one click away instead.
 * @param {Record<string, unknown>|null} vehicle
 * @param {string} defaultText
 * @returns {string}
 */
export function cardPriceNoteText(vehicle, defaultText) {
  return vehicle && vehicle.slug && !isUnlistedVehicle(vehicle) ? 'Exact price for your van on the next page' : defaultText;
}

/**
 * The home selector's button once a `return` is waiting: it goes back to the
 * page the shopper came from, so "Show wheels for your ..." would promise a
 * list that never appears.
 * @param {boolean} unlistedMode
 * @param {Record<string, unknown>|null} vehicle
 * @returns {string}
 */
export function returnConfirmLabel(unlistedMode, vehicle) {
  const van = unlistedMode ? '' : cardFitsVanName(vehicle);
  return van ? `Continue with your ${van}` : 'Save and continue';
}

/**
 * The confirm button inside the product page's inline vehicle panel
 * (snippets/rogue-inline-vehicle.liquid). A wheel page checks fit on the
 * spot; a tyre page only saves the vehicle for the wheels-with-tyres list.
 * @param {'wheel'|'tyre'|string} context
 * @param {boolean} unlistedMode
 * @param {Record<string, unknown>|null} vehicle
 * @returns {string}
 */
/**
 * How far to scroll after the inline vehicle panel closes (25 Oct 2026,
 * Daniel: the picker "is a little all over the page"). The browser's own
 * focus scroll centred the fit line, which sits above the title, so a phone
 * jumped up about 540px and lost the Add button below the fold; on desktop
 * nothing moved and the button stayed off-screen. This moves only as far as
 * needed: none when the fit line and the end of the buy area are both on
 * screen, the smallest move that shows both when they fit, and when they
 * can't both fit (a phone), none if the fit line is already in view, else the
 * fit line just under the sticky header. The caller then shows the sticky
 * Add bar on a phone.
 * @param {{ resultTop: number, endBottom: number, viewportHeight: number, topInset?: number, margin?: number }} params
 *   viewport-relative pixel positions, as from getBoundingClientRect
 * @returns {{ delta: number, bothVisible: boolean }} delta > 0 scrolls down
 */
export function fitResultScroll({ resultTop, endBottom, viewportHeight, topInset = 0, margin = 16 }) {
  const top = topInset + margin;
  const bottom = viewportHeight - margin;
  const end = Number.isFinite(endBottom) && endBottom > resultTop ? endBottom : resultTop;
  if (resultTop >= top && end <= bottom) return { delta: 0, bothVisible: true };
  if (end - resultTop <= bottom - top) {
    const delta = end > bottom ? end - bottom : resultTop - top;
    return { delta: Math.round(delta), bothVisible: true };
  }
  const resultInView = resultTop >= top && resultTop + 40 <= bottom;
  return { delta: resultInView ? 0 : Math.round(resultTop - top), bothVisible: false };
}

export function inlineConfirmLabel(context, unlistedMode, vehicle, toVanWheels = false) {
  if (unlistedMode) return 'Save my vehicle';
  const van = cardFitsVanName(vehicle);
  if (toVanWheels && van) return `Show wheels for my ${van}`;
  if (context === 'tyre') return van ? `Save my ${van}` : 'Save my vehicle';
  return van ? `Check it fits my ${van}` : 'Check it fits';
}

/**
 * Whether confirming this van goes to its own wheels page whatever page the
 * selector is on (31 Oct 2026, Skye): yes for the first van picked, when
 * none was saved before, so the product page's panel and the home
 * selector's `return` both give way to it. Only a listed van has a wheels
 * page of its own; "my vehicle isn't listed", and every later change of
 * van, keep the page as before. On a wheel's own page a first van the
 * wheel fits stays there, price and Add to basket at hand (Skye, later the
 * same day: a shopper from a Shopping ad for that wheel was sent away to a
 * list, and from a van's list they went round in a circle).
 * @param {boolean} hadVehicle - a vehicle was already saved before this pick
 * @param {Record<string, unknown>|null} vehicle - the van being confirmed
 * @param {boolean} [onFittingWheel] - picked on a wheel's own page, and that wheel fits this van
 * @returns {boolean}
 */
export function goesToVanWheels(hadVehicle, vehicle, onFittingWheel = false) {
  return !hadVehicle && !onFittingWheel && Boolean(vehicle && vehicle.collection_handle) && !isUnlistedVehicle(vehicle);
}

/**
 * Whether this page was reached from outside the site (31 Oct 2026, the van
 * mix-up sweep): a search result, an ad or a shared link, rather than a click
 * on one of our own pages. A home photo of a Transit Custom 2024 onwards had
 * saved that generation for a 2011 to 2023 owner just browsing. No referrer
 * (a typed or shared link) counts as outside.
 * @param {unknown} referrer - document.referrer
 * @param {unknown} origin - window.location.origin
 * @returns {boolean}
 */
export function arrivedFromOutside(referrer, origin) {
  if (typeof referrer !== 'string' || referrer === '') return true;
  try {
    return new URL(referrer).origin !== origin;
  } catch {
    return true;
  }
}

/** Set by Clear, so a van page's landing save never puts the van straight back. */
export const VEHICLE_CLEARED_SESSION_KEY = 'rogue.vehicle.cleared';

function vehicleClearedThisSession() {
  try {
    return window.sessionStorage?.getItem(VEHICLE_CLEARED_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberVehicleCleared() {
  try {
    window.sessionStorage?.setItem(VEHICLE_CLEARED_SESSION_KEY, '1');
  } catch {
    /* storage unavailable - the referrer check still stops most re-saves */
  }
}

/**
 * The listed van whose own wheels page this is (31 Oct 2026, Skye): a
 * shopper arriving there from Google, an ad or a shared link with no van
 * saved gets that van saved, instead of being asked for the van the page is
 * about. Also matches a product reached through that page
 * (/collections/<van>/products/<wheel>). Only an exact, unique match counts.
 * Only an arrival from outside the site saves (arrivedFromOutside): a photo,
 * card or menu link on this site never picks a van for the shopper.
 * @param {string} pathname - window.location.pathname
 * @param {Array<Record<string, unknown>>} vehicles - the active vehicle list
 * @param {string} [collectionsRootPath] - routes.collections_url, e.g. "/collections" or "/en-ie/collections"
 * @returns {Record<string, unknown>|null}
 */
export function vehicleForCollectionPath(pathname, vehicles, collectionsRootPath = '/collections') {
  if (typeof pathname !== 'string') return null;
  const root = (typeof collectionsRootPath === 'string' && collectionsRootPath !== '' ? collectionsRootPath : '/collections').replace(/\/$/, '');
  if (!pathname.startsWith(`${root}/`)) return null;
  const segment = pathname.slice(root.length + 1).split('/')[0];
  if (!segment) return null;
  let handle = segment;
  try { handle = decodeURIComponent(segment); } catch { /* keep it as written */ }
  const matches = (Array.isArray(vehicles) ? vehicles : []).filter((v) => v && v.collection_handle === handle && !isUnlistedVehicle(v));
  return matches.length === 1 ? matches[0] : null;
}

/* -------------------------------------------------------------------------
 * Pure functions: PCD (stud pattern) parsing/formatting/matching.
 * ---------------------------------------------------------------------- */

/**
 * Parses a stud pattern string such as "5x120" into { holes, pcd }.
 * Returns null for anything that doesn't parse cleanly. Tolerates a bare
 * number (e.g. `120`, from a `rogue.pcd_1`/`rogue.pcd_2` metafield that only
 * stores the diameter) by stringifying it first - it will simply fail to
 * match the "holes x pcd" shape and fall through to null, same as any other
 * non-conforming string.
 * @param {string|number} pcdString
 * @returns {{ holes: number, pcd: number }|null}
 */
export function parsePcd(pcdString) {
  if (typeof pcdString !== 'string' && typeof pcdString !== 'number') return null;
  const match = String(pcdString).trim().match(/^(\d+)\s*[xX]\s*(\d+(?:\.\d+)?)$/);
  if (!match) return null;
  return { holes: Number(match[1]), pcd: Number(match[2]) };
}

/**
 * @param {string|number} pcdString - e.g. " 5 X 120 ", or a bare number
 * @returns {string} a normalised display label, e.g. " 5 X 120 " -> "5x120".
 *   Tolerant of a bare number: returns it stringified rather than "" so a
 *   caller combining it with a separately-known holes count (see
 *   {@link pcdLabelsForVariant}) never has to special-case it.
 */
export function formatPcdLabel(pcdString) {
  const parsed = parsePcd(pcdString);
  if (parsed) return `${parsed.holes}x${parsed.pcd}`;
  if (typeof pcdString === 'string') return pcdString.trim();
  if (typeof pcdString === 'number' && Number.isFinite(pcdString)) return String(pcdString);
  return '';
}

/**
 * Builds the option list for a stud-pattern radio group.
 * @param {string[]} pcdOptions
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildPcdRadioOptions(pcdOptions) {
  if (!Array.isArray(pcdOptions)) return [];
  return pcdOptions
    .filter((option) => typeof option === 'string' && option.trim() !== '')
    .map((option) => ({ value: option, label: formatPcdLabel(option) }));
}

/**
 * Whether a variant's PCD (either drilling, for dual-drilled variants such
 * as "5x108/112") matches any of a vehicle's accepted stud patterns.
 *
 * `variantPcd1`/`variantPcd2` are usually already a full "5x120" string, but
 * a `rogue.pcd_1`/`rogue.pcd_2` metafield can also be a bare number field
 * holding only the diameter (e.g. `120`) - Rogue's own schema does this. A
 * bare number/numeric string is combined with `holes` (typically the
 * variant's `rogue.holes` metafield, or the Fitment option's own holes
 * figure) to build the same "5x120" label before comparing; without a holes
 * count a bare number can't be normalised and is dropped, same as before.
 * @param {string|number|null|undefined} variantPcd1
 * @param {string|number|null|undefined} variantPcd2
 * @param {string[]} vehiclePcdOptions
 * @param {number|string|null} [holes]
 * @returns {boolean}
 */
export function pcdMatches(variantPcd1, variantPcd2, vehiclePcdOptions, holes) {
  if (!Array.isArray(vehiclePcdOptions) || vehiclePcdOptions.length === 0) return false;
  const normalisedOptions = vehiclePcdOptions.map(formatPcdLabel).filter(Boolean);
  const holesNumber = holes !== null && holes !== undefined && String(holes).trim() !== '' ? Number(holes) : NaN;

  const candidates = [variantPcd1, variantPcd2]
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
    .map((v) => {
      if (parsePcd(v)) return formatPcdLabel(v);
      if (Number.isFinite(holesNumber)) return formatPcdLabel(`${holesNumber}x${v}`);
      return '';
    })
    .filter(Boolean);

  return candidates.some((candidate) => normalisedOptions.includes(candidate));
}

/**
 * Every stud-pattern label a Fitment option value carries, e.g. "5x120 ET35
 * 7J" -> ["5x120"], or a dual-drilled "5x108/112 ET42 7.5J" -> ["5x108",
 * "5x112"]. Returns [] for a value that doesn't start with a "holes x pcd"
 * token (or isn't a string at all).
 * @param {string|null|undefined} fitmentValue
 * @returns {string[]}
 */
export function pcdLabelsFromFitmentValue(fitmentValue) {
  if (typeof fitmentValue !== 'string') return [];
  const token = fitmentValue.trim().split(/\s+/)[0] || '';
  const match = token.match(/^(\d+)\s*[xX]\s*(.+)$/);
  if (!match) return [];
  const holes = match[1];
  return match[2]
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((pcd) => formatPcdLabel(`${holes}x${pcd}`));
}

/**
 * The stud-pattern label(s) a variant carries, for matching against a
 * vehicle's `pcd_options`. Preferred source: the variant's own Fitment
 * option value (see {@link pcdLabelsFromFitmentValue}) - it already carries
 * both the holes count and the diameter(s) together, so it's correct even
 * when `rogue.pcd_1`/`rogue.pcd_2` are bare-number metafields. Falls back to
 * `rogue.holes` + `rogue.pcd_1`/`rogue.pcd_2` (formatted the same way
 * {@link pcdMatches} does) only when the product has no Fitment option or
 * its value doesn't parse.
 * @param {RogueVariantMeta} variant
 * @param {RogueOptionPositions} positions
 * @returns {string[]}
 */
export function pcdLabelsForVariant(variant, positions) {
  const fromFitment = pcdLabelsFromFitmentValue(variantOptionValue(variant, positions, 'fitment'));
  if (fromFitment.length > 0) return fromFitment;

  const holes = variant?.holes;
  const holesNumber = holes !== null && holes !== undefined && String(holes).trim() !== '' ? Number(holes) : NaN;
  const labels = [];
  for (const raw of [variant?.pcd_1, variant?.pcd_2]) {
    if (raw === null || raw === undefined || String(raw).trim() === '') continue;
    if (parsePcd(raw)) {
      labels.push(formatPcdLabel(raw));
    } else if (Number.isFinite(holesNumber)) {
      labels.push(formatPcdLabel(`${holesNumber}x${raw}`));
    }
  }
  return labels;
}

/**
 * Review finding (Minor): the Specification disclosure's "Fitment for your
 * van" cell showed only the PCD ("5x118") - {@link pcdLabelsForVariant}
 * deliberately strips ET/width off for stud-pattern MATCHING, a different
 * job, so RogueFitmentGate.updateSpec reused it and lost them along the
 * way. The resolved variant's raw Fitment option value already carries all
 * three space-separated ("5x118 ET45 7J"); this just re-joins them with
 * commas for the spec table ("5x118, ET45, 7J"), falling back to the PCD-
 * only labels when there's no parseable Fitment value at all (the same
 * fallback pcdLabelsForVariant itself uses for a product with no Fitment
 * option).
 * @param {string|null|undefined} fitmentValue - the resolved variant's raw Fitment option value
 * @param {string[]} [pcdLabels] - pcdLabelsForVariant's own fallback labels
 * @returns {string}
 */
export function resolvedFitmentLabel(fitmentValue, pcdLabels) {
  const parts = typeof fitmentValue === 'string' ? fitmentValue.trim().split(/\s+/).filter(Boolean) : [];
  if (parts.length > 0) return parts.join(', ');
  return Array.isArray(pcdLabels) && pcdLabels.length > 0 ? pcdLabels.join(', ') : '';
}

/**
 * The master "does this variant fit this vehicle" check, used everywhere a
 * Colour/Size product needs to know which variants are candidates for the
 * stored van (resolving the Fitment variant, deciding which Sizes to offer,
 * greying out a Finish swatch).
 *
 * Order of preference (fix for the bug where every product reported "not
 * available" despite a correct `fits_vehicles` list): when the variant
 * carries a `rogue.fits_vehicles` list, that list is authoritative
 * - inclusion of the stored vehicle's slug decides it, full stop, before any
 * PCD reasoning runs. An empty list explicitly fits no vehicles. Only when
 * `fits_vehicles` is absent does this
 * fall back to comparing {@link pcdLabelsForVariant} against the vehicle's
 * `pcd_options`.
 * @param {RogueVariantMeta} variant
 * @param {RogueOptionPositions} positions
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {boolean}
 */
export function variantFitsVehicle(variant, positions, vehicle) {
  if (!vehicle) return false;

  if (Array.isArray(variant?.fits_vehicles)) {
    return variant.fits_vehicles.includes(vehicle.slug);
  }

  const vehicleLabels = Array.isArray(vehicle.pcd_options) ? vehicle.pcd_options.map(formatPcdLabel).filter(Boolean) : [];
  if (vehicleLabels.length === 0) return false;

  const variantLabels = pcdLabelsForVariant(variant, positions);
  return variantLabels.some((label) => vehicleLabels.includes(label));
}

/* -------------------------------------------------------------------------
 * Pure functions: product options (Colour / Size / Fitment), read by name.
 *
 * One Shopify product per wheel DESIGN, not per design+colour (2026-09-13
 * decision). Every Rogue product has exactly three options, always in this
 * order: Colour (customer choice, swatches), Size (customer choice only
 * when more than one size fits the stored vehicle, rendered as pills),
 * Fitment (never shown to the customer - resolved from the stored
 * vehicle's PCD options against variant metafields rogue.pcd_1/pcd_2).
 *
 * Positions are still looked up BY NAME rather than assumed (index 0/1/2)
 * so a product with options in a different order, or missing one, still
 * degrades sanely instead of silently mismatching values.
 * ---------------------------------------------------------------------- */

/**
 * @typedef {object} RogueOptionPositions
 * @property {number} colour - index into a variant's `options` array, or -1 if absent
 * @property {number} size
 * @property {number} fitment
 */

/**
 * @param {string[]} optionNames - `product.options` (names only, in position order)
 * @returns {RogueOptionPositions}
 */
export function optionPositionsByName(optionNames) {
  const positions = { colour: -1, size: -1, fitment: -1 };
  if (!Array.isArray(optionNames)) return positions;

  optionNames.forEach((name, index) => {
    const normalised = typeof name === 'string' ? name.trim().toLowerCase() : '';
    if (normalised === 'colour' || normalised === 'color') positions.colour = index;
    else if (normalised === 'size') positions.size = index;
    else if (normalised === 'fitment') positions.fitment = index;
  });

  return positions;
}

/**
 * @typedef {object} RogueVariantMeta
 * @property {string} id
 * @property {string[]} options - option values in product-option order
 * @property {boolean} [available]
 * @property {string|null} [image]
 * @property {string[]} [fits_vehicles] - authoritative vehicle slugs this variant fits, when the platform has set it
 * @property {string|number|null} [pcd_1] - a full "5x120" string, or a bare diameter number/string
 * @property {string|number|null} [pcd_2]
 * @property {string|number|null} [holes] - `rogue.holes`, needed to normalise a bare-number pcd_1/pcd_2
 * @property {boolean} [is_preorder]
 * @property {string|null} [preorder_due_month]
 * @property {string|null} [due_in] Paul's "Due In" wording from the Wheel Database ("Mid November").
 */

/**
 * @param {RogueVariantMeta} variant
 * @param {RogueOptionPositions} positions
 * @param {'colour'|'size'|'fitment'} key
 * @returns {string|null}
 */
export function variantOptionValue(variant, positions, key) {
  const index = positions?.[key];
  if (index == null || index === -1 || !Array.isArray(variant?.options)) return null;
  const value = variant.options[index];
  return typeof value === 'string' && value !== '' ? value : null;
}

/* -------------------------------------------------------------------------
 * Finish swatch colours.
 *
 * The single source of truth for "what colour is a disc for the finish
 * called X". Both sides of the storefront read it: the collection card
 * colour dots (snippets/rogue-product-card.liquid, painted by
 * <rogue-product-card> below) and the product page 44px finish swatches
 * (snippets/rogue-fitment-gate.liquid). Inlined here rather than imported
 * from its own module - see the file header for why.
 *
 * Values are taken from the Direction A artboards
 * (design/storefront-direction/VehicleResults.dc.html and ProductPage.dc.html).
 * A variant image always wins over these discs where one exists; the discs
 * are the fallback, and the neutral grey is the fallback's fallback.
 * ---------------------------------------------------------------------- */

/**
 * Flat finishes: name (lower case) -> CSS colour.
 * @type {Record<string, string>}
 */
export const ROGUE_COLOUR_SWATCH_MAP = {
  black: '#141416',
  'gloss black': '#0b0b0c',
  'matte black': '#1b1b1e',
  'matt black': '#1b1b1e',
  'satin black': '#141416',
  'diamond black': '#1a1a1a',
  gunmetal: '#4a4a50',
  'grey and gunmetal': '#6e6e74',
  grey: '#6e6e74',
  gray: '#6e6e74',
  anthracite: '#3a4045',
  graphite: '#46484a',
  silver: '#c7c7c7',
  'gloss silver': '#cfcfcf',
  titanium: '#8d8f92',
  'titanium grey': '#8d8f92',
  'titanium gray': '#8d8f92',
  bronze: '#8a6a3d',
  'satin bronze': '#8a6a3d',
  gold: '#b5922f',
  sand: '#c9b99a',
  white: '#f1efea',
  'gloss white': '#f4f2ee',
  red: '#9e2b25',
};

/** Fallback swatch colour for a name not in {@link ROGUE_COLOUR_SWATCH_MAP}. */
export const ROGUE_NEUTRAL_SWATCH_COLOUR = '#9a958c';

/**
 * The face colour used for the centre of a "coloured lip" finish. The
 * artboards draw those as a dark disc with a coloured rim.
 */
export const ROGUE_LIP_FACE_COLOUR = '#141416';

/**
 * Lip finishes: the trailing word is "lip" and the leading word(s) name the
 * rim colour, e.g. "Bronze Lip" -> a #141416 face with an #8a6a3d rim.
 * @type {Record<string, string>}
 */
export const ROGUE_LIP_COLOUR_MAP = {
  bronze: '#8a6a3d',
  red: '#9e2b25',
  silver: '#c7c7c7',
  gold: '#b5922f',
  white: '#f1efea',
  grey: '#6e6e74',
  gunmetal: '#4a4a50',
  sand: '#c9b99a',
};

/**
 * @param {unknown} name
 * @returns {string} the name lower-cased and trimmed, or '' for non-strings
 */
export function normaliseFinishName(name) {
  return typeof name === 'string' ? name.trim().toLowerCase() : '';
}

/**
 * Whether a finish name describes a coloured lip ("Bronze Lip", "Red lip").
 * @param {unknown} name
 * @returns {boolean}
 */
export function isLipFinish(name) {
  return /\slip$/.test(normaliseFinishName(name));
}

/**
 * A flat CSS colour for a finish name. Lip finishes report their rim colour
 * here; {@link colourSwatchBackground} is what actually draws them.
 * @param {unknown} name
 * @returns {string} a CSS colour
 */
export function colourSwatchColor(name) {
  const normalised = normaliseFinishName(name);
  if (normalised === '') return ROGUE_NEUTRAL_SWATCH_COLOUR;
  if (ROGUE_COLOUR_SWATCH_MAP[normalised]) return ROGUE_COLOUR_SWATCH_MAP[normalised];

  if (isLipFinish(normalised)) {
    const rim = normalised.replace(/\slip$/, '');
    if (ROGUE_LIP_COLOUR_MAP[rim]) return ROGUE_LIP_COLOUR_MAP[rim];
    if (ROGUE_COLOUR_SWATCH_MAP[rim]) return ROGUE_COLOUR_SWATCH_MAP[rim];
  }

  return ROGUE_NEUTRAL_SWATCH_COLOUR;
}

/**
 * A CSS `background` value for a finish name: a radial gradient for a lip
 * finish (dark face, coloured rim - the artboard treatment), a flat colour
 * for everything else.
 * @param {unknown} name
 * @returns {string}
 */
export function colourSwatchBackground(name) {
  const colour = colourSwatchColor(name);
  if (!isLipFinish(name)) return colour;
  return `radial-gradient(circle at 50% 50%, ${ROGUE_LIP_FACE_COLOUR} 0 58%, ${colour} 60%)`;
}

/**
 * @typedef {object} RogueColourSwatch
 * @property {string} value - the Colour option value, e.g. "Gunmetal"
 * @property {string|null} image - a variant image to use as the swatch thumbnail, when one exists
 * @property {string} color - a flat CSS colour to use when there is no image
 * @property {string} background - a CSS `background` value (a lip gradient for "... Lip" finishes)
 * @property {boolean} available - true if any variant with this colour is available
 */

/**
 * Builds the swatch list for the Colour option: one entry per distinct
 * Colour value, in first-seen order, each with a thumbnail image (from the
 * first variant of that colour that has one) or a colour-map disc.
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @returns {RogueColourSwatch[]}
 */
export function buildColourSwatches(variants, positions) {
  if (!Array.isArray(variants) || positions.colour === -1) return [];

  /** @type {Map<string, RogueColourSwatch>} */
  const byValue = new Map();

  for (const variant of variants) {
    const value = variantOptionValue(variant, positions, 'colour');
    if (!value) continue;

    if (!byValue.has(value)) {
      byValue.set(value, {
        value,
        image: null,
        color: colourSwatchColor(value),
        background: colourSwatchBackground(value),
        available: false,
      });
    }
    const entry = byValue.get(value);
    if (!entry.image && variant.image) entry.image = variant.image;
    if (variant.available) entry.available = true;
  }

  return Array.from(byValue.values());
}

/**
 * @param {string|null|undefined} value - a Size option value, e.g. "17" or "17 inch"
 * @returns {string} e.g. "17 inch"
 */
export function formatSizeLabel(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (/inch/i.test(trimmed)) return trimmed;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed} inch`;
  return trimmed;
}

/**
 * Distinct Size values available for a given Colour (or across all
 * variants, when colourValue is null), in first-seen order.
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @param {string|null} colourValue
 * @returns {string[]}
 */
export function sizesForColour(variants, positions, colourValue) {
  if (!Array.isArray(variants) || positions.size === -1) return [];

  const seen = new Set();
  const result = [];
  for (const variant of variants) {
    if (colourValue != null) {
      const variantColour = variantOptionValue(variant, positions, 'colour');
      if (variantColour !== colourValue) continue;
    }
    const value = variantOptionValue(variant, positions, 'size');
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

/**
 * Distinct Size values, among variants of the given Colour, that have at
 * least one Fitment value matching the stored vehicle's PCD options.
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @param {string|null} colourValue
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {string[]}
 */
export function fittingSizesForVehicle(variants, positions, colourValue, vehicle) {
  if (!Array.isArray(variants) || positions.size === -1 || !vehicle) return [];

  const seen = new Set();
  const result = [];
  for (const variant of variants) {
    if (colourValue != null) {
      const variantColour = variantOptionValue(variant, positions, 'colour');
      if (variantColour !== colourValue) continue;
    }
    if (!variantFitsVehicle(variant, positions, vehicle)) continue;
    const value = variantOptionValue(variant, positions, 'size');
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

/**
 * @typedef {object} RogueSizeSelectorState
 * @property {boolean} visible - whether the Size pills should render at all
 * @property {string[]} sizes - the Size values to offer (only meaningful when visible)
 * @property {string|null} autoSelected - the Size value to select automatically when not visible
 * @property {string|null} autoText - the text to show in place of the pills when not visible
 */

/**
 * Decides how the Size option renders for the current Colour + stored
 * vehicle (Appendix: "Size ... shown as pills ... visible only when the
 * fitting variants for the stored vehicle span more than one size;
 * otherwise auto-selected and shown as text").
 *
 * - No stored vehicle: we don't yet know which size fits, so every size
 *   that exists for this colour is offered; if there's only one, it's
 *   auto-selected and shown as plain text (no "fits your van" clause -
 *   nothing has been checked against a vehicle yet).
 * - Stored vehicle: only sizes with a PCD-matching variant count. More
 *   than one -> pills for just those sizes. Exactly one -> auto-selected,
 *   shown as "{size}, fits your {display_name}". None -> nothing to
 *   select; the fitment gate reports the product as not available for
 *   this vehicle.
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @param {string|null} colourValue
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {RogueSizeSelectorState}
 */
export function sizeSelectorState(variants, positions, colourValue, vehicle) {
  if (positions.size === -1) return { visible: false, sizes: [], autoSelected: null, autoText: null };

  if (!vehicle) {
    const sizes = sizesForColour(variants, positions, colourValue);
    if (sizes.length > 1) return { visible: true, sizes, autoSelected: null, autoText: null };
    if (sizes.length === 1) {
      return { visible: false, sizes, autoSelected: sizes[0], autoText: formatSizeLabel(sizes[0]) };
    }
    return { visible: false, sizes: [], autoSelected: null, autoText: null };
  }

  const fitting = fittingSizesForVehicle(variants, positions, colourValue, vehicle);
  if (fitting.length > 1) return { visible: true, sizes: fitting, autoSelected: null, autoText: null };
  if (fitting.length === 1) {
    return {
      visible: false,
      sizes: fitting,
      autoSelected: fitting[0],
      // Short form (spec 2.3), not the full display_name - matches the fit
      // line above it, which uses shortVanLabel for the same "van short".
      autoText: `${formatSizeLabel(fitting[0])}, fits your ${shortVanLabel(vehicle)}`,
    };
  }
  return { visible: false, sizes: [], autoSelected: null, autoText: null };
}

/**
 * Which Size pill should be pre-selected when the pills are visible (more
 * than one size fits) - walk finding (Daniel, 25 Sep 2026): the Transporter
 * T5-T6.1 opened Rocky at 16 inch, the size Shopify happened to list first,
 * even though the van's only tyre size on file is 17 inch - "we don't have
 * your van's tyre size" on the very first size a shopper saw, for a wheel
 * that fits it fine at 17. This is the fallback order once
 * RogueFitmentGate.renderSizeSelector has decided a preference even applies
 * at all - a `?variant=` link in the URL outranks everything here (see its
 * own `forcePreferred`/`_sizeSeeded` doc comment: this function is never
 * even called for that first render). Order of preference (H3, 25 Sep
 * review - the shopper's own stored choice now outranks the statistic,
 * since a shopper who picked 16 inch and is shown 17 can add the wrong size
 * without noticing):
 *
 *  1. The shopper's own stored `rim_size` from the vehicle selector, when
 *     it's among the fitting sizes - they said so themselves, twice
 *     (selector, then here), and a statistic about OTHER owners should
 *     never override that.
 *  2. Failing that, `popular_rim` (the platform is adding this - what
 *     Paul's order history shows most owners of this van actually buy,
 *     C:\...\data\paul-sheets\tyre-sizes-from-orders.csv), when this wheel
 *     offers it as a fitting size. Read defensively (enrichVehicle only
 *     merges it in when the metaobject carries it - see its own doc
 *     comment).
 *  3. Failing that, a fitting size this van has a real tyre size on file for
 *     (tyreSizeIsKnown) - opening on ANY size we can't sell a tyre for is the
 *     same bug even without popular_rim data yet.
 *  4. Failing that, the first fitting size.
 * @param {RogueSizeSelectorState} state
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {string|null}
 */
export function preferredFittingSize(state, vehicle) {
  if (!state?.visible) return null;

  const rimSize = vehicle && vehicle.rim_size ? formatSizeLabel(String(vehicle.rim_size)) : '';
  if (rimSize && state.sizes.includes(rimSize)) return rimSize;

  const popularRim =
    vehicle && vehicle.popular_rim !== null && vehicle.popular_rim !== undefined && String(vehicle.popular_rim).trim() !== ''
      ? formatSizeLabel(String(vehicle.popular_rim))
      : '';
  if (popularRim && state.sizes.includes(popularRim)) return popularRim;

  const knownTyreSize = state.sizes.find((size) => tyreSizeIsKnown(vehicle, size));
  if (knownTyreSize) return knownTyreSize;

  return state.sizes[0] || null;
}

/**
 * Extracts the offset (ET) figure from a Fitment option value such as
 * "5x120 ET35 7J" or "5x108/112 ET35 7J". Returns null when none is found.
 * @param {string|null|undefined} value
 * @returns {number|null}
 */
export function parseOffsetFromFitmentValue(value) {
  if (typeof value !== 'string') return null;
  const match = value.match(/ET\s*(-?\d+(?:\.\d+)?)/i);
  return match ? Number(match[1]) : null;
}

/**
 * @typedef {object} RogueVariantResolution
 * @property {RogueVariantMeta|null} variant - the resolved variant, or null if nothing matches Colour/Size
 * @property {boolean|null} fits - true/false once checked against a stored vehicle; null with no vehicle stored
 */

/**
 * Resolves the variant for a Colour + Size selection. Fitment is never
 * chosen by the customer - it's resolved here by matching the stored
 * vehicle's PCD options against each candidate's rogue.pcd_1/rogue.pcd_2.
 *
 * NOTE for the platform team: the "nearest the middle of the vehicle's
 * offset range" tie-break only activates once the rogue_vehicle metaobject
 * carries an offset range (`offset_min`/`offset_max` - not in Appendix B
 * yet, and not read by snippets/rogue-vehicle-data.liquid today). Until
 * that field exists every vehicle falls through to "first matching Fitment
 * value", which is fine while most models only have one matching drilling
 * per size, but should be revisited once a multi-fitment model is seeded
 * for a vehicle with a real offset range.
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @param {{ colourValue?: string|null, sizeValue?: string|null }} selection
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {RogueVariantResolution}
 */
export function resolveVariant(variants, positions, selection, vehicle) {
  const { colourValue = null, sizeValue = null, quantity = 1, preferredVariantId = null } = selection || {};
  let candidates = Array.isArray(variants) ? variants.slice() : [];

  if (positions.colour !== -1 && colourValue != null) {
    candidates = candidates.filter((variant) => variantOptionValue(variant, positions, 'colour') === colourValue);
  }
  if (positions.size !== -1 && sizeValue != null) {
    candidates = candidates.filter((variant) => variantOptionValue(variant, positions, 'size') === sizeValue);
  }

  if (!vehicle) {
    const buyable = candidates.filter((variant) => !variantQuantityError(variant, quantity));
    const pool = buyable.length ? buyable : candidates.filter((variant) => variant.available !== false);
    return { variant: pool.find((variant) => String(variant.id) === String(preferredVariantId)) || pool[0] || candidates[0] || null, fits: null };
  }

  let fitting = candidates.filter((variant) => variantFitsVehicle(variant, positions, vehicle));

  if (fitting.length === 0) {
    return { variant: candidates[0] || null, fits: false };
  }
  // Stock can choose between already-approved drillings; it can never add a fit.
  const quantityReady = fitting.filter((variant) => !variantQuantityError(variant, quantity));
  const available = fitting.filter((variant) => variant.available !== false);
  fitting = quantityReady.length ? quantityReady : available.length ? available : fitting;
  const preferred = fitting.find((variant) => String(variant.id) === String(preferredVariantId));
  if (preferred) return { variant: preferred, fits: true };
  if (fitting.length === 1) {
    return { variant: fitting[0], fits: true };
  }

  const offsetMin = typeof vehicle.offset_min === 'number' ? vehicle.offset_min : null;
  const offsetMax = typeof vehicle.offset_max === 'number' ? vehicle.offset_max : null;

  if (offsetMin != null && offsetMax != null) {
    const middle = (offsetMin + offsetMax) / 2;
    let best = fitting[0];
    let bestDistance = Infinity;
    for (const candidate of fitting) {
      const offset = parseOffsetFromFitmentValue(variantOptionValue(candidate, positions, 'fitment'));
      if (offset == null) continue;
      const distance = Math.abs(offset - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }
    return { variant: best, fits: true };
  }

  // No offset range on the vehicle (the common case today - see the note
  // above): first matching Fitment value.
  return { variant: fitting[0], fits: true };
}

/* -------------------------------------------------------------------------
 * Pure functions: fitment gate state.
 * ---------------------------------------------------------------------- */

/**
 * @typedef {object} VariantFitmentMeta
 * @property {string[]} fits_vehicles
 * @property {string|null} [pcd_1]
 * @property {string|null} [pcd_2]
 * @property {boolean} [is_preorder]
 * @property {string|null} [preorder_due_month]
 * @property {string|null} [due_in] Paul's "Due In" wording from the Wheel Database ("Mid November").
 */

/**
 * @typedef {'no-vehicle'|'fits'|'not-fits'|'unlisted'|'manual-review'} FitmentState
 */

/**
 * Core gating decision: given the shopper's stored vehicle (or none) and a
 * variant's `fits_vehicles` metafield, decide what the product page should
 * show. An unlisted van (see isUnlistedVehicle) is neither "fits" nor
 * "not-fits" - there is no PCD data to check it against - it gets its own
 * state so the badge and add-to-cart gating never claim a false fit; see
 * RogueFitmentGate.computeStateFromResolution for the Colour/Size-driven
 * equivalent.
 * @param {RogueVehicleSelection|null} vehicle
 * @param {VariantFitmentMeta|null} variantMeta
 * @returns {FitmentState}
 */
export function computeFitmentState(vehicle, variantMeta) {
  if (!vehicle) return 'no-vehicle';
  if (isUnlistedVehicle(vehicle)) return 'unlisted';
  const fitsList = Array.isArray(variantMeta?.fits_vehicles) ? variantMeta.fits_vehicles : [];
  if (!fitsList.includes(vehicle.slug)) return 'not-fits';
  return requiresManualReview(vehicle) ? 'manual-review' : 'fits';
}

/**
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {boolean} whether the stud-pattern verification question should show
 */
export function needsStudPatternVerification(vehicle) {
  return Boolean(vehicle && vehicle.verification_mode === 'ask_stud_pattern');
}

/**
 * Review 2, finding 11: "Confirm your stud pattern" is only a real question
 * once there is a real choice - two or more patterns to choose between (see
 * {@link buildPcdRadioOptions}). A vehicle needing verification but with one
 * pattern or none (every unlisted van - S3.2a's sentinel always carries
 * `pcd_options: []`, since there is no PCD data at all for a van Rogue
 * doesn't recognise) offered only "Not sure, check it for me", which review
 * finding 11 called a fake callout rather than a question. The caller drops
 * the whole block in that case and still lands the order on the hold path
 * as if "not sure" had been picked (see RogueFitmentGate.updateStudPatternSection/
 * updateCartAttributesFields) - Rogue checks fitment before anything ships
 * either way.
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {boolean}
 */
export function hasStudPatternChoice(vehicle) {
  return needsStudPatternVerification(vehicle) && buildPcdRadioOptions(vehicle?.pcd_options).length >= 2;
}

/**
 * Builds the cart attributes + line item properties for the "not sure of my
 * stud pattern" add-to-cart flow.
 * @param {{ vehicle: RogueVehicleSelection|null, studPattern: string|null, notSure: boolean }} params
 * @returns {{ attributes: Record<string, string>, properties: Record<string, string> }}
 */
export function buildVerificationCartData({ vehicle, studPattern, notSure }) {
  if (requiresManualReview(vehicle)) {
    return {
      attributes: { fitment_verification: 'requested' },
      // 25 Oct 2026 (Daniel): the visible value reads as a free service, not a
      // hurdle. The key, `_fitment_verification` and the cart attribute are
      // unchanged; nothing in the platform or Flow reads this value.
      properties: { _fitment_verification: 'requested', Vehicle: vehicle?.display_name || '', 'Fitment review': MANUAL_REVIEW_LINE_VALUE },
    };
  }
  if (!notSure) {
    return { attributes: {}, properties: {} };
  }

  return {
    attributes: { fitment_verification: 'requested' },
    properties: {
      _fitment_verification: 'requested',
      Vehicle: vehicle?.display_name || '',
      'Stud pattern': studPattern && studPattern.trim() !== '' ? studPattern : 'unknown',
    },
  };
}

/**
 * Paul's "Due In" wording ("Mid November") as it reads mid-sentence: a leading
 * Mid, Early, Late, End, Start or Beginning goes lower case ("due mid
 * November"); a month name keeps its capital.
 *
 * @param {unknown} text
 * @returns {string}
 */
export function dueInPhrase(text) {
  const clean = typeof text === 'string' ? text.trim().replace(/\s+/g, ' ') : '';
  return clean.replace(/^(mid|early|late|end|start|beginning)\b/i, (word) => word.toLowerCase());
}

/**
 * @param {VariantFitmentMeta|null} variantMeta
 * @returns {{ isPreorder: boolean, dueMonth: string, dueIn: string }}
 */
export function preorderBadgeData(variantMeta) {
  // 27 Oct 2026 (Paul's fixes rows 12 and 24): a "Due In" date is stock on
  // its way, sold up to the count Paul gives. It is a pre-order however many
  // that is, so the shopper knows it will take longer.
  const dueIn = dueInPhrase(variantMeta?.due_in);
  if (dueIn !== '') return { isPreorder: true, dueMonth: dueIn, dueIn };
  // 26 Oct 2026 (Paul): a wheel with stock sells as in stock. Pre-order is for a
  // line Rogue has none of yet, whatever the sheet's Stock Type still says.
  const inStock = variantMeta?.inventory_management === 'shopify' && Number(variantMeta?.inventory_quantity) > 0;
  const isPreorder = Boolean(variantMeta?.is_preorder) && !inStock;
  const dueMonth = typeof variantMeta?.preorder_due_month === 'string' ? variantMeta.preorder_due_month : '';
  return { isPreorder, dueMonth, dueIn: '' };
}

/* -------------------------------------------------------------------------
 * Query-param deep link: `?vehicle=slug`
 * ---------------------------------------------------------------------- */

/**
 * @param {string} search - `location.search`, e.g. "?vehicle=vw-t6-2015-on"
 * @returns {string|null}
 */
export function vehicleSlugFromSearch(search) {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const slug = params.get('vehicle');
  return slug && slug.trim() !== '' ? slug.trim() : null;
}


/* -------------------------------------------------------------------------
 * Pure functions: shop surfaces (collection cards, collection head, product
 * buy panel). Everything below is DOM-free and unit tested by
 * tests/rogue-fitment.test.js.
 * ---------------------------------------------------------------------- */

/** The number of wheels Rogue sells as a set. */
export const ROGUE_SET_SIZE = 4;

const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
];

/**
 * Spells a small number out in words ("six"), falling back to the numeral
 * above twelve. Used for "a set of four" and "Six wheel designs ...".
 * @param {number} value
 * @returns {string}
 */
/**
 * Formats a list as an Oxford-free serial list - "16", "16 and 17", or
 * "16, 17, 18, 20 and 22" - the same algorithm as
 * snippets/rogue-serial-list.liquid, so a card built from a JSON fetch (e.g.
 * sections/rogue-popular-for-van.liquid, which cannot render a Liquid
 * snippet client side) reads identically to one Liquid renders. Blank
 * entries are skipped.
 * @param {Array<string|number>} items
 * @returns {string}
 */
export function serialList(items) {
  const values = (Array.isArray(items) ? items : [])
    .map((item) => (item === null || item === undefined ? '' : String(item)))
    .filter((item) => item.trim() !== '');

  if (values.length === 0) return '';
  if (values.length === 1) return values[0];
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

export function numberWord(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return '';
  const rounded = Math.round(value);
  return NUMBER_WORDS[rounded] || String(rounded);
}

/**
 * Formats a `rogue.load_rating` metafield value ("1200", 1200, or already
 * "1200 kg") as "1,200 kg": a thousands separator plus the unit, wherever
 * the load rating is shown (the product page spec table, and any card or
 * gate copy that reads the same field). Returns null for anything that
 * doesn't contain a number, so the caller can fall back to its own
 * placeholder copy ("[Load rating]") instead of printing "null kg".
 * @param {string|number|null|undefined} raw
 * @returns {string|null}
 */
export function formatLoadRating(raw) {
  if (raw === null || raw === undefined) return null;
  const digits = String(raw).replace(/[^0-9]/g, '');
  if (digits === '') return null;
  const num = Number(digits);
  if (!Number.isFinite(num)) return null;
  return `${num.toLocaleString('en-GB')} kg`;
}

/**
 * The wheel product page's H1 parts (25 Oct 2026): `16" CC1`, `SATIN BLACK`,
 * `5000KG LOAD RATING`. The load rating is the SET rating, the per-wheel
 * `rogue.load_rating` times four (1,250 kg a wheel reads 5000KG). With no
 * size chosen yet the model carries the size range (`CC1 16-17"`, en dash) and
 * the load part reads just `LOAD RATED`, because the rating can differ by size.
 * @param {{model: string, size?: string|null, sizes?: string[], finish?: string|null, loadRaw?: string|number|null}} input
 * @returns {{main: string, finish: string, load: string}}
 */
export function wheelTitleParts({ model, size, sizes, finish, loadRaw }) {
  const bare = (value) => String(value ?? '').replace(/inch/gi, '').replace(/"/g, '').trim();
  const chosen = bare(size);
  const name = String(model ?? '').trim();
  let main = name;
  if (chosen !== '') {
    main = `${chosen}" ${name}`;
  } else {
    const numbers = (Array.isArray(sizes) ? sizes : []).map((v) => parseFloat(bare(v))).filter((n) => Number.isFinite(n));
    if (numbers.length > 0) {
      const low = Math.min(...numbers);
      const high = Math.max(...numbers);
      main = `${name} ${low === high ? low : `${low}–${high}`}"`;
    }
  }
  const digits = String(loadRaw ?? '').replace(/[^0-9]/g, '');
  let load = 'LOAD RATED';
  if (chosen !== '' && digits !== '' && Number(digits) > 0) load = `${Number(digits) * 4}KG LOAD RATING`;
  return { main, finish: String(finish ?? '').trim(), load };
}

/**
 * Merges the extra fields of a full `rogue_vehicle` record onto the small
 * stored selection, so callers get `offset_min` / `offset_max` /
 * `tyre_sizes_json` / `centre_bore` / `popular_rim` when the metaobject
 * carries them and simply nothing when it does not (the platform team is
 * adding the offset range, and now `popular_rim`, over time - read each one
 * defensively, never require it).
 *
 * The active catalogue owns fitment facts; storage owns only the chosen slug/rim.
 * @param {RogueVehicleSelection|null} stored
 * @param {Array<Record<string, unknown>>} vehicles - the `[data-rogue-vehicles]` payload
 * @returns {RogueVehicleSelection|null}
 */
export function enrichVehicle(stored, vehicles) {
  if (!stored || !stored.slug) return null;
  if (isUnlistedVehicle(stored)) return stored;
  const full = findVehicleBySlug(Array.isArray(vehicles) ? vehicles : [], stored.slug);
  if (!full) return null;
  const enriched = { ...full };
  if (stored.rim_size != null) enriched.rim_size = stored.rim_size;
  for (const key of ['offset_min', 'offset_max']) {
    if (typeof enriched[key] === 'string') enriched[key] = Number(enriched[key]);
    if (typeof enriched[key] !== 'number' || Number.isNaN(enriched[key])) delete enriched[key];
  }
  return enriched;
}

/* ---------------------------- product tags ----------------------------- */

/**
 * Strips the `pcd-` prefix from a stud-pattern tag and normalises what is
 * left, so `pcd-5x120` and `PCD-5X120` both become `5x120`.
 * @param {unknown} tag
 * @returns {string|null}
 */
export function pcdFromTag(tag) {
  if (typeof tag !== 'string') return null;
  const match = tag.trim().match(/^pcd-(.+)$/i);
  if (!match) return null;
  const label = formatPcdLabel(match[1]);
  return label === '' ? null : label;
}

/**
 * Every stud pattern a product carries, read from its `pcd-*` tags. A dual
 * drilled design carries one tag per drilling.
 * @param {string[]} tags
 * @returns {string[]}
 */
export function pcdTagsFromProductTags(tags) {
  if (!Array.isArray(tags)) return [];
  const seen = new Set();
  const result = [];
  for (const tag of tags) {
    const pcd = pcdFromTag(tag);
    if (!pcd || seen.has(pcd)) continue;
    seen.add(pcd);
    result.push(pcd);
  }
  return result;
}

/**
 * Collection-card fitment test: does any of the product's `pcd-*` tags match
 * one of the stored vehicle's accepted stud patterns? Tags are the only
 * fitment signal a collection card has - variant metafields are not rendered
 * into the grid.
 * @param {string[]} tags
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {boolean}
 */
export function productFitsVehicleByTags(tags, vehicle) {
  if (!vehicle) return false;
  const vehiclePcds = Array.isArray(vehicle.pcd_options)
    ? vehicle.pcd_options.map(formatPcdLabel).filter(Boolean)
    : [];
  if (vehiclePcds.length === 0) return false;
  return pcdTagsFromProductTags(tags).some((pcd) => vehiclePcds.includes(pcd));
}

/** Card eligibility comes from the same approved variant lists as the PDP. */
export function productFitsVehicle(card, vehicle) {
  if (!vehicle || isUnlistedVehicle(vehicle)) return false;
  if (Object.prototype.hasOwnProperty.call(card || {}, 'fits_vehicles')) {
    return Array.isArray(card.fits_vehicles) && card.fits_vehicles.includes(vehicle.slug);
  }
  return productFitsVehicleByTags(card?.tags, vehicle);
}

/**
 * @typedef {object} RogueCardBadges
 * @property {boolean} fits
 * @property {string} fitsLabel - "" when `fits` is false
 * @property {boolean} preorder
 * @property {string} preorderLabel - "" when `preorder` is false
 */

/**
 * The two badges a collection card can carry, from the artboard: the green
 * "Fits your {van}" tick and the orange "Pre-order, due {month}".
 *
 * `vanShortName` is the short form the artboard uses in the badge ("T6"),
 * falling back to the full display name when no short name is stored.
 * @param {{ tags?: string[], isPreorder?: boolean, preorderDueMonth?: string|null }} card
 * @param {RogueVehicleSelection|null} vehicle
 * @param {string} [vanShortName]
 * @returns {RogueCardBadges}
 */
/**
 * C2 (31 Oct 2026): a card on a van's own collection page hides its "Fits
 * your ..." badge (the line above the grid says it for every wheel). Since
 * the van mix-up sweep (31 Oct) that holds whichever van is stored: a Caddy
 * page badged "Fits your Transit Custom" on every card read as if the page
 * were for the stored van. The notice above the grid offers that van's wheels.
 * @param {string} ownCollectionHandle - the page's van collection, or ''
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {boolean}
 */
export function hideBadgeOnOwnCollection(ownCollectionHandle, vehicle) {
  return Boolean(ownCollectionHandle && vehicle);
}

/**
 * A van's own page seen with a different van stored (31 Oct 2026, the van
 * mix-up sweep): its lines are written for that van's owner ("All 10 fit
 * your Caddy", "load-rated for your Caddy"), which contradicts "Your vehicle
 * is set to Transit Custom" in the notice. They read "the Caddy" instead.
 * @param {string} text
 * @param {string} pageVan - the page's van, as the line names it
 * @returns {string}
 */
export function otherVanWording(text, pageVan) {
  if (typeof text !== 'string' || typeof pageVan !== 'string' || pageVan.trim() === '') return text;
  return text.split(`your ${pageVan}`).join(`the ${pageVan}`);
}

/**
 * "Pick your vehicle" buttons: any button to the vehicles page, or one marked
 * data-rogue-van-cta; never one marked data-rogue-van-cta-skip (a browse-all
 * link such as the camper page's "See every van we fit", final review 31 Oct).
 */
export const VAN_BUTTON_SELECTOR = 'a.rogue-btn[href="/pages/vehicles"]:not([data-rogue-van-cta-skip]), a[data-rogue-van-cta]';

/**
 * What a "Pick your vehicle" button becomes once a van is stored (31 Oct
 * 2026, the van mix-up sweep: the button stayed under "Selected Transit
 * Custom" and sent the shopper round the picker again). null leaves the
 * button as written: no van, or one with no wheels page of its own.
 * @param {RogueVehicleSelection|null} vehicle
 * @param {string} [collectionsRootPath]
 * @returns {{ label: string, href: string, van: string }|null}
 */
export function vanButtonTarget(vehicle, collectionsRootPath = '/collections') {
  if (!vehicle || isUnlistedVehicle(vehicle) || !vehicle.collection_handle) return null;
  return {
    label: `See wheels for your ${shortVanLabel(vehicle)}`,
    href: buildVehicleCollectionUrl(collectionsRootPath, vehicle.collection_handle, null),
    van: shortVanLabel(vehicle),
  };
}

export function collectionCardBadges(card, vehicle, vanShortName) {
  const fits = productFitsVehicle(card, vehicle);
  const van = (vanShortName && vanShortName.trim()) || (vehicle && vehicle.display_name) || '';
  const { isPreorder, dueMonth } = preorderBadgeData({
    is_preorder: card && card.isPreorder,
    preorder_due_month: card && card.preorderDueMonth,
  });

  let preorderLabel = '';
  if (isPreorder) preorderLabel = dueMonth ? 'Pre-order, due ' + dueMonth : 'Pre-order';

  return {
    fits,
    fitsLabel: fits && van !== '' ? 'Fits your ' + van : '',
    preorder: isPreorder,
    preorderLabel,
  };
}

/* -------------------------------------------------------------------------
 * Fit-only browsing (site-wide): once a van is stored, every wheel and
 * tyre listing on the site shows only what fits it, with an honest way
 * out. Every DOM class below (RogueFitFilterBar) is built on the pure
 * functions in this section - what to show, what the slim bar above the
 * grid says, and whether its toggle is offered - so the decisions
 * themselves are unit tested with no DOM at all.
 *
 * Five page "modes" cover every listing on the site:
 *   no-van          - nothing stored: unfiltered, no bar (today's behaviour).
 *   unlisted        - the "My van isn't listed" sentinel: nothing to match
 *                      against, so unfiltered, with a one-line "we'll check
 *                      it" bar.
 *   own-collection  - the page IS the stored van's own collection: every
 *                      product already fits, so unfiltered, no bar.
 *   other-van       - the page is a DIFFERENT van's own collection: not
 *                      filtered (would hide every card), a notice instead.
 *   filterable      - a generic multi-van listing (all-wheels, wheel-and-
 *                      tyre-packages, a wheels-<pcd> stud-pattern collection,
 *                      search results): filtered to fitting cards, with a
 *                      slim bar and a "show all"/"show only fitting" toggle.
 * ---------------------------------------------------------------------- */

/** @typedef {'no-van'|'unlisted'|'manual-review'|'own-collection'|'other-van'|'filterable'} RogueFitPageMode */

/** The home selector, coming back here afterwards: the same link the header's van control uses. */
export function pickVehicleHref(returnPath) {
  const path = typeof returnPath === 'string' && returnPath.startsWith('/') ? returnPath : '/';
  return `/?change_vehicle=1&return=${encodeURIComponent(path)}#rogue-vehicle-selector`;
}

export const ROGUE_FIT_MODE_NO_VAN = 'no-van';
export const ROGUE_FIT_MODE_UNLISTED = 'unlisted';
export const ROGUE_FIT_MODE_MANUAL = 'manual-review';
export const ROGUE_FIT_MODE_OWN = 'own-collection';
export const ROGUE_FIT_MODE_OTHER_VAN = 'other-van';
export const ROGUE_FIT_MODE_FILTERABLE = 'filterable';

/**
 * Whether a collection/search card is a candidate for wheel fit-only
 * filtering at all: it carries at least one pcd-* tag. A card with none (an
 * accessory, tyre, merch item, or a page/article result) is never a wheel
 * fitment thing to judge - this is what keeps "all-wheels" (a real "full
 * range" listing across every product type - see templates/collection.all.json)
 * and search results (which mix products, pages and articles) from having
 * their non-wheel cards hidden the moment any van was stored (spec item 3:
 * "Accessories and merch are never filtered"). Callers (RogueFitFilterBar,
 * RogueFitOnlyGrid) leave a non-participating card exactly as rendered:
 * never hidden, never counted toward "how many fit" either way.
 * @param {string[]} tags
 * @returns {boolean}
 */
export function isWheelFitCard(tags) {
  return pcdTagsFromProductTags(tags).length > 0;
}

/**
 * The full vehicle catalog record whose OWN collection is this handle, if
 * any - not necessarily the stored van. Used to detect "another van's
 * collection" (mode {@link ROGUE_FIT_MODE_OTHER_VAN}) and to resolve the
 * full record a "Switch to X" action stores.
 * @param {Array<Record<string, unknown>>} vehicles
 * @param {string} collectionHandle
 * @returns {Record<string, unknown>|null}
 */
export function vehicleByCollectionHandle(vehicles, collectionHandle) {
  if (!collectionHandle || !Array.isArray(vehicles)) return null;
  return vehicles.find((v) => v && v.collection_handle === collectionHandle) || null;
}

/**
 * The page mode a wheel/tyre listing is in, given the stored van, the page's
 * own collection handle (blank for search results - never anyone's own
 * collection) and the full vehicle catalog (to recognise another van's
 * collection even when it isn't the one stored).
 * @param {{ collectionHandle?: string, vehicle: RogueVehicleSelection|null, vehicles?: Array<Record<string, unknown>> }} params
 * @returns {RogueFitPageMode}
 */
export function fitFilterMode({ collectionHandle, vehicle, vehicles }) {
  if (!vehicle) return ROGUE_FIT_MODE_NO_VAN;
  if (isUnlistedVehicle(vehicle)) return ROGUE_FIT_MODE_UNLISTED;
  const handle = collectionHandle || '';
  if (vehicle.collection_handle && vehicle.collection_handle === handle) return ROGUE_FIT_MODE_OWN;
  if (vehicleByCollectionHandle(vehicles, handle)) return ROGUE_FIT_MODE_OTHER_VAN;
  return ROGUE_FIT_MODE_FILTERABLE;
}

/**
 * @typedef {object} RogueFitAction
 * @property {string} label
 * @property {string} [href] - present for a plain link action
 */

/**
 * @typedef {object} RogueListingBarState
 * @property {boolean} visible
 * @property {string} message
 * @property {boolean} applyFilter - whether non-matching cards should be hidden right now
 * @property {boolean} toggleVisible
 * @property {string} toggleLabel
 * @property {RogueFitAction|null} primary - a plain link (href set)
 * @property {RogueFitAction|null} switchAction - a button that stores another van (no href: the caller looks the record up itself)
 */

const ROGUE_HIDDEN_BAR_STATE = Object.freeze({
  visible: false,
  message: '',
  applyFilter: false,
  toggleVisible: false,
  toggleLabel: '',
  primary: null,
  switchAction: null,
});

/**
 * "Show all N wheels" / "Show only wheels that fit" (spec item 1) - the
 * wheel listing's own toggle wording, count included per the spec's literal
 * example.
 * @param {boolean} showAll
 * @param {number} totalCount
 * @returns {string}
 */
export function wheelShowAllToggleLabel(showAll, totalCount) {
  if (showAll) return 'Show only wheels that fit';
  const count = Number.isFinite(totalCount) ? totalCount : 0;
  return `Show all ${count} wheel${count === 1 ? '' : 's'}`;
}

/**
 * "Show all tyres" / "Show only tyres in your size" (spec item 4) - no
 * count, unlike the wheel toggle: the spec's own literal wording for the
 * tyre listing never names a number.
 * @param {boolean} showAll
 * @returns {string}
 */
export function tyreShowAllToggleLabel(showAll) {
  return showAll ? 'Show only tyres in your size' : 'Show all tyres';
}

/**
 * The slim bar above a wheel listing's grid (spec item 1 - "all-wheels",
 * "wheel-and-tyre-packages", every wheels-<pcd> collection, and search
 * results) plus the "another van's collection" notice (spec item 2) and the
 * "nothing to check yet" unlisted-van line (spec item 6). One function
 * covers every mode so the bar's DOM class never has to duplicate this
 * decision - it only ever renders whatever this returns.
 *
 * `singlePage` (spec item 5): a collection with more products than fit on
 * one page cannot be filtered completely client side, so a partial page is
 * never filtered at all - the notice offers the van's own collection
 * instead, same shape as the other-van notice.
 * @param {object} params
 * @param {RogueFitPageMode} params.mode
 * @param {RogueVehicleSelection|null} params.vehicle - the stored van
 * @param {Record<string, unknown>|null} [params.otherVehicle] - the page's own van, when mode is 'other-van'
 * @param {number} params.totalCount - cards on the page
 * @param {number} params.fittingCount - of those, how many fit the stored van
 * @param {boolean} params.showAll - the shopper's current toggle choice (remembered for the session)
 * @param {boolean} [params.singlePage] - false when the collection has more products than this one page shows
 * @param {string} [params.collectionPcdLabel] - e.g. "5x118", for a stud-pattern collection's "None of these fit" line
 * @param {string} [params.collectionsRootPath] - e.g. "/collections"
 * @returns {RogueListingBarState}
 */
export function wheelListingBarState({
  mode,
  vehicle,
  otherVehicle,
  totalCount,
  fittingCount,
  showAll,
  singlePage = true,
  collectionPcdLabel = '',
  collectionsRootPath = '/collections',
  pickVehicleHref = '',
}) {
  // C4 (31 Oct 2026, D19): with no van stored, a generic listing (all
  // wheels, a stud pattern) gets one "Pick your vehicle" row above the
  // filters, with the grid's own count. The caller passes the link only
  // when the page is not a van's own collection.
  if (mode === ROGUE_FIT_MODE_NO_VAN && pickVehicleHref && totalCount > 0) {
    return {
      visible: true,
      message: `${totalCount} wheel${totalCount === 1 ? '' : 's'}. Pick your vehicle and we show only the ones that fit.`,
      applyFilter: false,
      toggleVisible: false,
      toggleLabel: '',
      primary: { label: 'Pick your vehicle', href: pickVehicleHref },
      switchAction: null,
    };
  }
  if (mode === ROGUE_FIT_MODE_NO_VAN || mode === ROGUE_FIT_MODE_OWN) {
    return { ...ROGUE_HIDDEN_BAR_STATE };
  }

  if (mode === ROGUE_FIT_MODE_OTHER_VAN) {
    const yourVan = shortVanLabel(vehicle);
    const theirVan = otherVehicle ? shortVanLabel(otherVehicle) : '';
    const primary = vehicle.collection_handle
      ? { label: `See wheels for your ${yourVan}`, href: `${collectionsRootPath}/${vehicle.collection_handle}` }
      : null;
    return {
      visible: true,
      message: `This page is for the ${theirVan}. Your vehicle is set to ${yourVan}.`,
      applyFilter: false,
      toggleVisible: false,
      toggleLabel: '',
      primary,
      switchAction: theirVan ? { label: `Switch to ${theirVan}` } : null,
    };
  }

  if (mode === ROGUE_FIT_MODE_MANUAL) {
    return {
      ...ROGUE_HIDDEN_BAR_STATE, visible: true,
      message: `Order as normal for your ${manualReviewVehicleName(vehicle) || 'vehicle'}. We'll double-check the fit after you order, at no extra cost, before anything is sent.`,
      primary: { label: 'Browse all wheels', href: `${collectionsRootPath}/all-wheels` },
    };
  }

  if (mode === ROGUE_FIT_MODE_UNLISTED) {
    return {
      visible: true,
      message: `We'll check fitment for your ${vanShortName(vehicle)} before anything ships`,
      applyFilter: false,
      toggleVisible: false,
      toggleLabel: '',
      primary: null,
      switchAction: null,
    };
  }

  // mode === 'filterable'
  const van = shortVanLabel(vehicle);

  if (!singlePage) {
    const primary = vehicle.collection_handle
      ? { label: `See wheels for your ${van}`, href: `${collectionsRootPath}/${vehicle.collection_handle}` }
      : null;
    return {
      visible: true,
      message: `There are more wheels here than we can check for fit on one page.`,
      applyFilter: false,
      toggleVisible: false,
      toggleLabel: '',
      primary,
      switchAction: null,
    };
  }

  if (totalCount <= 0 || fittingCount === totalCount) {
    // Nothing to filter, or filtering would change nothing - no bar, same
    // as a van-specific collection where everything already fits.
    return { ...ROGUE_HIDDEN_BAR_STATE };
  }

  const toggleLabel = wheelShowAllToggleLabel(showAll, totalCount);
  // Once "Show all" is chosen the grid holds every wheel, fitting or not -
  // a stale "Showing 14 that fit" over 23 visible cards would be wrong, not
  // just unfiltered, so the message itself switches to a plain count too.
  const showingAllMessage = `Showing all ${totalCount} wheel${totalCount === 1 ? '' : 's'}`;

  if (fittingCount <= 0) {
    const suffix = collectionPcdLabel ? ` (${collectionPcdLabel})` : '';
    const primary = vehicle.collection_handle
      ? { label: `See wheels for your ${van}`, href: `${collectionsRootPath}/${vehicle.collection_handle}` }
      : null;
    return {
      visible: true,
      message: showAll ? showingAllMessage : `None of these fit your ${van}${suffix}`,
      applyFilter: !showAll,
      toggleVisible: true,
      toggleLabel,
      primary,
      switchAction: null,
    };
  }

  return {
    visible: true,
    message: showAll
      ? showingAllMessage
      : `Showing ${fittingCount} wheel${fittingCount === 1 ? '' : 's'} that fit${fittingCount === 1 ? 's' : ''} your ${van}`,
    applyFilter: !showAll,
    toggleVisible: true,
    toggleLabel,
    primary: null,
    switchAction: null,
  };
}

/**
 * The tyres collection's own bar (spec item 4): tyre lines in one of the
 * van's tyre sizes shown first, the rest behind a "Show all tyres" toggle.
 * A van with no tyre size on file at all (`sizeKnown` false - see
 * tyreSizeIsKnown/tyreSizesKnownForVehicle) shows every tyre with one
 * honest line instead of pretending to have filtered anything; so does the
 * "My van isn't listed" sentinel, since it never carries `tyre_sizes_json`.
 * @param {object} params
 * @param {RogueVehicleSelection|null} params.vehicle
 * @param {boolean} params.sizeKnown - whether the van has ANY resolved tyre size on file
 * @param {number} params.totalCount
 * @param {number} params.matchingCount - tyre lines in one of the van's sizes
 * @param {boolean} params.showAll
 * @param {boolean} [params.singlePage]
 * @returns {RogueListingBarState}
 */
export function tyreListingBarState({ vehicle, sizeKnown, totalCount, matchingCount, showAll, singlePage = true }) {
  if (!vehicle) return { ...ROGUE_HIDDEN_BAR_STATE };

  const noSizeMessage = "We'll confirm the tyre size for your van after you order.";

  if (isUnlistedVehicle(vehicle) || !sizeKnown) {
    return {
      visible: true,
      message: noSizeMessage,
      applyFilter: false,
      toggleVisible: false,
      toggleLabel: '',
      primary: null,
      switchAction: null,
    };
  }

  if (!singlePage) {
    return {
      visible: true,
      message: 'There are more tyres here than we can check for size on one page.',
      applyFilter: false,
      toggleVisible: false,
      toggleLabel: '',
      primary: null,
      switchAction: null,
    };
  }

  if (totalCount <= 0 || matchingCount === totalCount) {
    return { ...ROGUE_HIDDEN_BAR_STATE };
  }

  const toggleLabel = tyreShowAllToggleLabel(showAll);

  if (matchingCount <= 0) {
    return {
      visible: true,
      message: noSizeMessage,
      applyFilter: false,
      toggleVisible: false,
      toggleLabel: '',
      primary: null,
      switchAction: null,
    };
  }

  return {
    visible: true,
    // Once "Show all" is chosen every tyre is on the page, so the message
    // switches to a plain count too - a stale "3 in your size" over 9
    // visible lines would be wrong, not just unfiltered.
    message: showAll
      ? `Showing all ${totalCount} tyre${totalCount === 1 ? '' : 's'}`
      : `Showing ${matchingCount} tyre${matchingCount === 1 ? '' : 's'} in your size`,
    applyFilter: !showAll,
    toggleVisible: true,
    toggleLabel,
    primary: null,
    switchAction: null,
  };
}

/**
 * Fit-only verifier finding (25 Sep 2026): once "Show all tyres" (spec item
 * 4) lifts the tyre grid's filter, every line reappears in the collection's
 * own catalogue order - the van's own resolved size scattered among sizes
 * it doesn't come in, on the one page whose point was "here's your size".
 * Grid position, in the van's own size first (existing relative order kept
 * within each group), then everything else (existing relative order kept).
 * Pure so it's testable without a DOM - the one caller
 * (RogueFitFilterBar.render, below) re-appends the real `<li>` elements in
 * the order this returns.
 * @param {boolean[]} matches - parallel to the grid's `<li>` children; true where that line is in the van's resolved size
 * @returns {number[]} the same indices, matching lines first
 */
export function tyreShowAllOrder(matches) {
  const order = [];
  matches.forEach((matched, index) => {
    if (matched) order.push(index);
  });
  matches.forEach((matched, index) => {
    if (!matched) order.push(index);
  });
  return order;
}

/* --------------------------- collection head --------------------------- */

/**
 * The collection page heading. The collection title wins whenever there is
 * one: since the 13 Sep SEO baseline the platform writes the ranking phrase
 * into it ("Volkswagen Transporter alloy wheels: T6 (2015 to 2019)",
 * "6x120 alloy wheels"), Liquid renders it as the H1, and swapping it for the
 * bare vehicle name client side would throw that phrase away for every
 * shopper. The vehicle name is only a fallback for a collection with no title
 * of its own: from the collection's metafield first, then from the stored van
 * when it points at this handle.
 * @param {object} params
 * @param {string} params.collectionHandle - the handle of the page being viewed
 * @param {string} params.collectionTitle - the heading rendered by Liquid
 * @param {string} [params.metafieldDisplayName] - rogue vehicle name on the collection itself
 * @param {RogueVehicleSelection|null} [params.vehicle] - the stored van
 * @returns {string}
 */
/**
 * @typedef {Object} RogueVanChip
 * @property {string} href - the stored van's own collection
 * @property {string} label - the template with [van] replaced by the van's short name
 */

/**
 * The "Only wheels for your T6" chip on a collection that is not the van's
 * own. Null when there is no stored van, when the van has no collection, or
 * when this page already is that collection (the chip would point at itself).
 * @param {{ vehicle: RogueVehicleSelection|null, collectionHandle: string, template: string, collectionsRootPath?: string }} params
 * @returns {RogueVanChip|null}
 */
export function vanChip({ vehicle, collectionHandle, template, collectionsRootPath }) {
  if (!vehicle || !vehicle.collection_handle) return null;
  if (vehicle.collection_handle === collectionHandle) return null;
  const text = String(template || '').trim();
  if (text === '') return null;
  return {
    href: buildVehicleCollectionUrl(collectionsRootPath || '/collections', vehicle.collection_handle, null),
    // THEME_A finding (24 Sep 2026, Minor): vanShortName tries `generation`
    // FIRST (a bare "L1, L2, L3" with no model), same bug shortVanLabel was
    // written to fix everywhere else this short form appears (breadcrumb,
    // header van bar, van picker tile) - see shortVanLabel's own doc comment.
    label: text.split('[van]').join(shortVanLabel(vehicle)),
  };
}

export function normaliseHeadingText(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

export function collectionHeading({ collectionHandle, collectionTitle, metafieldDisplayName, vehicle }) {
  if (typeof collectionTitle === 'string' && collectionTitle.trim() !== '') {
    return collectionTitle.trim();
  }
  if (typeof metafieldDisplayName === 'string' && metafieldDisplayName.trim() !== '') {
    return metafieldDisplayName.trim();
  }
  if (vehicle && vehicle.collection_handle && vehicle.collection_handle === collectionHandle) {
    return vehicle.display_name;
  }
  return '';
}

/** The generic "wheel and tyre packages" collection (review N6) - every set for every van, not one van's own fits. */
export const ROGUE_PACKAGES_COLLECTION_HANDLE = 'wheel-and-tyre-packages';

/** Eyebrow text for a collection with no single van's fitment to promise. */
export const ROGUE_EYEBROW_ALL_WHEELS = 'All wheels';

/** Eyebrow text for the packages collection (review 2 N17). */
export const ROGUE_EYEBROW_PACKAGES = 'Wheel and tyre sets';

/**
 * The collection-head eyebrow (spec 9, BUY item 8a; review 2 N17): the
 * section setting's own eyebrow (default "Wheels that fit") is only true on
 * a collection that actually names one van's fitment. The packages
 * collection ({@link ROGUE_PACKAGES_COLLECTION_HANDLE}) lists sets for every
 * van, so it always reads "Wheel and tyre sets" regardless of what's stored.
 * The unlisted van's fallback collection
 * ({@link UNLISTED_VEHICLE_COLLECTION_HANDLE}, "all wheels") is the same
 * generic listing whether nothing is stored yet (the no-van state) or the
 * "My van isn't listed" sentinel is (the unlisted state) - neither has a
 * real fitting van to name, so it reads "All wheels" instead of the
 * section's default. Any other collection (a real van's own, a PCD or
 * stud-pattern listing) keeps the section's own text untouched.
 * @param {{ collectionHandle: string, defaultText: string }} params
 * @returns {string}
 */
export function collectionEyebrowText({ collectionHandle, defaultText }) {
  const fallback = typeof defaultText === 'string' ? defaultText : '';
  if (collectionHandle === ROGUE_PACKAGES_COLLECTION_HANDLE) return ROGUE_EYEBROW_PACKAGES;
  if (collectionHandle === UNLISTED_VEHICLE_COLLECTION_HANDLE) return ROGUE_EYEBROW_ALL_WHEELS;
  return fallback;
}

/**
 * The product breadcrumb's link text and target (review finding, Minor).
 * Two bugs in one: `vanShortName` tries `generation` FIRST, so a van whose
 * generation is set but whose model also matters ("Ducato L1, L2, L3")
 * read as the bare generation alone ("Wheels for your L1, L2, L3") -
 * shortVanLabel (model plus generation) is the fix, same as the fit line
 * already uses. And the crumb named the stored van's OWN collection
 * regardless of whether THIS product is in it - "Wheels for your L1, L2,
 * L3" on Rueda even though Rueda doesn't fit the Ducato at all. "All
 * wheels" (to the unfiltered collection) replaces it for both cases that
 * have nothing true to say about fitment: an unlisted van (no PCD data at
 * all - checked synchronously, RogueBreadcrumb doesn't need to wait for
 * anything) and a confirmed non-fit (`fitmentState`, only known once
 * RogueFitmentGate has resolved THIS product against the stored van - see
 * its `rogue:variant-change` event).
 * @param {Record<string, unknown>|null} vehicle
 * @param {FitmentState|null} [fitmentState] - RogueFitmentGate's resolved state for this product, once known
 * @param {string} [template] - "Wheels for your {van}"
 * @returns {{ label: string, useAllWheels: boolean } | null} null when there's nothing to show at all
 */
export function breadcrumbVanLabel(vehicle, fitmentState, template) {
  if (!vehicle) return null;
  if (isUnlistedVehicle(vehicle) || fitmentState === 'not-fits') {
    return { label: ROGUE_EYEBROW_ALL_WHEELS, useAllWheels: true };
  }
  if (!vehicle.collection_handle) return null;
  const tmpl = typeof template === 'string' && template !== '' ? template : 'Wheels for your {van}';
  return { label: tmpl.split('{van}').join(shortVanLabel(vehicle)), useAllWheels: false };
}

/**
 * Fills `{count}` / `{sizes}` / `{van}` in a collection summary template.
 * @param {string} template
 * @param {{ count?: string, sizes?: string, van?: string }} tokens
 * @returns {string}
 */
export function fillSummaryTemplate(template, tokens) {
  if (typeof template !== 'string' || template === '') return '';
  const values = tokens || {};
  const sizes = values.sizes == null ? '' : String(values.sizes).trim();
  // No sizes to name: drop the clause rather than print "in ,". Mirrors the
  // Liquid in sections/rogue-collection.liquid.
  let source = sizes === '' ? template.split(' in {sizes}').join('') : template;
  // One design reads "One wheel design ..., load-rated for your {van}", not
  // "One wheel designs ..., every one load-rated". Mirrors the Liquid.
  const count = values.count == null ? '' : String(values.count).trim();
  if (count === 'One' || count === '1') {
    source = source
      .split('{count} wheel designs')
      .join('{count} wheel design')
      .split('every one load-rated')
      .join('load-rated');
  }
  return source
    .split('{count}')
    .join(values.count == null ? '' : String(values.count))
    .split('{sizes}')
    .join(values.sizes == null ? '' : String(values.sizes))
    .split('{van}')
    .join(values.van == null ? '' : String(values.van))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Chooses and fills the right summary line: the unlisted-van template when
 * the stored van is the "My van isn't listed" sentinel (S3.2a - it has no
 * stud pattern/offset data, so the vehicle-aware template's "already taken
 * care of" claim would be false), the vehicle-aware template when there is a
 * real van to name, the generic one otherwise. A template whose `{van}`
 * cannot be filled falls back to the vehicle-free template rather than
 * printing an empty clause.
 * @param {{ vehicleTemplate?: string, genericTemplate?: string, unlistedTemplate?: string, count?: string, sizes?: string, van?: string, isUnlisted?: boolean, manualReview?: boolean }} params
 * @returns {string}
 */
export function collectionSummary({ vehicleTemplate, genericTemplate, unlistedTemplate, count, sizes, van, isUnlisted, manualReview = false }) {
  if (manualReview) return MANUAL_REVIEW_COLLECTION_SUMMARY;
  const hasVan = typeof van === 'string' && van.trim() !== '';
  let template = hasVan ? vehicleTemplate : genericTemplate;
  if (isUnlisted && hasVan && typeof unlistedTemplate === 'string' && unlistedTemplate.trim() !== '') {
    template = unlistedTemplate;
  }
  return fillSummaryTemplate(template, { count, sizes, van: hasVan ? van.trim() : '' });
}

/* ---------------------------- buy panel -------------------------------- */

/**
 * The price of a set, in the shop's minor units.
 * @param {number} unitPriceCents
 * @param {number} [setSize]
 * @returns {number|null} null when the unit price is not a usable number
 */
export function setPriceCents(unitPriceCents, setSize = ROGUE_SET_SIZE) {
  if (typeof unitPriceCents !== 'number' || !Number.isFinite(unitPriceCents) || unitPriceCents < 0) return null;
  const size = typeof setSize === 'number' && setSize > 0 ? Math.round(setSize) : ROGUE_SET_SIZE;
  return Math.round(unitPriceCents) * size;
}

/**
 * The artboard's second price line: "£480 for a set of four". Returns "" when
 * there is no formatted set price to show, so the caller can hide the line
 * rather than print half a sentence.
 * @param {string|null|undefined} setPriceFormatted - already money-filtered by Liquid
 * @param {number} [setSize]
 * @returns {string}
 */
export function formatSetPriceLine(setPriceFormatted, setSize = ROGUE_SET_SIZE) {
  if (typeof setPriceFormatted !== 'string' || setPriceFormatted.trim() === '') return '';
  return setPriceFormatted.trim() + ' for a set of ' + numberWord(setSize);
}

/* -------------------------------------------------------------------------
 * Pure functions: the "set of four plus a spare" quantity pills (S3.3a).
 *
 * One in five wheel sets on the old site was bought as five (four plus a
 * spare), and "spare" is the fifth most common word in buyer notes - see
 * the build brief. Two pills above the add-to-basket row write a fixed
 * quantity (setSize or setSize + 1) into the free quantity stepper; hand-
 * editing the stepper afterwards deselects both (see
 * RogueFitmentGate.clearSetChoice), so the pills and the stepper never
 * disagree about what's in the basket.
 * ---------------------------------------------------------------------- */

/** The default quantity choice: a plain set (setSize wheels, no spare). */
export const SET_CHOICE_FOUR = 'four';

/** The "plus a spare" quantity choice (setSize + 1 wheels). */
export const SET_CHOICE_PLUS_SPARE = 'plus-spare';

/**
 * The add-to-cart button label, which names the set size rather than saying
 * "Add to cart": "Add set of four to basket", or "Add four plus a spare to
 * basket" once the plus-a-spare pill is chosen (see setChoiceQuantity).
 *
 * Hand-editing the stepper away from either pill's amount clears the pill
 * selection (see RogueFitmentGate.clearSetChoice) - `choice` is then
 * `null`/`undefined`, and the label follows `quantity` (the stepper's
 * actual value) instead of silently defaulting back to "set of four",
 * which used to say "Add set of four to basket" with a 7 sitting in the
 * stepper right next to it (review finding 8).
 * @param {number} [setSize]
 * @param {string|null} [choice] - SET_CHOICE_FOUR, SET_CHOICE_PLUS_SPARE, or null/undefined for no pill selected
 * @param {number} [quantity] - the stepper's current value, used only when `choice` names neither pill
 * @returns {string}
 */
export function addSetButtonLabel(setSize = ROGUE_SET_SIZE, choice = SET_CHOICE_FOUR, quantity) {
  if (choice === SET_CHOICE_PLUS_SPARE) return 'Add ' + numberWord(setSize) + ' plus a spare to basket';
  const hasExplicitQuantity = typeof quantity === 'number' && Number.isFinite(quantity) && quantity > 0;
  if (choice == null && hasExplicitQuantity && Math.round(quantity) !== setSize) {
    return 'Add ' + numberWord(Math.round(quantity)) + ' to basket';
  }
  return 'Add set of ' + numberWord(setSize) + ' to basket';
}

/**
 * The quantity a set-choice pill writes into the quantity stepper.
 * @param {string} choice - SET_CHOICE_FOUR or SET_CHOICE_PLUS_SPARE
 * @param {number} [setSize]
 * @returns {number|null} null for an unrecognised choice, so the caller can leave the stepper alone
 */
export function setChoiceQuantity(choice, setSize = ROGUE_SET_SIZE) {
  const size = typeof setSize === 'number' && setSize > 0 ? Math.round(setSize) : ROGUE_SET_SIZE;
  if (choice === SET_CHOICE_FOUR) return size;
  if (choice === SET_CHOICE_PLUS_SPARE) return size + 1;
  return null;
}

/**
 * The set-price line under the price for the currently chosen quantity:
 * "£780 for a set of four" for SET_CHOICE_FOUR, "£975 for four plus a
 * spare" for SET_CHOICE_PLUS_SPARE. `formatted` is already money-filtered
 * by Liquid, for whichever quantity the choice actually adds (see
 * setChoiceQuantity) - the line always names the quantity actually going
 * into the basket, never a fixed "set of four" regardless of choice.
 * @param {string} choice - SET_CHOICE_FOUR or SET_CHOICE_PLUS_SPARE
 * @param {string|null|undefined} formatted
 * @param {number} [setSize]
 * @returns {string} "" when there is no formatted price to show
 */
export function setPriceLine(choice, formatted, setSize = ROGUE_SET_SIZE) {
  if (typeof formatted !== 'string' || formatted.trim() === '') return '';
  if (choice === SET_CHOICE_PLUS_SPARE) return formatted.trim() + ' for ' + numberWord(setSize) + ' plus a spare';
  return formatted.trim() + ' for a set of ' + numberWord(setSize);
}

/* -------------------------------------------------------------------------
 * Pure functions: the combined wheels-plus-tyres price and button label
 * (review 2026-09-23, Blocker 2). Once the add-to-basket button can put two
 * different products in the same basket action, "Add set of four to
 * basket" next to "£780 for a set of four" no longer says what is actually
 * happening once tyres are added - these supersede setPriceLine/
 * addSetButtonLabel for what RogueFitmentGate actually renders (see
 * updatePrices/renderSetChoice); the older two are kept, tested, and still
 * exported, exactly like setPriceCents/formatSetPriceLine above them.
 * ---------------------------------------------------------------------- */

/**
 * The quantity phrase shared by the set-price line and the button summary
 * line: "four wheels" / "five wheels" (a spare - spec 2.3: the "four plus a
 * spare" wording the pills used is gone now that the spare is a plain
 * quantity bump, not a second product choice), with " and tyres" appended
 * once a chargeable tyre is actually going in the basket alongside them.
 * `quantity` only matters when `choice` is neither pill (the shopper hand-
 * edited the stepper - see RogueFitmentGate.clearSetChoice): it names the
 * bespoke number instead of assuming `setSize` wheels, same fallback
 * addSetButtonLabel already used.
 * @param {number} [setSize]
 * @param {string|null} [choice] - SET_CHOICE_FOUR, SET_CHOICE_PLUS_SPARE, or null/undefined
 * @param {boolean} [tyresEnabled]
 * @param {number} [quantity]
 * @returns {string}
 */
export function setAndTyresPhrase(setSize = ROGUE_SET_SIZE, choice = SET_CHOICE_FOUR, tyresEnabled = false, quantity) {
  const hasExplicitQuantity = typeof quantity === 'number' && Number.isFinite(quantity) && quantity > 0;
  const count = choice === SET_CHOICE_PLUS_SPARE ? setSize + 1
    : choice == null && hasExplicitQuantity ? Math.round(quantity) : setSize;
  const base = numberWord(count) + (count === 1 ? ' wheel' : ' wheels');
  return tyresEnabled ? base + (count === 1 ? ' and tyre' : ' and tyres') : base;
}

/**
 * The set-price line under the price, split into the big number and the
 * "for four wheels" note beside it (spec 2.2/2.3: the amount is the large
 * Oswald figure, the phrase is a separate, smaller note) - "£780"/"for four
 * wheels" wheels-only, "£1,660"/"for four wheels and tyres" once a
 * chargeable tyre is resolved and `tyresEnabled`, "£975"/"for five wheels"
 * with the spare. See {@link combinedSetPriceCents}/{@link formatGbpPence}
 * for how `combinedFormatted` is produced. Falls back to the wheels-only
 * amount whenever there's nothing usable to combine yet, so choosing "Wheels
 * and tyres" before a tyre line is resolved never claims a price it can't
 * back up.
 * @param {{ wheelsFormatted: string|null|undefined, combinedFormatted?: string|null, tyresEnabled: boolean, choice?: string|null, setSize?: number, quantity?: number }} params
 * @returns {{ amount: string, note: string }} both "" when there is no usable formatted price at all
 */
export function setAndTyresPriceLine({
  wheelsFormatted,
  combinedFormatted,
  tyresEnabled,
  choice = SET_CHOICE_FOUR,
  setSize = ROGUE_SET_SIZE,
  quantity,
}) {
  const usingCombined = Boolean(tyresEnabled) && typeof combinedFormatted === 'string' && combinedFormatted.trim() !== '';
  const phrase = setAndTyresPhrase(setSize, choice, usingCombined, quantity);
  const amount = usingCombined
    ? combinedFormatted.trim()
    : typeof wheelsFormatted === 'string'
      ? wheelsFormatted.trim()
      : '';
  if (amount === '') return { amount: '', note: '' };
  return { amount, note: `for ${phrase}` };
}

/**
 * The "From" price (25 Oct 2026 pricing audit item 7): the cheapest variant's
 * price in pence, but only when the variants really differ in price - the
 * price a shopper sees before a vehicle narrows the choice. null when there
 * is one price (nothing to say "from" about) or no usable price at all.
 * @param {Array<{ price?: number|null }>|null|undefined} variants
 * @returns {number|null}
 */
export function fromPriceCents(variants) {
  const prices = (Array.isArray(variants) ? variants : [])
    .map((v) => v?.price)
    .filter((price) => typeof price === 'number' && Number.isFinite(price));
  if (prices.length < 2) return null;
  const min = Math.min(...prices);
  return prices.some((price) => price !== min) ? min : null;
}

/**
 * "From £700" for the set price while no vehicle is chosen; the exact price
 * (unchanged) otherwise.
 * @param {string|null|undefined} formatted
 * @param {boolean} isFrom
 * @returns {string}
 */
export function fromPriceText(formatted, isFrom) {
  const text = typeof formatted === 'string' ? formatted.trim() : '';
  return text !== '' && isFrom ? `From ${text}` : text;
}

/**
 * "Add a spare wheel +£140" / "Add a spare wheel and tyre +£240" (spec 2.3):
 * the spare line's own price, always one wheel's unit price, plus one
 * tyre's unit price once a chargeable tyre line is resolved and tyres are
 * on. Pure pence maths via {@link formatGbpPence}, same as the combined set
 * total - a spare wheel is one extra of whatever is already in the basket.
 * @param {{ wheelUnitCents: number|null|undefined, tyreUnitCents?: number|null, tyresEnabled: boolean }} params
 * @returns {string} "Add a spare wheel" alone (no price) when the wheel's own unit price isn't a usable number
 */
export function spareWheelLabel({ wheelUnitCents, tyreUnitCents, tyresEnabled }) {
  const base = 'Add a spare wheel';
  if (typeof wheelUnitCents !== 'number' || !Number.isFinite(wheelUnitCents)) return base;
  const usingTyre = Boolean(tyresEnabled) && typeof tyreUnitCents === 'number' && Number.isFinite(tyreUnitCents);
  const cents = usingTyre ? wheelUnitCents + tyreUnitCents : wheelUnitCents;
  const priceText = formatGbpPence(cents);
  if (priceText === '') return base;
  return usingTyre ? `${base} and tyre +${priceText}` : `${base} +${priceText}`;
}

/**
 * The buy box's persistent summary line, under the button (spec 2.3): what
 * is actually going in the basket in plain words - "4 x Raptor 16 inch
 * Satin Black", "... and 4 x Van Rated Winter 225/75R16" once a tyre line is
 * resolved, or "... Tyres to confirm with you." when tyres are wanted but no
 * size is known yet (spec 2.3's no-size button summary). Reused verbatim as
 * the body of the post-add confirmation ("Added to your basket." plus this
 * same line - spec 2.3's "After add" row), so there is exactly one place
 * that decides what the basket is about to hold.
 * @param {{
 *   quantity: number|null|undefined,
 *   wheelTitle?: string|null,
 *   wheelSize?: string|null,
 *   wheelFinish?: string|null,
 *   tyresEnabled?: boolean,
 *   sizeKnown?: boolean,
 *   tyreLine?: { title?: string|null, variant?: { size?: string|null } }|null,
 *   preorder?: boolean,
 * }} params
 * @returns {string} "" when there isn't enough to say anything with
 */
export function buildBuySummaryLine({
  quantity,
  wheelTitle,
  wheelSize,
  wheelFinish,
  tyresEnabled,
  sizeKnown,
  tyreLine,
  preorder,
}) {
  const qty = wheelQuantityValue(quantity);
  const title = typeof wheelTitle === 'string' ? wheelTitle.trim() : '';
  if (qty === null || title === '') return '';

  const sizeLabel = wheelSize ? ` ${formatSizeLabel(String(wheelSize))}` : '';
  const finishLabel = typeof wheelFinish === 'string' && wheelFinish.trim() !== '' ? ` ${wheelFinish.trim()}` : '';
  const wheelPart = `${qty} x ${title}${sizeLabel}${finishLabel}`;

  if (!tyresEnabled) return wheelPart;
  if (sizeKnown === false) return `${wheelPart}. ${qty === 1 ? 'Tyre' : 'Tyres'} to confirm with you.`;

  const tyreTitle = typeof tyreLine?.title === 'string' ? tyreLine.title.trim() : '';
  if (tyreTitle === '') return wheelPart;
  // No tyre size here (31 Oct 2026): see tyreSizedForText.
  const combined = `${wheelPart} and ${qty} x ${tyreTitle}`;
  // N6 (26 Sep review round 3): a wheel on pre-order still gets its tyre
  // line charged and packed with it now (buildAddToBasketPayload's
  // "Pre-order: yes" tyre property, set from the same flag) - one line
  // here says why the tyres don't ship today either, rather than leaving
  // the fitter, or the shopper, to work out why a paid-for tyre is sitting
  // beside a pre-order wheel.
  return preorder ? `${combined}. ${qty === 1 ? 'The tyre is fitted when the wheel arrives.' : 'Tyres are fitted when the wheels arrive.'}` : combined;
}

/**
 * The add confirmation's line with the set's total on the end - wheels and
 * fitted tyres together, the same figure the button carried (25 Oct 2026).
 * @param {string} summary - buildBuySummaryLine's text
 * @param {string} total - e.g. "£1,480"
 * @returns {string}
 */
export function summaryWithSetTotal(summary, total) {
  const text = typeof summary === 'string' ? summary.trim() : '';
  const amount = typeof total === 'string' ? total.trim() : '';
  if (text === '' || amount === '') return text;
  return `${text.replace(/\.$/, '')}. Set total ${amount}.`;
}

/**
 * The tyre rows sorted lowest price first (spec 2.3: "Sorted by price,
 * lowest first"). A missing price sorts last rather than throwing off the
 * rest of the order.
 * @param {Array<{ variant?: { price_cents?: number } }>} lines
 * @returns {Array<object>} a new array; `lines` itself is left untouched
 */
export function sortedTyreLines(lines) {
  const list = Array.isArray(lines) ? [...lines] : [];
  const priceOf = (line) => {
    const cents = line?.variant?.price_cents;
    return typeof cents === 'number' && Number.isFinite(cents) ? cents : Number.POSITIVE_INFINITY;
  };
  return list.sort((a, b) => priceOf(a) - priceOf(b));
}

/**
 * One tyre row's own price line ("£880 for four" / "£1,100 for five"),
 * scaled to the wheel quantity actually going into the basket - tyres always
 * match the wheel quantity (spec 2.3, cartPairingCorrections), so this must
 * move with "Add a spare wheel" exactly like priceSetAmount/addToCartPrice/
 * the sticky bar do (walk finding, 25 Sep 2026: renderTyreRows previously
 * only ran when the tyre choice or Size changed, not when the spare
 * checkbox did, so every row kept reading "for four" once it was ticked even
 * though the total charged had already moved to five - see
 * RogueFitmentGate.applySpareChoice). Falls back to the variant's own
 * already-formatted single price when price_cents isn't a usable number.
 * @param {number|null|undefined} priceCents - the tyre variant's own per-tyre price
 * @param {string|null|undefined} priceFormatted - fallback single-tyre price text
 * @param {number} quantity
 * @returns {string} "" when there is nothing usable to show a price for
 */
export function tyreRowPriceLine(priceCents, priceFormatted, quantity) {
  const priceText =
    typeof priceCents === 'number' && Number.isFinite(priceCents)
      ? formatGbpPence(priceCents * quantity)
      : typeof priceFormatted === 'string'
        ? priceFormatted
        : '';
  return priceText ? `${priceText} for ${numberWord(quantity)}` : '';
}

/**
 * @typedef {object} RogueSwatchState
 * @property {string} value
 * @property {string|null} image
 * @property {string} color
 * @property {string} background
 * @property {boolean} selectable - false greys the swatch out; it is never hidden
 * @property {string} reason - "" when selectable, otherwise a short (two-word) caption to print under the disc
 * @property {string} reasonTitle - "" when selectable, otherwise the full sentence for a `title`/`aria-label`
 */

/**
 * Availability and fitment facts for every finish. The product page uses
 * these to hide incompatible finishes for a known vehicle and place fitting
 * sold-out choices in a separate disclosure.
 *
 * `reason` is kept to a short caption ("Not for your van", "Out of stock")
 * so the swatch row (`.rogue-buy__swatches`, a wrapping flex row) stays
 * horizontal instead of a long sentence forcing each swatch onto its own
 * line; `reasonTitle` carries the full sentence for a `title` attribute and
 * `aria-label` instead.
 *
 * Order of reasons, most specific first:
 *  1. nothing in this finish is drilled for the stored van
 *  2. the fitting variants in this finish are all out of stock
 *  3. (no van stored) every variant in this finish is out of stock
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {RogueSwatchState[]}
 */
export function colourSwatchStates(variants, positions, vehicle) {
  const swatches = buildColourSwatches(variants, positions);
  if (swatches.length === 0) return [];

  const vanName = (vehicle && vehicle.display_name) || 'van';
  const all = Array.isArray(variants) ? variants : [];

  return swatches.map((swatch) => {
    const inColour = all.filter((variant) => variantOptionValue(variant, positions, 'colour') === swatch.value);

    if (!vehicle) {
      const anyAvailable = inColour.some((variant) => variant.available !== false);
      return {
        ...swatch,
        selectable: anyAvailable,
        reason: anyAvailable ? '' : 'Out of stock',
        reasonTitle: anyAvailable ? '' : 'Out of stock',
      };
    }

    const fitting = inColour.filter((variant) => variantFitsVehicle(variant, positions, vehicle));
    if (fitting.length === 0) {
      return {
        ...swatch,
        selectable: false,
        reason: 'Not for your van',
        reasonTitle: 'Does not fit your ' + vanName,
      };
    }

    const anyAvailable = fitting.some((variant) => variant.available !== false);
    return {
      ...swatch,
      selectable: anyAvailable,
      reason: anyAvailable ? '' : 'Out of stock',
      reasonTitle: anyAvailable ? '' : 'Out of stock',
    };
  });
}

/**
 * Whether this wheel can be bought for the vehicle in ANY finish and size -
 * the product-level answer, as opposed to resolveVariant's answer for the
 * one finish and size currently chosen. 'none' is Daniel's "this won't fit
 * your vehicle" (25 Oct 2026): the buy options go and fitting wheels show
 * instead. Unlisted and manual-review vehicles carry nothing to check, so
 * they are 'unchecked', never 'none'.
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {'no-vehicle'|'unchecked'|'fits'|'none'}
 */
export function productFitSummary(variants, positions, vehicle) {
  if (!vehicle) return 'no-vehicle';
  if (isUnlistedVehicle(vehicle)) return 'unchecked';
  const list = Array.isArray(variants) ? variants : [];
  return list.some((variant) => variantFitsVehicle(variant, positions, vehicle)) ? 'fits' : 'none';
}

/** Second line of the no-fit panel, above the fitting wheels. */
export const NO_FIT_ALTERNATIVES_TEXT = 'But here are some awesome wheels that will.';

/**
 * Where the finish block goes in the buy box (B3, 31 Oct 2026): straight
 * under the photo on a phone, after the title and price otherwise.
 * @param {boolean} isNarrow - the viewport is under 750px
 * @returns {'before-header'|'after-header'}
 */
export function finishBlockPlacement(isNarrow) {
  return isNarrow ? 'before-header' : 'after-header';
}

/**
 * Copy for the product-level no-fit panel.
 * @param {RogueVehicleSelection|null} vehicle
 * @param {string} [title] - the wheel's display title, e.g. "CC1"
 * @returns {{ heading: string, message: string, cta: string }}
 */
export function noFitCopy(vehicle, title) {
  const van = shortVanLabel(vehicle) || 'vehicle';
  const wheel = typeof title === 'string' && title.trim() !== '' ? title.trim() : 'This wheel';
  return {
    heading: `Sorry, this wheel won't fit your ${van}`,
    message: `${wheel} isn't made in a fitting for your ${van}.`,
    cta: `See all wheels for your ${van}`,
  };
}

/**
 * The product handle in a product link ("/products/cc1", or a collection-
 * scoped "/collections/x/products/cc1?variant=1").
 * @param {unknown} href
 * @returns {string}
 */
export function productHandleFromUrl(href) {
  const match = typeof href === 'string' ? href.match(/\/products\/([^/?#]+)/) : null;
  if (!match) return '';
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

/**
 * Picks the wheels to offer instead: approved for this exact vehicle (the
 * same `fits_vehicles` evidence the collection badge and Popular row use),
 * never the wheel already on screen, first `limit` in collection order.
 * @template T
 * @param {Array<T & { handle: string, fits: unknown }>} cards
 * @param {string} slug
 * @param {string} excludeHandle
 * @param {number} [limit]
 * @returns {T[]}
 */
export function fittingAlternatives(cards, slug, excludeHandle, limit = 4) {
  if (!slug || !Array.isArray(cards)) return [];
  const seen = new Set();
  const picked = [];
  for (const card of cards) {
    const handle = card?.handle || '';
    if (!handle || handle === excludeHandle || seen.has(handle)) continue;
    if (!Array.isArray(card.fits) || !card.fits.includes(slug)) continue;
    seen.add(handle);
    picked.push(card);
    if (picked.length >= limit) break;
  }
  return picked;
}

/**
 * Legacy aggregate wording; the current product page uses separate groups.
 * The old finish note under the swatch row (spec 2.1/2.3): the old per-
 * swatch "Not for your van"/"Out of stock" captions are gone (acceptance:
 * "no per-swatch labels"), replaced by a single aggregate sentence, shown
 * only when at least one swatch is greyed out. Reads the same `reason`
 * strings {@link colourSwatchStates} already produces, so the two can never
 * disagree about which finishes are greyed and why.
 * @param {RogueSwatchState[]} states
 * @returns {string} "" when nothing is greyed out
 */
export function finishNoteText(states) {
  const list = Array.isArray(states) ? states : [];
  // Review finding (Minor, "two 'not made' lines"): when EVERY swatch is
  // greyed for fit, the product doesn't fit this van at all, and
  // RogueFitmentGate.updateStateCollapse's dedicated not-fits panel already
  // says so in its own single line ("[Wheel] isn't made in a drilling that
  // fits your [van]."). This note would just repeat it (measured live on
  // Rueda with a Ducato stored: both lines on screen at once) - suppressed
  // here rather than in the not-fits panel, so a genuinely MIXED row (some
  // finishes fit, one greyed) still gets its note as before.
  const allNotFit = list.length > 0 && list.every((s) => s.reason === 'Not for your van');
  if (allNotFit) return '';
  const anyNotFit = list.some((s) => s.reason === 'Not for your van');
  const anyOutOfStock = list.some((s) => s.reason === 'Out of stock');
  if (anyNotFit && anyOutOfStock) return 'Greyed finishes are out of stock or not made for your van.';
  if (anyNotFit) return "Greyed finishes aren't made for your van.";
  if (anyOutOfStock) return 'Greyed finishes are out of stock.';
  return '';
}

/**
 * @typedef {Object} RoguePreferredSelection
 * @property {string|null} colour
 * @property {string|null} size
 * @property {boolean} changed - true when the caller's selection was moved
 */

/**
 * The Colour/Size the product page should land on. Shopify picks the first
 * variant (or the one in the URL) with no idea of stock or the stored van,
 * so a shopper can arrive on a sold-out finish with the basket button dead
 * and no hint that Grey is one click away. This keeps the caller's choice
 * whenever it can be bought for the van, otherwise moves to the first
 * finish (in swatch order) with a fitting variant in stock, keeping the size
 * if that finish has it. With nothing buyable it leaves the selection alone
 * so the greyed-out state still shows what exists.
 * @param {RogueVariantMeta[]} variants
 * @param {RogueOptionPositions} positions
 * @param {RogueVehicleSelection|null} vehicle
 * @param {{ colourValue?: string|null, sizeValue?: string|null }} current
 * @returns {RoguePreferredSelection}
 */
export function preferredSelection(variants, positions, vehicle, current) {
  const { colourValue = null, sizeValue = null } = current || {};
  const all = Array.isArray(variants) ? variants : [];
  const buyable = all.filter(
    (variant) => variant.available !== false && (!vehicle || variantFitsVehicle(variant, positions, vehicle))
  );
  const keep = { colour: colourValue, size: sizeValue, changed: false };
  if (buyable.length === 0) return keep;

  const colourOf = (variant) => (positions.colour === -1 ? null : variantOptionValue(variant, positions, 'colour'));
  const sizeOf = (variant) => (positions.size === -1 ? null : variantOptionValue(variant, positions, 'size'));
  const matches = (variant, colour, size) =>
    (colour == null || colourOf(variant) === colour) && (size == null || sizeOf(variant) === size);

  if (buyable.some((variant) => matches(variant, colourValue, sizeValue))) return keep;

  // Same finish, another size that can be bought.
  const sameColour = buyable.find((variant) => matches(variant, colourValue, null));
  if (colourValue != null && sameColour) {
    return { colour: colourValue, size: sizeOf(sameColour), changed: true };
  }

  // First finish in swatch order with something buyable; keep the size if it has it.
  const order = buildColourSwatches(all, positions).map((swatch) => swatch.value);
  const ranked = buyable.slice().sort((a, b) => order.indexOf(colourOf(a)) - order.indexOf(colourOf(b)));
  const withSize = ranked.find((variant) => matches(variant, colourOf(ranked[0]), sizeValue));
  const pick = withSize || ranked[0];
  return { colour: colourOf(pick), size: sizeOf(pick), changed: true };
}

/**
 * The tyre size a van takes on a given rim, read from the vehicle's
 * `tyre_sizes_json` field. That field is a JSON object keyed by rim diameter
 * ({"17": "235/55 R17"}); it is looked up by bare diameter so both "17" and
 * "17 inch" resolve. Returns "" when the vehicle has no mapping yet - the
 * caller then keeps the "Set by your van" placeholder from the artboard.
 * @param {string|object|null|undefined} tyreSizesJson
 * @param {string|null} sizeValue
 * @returns {string}
 */
export function tyreSizeForRim(tyreSizesJson, sizeValue) {
  if (!tyreSizesJson || !sizeValue) return '';

  let map = tyreSizesJson;
  if (typeof map === 'string') {
    try {
      map = JSON.parse(map);
    } catch {
      return '';
    }
  }
  if (!map || typeof map !== 'object') return '';

  const diameter = String(sizeValue).trim().match(/\d+(?:\.\d+)?/);
  if (!diameter) return '';

  const candidates = [String(sizeValue).trim(), diameter[0], diameter[0] + ' inch'];
  for (const key of candidates) {
    const found = map[key];
    if (typeof found === 'string' && found.trim() !== '') return found.trim();
  }
  return '';
}

/**
 * Every tyre size a vehicle's `tyre_sizes_json` maps to. SHOP finding 2 (24
 * Sep 2026): the home selector's van step leaves rim size blank (it's
 * optional), so most stored vans carry `rim_size` "" - {@link tyreSizeForRim}
 * always returns "" for a blank size, so with no one rim chosen this matches
 * against every rim the van has a mapped size for, not just one. Returns []
 * for no vehicle, an unlisted van (S3.2a never carries `tyre_sizes_json`),
 * and a real van with no tyre size mapped at all - the caller (fit-only
 * browsing's tyre bar, spec item 4) shows the honest "we'll confirm the
 * tyre size" line in every one of those cases rather than guessing.
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {string[]}
 */
export function resolvedTyreSizesForVehicle(vehicle) {
  if (!vehicle) return [];
  let map = vehicle.tyre_sizes_json;
  if (typeof map === 'string') {
    try {
      map = JSON.parse(map);
    } catch {
      return [];
    }
  }
  if (!map || typeof map !== 'object') return [];

  if (vehicle.rim_size) {
    const size = tyreSizeForRim(map, vehicle.rim_size);
    return size ? [size] : [];
  }

  const sizes = [];
  Object.keys(map).forEach((rim) => {
    const size = tyreSizeForRim(map, rim);
    if (size && !sizes.includes(size)) sizes.push(size);
  });
  return sizes;
}

/* -------------------------------------------------------------------------
 * Pure functions: tyres as real, charged products (review 2026-09-23,
 * Blocker 2 - "Tyres are shown but not charged"). Tyres now live in the
 * `tyres` collection as one product per line (e.g. "Michelin Agilis"), one
 * variant per fitted size - the variant's option value is the ISO size
 * string itself (e.g. "215/65R16C"), not a brand/tier. See the
 * `data-rogue-tyres` payload built in snippets/rogue-fitment-gate.liquid for
 * the exact shape read here: `{ handle, title, brand, type, variants:
 * [{ id, size, price_cents, price_formatted, available }] }` per product.
 * ---------------------------------------------------------------------- */

/**
 * Whether the tyre panel can offer a real, priced tyre at all: false for no
 * stored vehicle, for the "My van isn't listed" sentinel (S3.2a - it carries
 * no `tyre_sizes_json`), and for a real van with nothing mapped for this rim
 * (some twin-rear-wheel and Maxi Heavy variants). The caller shows the
 * honest "we'll confirm tyres with you" line and hides the brand select in
 * every one of these cases rather than guessing - and never stores
 * `tyreSizeForRim`'s "" as a size (the previous bug stored the literal
 * placeholder text "Set by your van" instead).
 * @param {RogueVehicleSelection|null} vehicle
 * @param {string|null} rimSize
 * @returns {boolean}
 */
export function tyreSizeIsKnown(vehicle, rimSize) {
  return tyreSizeForRim(vehicle?.tyre_sizes_json, rimSize) !== '';
}

/**
 * The rim diameters a vehicle's `tyre_sizes_json` actually has a tyre size
 * for, sorted ascending - e.g. `{"17":"235/65R17"}` -> `[17]`. Used by
 * {@link tyreNoSizeMessage}.
 * @param {string|object|null|undefined} tyreSizesJson
 * @returns {number[]}
 */
export function knownTyreRimSizes(tyreSizesJson) {
  if (!tyreSizesJson) return [];

  let map = tyreSizesJson;
  if (typeof map === 'string') {
    try {
      map = JSON.parse(map);
    } catch {
      return [];
    }
  }
  if (!map || typeof map !== 'object') return [];

  return Object.keys(map)
    .filter((key) => typeof map[key] === 'string' && map[key].trim() !== '')
    .map((key) => Number(String(key).trim().match(/\d+/)?.[0]))
    .filter((diameter) => Number.isFinite(diameter))
    .sort((a, b) => a - b);
}

/**
 * "16 inch" / "16 and 18 inch" / "16, 17 and 18 inch" - a sorted list of rim
 * diameters as a sentence fragment.
 * @param {number[]} sizes
 * @returns {string}
 */
function formatRimSizeList(sizes) {
  if (sizes.length === 0) return '';
  if (sizes.length === 1) return `${sizes[0]} inch`;
  return `${sizes.slice(0, -1).join(', ')} and ${sizes[sizes.length - 1]} inch`;
}

/**
 * The tyre panel's no-size sentence (THEME_A finding, 24 Sep 2026, Minor):
 * `fallbackText` (the theme setting) is a blanket "we don't have your van's
 * tyre size on file yet", which read as untrue whenever the van's
 * `tyre_sizes_json` DOES carry a size for a different rim - e.g. a
 * Transporter T5-T6.1 with `{"17":"235/65R17"}` only, viewed on this wheel's
 * 16 inch size. Names the rim(s) that ARE on file instead, once there is at
 * least one; falls back to the generic text only when the van has no tyre
 * data at all (no vehicle, the "My van isn't listed" sentinel, or a real van
 * with nothing mapped for any rim).
 * @param {RogueVehicleSelection|null} vehicle
 * @param {string} fallbackText
 * @returns {string}
 */
export function tyreNoSizeMessage(vehicle, fallbackText) {
  const knownSizes = knownTyreRimSizes(vehicle?.tyre_sizes_json);
  if (knownSizes.length === 0) return fallbackText;
  return `We have your vehicle's tyre size on file for ${formatRimSizeList(knownSizes)}, not this size. Order the wheels and we'll call you to confirm tyres before anything ships.`;
}

/**
 * The tyre panel's sentence for H4 (25 Sep review, finding P4 - the Fiat
 * Ducato at 15 inch today): a rim size the van's `tyre_sizes_json` DOES
 * carry a real size for, but with no tyre PRODUCT that has a variant in
 * that size (Paul's price sheet has no 15 inch column yet), used to leave
 * the tyre panel showing the tick line and then nothing at all - no rows,
 * no sentence - and "Add to basket" quietly added the wheels alone with no
 * `Tyres wanted` property either. Treated the same as an unknown size
 * everywhere it matters (see RogueFitmentGate.updateTyrePanel, which shows
 * this instead of {@link tyreNoSizeMessage} exactly when the size IS known
 * but `availableTyreLines` is empty, and passes that same "empty" test to
 * {@link tyresWantedCartProperty} so the follow-up property still gets
 * written) - only the wording differs, naming the actual size that's
 * missing rather than the generic fallback.
 * @param {string} resolvedSize - the van's own tyre size for this rim, e.g. "225/70R15"
 * @returns {string} "" when there's no size to name
 */
export function tyreSizeHasNoStockMessage(resolvedSize) {
  const size = typeof resolvedSize === 'string' ? resolvedSize.trim() : '';
  if (size === '') return '';
  return "We don't sell tyres in your van's size online yet. Order the wheels and we'll call you to confirm tyres before anything ships.";
}

/**
 * A fitting size THIS wheel offers (for the currently selected colour) that
 * has a tyre size on file for the van, when the currently selected size does
 * not - Daniel's reproduction (25 Sep 2026): the Transporter T5-T6.1 opens
 * Rocky at 16 inch, which has no tyre size on file, but Rocky also offers 17
 * inch, which does (235/65R17 - 140 of 156 of Paul's orders for this van,
 * data/paul-sheets/tyre-sizes-from-orders.csv). Only ever offers a size this
 * SAME wheel actually comes in, never a rim the van has a tyre size for on
 * some other, unrelated wheel - see {@link tyreNoSizeMessage} for the honest
 * fallback once nothing here qualifies.
 * @param {string[]} fittingSizes - the sizes this wheel offers for the stored vehicle (sizeSelectorState's own `sizes`)
 * @param {string|null} currentSize
 * @param {RogueVehicleSelection|null} vehicle
 * @returns {string|null}
 */
export function tyreSizeSwitchTarget(fittingSizes, currentSize, vehicle) {
  if (!vehicle || !Array.isArray(fittingSizes)) return null;
  if (tyreSizeIsKnown(vehicle, currentSize)) return null;
  return fittingSizes.find((size) => size !== currentSize && tyreSizeIsKnown(vehicle, size)) || null;
}

/**
 * "We have your van's tyre size for 17 inch wheels, not 16 inch. Switch to
 * add tyres now, or stay on 16 inch and we'll call you to confirm tyres." -
 * the one-line offer shown beside {@link tyreSizeSwitchTarget}'s "Switch to
 * ..." button.
 *
 * M2 (25 Sep review, finding P6): the previous copy ("We fit tyres for your
 * [van] on 17 inch wheels") read as if Rogue simply couldn't fit tyres to
 * the smaller wheel at all, when the real reason is narrower - Rogue just
 * doesn't have THIS size on file yet - and it never mentioned the size the
 * shopper is currently on, or that staying on it is still a real option
 * (the page already records `Tyres wanted` either way - see
 * tyresWantedCartProperty). Naming both sizes, and both paths, needs no
 * `vehicle` at all - the van is already named in the fit line right above
 * this. "" when there's nothing to switch to, so the caller can hide the
 * whole line.
 * @param {string|null} switchSize - the size THIS wheel offers that the van's tyre size IS known for
 * @param {string|null} [currentSize] - the size currently selected, e.g. "16 inch"
 * @returns {string}
 */
export function tyreSwitchSizeMessage(switchSize, currentSize) {
  if (!switchSize) return '';
  const current = typeof currentSize === 'string' ? currentSize.trim() : '';
  if (current === '' || current === switchSize) {
    return `We have your vehicle's tyre size for ${switchSize} wheels. Switch to add tyres now.`;
  }
  return `We have your vehicle's tyre size for ${switchSize} wheels, not ${current}. Switch to add tyres now, or stay on ${current} and we'll call you to confirm tyres.`;
}

/**
 * The line property that records tyre intent when there is nothing priced to
 * offer (review N7): "Add matching tyres" is on, but the van's tyre size for
 * this rim isn't on file (an unlisted van, or a real van with nothing mapped
 * for it - see {@link tyreSizeIsKnown}), so the panel has collapsed to one
 * sentence with no brand select. Nothing is charged either way; this is
 * a request for Rogue to follow up, with the existing fulfilment hold marker
 * written onto the wheel line
 * itself (there is no tyre line to write it onto, since none was resolved).
 * @param {{ tyresEnabled: boolean, sizeKnown: boolean, wanted: boolean }} params
 * @returns {Record<string, string>} `{}` unless every condition holds
 */
export function tyresWantedCartProperty({ tyresEnabled, sizeKnown, wanted }) {
  if (!tyresEnabled || sizeKnown || !wanted) return {};
  return { 'Tyres wanted': 'yes, size to confirm', _fitment_verification: 'requested' };
}

/**
 * The variant on one tyre product whose size matches the van's tyre size for
 * a given rim - the only thing that makes a tyre "the right one" for this
 * wheel. Matches on size alone, regardless of stock, so the caller can tell
 * "wrong size" apart from "right size, sold out" - see {@link
 * availableTyreLines} for the stock-filtered list the brand select actually
 * offers.
 * @param {{ tyreProduct: { variants?: Array<{ id: string|number, size: string, price_cents?: number, price_formatted?: string, available?: boolean }> }, vehicle: RogueVehicleSelection|null, rimSize: string|null }} params
 * @returns {{ id: string|number, size: string, price_cents?: number, price_formatted?: string, available?: boolean }|null}
 */
export function resolveTyreVariant({ tyreProduct, vehicle, rimSize, rules = [] }) {
  const size = tyreSizeForLine(vehicle, rimSize, tyreProduct?.type, rules);
  if (size === '') return null;
  const variants = Array.isArray(tyreProduct?.variants) ? tyreProduct.variants : [];
  return variants.find((variant) => typeof variant?.size === 'string' && variant.size.trim() === size) || null;
}

/**
 * White letters by rim size (Paul's fixes list, 27 Oct 2026, item 32: "in
 * 17s comforsor, davanti, BFG have white letters available, 16s all have
 * white letters available except falken, all van rated all season, summer,
 * winter dont have white letters"). One rule per line of the product
 * section's "White letters by rim size" setting: `rim | handle, handle`
 * lists every tyre line with white letters at that rim, and
 * `never | handle, handle` lines never offer them. A rim no rule names keeps
 * the tyre's own rogue.white_letter answer.
 * @param {string|null|undefined} text
 * @returns {{ byRim: Record<string, string[]>, never: string[] }}
 */
export function parseWhiteLetterRules(text) {
  const rules = { byRim: {}, never: [] };
  for (const line of String(text || '').split(/\r?\n/)) {
    const parts = line.split('|').map((part) => part.trim());
    if (parts.length !== 2 || parts[0] === '') continue;
    const handles = parts[1].split(',').map((handle) => handle.trim().toLowerCase()).filter(Boolean);
    if (parts[0].toLowerCase() === 'never') {
      rules.never.push(...handles);
      continue;
    }
    const rim = (parts[0].match(/\d+/) || [''])[0];
    if (rim !== '') rules.byRim[rim] = [...(rules.byRim[rim] || []), ...handles];
  }
  return rules;
}

/**
 * Whether one tyre line offers white letters at this rim: never for a
 * `never` line, the rim's own list when a rule names the rim, else the
 * tyre's rogue.white_letter (null when unset).
 * @param {string|null|undefined} handle
 * @param {string|null} rimSize
 * @param {boolean|null} productValue
 * @param {{ byRim: Record<string, string[]>, never: string[] }|null} [rules]
 * @returns {boolean|null}
 */
export function whiteLetterForLine(handle, rimSize, productValue, rules) {
  const key = String(handle || '').toLowerCase();
  if (rules?.never?.includes(key)) return false;
  const rim = (String(rimSize || '').match(/\d+/) || [''])[0];
  const listed = rules?.byRim?.[rim];
  if (Array.isArray(listed)) return listed.includes(key);
  return productValue;
}

/** "All-season", "all season" and "ALL SEASON" are one tyre type. */
function tyreTypeKey(type) {
  return String(type || '').toLowerCase().replace(/[^a-z]/g, '');
}

/**
 * Tyre size rules by tyre type (Paul's fixes list, 26 Oct 2026, item 13:
 * "crafter and man tge all season size needs to be changed to 235/60R17").
 * A van's `tyre_sizes_json` holds one size per rim, the all-terrain size
 * most sets go out on; a rule swaps in another size for one tyre type. One
 * rule per line of the product section's "Tyre size by tyre type" setting:
 * `slug, slug | rim | tyre type | size`. Malformed lines are skipped.
 * @param {string|null|undefined} text
 * @returns {Array<{ slugs: string[], rim: string, type: string, size: string }>}
 */
export function parseTyreSizeRules(text) {
  const rules = [];
  for (const line of String(text || '').split(/\r?\n/)) {
    const parts = line.split('|').map((part) => part.trim());
    if (parts.length !== 4 || parts.some((part) => part === '')) continue;
    const slugs = parts[0].split(',').map((slug) => slug.trim().toLowerCase()).filter(Boolean);
    const rim = (parts[1].match(/\d+/) || [''])[0];
    if (slugs.length === 0 || rim === '') continue;
    rules.push({ slugs, rim, type: tyreTypeKey(parts[2]), size: parts[3] });
  }
  return rules;
}

/**
 * The tyre size for one tyre line: a matching rule's size, else the van's
 * own size for this rim. A rule only ever swaps a size the van already has
 * for this rim; it never invents one for a rim the van has none for.
 * @param {RogueVehicleSelection|null} vehicle
 * @param {string|null} rimSize
 * @param {string|null|undefined} tyreType
 * @param {Array<{ slugs: string[], rim: string, type: string, size: string }>} rules
 * @returns {string}
 */
export function tyreSizeForLine(vehicle, rimSize, tyreType, rules) {
  const size = tyreSizeForRim(vehicle?.tyre_sizes_json, rimSize);
  if (size === '') return '';
  const rim = (String(rimSize || '').match(/\d+/) || [''])[0];
  const slug = String(vehicle?.slug || '').toLowerCase();
  const type = tyreTypeKey(tyreType);
  const rule = (Array.isArray(rules) ? rules : []).find((r) => r.rim === rim && r.type === type && r.slugs.includes(slug));
  return rule ? rule.size : size;
}

/**
 * N1 (26 Sep review round 3): the popularity rank a tyre product carries
 * FOR ONE RIM SIZE, read from its `rogue.popularity_by_rim` metafield - a
 * JSON object keyed by bare rim diameter, e.g. `{"16": 1, "17": 2}` (same
 * "keyed by bare diameter, matched by bare diameter or '<n> inch'"
 * convention as {@link tyreSizeForRim}'s `tyre_sizes_json`, so both "17"
 * and "17 inch" resolve). A global single rank
 * (`rogue.popularity_rank`) told every van the same tyre was "most
 * popular", even on a rim it barely sells on; this is what lets
 * {@link availableTyreLines} prefer the rim-specific figure instead, and
 * fall back to the global one only where the platform hasn't set a
 * per-rim rank yet.
 * @param {string|object|null|undefined} popularityByRim
 * @param {string|null} rimSize
 * @returns {number|null}
 */
export function popularityRankForRim(popularityByRim, rimSize) {
  if (!popularityByRim || !rimSize) return null;

  let map = popularityByRim;
  if (typeof map === 'string') {
    try {
      map = JSON.parse(map);
    } catch {
      return null;
    }
  }
  if (!map || typeof map !== 'object') return null;

  const diameter = String(rimSize).trim().match(/\d+(?:\.\d+)?/);
  if (!diameter) return null;

  const candidates = [String(rimSize).trim(), diameter[0], diameter[0] + ' inch'];
  for (const key of candidates) {
    const rank = map[key];
    if (typeof rank === 'number' && Number.isFinite(rank)) return rank;
  }
  return null;
}

/**
 * The tyre lines the brand select offers: one entry per tyre product that
 * has a variant in stock in the van's size for this rim - never the "21
 * options with duplicates" list review finding 9 found, because each entry
 * here is a distinct sellable product, not a deduped vendor name. Empty
 * whenever {@link tyreSizeIsKnown} would be false, so the caller never has
 * to check both.
 * @param {Array<{ handle: string, title: string, brand: string, type?: string, variants?: Array<object> }>} tyreProducts
 * @param {RogueVehicleSelection|null} vehicle
 * @param {string|null} rimSize
 * @param {Array<{ slugs: string[], rim: string, type: string, size: string }>} [rules] - see {@link parseTyreSizeRules}
 * @param {{ byRim: Record<string, string[]>, never: string[] }|null} [letterRules] - see {@link parseWhiteLetterRules}
 * @returns {Array<{ handle: string, title: string, brand: string, type: string, variant: object }>}
 */
export function availableTyreLines(tyreProducts, vehicle, rimSize, rules = [], letterRules = null) {
  const products = Array.isArray(tyreProducts) ? tyreProducts : [];
  const lines = [];
  for (const product of products) {
    const variant = resolveTyreVariant({ tyreProduct: product, vehicle, rimSize, rules });
    if (variant && variant.available !== false) {
      const rawRank = product?.popularity_rank;
      const rawWhiteLetter = product?.white_letter;
      const globalRank = typeof rawRank === 'number' && Number.isFinite(rawRank) ? rawRank : null;
      const rimRank = popularityRankForRim(product?.popularity_by_rim, rimSize);
      lines.push({
        handle: product.handle,
        title: product.title,
        brand: product.brand,
        type: product.type || '',
        variant,
        // H1 (25 Sep review P1)/N1 (26 Sep review round 3): the rim-specific
        // rank wins whenever the platform has published one for THIS rim,
        // else the old global `rogue.popularity_rank`, else `null` (read
        // defensively either way - a missing/non-numeric value never wins a
        // "lowest rank" comparison by accident) - see defaultTyreHandle and
        // renderTyreRows' "Most popular" label, which both read this one
        // field regardless of which source it came from.
        popularityRank: rimRank !== null ? rimRank : globalRank,
        // M1 (25 Sep review, finding P5): `rogue.white_letter` - read
        // defensively, a missing/non-boolean value is `null` - see
        // tyreLineOffersWhiteLetter, which treats that the same as `true`
        // ("keep today's behaviour" for a line Paul hasn't answered yet).
        whiteLetter: whiteLetterForLine(product.handle, rimSize, typeof rawWhiteLetter === 'boolean' ? rawWhiteLetter : null, letterRules),
      });
    }
  }
  return lines;
}

/**
 * Which tyre line the buy panel should preselect the moment tyres are
 * enabled and nothing is chosen yet (H1, 25 Sep review, finding P1 -
 * Daniel's "the prices weren't changing" reproduction): the one button
 * whose job is "add tyres" must never leave the shopper able to add wheels
 * alone with no warning. Order of preference:
 *
 *  1. `preferredHandle` (Paul's own pick - the `default_tyre_line` block
 *     setting, snippets/rogue-fitment-gate.liquid), when it's actually on
 *     offer at this rim size.
 *  2. Failing that, the line with the best (lowest) `popularityRank` -
 *     `rogue.popularity_rank`, which the platform is publishing now (read
 *     defensively - see {@link availableTyreLines}).
 *  3. Failing that, simply the first line - `lines` arrives already sorted
 *     cheapest-first (see {@link sortedTyreLines}), so that's the cheapest
 *     tyre on offer, same as what a shopper scanning the rows would land on
 *     first.
 * @param {Array<{ handle: string, popularityRank?: number|null }>} lines
 * @param {string|null|undefined} preferredHandle
 * @returns {string|null} `null` only when `lines` is empty
 */
export function defaultTyreHandle(lines, preferredHandle) {
  const list = Array.isArray(lines) ? lines : [];
  if (list.length === 0) return null;

  const preferred = typeof preferredHandle === 'string' ? preferredHandle.trim() : '';
  if (preferred !== '' && list.some((line) => line.handle === preferred)) return preferred;

  let best = null;
  for (const line of list) {
    const rank = line?.popularityRank;
    if (typeof rank !== 'number' || !Number.isFinite(rank)) continue;
    if (best === null || rank < best.popularityRank) best = line;
  }
  if (best) return best.handle;

  return list[0].handle;
}

/**
 * N7 (26 Sep review round 3, finding N7): the one line among
 * `availableTyreLines` that actually earns the "Most popular" badge - the
 * lowest `popularityRank` anywhere in the list, regardless of which line
 * the shopper has ticked. Round 3's `renderTyreRows` badged whichever line
 * was SELECTED, so a shopper who picked Van Rated Winter (rank 15 of 15 at
 * 17 inch) saw "Most popular" printed on it - an untrue claim on a product
 * page. `null` whenever nothing in `lines` carries a rank at all: the
 * default is then just "the cheapest survivor" (see `defaultTyreHandle`),
 * not genuinely popular, and nothing should be labelled.
 * @param {Array<{ handle: string, popularityRank?: number|null }>} lines
 * @returns {string|null}
 */
export function mostPopularTyreHandle(lines) {
  const list = Array.isArray(lines) ? lines : [];
  let best = null;
  for (const line of list) {
    const rank = line?.popularityRank;
    if (typeof rank !== 'number' || !Number.isFinite(rank)) continue;
    if (best === null || rank < best.popularityRank) best = line;
  }
  return best ? best.handle : null;
}

/**
 * N7 (26 Sep review round 3, finding N7): the rows `renderTyreRows` shows -
 * cheapest first (`lines` already arrives that way, see `sortedTyreLines`),
 * except the one line {@link mostPopularTyreHandle} names (if any) always
 * leads, whether or not it's ticked. Round 3 instead moved whichever line
 * was SELECTED to the front on every click, so the real top-ranked line
 * dropped out of the collapsed five the moment a shopper picked something
 * else, and the row the shopper had just tapped jumped away from their
 * finger. The ticked line still has to be somewhere in the five shown when
 * collapsed - a shopper must always be able to see what they chose - so a
 * ticked line that falls outside the natural top five is swapped in for the
 * last slot instead, never moved to the front (unless it's also the most
 * popular line, in which case it's there already). Since 31 Oct 2026 (B6)
 * the collapsed list is three rows, not five: TYRE_ROWS_COLLAPSED.
 * @param {Array<{ handle: string, popularityRank?: number|null }>} lines - already cheapest-first
 * @param {string|null|undefined} selectedHandle
 * @param {boolean} expanded
 * @returns {Array<object>} a new array; `lines` itself is left untouched
 */
export const TYRE_ROWS_COLLAPSED = 3;

/** The reveal button under the collapsed tyre rows: "More tyres (11)". */
export function moreTyresLabel(remaining) {
  const count = Math.max(0, Number(remaining) || 0);
  return `More tyres (${count})`;
}

export function visibleTyreRows(lines, selectedHandle, expanded) {
  const list = Array.isArray(lines) ? [...lines] : [];

  let ordered = list;
  const popularHandle = mostPopularTyreHandle(list);
  if (popularHandle) {
    const index = list.findIndex((line) => line.handle === popularHandle);
    if (index > 0) {
      ordered = [...list];
      const [popular] = ordered.splice(index, 1);
      ordered.unshift(popular);
    }
  }

  if (expanded) return ordered;

  // B6 (31 Oct 2026): three rows when collapsed (was five), Most popular first.
  const visible = ordered.slice(0, TYRE_ROWS_COLLAPSED);
  const selectedIsVisible = !selectedHandle || visible.some((line) => line.handle === selectedHandle);
  if (!selectedIsVisible) {
    const selectedLine = ordered.find((line) => line.handle === selectedHandle);
    if (selectedLine) visible[visible.length - 1] = selectedLine;
  }
  return visible;
}

/**
 * N2 (26 Sep review round 3, finding N2): which tyre line should end up
 * selected at THIS rim size, and the one-line note to show under the rows
 * when the shopper's own earlier pick had to be swapped away from because
 * it genuinely isn't made in this size. Round 2 evidence: Transit Custom,
 * Rollex - Continental Vanco Contact (chosen at 16 inch) silently became
 * General AT3 All Terrain at 18 inch, where Vanco isn't offered, with
 * nothing on screen saying so; going back to 16 inch left General AT3
 * still selected even though Vanco was on offer again, because the old
 * `applyDefaultTyreSelection` only restores `lastHandle` when nothing at
 * all is selected yet, and the 18 inch default had already filled it.
 *
 * Order, every time this runs (a fresh render at this size, not just the
 * moment it changes - so a same-size re-render, e.g. the spare checkbox,
 * never disturbs an already-correct selection or resurfaces a stale note):
 *
 *  1. The shopper's own last pick (`lastHandle`), whenever it's on offer
 *     at this size - restored even if a different size's default ended up
 *     selected in between. No note: this is simply "still what you chose".
 *  2. Otherwise {@link defaultTyreHandle} - Paul's pick, then popularity,
 *     then cheapest, the same order H1 already established. A note names
 *     the swap ONLY when there was a real pick to swap away from
 *     (`lastHandle`/`lastTitle` both set) and a resolved size to name it
 *     in - a shopper who never made a pick at all (relying on the default
 *     throughout) sees no note, because nothing of theirs was lost.
 * @param {{ lastHandle: string|null|undefined, lastTitle: string|null|undefined, size: string|null|undefined, lines: Array<{handle:string,title:string}>, preferredHandle?: string|null }} params
 * @returns {{ handle: string|null, note: string }} `handle` is `null` only when `lines` is empty; `note` is "" unless a swap just happened
 */
export function resolveTyreSelectionForSize({ lastHandle, lastTitle, size, lines, preferredHandle }) {
  const list = Array.isArray(lines) ? lines : [];
  if (lastHandle && list.some((line) => line.handle === lastHandle)) {
    return { handle: lastHandle, note: '' };
  }

  const handle = defaultTyreHandle(list, preferredHandle);
  if (!handle) return { handle: null, note: '' };

  const cleanedLastTitle = typeof lastTitle === 'string' ? lastTitle.trim() : '';
  const cleanedSize = typeof size === 'string' ? size.trim() : '';
  if (!lastHandle || cleanedLastTitle === '' || cleanedSize === '') return { handle, note: '' };

  const chosenTitle = (list.find((line) => line.handle === handle)?.title || '').trim();
  if (chosenTitle === '') return { handle, note: '' };

  // 31 Oct 2026 (Skye, from Rogue): no tyre-size numbers before checkout.
  return { handle, note: `${cleanedLastTitle} isn't made in your van's size, so we've chosen ${chosenTitle}.` };
}

/**
 * The buy box's tyre line (31 Oct 2026, Skye, from Rogue): "Sized for your
 * Transit Custom 2011 to 2023", never the size itself. Shoppers looked their
 * size up, or asked a chatbot or a forum, and were told the size Rogue fits
 * would not work when it does. Checkout and the order still show the size.
 * @param {Record<string, unknown>|null} vehicle
 * @returns {string}
 */
export function tyreSizedForText(vehicle) {
  const van = shortVanLabel(vehicle);
  return van ? `Sized for your ${van}` : 'Sized for your vehicle';
}

/**
 * Whether the Black/White lettering choice should be offered for a tyre
 * line (M1, 25 Sep review, finding P5): only some lines actually come with
 * raised white letters, and often only in some sizes, so offering the
 * choice on every line let a buyer pay for, and expect, letters a tyre
 * doesn't have. Reads `rogue.white_letter` (via {@link availableTyreLines})
 * defensively: `false` hides the choice, and anything else - `true`, or the
 * metafield simply missing (`null`) - keeps today's behaviour of offering
 * it, since most tyre products have not had this metafield set yet.
 * @param {{ whiteLetter?: boolean|null }|null|undefined} line
 * @returns {boolean}
 */
export function tyreLineOffersWhiteLetter(line) {
  return line?.whiteLetter !== false;
}

/**
 * "<wheel title>, <size>, <finish>" - the "Fitted to" line-item property
 * that tells the warehouse which wheel line a charged tyre line belongs to
 * (review Blocker 2: previously the only record was an unpriced note on the
 * wheel itself, and this string didn't exist at all). Any missing part is
 * left out rather than printed as "undefined" or an empty segment.
 * @param {{ wheelTitle?: string|null, size?: string|null, finish?: string|null }} params
 * @returns {string}
 */
export function tyreFittedToLabel({ wheelTitle, size, finish }) {
  const parts = [wheelTitle, size ? formatSizeLabel(size) : '', finish]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part !== '');
  return parts.join(', ');
}

/**
 * Review 2, finding 12: the inline confirmation shown after a successful
 * add - "Added: 4 x Raptor 16 inch and 4 tyres." - since the theme's cart
 * drawer exists but doesn't auto-open (auto_open_cart_drawer is off in
 * config/settings_data.json - checked, not assumed). Used for both the
 * wheel-and-tyre add (RogueFitmentGate.submitWheelAndTyre) and Horizon's
 * own native single-item submit, driven by the CartLinesUpdateEvent both
 * paths already dispatch - see RogueFitmentGate.bindAddConfirmation.
 * Returns "" when there isn't enough to say anything with (no usable
 * quantity, or no wheel title at all).
 * @param {{ quantity: number|null|undefined, wheelTitle?: string|null, wheelSize?: string|null, tyresIncluded?: boolean }} params
 * @returns {string}
 */
export function addConfirmationText({ quantity, wheelTitle, wheelSize, tyresIncluded }) {
  const qty = typeof quantity === 'number' && Number.isFinite(quantity) && quantity > 0 ? Math.round(quantity) : null;
  const title = typeof wheelTitle === 'string' ? wheelTitle.trim() : '';
  if (qty === null || title === '') return '';

  const sizeLabel = wheelSize ? ` ${formatSizeLabel(String(wheelSize))}` : '';
  const tyresPart = tyresIncluded ? ` and ${qty} ${qty === 1 ? 'tyre' : 'tyres'}` : '';
  return `Added: ${qty} x ${title}${sizeLabel}${tyresPart}.`;
}

/**
 * The two-line `/cart/add.js` payload for "Add matching tyres" (review
 * Blocker 2): the wheel variant and the resolved tyre variant, same
 * quantity, each carrying the line-item properties that tie them back
 * together - `Fitted to` / `Tyre size` / `Lettering` on the tyre line,
 * `Tyres` on the wheel line. Returns null when there is no wheel variant or
 * no usable quantity at all; the guarded caller then makes no request.
 * With no `tyreVariantId` (tyres off, or nothing resolved yet) this still
 * returns a valid single-item payload, so the same function covers both
 * shapes the caller ever needs to post.
 * @param {{
 *   wheelVariantId: string|number|null|undefined,
 *   tyreVariantId?: string|number|null,
 *   quantity: number,
 *   wheelTitle?: string|null,
 *   wheelSize?: string|null,
 *   wheelFinish?: string|null,
 *   tyreSize?: string|null,
 *   letterColour?: string|null,
 *   tyreLine?: string|null,
 *   setId?: string|null,
 *   spare?: boolean,
 *   preorder?: boolean,
 *   verificationProperties?: Record<string, string>,
 * }} params
 * @returns {{ items: Array<{ id: string|number, quantity: number, properties?: Record<string, string> }> } | null}
 */
/** A quantity is an exact physical count, never a rounded order intent. */
export function wheelQuantityValue(value) {
  const quantity = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value.trim()) ? Number(value) : NaN;
  return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
}

/** Explicit intent only. Old Spare=Yes means one spare, never quantity modulo four. */
export function packageSpareQuantity(item) {
  const quantity = wheelQuantityValue(item?.quantity) || 0;
  const properties = item?.properties || {};
  if (Object.prototype.hasOwnProperty.call(properties, '_spare_quantity')) {
    const count = Number(properties._spare_quantity);
    return Number.isSafeInteger(count) && count >= 0 ? Math.min(quantity, count) : 0;
  }
  return properties.Spare === 'Yes' ? Math.min(quantity, 1) : 0;
}

export function packageSpareProperties(properties, spareQuantity) {
  const next = { ...(properties || {}), _spare_quantity: String(spareQuantity) };
  if (spareQuantity > 0) next.Spare = 'Yes';
  else delete next.Spare;
  return next;
}

export function spareCartChange(item, { included, remove = false }) {
  const quantity = wheelQuantityValue(item?.quantity);
  if (!item?.key || quantity === null) return null;
  const count = packageSpareQuantity(item);
  if (remove && count === 0) return null;
  return { id: item.key, quantity: remove ? quantity - 1 : quantity,
    properties: packageSpareProperties(item.properties, remove ? Math.max(0, count - 1) : included ? 1 : 0) };
}

/**
 * The buy box's spare tick after the wheel count changes (31 Oct 2026, Skye:
 * picking 5 wheels should tick "One of these wheels is a spare"). Moving to
 * the set size plus one ticks it; moving away clears it again, but only when
 * it was this rule that ticked it, never the shopper. The tick stays visible
 * and the shopper can always change it: the basket still records only what
 * the box says (packageSpareProperties), never a spare guessed from quantity.
 * @param {{ quantity: number|null, previousQuantity: number|null, plusSpareSize: number, checked: boolean, auto: boolean }} state
 * @returns {{ checked: boolean, auto: boolean }}
 */
export function spareTickForQuantity({ quantity, previousQuantity, plusSpareSize, checked, auto }) {
  const plusSpare = quantity !== null && quantity === plusSpareSize;
  if (plusSpare && previousQuantity !== quantity && !checked) return { checked: true, auto: true };
  if (!plusSpare && checked && auto) return { checked: false, auto: false };
  return { checked, auto: checked && auto };
}

/** Shopify's availability/policy wins over a merchandising preorder label. */
export function variantQuantityError(variant, quantity, alreadyInCart = 0) {
  if (wheelQuantityValue(quantity) === null) return 'Enter a whole number of wheels, at least 1.';
  if (variant?.available === false) return 'This size and finish is not available to order.';
  const min = Number(variant?.quantity_min) || 1;
  const step = Number(variant?.quantity_increment) || 1;
  const max = Number(variant?.quantity_max);
  if (quantity < min || (quantity - min) % step !== 0) return `This variant must be ordered from ${min}, in steps of ${step}.`;
  if (max > 0 && quantity + alreadyInCart > max) return `This variant allows up to ${max} in your basket.`;
  if (variant?.inventory_management && variant.inventory_policy === 'deny' && Number.isFinite(variant.inventory_quantity)) {
    const remaining = Math.max(0, variant.inventory_quantity - alreadyInCart);
    if (quantity > remaining) return `Only ${remaining} more of this variant can be added to your basket.`;
  }
  return '';
}

export function createWheelPackageId() {
  return globalThis.crypto?.randomUUID?.() || `wheel-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function buildAddToBasketPayload({
  wheelVariantId,
  tyreVariantId,
  quantity,
  wheelTitle,
  wheelSize,
  wheelFinish,
  tyreSize,
  letterColour,
  tyreLine,
  setId,
  van,
  vehicleSlug,
  spare,
  preorder,
  due = '',
  verificationProperties = {},
}) {
  const qty = wheelQuantityValue(quantity);
  if (!wheelVariantId || qty === null) return null;
  const cleanedSetId = typeof setId === 'string' && setId.trim() ? setId.trim() : createWheelPackageId();

  // Review finding (Major): basket lines carried no record of which van
  // they were added for, so a later van change silently rewrote the
  // basket's ("Vehicle" cart attribute) and every line onscreen alike -
  // measured live: a Rocky line added under the Transporter read as if it
  // were for the Ducato once the shopper switched back, with no warning.
  // "Van" on the line itself (not hidden - it reads next to Tyres/Fitted
  // to/Lettering like any other order detail) is what
  // snippets/cart-products-row.liquid compares against the currently
  // stored van to show that warning.
  const cleanedVan = typeof van === 'string' ? van.trim() : '';

  const wheelItem = { id: wheelVariantId, quantity: qty };
  const wheelProperties = packageSpareProperties({ ...verificationProperties, _set_id: cleanedSetId }, spare ? 1 : 0);
  if (typeof vehicleSlug === 'string' && vehicleSlug.trim()) wheelProperties._vehicle_slug = vehicleSlug.trim();
  const tyreLabel = typeof tyreLine === 'string' ? tyreLine.trim() : '';
  if (tyreLabel !== '') wheelProperties.Tyres = tyreLabel;
  if (cleanedVan !== '') wheelProperties.Van = cleanedVan;
  // packageSpareProperties records explicit intent for the fitter; the
  // physical quantity already includes this spare.
  // N10 (26 Sep review round 3): the wheel is the line actually on
  // pre-order - the tyre line only rides along with it (see below) - so
  // the wheel line carries "Pre-order: yes" too, not just the tyre line.
  // Without this the basket read as if the TYRES were on pre-order and
  // the wheels weren't.
  if (preorder) wheelProperties['Pre-order'] = 'yes';
  // 27 Oct 2026 (Paul's rows 12 and 24): the due date on the order itself, in
  // his words, so the pick sheet and the customer's receipt both say it.
  const cleanedDue = typeof due === 'string' ? due.trim().replace(/\s+/g, ' ') : '';
  if (cleanedDue !== '') wheelProperties.Due = cleanedDue;
  if (Object.keys(wheelProperties).length > 0) wheelItem.properties = wheelProperties;

  if (!tyreVariantId) return { items: [wheelItem] };

  const tyreProperties = {};
  if (wheelProperties._vehicle_slug) tyreProperties._vehicle_slug = wheelProperties._vehicle_slug;
  const fittedTo = tyreFittedToLabel({ wheelTitle, size: wheelSize, finish: wheelFinish });
  if (fittedTo !== '') tyreProperties['Fitted to'] = fittedTo;
  const cleanedSize = typeof tyreSize === 'string' ? tyreSize.trim() : '';
  if (cleanedSize !== '') tyreProperties[TYRE_SIZE_PROPERTY] = cleanedSize;
  // Always set, same default as buildOrderFieldsCartData's "Tyre letter
  // colour" cart attribute - a tyre line being added always has a lettering
  // colour (Black unless White was chosen), never an "unset" state.
  const cleanedLetter = typeof letterColour === 'string' ? letterColour.trim().toLowerCase() : '';
  tyreProperties['Lettering'] = cleanedLetter === 'white' ? 'White' : 'Black';
  if (cleanedVan !== '') tyreProperties.Van = cleanedVan;
  // N6 (26 Sep review round 3): the wheel variant being on pre-order still
  // charges and packs the tyre line now - this keeps the two together on
  // the pick sheet ("Pre-order: yes" beside the wheel's own pre-order
  // state, set above - see N10) rather than leaving the fitter to notice a
  // boxed, paid-for tyre sitting on a wheel that hasn't arrived yet.
  if (preorder) tyreProperties['Pre-order'] = 'yes';
  if (cleanedDue !== '') tyreProperties.Due = cleanedDue;

  // Review N9: a shared, hidden (leading underscore - see cart-products.liquid's
  // own "_" convention for hiding a property from the visible receipt) line
  // property is the only thing that ties this wheel line to this exact tyre
  // line - see cartLinePairs/cartPairingCorrections below for what reads it
  // back out of the cart.
  if (cleanedSetId !== '') {
    wheelItem.properties = { ...(wheelItem.properties || {}), _set_id: cleanedSetId };
    tyreProperties._set_id = cleanedSetId;
  }

  const tyreItem = { id: tyreVariantId, quantity: qty, properties: tyreProperties };
  return { items: [wheelItem, tyreItem] };
}

/* -------------------------------------------------------------------------
 * Pure functions: cart line pairing (review N9 - group the wheel and tyre
 * lines in the cart). A wheel line and the tyre line fitted to it share a
 * `_set_id` line property (see buildAddToBasketPayload above); everything
 * here works off a small, DOM-free line shape so it's testable without a
 * cart fetch - see RogueCartPairing/initRogueCartPairing for the one place
 * that turns a real `/cart.js` response into this shape.
 * ---------------------------------------------------------------------- */

/**
 * @typedef {object} RogueCartLineLite
 * @property {string} key
 * @property {number} quantity
 * @property {string} setId - "" when the line carries no `_set_id` property
 * @property {'wheel'|'tyre'|''} role
 * @property {Record<string, string>} [properties] - the line's raw cart
 *   properties (N11, 26 Sep review round 3 - read to check a wheel line's
 *   own `Spare` property against its current quantity; optional so every
 *   existing caller/fixture that never carried this still works unchanged)
 */

/**
 * Every wheel/tyre pair in the cart: a Tyre-role line and the Wheel-role
 * line sharing its `_set_id`. A tyre whose `_set_id` matches no wheel line
 * (the wheel was removed some other way - see cartPairingCorrections) is
 * simply left out, same as a tyre bought on its own (setId "").
 * @param {RogueCartLineLite[]} items
 * @returns {Array<{ wheelKey: string, tyreKey: string, setId: string }>}
 */
export function cartLinePairs(items) {
  const list = Array.isArray(items) ? items : [];
  /** @type {Map<string, string>} */
  const wheelKeyBySetId = new Map();
  for (const item of list) {
    if (item && item.role === 'wheel' && item.setId) wheelKeyBySetId.set(item.setId, item.key);
  }

  const pairs = [];
  for (const item of list) {
    if (item && item.role === 'tyre' && item.setId && wheelKeyBySetId.has(item.setId)) {
      pairs.push({ wheelKey: /** @type {string} */ (wheelKeyBySetId.get(item.setId)), tyreKey: item.key, setId: item.setId });
    }
  }
  return pairs;
}

/**
 * @param {RogueCartLineLite[]} items
 * @param {string} wheelKey
 * @returns {string|null} the paired tyre line's key, or null when this wheel has no tyres attached
 */
export function pairedTyreKey(items, wheelKey) {
  const pair = cartLinePairs(items).find((p) => p.wheelKey === wheelKey);
  return pair ? pair.tyreKey : null;
}

/**
 * Keep each package's tyre quantity equal to its wheel quantity and remove
 * orphaned paired tyres. Preserve explicit spare intent on normal quantity
 * edits, clamping only impossible spare counts. Legacy unpaired tyre lines
 * are left untouched. A separate control removes or reclassifies a spare.
 * @param {RogueCartLineLite[]} items
 * @returns {Array<{ key: string, quantity: number, properties?: Record<string, string> }>}
 */
export function cartPairingCorrections(items) {
  const list = Array.isArray(items) ? items : [];
  const corrections = [];

  for (const pair of cartLinePairs(list)) {
    const wheel = list.find((item) => item.key === pair.wheelKey);
    const tyre = list.find((item) => item.key === pair.tyreKey);
    if (wheel && tyre && wheel.quantity !== tyre.quantity) {
      corrections.push({ key: tyre.key, quantity: wheel.quantity });
    }
  }

  // Reconcile derived package properties after edits, keeping unrelated intent.
  for (const item of list) {
    if (!item || item.role !== 'wheel') continue;
    let properties = { ...(item.properties || {}) };
    if (Object.prototype.hasOwnProperty.call(properties, '_spare_quantity')) {
      properties = packageSpareProperties(properties, packageSpareQuantity(item));
    }
    if (item.setId && !pairedTyreKey(list, item.key)) {
      delete properties.Tyres;
      delete properties['Tyre quantity'];
      delete properties[TYRE_SIZE_PROPERTY];
      delete properties[LEGACY_TYRE_SIZE_PROPERTY];
    }
    if (JSON.stringify(properties) !== JSON.stringify(item.properties || {})) {
      corrections.push({ key: item.key, quantity: item.quantity, properties });
    }
  }

  const pairedSetIds = new Set(cartLinePairs(list).map((pair) => pair.setId));
  for (const item of list) {
    if (item.role === 'tyre' && item.setId && !pairedSetIds.has(item.setId)) {
      corrections.push({ key: item.key, quantity: 0 });
    }
  }

  return corrections;
}

/**
 * The storefront's active currency: a market's local currency once Europe is
 * on (D65, 26 Oct 2026; Shopify sets `window.Shopify.currency.active`), and
 * GBP everywhere else, including Node tests.
 * @returns {string} an ISO 4217 code
 */
export function activeCurrency() {
  const code = typeof window !== 'undefined' ? window.Shopify?.currency?.active : undefined;
  return typeof code === 'string' && /^[A-Z]{3}$/.test(code) ? code : 'GBP';
}

/**
 * Formats a non-negative whole number of pence the way the shop's own
 * `money_without_trailing_zeros` filter would - "£1,660" for a round
 * number, "£219.99" otherwise. In GBP exactly as before; with a European
 * market's local currency active the amount Liquid handed over is already in
 * that currency, so it is shown with that currency's symbol ("€1,940"),
 * never a pound sign. This exists purely for the
 * combined wheels-plus-tyres total, a runtime sum Liquid cannot precompute
 * since it depends on the shopper's chosen tyre line and quantity - see
 * {@link combinedSetPriceCents}. Every other price on this page stays a
 * Liquid-formatted string, same as before.
 * @param {number} pence
 * @returns {string} "" when `pence` is not a usable non-negative number
 */
export function formatGbpPence(pence, currency = activeCurrency()) {
  if (typeof pence !== 'number' || !Number.isFinite(pence) || pence < 0) return '';
  const whole = Math.round(pence);
  const pounds = Math.floor(whole / 100);
  const remainder = whole % 100;
  if (currency !== 'GBP') {
    const digits = remainder === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(whole / 100);
    } catch {
      // An unknown code falls through to the plain GBP shape below rather than throwing.
    }
  }
  if (remainder === 0) return `£${pounds.toLocaleString('en-GB')}`;
  return `£${pounds.toLocaleString('en-GB')}.${String(remainder).padStart(2, '0')}`;
}

/**
 * The combined wheels-plus-tyres price, in pence, for the quantity actually
 * going into the basket (tyres always match the wheel quantity - see
 * buildAddToBasketPayload). Falls back to the wheels-only total whenever
 * tyres are off or the tyre price isn't usable yet (nothing resolved/chosen
 * - see availableTyreLines), so a broken or unset tyre price never blocks
 * the wheels-only line from showing.
 * @param {{ wheelUnitCents: number, tyreUnitCents?: number|null, quantity: number, tyresEnabled: boolean }} params
 * @returns {number|null} null only when the wheel price itself isn't usable
 */
export function combinedSetPriceCents({ wheelUnitCents, tyreUnitCents, quantity, tyresEnabled }) {
  const wheelsTotal = setPriceCents(wheelUnitCents, quantity);
  if (wheelsTotal === null) return null;
  if (!tyresEnabled || typeof tyreUnitCents !== 'number' || !Number.isFinite(tyreUnitCents) || tyreUnitCents < 0) {
    return wheelsTotal;
  }
  return wheelsTotal + Math.round(tyreUnitCents) * Math.round(quantity);
}

/**
 * The line item properties that record a tyre add-on against the wheel line.
 *
 * Decision (2026-09-13): the tyres toggle records the chosen brand and the
 * van-derived size as properties on the wheel line rather than posting a
 * second cart line. Shopify's classic product form posts one variant, and
 * Rogue fits and balances the tyres onto the wheels before they ship, so one
 * line with the tyre spelled out on it is what the warehouse actually needs.
 *
 * Superseded for the JS-enabled path by {@link buildAddToBasketPayload}
 * (review Blocker 2 - this was the bug: tyres shown at "+ £220 each" but
 * never charged, because they only ever existed as a note here). Kept, and
 * still wired up in RogueFitmentGate.updateTyreFields, purely as the no-JS
 * degrade path: a native form submit (no JS at all, or a keyboard-triggered
 * implicit submit that this build's click-only intercept cannot catch - see
 * RogueFitmentGate.handlePickerClick) still posts one wheel line, and this
 * is what lets that one line at least say a tyre was wanted, unpriced,
 * rather than losing the request outright.
 * @param {{ enabled: boolean, brand?: string|null, size?: string|null, quantity?: number }} params
 * @returns {Record<string, string>}
 */
/**
 * The tyre line's size note (Paul's fixes list row 13, 27 Oct 2026): "Likely
 * tyre size", because Rogue may still change the tyres after checking the
 * van. Lines already in a basket may carry the old "Tyre size"; both are
 * removed and hidden alike, and the backend reads either.
 */
export const TYRE_SIZE_PROPERTY = 'Likely tyre size';
export const LEGACY_TYRE_SIZE_PROPERTY = 'Tyre size';

export function buildTyreCartProperties({ enabled, brand, size, quantity }) {
  if (!enabled || typeof brand !== 'string' || brand.trim() === '') return {};
  const properties = { Tyres: brand.trim() };
  if (typeof size === 'string' && size.trim() !== '') properties[TYRE_SIZE_PROPERTY] = size.trim();
  if (typeof quantity === 'number' && quantity > 0) properties['Tyre quantity'] = String(Math.round(quantity));
  return properties;
}

/**
 * The cart attributes that replace the six custom fields the old Wix
 * checkout collected for Paul's order sheet (Vehicle is added separately by
 * the caller, from the stored van, since this helper has no vehicle of its
 * own to normalise). Keys match the Google Sheet feed exactly, so they must
 * not be renamed casually.
 *
 * Normalisation rules:
 * - `registration`: trimmed, upper-cased and stripped of all spaces, then
 *   omitted entirely when nothing is left (it is an optional field).
 * - `upplating`: omitted entirely unless the value is exactly "Yes" or "No"
 *   (case/whitespace tolerant) - L2 (25 Sep review, finding P10): this used
 *   to default anything else to "No", which the product page wrote the
 *   moment a wheel was added, before the shopper had ever seen the question
 *   - an order could read "Upplating: No" when nobody had answered it
 *   either way. It now stays unset until the shopper actually picks one, in
 *   the basket's own "Your details" panel (sections/main-cart.liquid,
 *   RogueCartDetails.postOrderFields) - see that panel's radio group, which
 *   likewise starts with neither option checked.
 * - `currentTyreSize`: trimmed, omitted when blank (optional).
 * - `letterColour`: only included when `tyresEnabled` is true - with tyres
 *   off there is nothing to letter, so the field is left out rather than
 *   sent as an empty or misleading value. "White" only when the value is
 *   exactly that, "Black" otherwise (the default). Callers pass
 *   `tyresEnabled` as "a real tyre line is actually resolved", not simply
 *   "Wheels and tyres is the current choice" - see
 *   RogueFitmentGate.effectiveLetterColour/updateCartAttributesFields - so
 *   this is never written for the H4 "known size, nothing to sell" state,
 *   where there is no tyre line to letter either.
 * @param {{ registration?: string|null, upplating?: string|null, currentTyreSize?: string|null, letterColour?: string|null, tyresEnabled?: boolean }} params
 * @returns {Record<string, string>}
 */
export function buildOrderFieldsCartData({ registration, upplating, currentTyreSize, letterColour, tyresEnabled }) {
  const attributes = {};

  const cleanedRegistration =
    typeof registration === 'string' ? registration.trim().toUpperCase().replace(/\s+/g, '') : '';
  if (cleanedRegistration !== '') attributes['Vehicle registration'] = cleanedRegistration;

  const cleanedUpplating = typeof upplating === 'string' ? upplating.trim().toLowerCase() : '';
  if (cleanedUpplating === 'yes') attributes['Upplating'] = 'Yes';
  else if (cleanedUpplating === 'no') attributes['Upplating'] = 'No';

  const cleanedTyreSize = typeof currentTyreSize === 'string' ? currentTyreSize.trim() : '';
  if (cleanedTyreSize !== '') attributes['Current tyre size'] = cleanedTyreSize;

  if (tyresEnabled) {
    const cleanedLetterColour = typeof letterColour === 'string' ? letterColour.trim().toLowerCase() : '';
    attributes['Tyre letter colour'] = cleanedLetterColour === 'white' ? 'White' : 'Black';
  }

  return attributes;
}

/**
 * Review SHOP-007 (17 Oct 2026): "Tyre letter colour" is a whole-basket
 * attribute written when a fitted tyre line is added (above) and was never
 * cleared - measured on the published theme, an empty basket still carried
 * "Black" after its last fitted package was removed. Returns the clearing
 * write when a `/cart.js` response still holds the attribute but no Tyre
 * line remains, or null when there is nothing to clear. The order-level
 * "Vehicle" attribute is deliberately not touched here.
 * @param {{ attributes?: Record<string, unknown>|null, items?: Array<{ product_type?: string }> }|null} cart
 * @returns {Record<string, string>|null}
 */
export function staleTyreLetterAttributes(cart) {
  const value = cart?.attributes?.['Tyre letter colour'];
  if (typeof value !== 'string' || value.trim() === '') return null;
  const items = Array.isArray(cart?.items) ? cart.items : [];
  if (items.some((item) => item?.product_type === 'Tyre')) return null;
  return { 'Tyre letter colour': '' };
}

/**
 * The short name the artboards use in badges and breadcrumbs ("T6"), rather
 * than the full stored display name ("Volkswagen Transporter T6, 2015 on").
 * Falls back through generation -> model -> display name, so there is always
 * something to print. An unlisted van (S3.2a) has neither generation nor
 * model, and its `display_name` carries the internal "Unlisted: " prefix
 * (see UNLISTED_VEHICLE_DISPLAY_PREFIX) - never shown to the shopper - so
 * this returns the typed text on its own instead of falling through to it.
 * @param {Record<string, unknown>|null} vehicle
 * @returns {string}
 */
export function vanShortName(vehicle) {
  if (!vehicle) return '';
  if (isUnlistedVehicle(vehicle)) return typedVehicleText(vehicle);
  for (const key of ['generation', 'model', 'display_name']) {
    const value = vehicle[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return '';
}

/**
 * The van name a product card's "Fits your ..." badge uses (review findings
 * 17 and 18): model plus generation, "Ducato Maxi Heavy L4" or "Transporter
 * T5 to T6.1", never the make as well (too long for a card) and never the
 * bare generation (a Ducato badge reading "Fits your Maxi Heavy L4" drops the
 * van). Falls back to vanShortName when either part is missing. The
 * breadcrumb, van chip and collection summary keep the short form.
 * @param {Record<string, unknown>|null} vehicle
 * @returns {string}
 */
export function cardFitsVanName(vehicle) {
  if (!vehicle) return '';
  if (isUnlistedVehicle(vehicle)) return typedVehicleText(vehicle);
  const model = typeof vehicle.model === 'string' ? vehicle.model.trim() : '';
  const generation = typeof vehicle.generation === 'string' ? vehicle.generation.trim() : '';
  // 25 Oct 2026: "Fits your Ranger All years" / "Check it fits my Defender
  // All years" read oddly; a literal "All years" is dropped, as shortVanLabel does.
  if (model && generation.toLowerCase() === 'all years') return model;
  if (model && generation) return model + ' ' + generation;
  return vanShortName(vehicle);
}

/**
 * Spec `direction-a-plus-2026-09-24.md` 2.3/9 (BUY item 8b): the short van
 * name used in the buy box fit line, the header van bar and the van picker's
 * "Your vehicle" tile - model plus generation, e.g. "Ducato L1, L2, L3",
 * "Transporter T5 to T6.1". Unlike {@link cardFitsVanName}, a generation of
 * literally "All years" (many single-generation vans carry that value, e.g.
 * Berlingo - see `data/paul-sheets/fitment-import-report.md`) is dropped
 * rather than printed, so the label reads "Berlingo", not "Berlingo All
 * years". Falls back like {@link vanShortName} whenever there's no model to
 * pair a generation with.
 * @param {Record<string, unknown>|null} vehicle
 * @returns {string}
 */
export function shortVanLabel(vehicle) {
  if (!vehicle) return '';
  if (isUnlistedVehicle(vehicle)) return typedVehicleText(vehicle);
  const model = typeof vehicle.model === 'string' ? vehicle.model.trim() : '';
  const generationRaw = typeof vehicle.generation === 'string' ? vehicle.generation.trim() : '';
  const generation = generationRaw.toLowerCase() === 'all years' ? '' : generationRaw;
  if (model && generation) return `${model} ${generation}`;
  if (model) return model;
  if (generation) return generation;
  return vanShortName(vehicle);
}

/**
 * Review 2, finding 22: with a van stored, the home selector's confirm
 * button read "Show wheels that fit" - the same generic copy shown with no
 * van at all, no acknowledgement that a return visitor is even recognised.
 * Names the model and generation, same short form as the card "Fits your
 * ..." badge (see {@link cardFitsVanName}), e.g. "Show wheels for your
 * Ducato L1, L2, L3". Falls back to the plain default label whenever there
 * is nothing to name a van by - no vehicle at all, or one with neither a
 * model nor a generation on file.
 * @param {Record<string, unknown>|null} vehicle
 * @param {string} defaultLabel
 * @returns {string}
 */
export function selectorConfirmLabel(vehicle, defaultLabel) {
  const van = cardFitsVanName(vehicle);
  return van === '' ? defaultLabel : `Show wheels for your ${van}`;
}

/**
 * What the confirm button should read right now - the single function
 * {@link RogueVehicleSelector.updateConfirmState} calls on every change
 * (make, model, generation, entering/exiting "my van isn't listed"), so the
 * label can never fall out of sync with whichever van is currently picked.
 *
 * Bug this replaces (found 26 Sep 2026, reproduced live picking a T5 on top
 * of an already-stored Ducato): a spec 6.2 refactor turned
 * updateConfirmState() into a no-op ("nothing left to update now the button
 * no longer disables/enables"), missing that it had a second job -
 * {@link selectorConfirmLabel}'s "Show wheels for your ..." text. Every
 * later change to make/model/generation left the CONFIRM BUTTON reading
 * whichever van produced the label last (the stored Ducato, from
 * preselectFromStore, since nothing ever ran again after it), while the
 * selects themselves showed T5 - confirm would have shown Ducato wheels
 * with a button that still said "Ducato". Extracted as a pure function
 * (rather than fixed inline) so this exact scenario - two calls, in
 * sequence, each with a different vehicle - has a regression test that
 * does not need a DOM to run.
 * @param {boolean} unlistedMode
 * @param {Record<string, unknown>|null} selectedVehicle
 * @param {string} defaultLabel
 * @param {string} unlistedLabel
 * @returns {string}
 */
export function confirmButtonLabel(unlistedMode, selectedVehicle, defaultLabel, unlistedLabel) {
  if (unlistedMode) return unlistedLabel;
  return selectorConfirmLabel(selectedVehicle, defaultLabel);
}

/**
 * The note beside the Size label: "Both sizes fit your T6" when exactly two
 * sizes fit, "Three sizes fit your T6" beyond that, and nothing at all when
 * there is no choice to make or no van stored.
 * @param {number} sizeCount - how many sizes are on offer
 * @param {string|null|undefined} vanName
 * @returns {string}
 */
export function sizeNoteText(sizeCount, vanName) {
  const van = typeof vanName === 'string' ? vanName.trim() : '';
  if (van === '' || typeof sizeCount !== 'number' || sizeCount < 2) return '';
  if (sizeCount === 2) return `Both sizes fit your ${van}`;
  const word = numberWord(sizeCount);
  return `${word.charAt(0).toUpperCase()}${word.slice(1)} sizes fit your ${van}`;
}

/**
 * The 'fits' fit-line text (spec 2.3): "Fits your [van short]", or "Fits
 * your [van short] in 16 inch" once exactly one size fits and the now-
 * dropped Size step's own line folds in here instead (review finding,
 * Major - see RogueFitmentGate.renderSizeSelector's singleFitSizeLabel,
 * the only caller that ever passes a non-empty `sizeLabel`).
 * @param {Record<string, unknown>|null} vehicle
 * @param {string|null} [sizeLabel] - e.g. "16 inch"; omit or "" for the plain line
 * @returns {string}
 */
export function fitsBadgeText(vehicle, sizeLabel) {
  if (requiresManualReview(vehicle)) return manualReviewBadgeText(vehicle);
  const base = `Fits your ${shortVanLabel(vehicle)}`;
  const size = typeof sizeLabel === 'string' ? sizeLabel.trim() : '';
  return size === '' ? base : `${base} in ${size}`;
}

/** The event the buy panel fires when the resolved variant changes. */
export const ROGUE_VARIANT_CHANGE_EVENT = 'rogue:variant-change';

/**
 * The last `rogue:variant-change` detail announced on this page. The buy
 * panel can announce its first pick (a finish that fits the stored van)
 * before <rogue-product-gallery> is listening, so the gallery replays
 * this on connect instead of missing it (ready check round 5, 26 Sep 2026).
 * @type {object|null}
 */
let lastVariantChangeDetail = null;

/**
 * The collection URL the vehicle selector navigates to on confirm. Rim size
 * is optional (the selector's confirm button no longer requires it - see
 * {@link RogueVehicleSelector.updateConfirmState}), so this only appends a
 * filter when one was actually chosen.
 *
 * Uses Shopify's native variant-option filter param, `filter.v.option.size`
 * (rogue-collection.liquid already renders its filter chips from
 * `collection.filters`/`url_to_add`, i.e. the Search & Discovery facets, so
 * the storefront filter app is already active on these collections - a
 * plain query param the template would have to read itself was the
 * alternative, used only if that filter turns out not to be configured).
 * The value is formatted the same way a product's Size option value is
 * ("17 inch" - see {@link formatSizeLabel}) so it matches exactly.
 * @param {string} collectionsRootPath - e.g. "/collections" (locale-aware root)
 * @param {string} collectionHandle
 * @param {string|number|null|undefined} rimSize
 * @returns {string}
 */
export function buildVehicleCollectionUrl(collectionsRootPath, collectionHandle, rimSize) {
  const root = typeof collectionsRootPath === 'string' && collectionsRootPath !== '' ? collectionsRootPath : '/collections';
  const base = `${root}/${collectionHandle}`;
  const size = rimSize !== null && rimSize !== undefined ? String(rimSize).trim() : '';
  if (size === '') return base;
  return `${base}?filter.v.option.size=${encodeURIComponent(formatSizeLabel(size))}`;
}

/** Header browsing uses current catalogue data, never a destination from storage. */
export function wheelNavigationTarget(stored, vehicles, collectionsRootPath = '/collections') {
  const fallback = { href: `${collectionsRootPath}/all-wheels`, label: 'Browse wheels' };
  const vehicle = findVehicleBySlug(Array.isArray(vehicles) ? vehicles : [], stored?.slug);
  if (!vehicle || isUnlistedVehicle(vehicle) ||
      !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(vehicle.collection_handle || '')) return fallback;
  const rim = rimSizesForVehicle(vehicle).includes(String(stored?.rim_size)) ? stored.rim_size : null;
  return {
    href: buildVehicleCollectionUrl(collectionsRootPath, vehicle.collection_handle, rim),
    label: `Wheels for ${vehicle.display_name || shortVanLabel(vehicle)}`,
  };
}

/* =========================================================================
 * DOM wiring. Guarded so this module can be imported under Node (for
 * `node --test`) without a `window`/`document`/`customElements` present.
 * ======================================================================= */

/**
 * Normalises `index` into the closed range [0, total) by wrapping past
 * either end - "previous" from the first photo goes to the last one, and
 * vice versa. Shared by <rogue-product-gallery>'s prev/next buttons, its
 * keyboard handler and its swipe gesture, so all three always land on the
 * same photo for the same input. Returns 0 for a non-positive `total`.
 * @param {number} index
 * @param {number} total
 * @returns {number}
 */
export function wrapIndex(index, total) {
  if (!Number.isFinite(total) || total <= 0) return 0;
  return ((index % total) + total) % total;
}

/**
 * Turns a horizontal swipe distance into a step direction: -1 (previous),
 * 1 (next), or 0 for anything short enough to be a tap or a vertical
 * scroll rather than a deliberate swipe.
 * @param {number} deltaX - end X minus start X
 * @param {number} [threshold] - minimum distance to count as a swipe
 * @returns {-1 | 0 | 1}
 */
export function swipeStepDirection(deltaX, threshold = 40) {
  if (Math.abs(deltaX) < threshold) return 0;
  return deltaX < 0 ? 1 : -1;
}

/**
 * "Photo 3 of 8" - <rogue-product-gallery>'s screen-reader/visible counter,
 * built in one place so the lightbox's dots (`aria-label`) and its counter
 * text (`aria-live`) never disagree on the wording.
 * @param {number} index - zero-based
 * @param {number} total
 * @returns {string}
 */
export function galleryPhotoLabel(index, total) {
  return `Photo ${index + 1} of ${total}`;
}

/**
 * Finish photos: the finish(es) a thumbnail's `data-rogue-image-colour`
 * attribute names (snippets/rogue-product-gallery.liquid renders it,
 * comma-separated - a photo shared by every Size of one Finish still
 * names that same colour more than once; harmless, only ever checked for
 * membership below).
 * @param {string|null|undefined} raw
 * @returns {string[]}
 */
export function parseImageColours(raw) {
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/**
 * Whether a thumbnail tagged with `imageColours` belongs to `colour` -
 * case-insensitive, so a swatch click's "bronze lip" matches the
 * server-rendered attribute's "Bronze Lip".
 * @param {string[]} imageColours
 * @param {string|null|undefined} colour
 * @returns {boolean}
 */
export function imageMatchesColour(imageColours, colour) {
  if (typeof colour !== 'string' || colour.trim() === '') return false;
  const target = colour.trim().toLowerCase();
  return imageColours.some((candidate) => candidate.toLowerCase() === target);
}

/**
 * Brings a finish's own photos to the front of the thumbnail row, stably:
 * everything matching `colour` first (in original relative order), then
 * everything else (likewise) - never a re-sort within either group. A
 * no-op (a copy of `entries`, unchanged) with no colour to sort by, or
 * nothing that matches it, so a Size-only change, or a finish the gallery
 * cannot tell any photo apart for, never shuffles the row.
 * @template {{ colours: string[] }} T
 * @param {T[]} entries
 * @param {string|null|undefined} colour
 * @returns {T[]}
 */
export function sortThumbsForColour(entries, colour) {
  if (typeof colour !== 'string' || colour.trim() === '') return entries.slice();
  const matching = [];
  const rest = [];
  for (const entry of entries) {
    if (imageMatchesColour(entry.colours, colour)) matching.push(entry);
    else rest.push(entry);
  }
  return matching.concat(rest);
}

/**
 * Spec item 2: the small, honest line under the gallery for a finish with
 * no photo of its own at all - '' (rendered `hidden`) with no finish name
 * to report against. Never wording that could read as though a different
 * finish's photo IS this one's - the gallery itself just keeps showing
 * whatever it already had (RogueProductGallery.onVariantChange below never
 * calls showImage/markSelected in this case).
 * @param {string|null|undefined} colour
 * @returns {string}
 */
export function missingFinishPhotoMessage(colour, kind = 'wheel') {
  const trimmed = typeof colour === 'string' ? colour.trim() : '';
  if (trimmed === '') return '';
  return `A photo of ${trimmed} isn't available yet. Gallery photos show this ${kind === 'wheel' ? 'wheel model' : 'product'}.`;
}

/**
 * The product gallery's main-image widths (snippets/rogue-product-gallery.
 * liquid) rebuilt from a Shopify CDN URL ending in `width=1600`, so a finish
 * change keeps a responsive image instead of the 1600px file on a phone.
 * @param {unknown} src - e.g. "//shop/cdn/shop/files/x.jpg?v=1&width=1600"
 * @returns {string} a srcset, or '' when the URL has no width=1600 to vary
 */
export function galleryMainSrcset(src) {
  if (typeof src !== 'string' || !/[?&]width=1600(?!\d)/.test(src)) return '';
  return [450, 600, 750, 900, 1200, 1600]
    .map((width) => `${src.replace(/([?&])width=1600(?!\d)/, `$1width=${width}`)} ${width}w`)
    .join(', ');
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && 'customElements' in window) {
  installDomBehaviour();
}

function installDomBehaviour() {
  if (window.__rogueDomInstalled) return;
  window.__rogueDomInstalled = true;
  const globalStore = createVehicleStore({
    storage: safeLocalStorage(),
    cookies: {
      read: () => document.cookie,
      write: (cookieString) => {
        document.cookie = cookieString;
      },
    },
  });

  // C7 (31 Oct 2026, D25): a document-level marker for copy that only makes
  // sense with a van stored (html[data-rogue-has-vehicle]; CSS hides
  // [data-rogue-van-only] without it).
  const markVehicle = () => document.documentElement?.toggleAttribute?.('data-rogue-has-vehicle', Boolean(globalStore.get()));
  markVehicle();
  // "Pick your vehicle" buttons read "See wheels for your <van>" once a van is
  // stored, and go back to their own words and link when it is cleared.
  const updateVanButtons = () => {
    const wanted = vanButtonTarget(enrichVehicle(globalStore.get(), readVehiclesFromDom()), collectionsRoot());
    document.querySelectorAll?.(VAN_BUTTON_SELECTOR)?.forEach((link) => {
      if (!link.hasAttribute('data-rogue-van-cta-label')) {
        link.setAttribute('data-rogue-van-cta', '');
        link.setAttribute('data-rogue-van-cta-label', (link.textContent || '').trim());
        link.setAttribute('data-rogue-van-cta-href', link.getAttribute('href') || '');
      }
      // A button can name its own place and words for a stored van (the tyres
      // page's goes to wheel and tyre packages, final review 31 Oct).
      const ownText = link.getAttribute('data-rogue-van-cta-text');
      link.textContent = wanted ? (ownText ? ownText.replace('{van}', wanted.van) : wanted.label) : link.getAttribute('data-rogue-van-cta-label');
      link.setAttribute('href', wanted ? (link.getAttribute('data-rogue-van-cta-to') || wanted.href) : link.getAttribute('data-rogue-van-cta-href'));
    });
  };
  updateVanButtons();
  document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, updateVanButtons);
  document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, markVehicle);

  // Update explicit change-vehicle links at activation, including keyboard
  // and context-menu focus, so a newly selected PDP variant survives the trip.
  for (const eventName of ['pointerdown', 'focusin', 'click']) {
    document.addEventListener(eventName, (event) => {
      const link = event.target?.closest?.('a[data-rogue-change-vehicle]');
      if (link) link.href = vehicleSelectorUrl(currentReturnPath());
    });
  }

  // 25 Oct 2026 (Daniel): "Pick your vehicle" on a wheel or tyre page opens
  // the inline panel instead of leaving for the home selector. The href stays
  // the no-JS / new-tab fallback; modified clicks keep native behaviour.
  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.('a[data-rogue-inline-vehicle]');
    if (!link || event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (openInlineVehicle(link)) event.preventDefault();
  });

  /**
   * The page a vehicle change should come back to. No buy-box `?variant=` is
   * added: on the way back that would lock the finish and size like a shared
   * link, instead of moving to one that fits the newly chosen vehicle.
   */
  function currentReturnPath() {
    return vehicleReturnPath(window.location.pathname + window.location.search + (window.location.hash || ''));
  }

  /** Opens this page's inline vehicle panel; false when there is none, so callers fall back to navigating. */
  function openInlineVehicle(trigger) {
    const host = document.querySelector?.('rogue-inline-vehicle[data-ready]');
    return Boolean(host && typeof host.open === 'function' && host.open(trigger));
  }

  /** Reads the shared `[data-rogue-vehicles]` JSON payload, if present on the page. */
  function readVehiclesFromDom() {
    const script = document.querySelector('script[data-rogue-vehicles]');
    if (!script || !script.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return filterActiveVehicles(Array.isArray(parsed) ? parsed : []);
    } catch {
      return [];
    }
  }

  function fitmentForCard(card) {
    const tags = (card?.getAttribute('data-rogue-pcd-tags') || '').split(/\s+/).filter(Boolean);
    let fits_vehicles = [];
    try {
      const parsed = JSON.parse(card?.getAttribute('data-rogue-fits-vehicles') || '[]');
      if (Array.isArray(parsed)) fits_vehicles = parsed;
    } catch { /* Invalid or absent approval data cannot prove fitment. */ }
    return { tags, fits_vehicles };
  }

  /** `routes.collections_url` (locale-aware), rendered onto the same script tag as the vehicle data. */
  function collectionsRoot() {
    const script = document.querySelector('script[data-rogue-vehicles]');
    return script?.getAttribute('data-collections-root') || '/collections';
  }

  // Resolve a `?vehicle=slug` deep link as early as possible, on every page
  // that carries the shared vehicle data (rendered by
  // snippets/rogue-vehicle-data.liquid, included by the your-van bar on
  // every page and by the selector section).
  const deepLinkSlug = vehicleSlugFromSearch(window.location.search);
  if (deepLinkSlug) {
    const vehicles = readVehiclesFromDom();
    const match = findVehicleBySlug(vehicles, deepLinkSlug);
    if (match) {
      globalStore.set(match);
      // THEME_A finding (24 Sep 2026, Major): this stored the van client
      // side but never told the cart, unlike RogueVehicleSelector.confirm/
      // confirmUnlisted below - so the basket's Vehicle/Typed vehicle
      // attributes (and cart-products-row.liquid's per-line mismatch
      // warning, which reads them) kept showing whatever van was stored
      // before this deep link, even though the header already read the
      // new one.
      syncVehicleCartAttributes(match);
    }
  }

  // A van's own wheels page, reached from outside the site with no van saved,
  // saves that van (vehicleForCollectionPath). Never replaces a van already
  // saved, never from a link on this site, never after Clear this session.
  if (!deepLinkSlug && !globalStore.get() && arrivedFromOutside(document.referrer, window.location.origin)
    && !vehicleClearedThisSession()) {
    const pageVan = vehicleForCollectionPath(window.location.pathname, readVehiclesFromDom(), collectionsRoot());
    if (pageVan) {
      globalStore.set(pageVan);
      syncVehicleCartAttributes(pageVan);
    }
  }

  // Keep real hrefs current for keyboard, new-tab and context-menu navigation.
  // Child-list observation covers Horizon's deferred/cloned mobile drawer.
  const updateWheelNavigation = (link) => {
    const target = wheelNavigationTarget(globalStore.get(), readVehiclesFromDom(), collectionsRoot());
    for (const anchor of link ? [link] : document.querySelectorAll?.('a[data-rogue-wheel-navigation]') || []) {
      anchor.setAttribute('href', target.href);
      anchor.setAttribute('aria-label', target.label);
    }
  };
  updateWheelNavigation();
  document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, () => updateWheelNavigation());
  watchHeaderMenuWidth(document, window.ResizeObserver, 'ontouchstart' in window && navigator.maxTouchPoints > 0);
  for (const name of ['pageshow', 'storage']) window.addEventListener?.(name, () => updateWheelNavigation());
  for (const name of ['pointerdown', 'focusin', 'click', 'contextmenu']) {
    document.addEventListener(name, (event) => {
      const link = event.target?.closest?.('a[data-rogue-wheel-navigation]');
      if (link) updateWheelNavigation(link);
    });
  }
  if (typeof MutationObserver !== 'undefined' && document.documentElement) {
    new MutationObserver((changes) => {
      if (changes.some((change) => [...change.addedNodes].some((node) =>
        node.matches?.('a[data-rogue-wheel-navigation]') || node.querySelector?.('a[data-rogue-wheel-navigation]')))) {
        updateWheelNavigation();
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  class RogueVehicleSelector extends HTMLElement {
    connectedCallback() {
      this.vehicles = readVehiclesFromDom();
      if (this.vehicles.length === 0) return; // nothing to enhance; keep the <noscript> fallback usable

      this.makeSelect = this.querySelector('[data-rogue-select="make"]');
      this.modelSelect = this.querySelector('[data-rogue-select="model"]');
      this.generationSelect = this.querySelector('[data-rogue-select="generation"]');
      this.modelField = this.querySelector('[ref="modelField"]');
      this.generationField = this.querySelector('[ref="generationField"]');
      this.rimSizeField = this.querySelector('[ref="rimSizeField"]');
      this.rimSizeSelect = this.querySelector('[data-rogue-select="rim_size"]');
      this.unlistedField = this.querySelector('[ref="unlistedField"]');
      this.unlistedInput = this.querySelector('[ref="unlistedInput"]');
      this.confirmButton = this.querySelector('[ref="confirmButton"]');
      this.confirmButtonLabel = this.querySelector('[ref="confirmButtonLabel"]');
      this.statusMessage = this.querySelector('[ref="statusMessage"]');
      this.form = this.querySelector('form');
      this.storedVanState = this.querySelector('[ref="storedVanState"]');
      this.storedVanNameEl = this.querySelector('[ref="storedVanName"]');
      this.storedWheelCountEl = this.querySelector('[ref="storedWheelCount"]');
      this.storedShowLink = this.querySelector('[ref="storedShowLink"]');
      this.storedShowLabel = this.querySelector('[ref="storedShowLabel"]');
      this.storedChangeButton = this.querySelector('[ref="storedChangeButton"]');
      this.storedClearButton = this.querySelector('[ref="storedClearButton"]');
      this.storedStatus = this.querySelector('[ref="storedStatus"]');
      this.selectionStatus = this.querySelector('[ref="selectionStatus"]');
      this.cancelChangeButton = this.querySelector('[ref="cancelChangeButton"]');
      this.heroHeading = this.closest?.('.rogue-hero')?.querySelector('.rogue-hero__heading-line2');
      this.defaultHeroHeading = this.heroHeading?.textContent || '';

      if (!this.makeSelect || !this.modelSelect || !this.generationSelect || !this.confirmButton) return;

      // Inline mode (snippets/rogue-inline-vehicle.liquid, 25 Oct 2026): the
      // same selector inside the product page's panel. It saves exactly as
      // the home one does, then stays on the page instead of navigating,
      // except for the first van ever picked (goesToVanWheels).
      this.inline = this.hasAttribute('data-inline');
      this.context = this.getAttribute('data-context') || 'wheel';
      this.unlistedMode = false;
      this.unlistedMakeLabel = this.getAttribute('data-unlisted-make-label') || "My vehicle isn't listed";
      this.defaultConfirmLabel =
        this.confirmButtonLabel?.textContent.trim() ||
        this.getAttribute('data-confirm-label') ||
        'Show wheels that fit';
      this.unlistedConfirmLabel = this.getAttribute('data-unlisted-confirm-label') || 'Show all wheels';
      this.pickMakeFirstLabel = this.getAttribute('data-pick-make-first') || 'Pick your make first';

      this.storedChangeButton?.addEventListener('click', () => this.showForm(true));
      this.cancelChangeButton?.addEventListener('click', () => {
        this.restoreSelection();
        const target = this.storedVanState?.hasAttribute('hidden') ? this.makeSelect : this.storedChangeButton;
        target?.focus({ preventScroll: true });
      });
      this.storedClearButton?.addEventListener('click', () => this.clearVehicle());
      this.onVehicleChange = (event) => {
        if (event?.type === 'storage' && event.key !== null && event.key !== VEHICLE_STORAGE_KEY) return;
        this.restoreSelection();
      };
      this.onPageShow = () => {
        this.restoreSelection();
        if (new URLSearchParams(window.location.search).get('change_vehicle') === '1') this.showForm(true);
      };
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('storage', this.onVehicleChange);

      this.populateMakes();
      this.makeSelect.addEventListener('change', () => this.onMakeChange());
      this.modelSelect.addEventListener('change', () => this.onModelChange());
      this.generationSelect.addEventListener('change', () => this.onGenerationChange());
      this.rimSizeSelect?.addEventListener('change', () => this.updateConfirmState());
      this.unlistedInput?.addEventListener('input', () => this.updateConfirmState());

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.confirm();
      });

      // A `?vehicle=slug` deep link is an explicit choice (e.g. a shared
      // link) and wins over whatever van happens to be stored already -
      // review 2, finding 22's pre-fill only runs when there wasn't one.
      // Deferred spec item ("the home hero's stored-van state"): a real
      // (listed) stored van shows the compact "Your Ducato L1, L2, L3."
      // state below instead of the form, same as the pre-footer van strip
      // (sections/rogue-van-strip.liquid) shows instead of its own no-van
      // copy. An unlisted van, or a deep link, still shows the form -
      // preselectFromStore already pre-fills it either way, so "Change
      // van" always lands on a form ready to edit rather than blank.
      const wonByQueryParam = this.preselectFromQueryParam();
      if (!wonByQueryParam) this.preselectFromStore();

      this.restoreSelection();
      if (new URLSearchParams(window.location.search).get('change_vehicle') === '1') this.showForm(true);

      this.removeAttribute('hidden');
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('storage', this.onVehicleChange);
    }

    restoreSelection() {
      const stored = globalStore.get();
      const vehicle = isUnlistedVehicle(stored) ? stored : findVehicleBySlug(this.vehicles, stored?.slug);
      this.preselectFromStore();
      if (vehicle) this.showStoredVanState(vehicle);
      else {
        this.makeSelect.value = '';
        this.onMakeChange();
        this.showForm();
      }
    }

    async clearVehicle() {
      if (this._savingVehicle) return;
      this._savingVehicle = true;
      const saved = await syncVehicleCartAttributes(null);
      this._savingVehicle = false;
      if (!saved) {
        if (this.selectionStatus) this.selectionStatus.textContent = 'Could not clear your vehicle. Please try again.';
        return;
      }
      globalStore.clear();
      rememberVehicleCleared();
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('vehicle');
      window.history?.replaceState(null, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
      document.dispatchEvent(new CustomEvent(ROGUE_VEHICLE_CHANGE_EVENT, { detail: { vehicle: null } }));
      this.showForm(true);
      if (this.selectionStatus) this.selectionStatus.textContent = 'Vehicle cleared. Existing basket items keep their vehicle details.';
    }

    /** Shows the compact stored-van state and hides the form, filling in
     *  the short van name, wheel count and "Show wheels for your ..." link
     *  from `vehicle`.
     *  @param {Record<string, unknown>} vehicle
     */
    showStoredVanState(vehicle) {
      if (!this.storedVanState) return;

      if (this.selectionStatus) this.selectionStatus.textContent = '';
      if (this.storedVanNameEl) {
        this.storedVanNameEl.textContent = isUnlistedVehicle(vehicle) ? typedVehicleText(vehicle) : vehicle.display_name;
      }
      // A manual-review vehicle is selected like any other (25 Oct 2026, Daniel);
      // only the note below says the fit is double-checked after ordering.
      const unlisted = isUnlistedVehicle(vehicle);
      const manualReview = requiresManualReview(vehicle);
      if (this.heroHeading) this.heroHeading.textContent = unlisted ? "Let's confirm your fitment." : this.defaultHeroHeading;
      this.setAttribute('data-vehicle-state', unlisted ? 'unlisted' : 'selected');
      if (this.storedStatus) this.storedStatus.textContent = unlisted ? 'Fitment to confirm' : 'Vehicle selected';
      if (this.storedShowLabel) {
        this.storedShowLabel.textContent = unlisted ? 'Browse wheels — fitment to confirm' : 'Shop wheels for this vehicle';
      }
      if (this.storedShowLink && vehicle.collection_handle) {
        this.storedShowLink.href = `${collectionsRoot()}/${vehicle.collection_handle}`;
      }
      // Arrived from a product (or basket) page: keeping this vehicle goes
      // back there, not on to a collection (25 Oct 2026).
      const back = this.returnTarget();
      if (back && this.storedShowLink) {
        this.storedShowLink.href = back;
        if (this.storedShowLabel) this.storedShowLabel.textContent = 'Continue with this vehicle';
      }
      if (this.storedWheelCountEl) this.storedWheelCountEl.textContent = unlisted
        ? 'We will check the fitment with you before dispatch.'
        : manualReview
        ? MANUAL_REVIEW_COLLECTION_SUMMARY
        : 'Your choice is saved for browsing. Product pages confirm the wheel specification.';

      this.form?.setAttribute('hidden', '');
      this.storedVanState.removeAttribute('hidden');
    }

    /** Reverts to the form (pre-filled by preselectFromStore, or blank with
     *  nothing stored) and hides the stored-van state - the "Change vehicle"
     *  button's handler, and the fallback whenever there is no real stored
     *  van to summarise. */
    showForm(focus = false) {
      this.storedVanState?.setAttribute('hidden', '');
      this.form?.removeAttribute('hidden');
      this.removeAttribute('data-vehicle-state');
      if (this.heroHeading) this.heroHeading.textContent = this.defaultHeroHeading;
      const stored = globalStore.get();
      const valid = isUnlistedVehicle(stored) || Boolean(findVehicleBySlug(this.vehicles, stored?.slug));
      if (this.cancelChangeButton) this.cancelChangeButton.hidden = !valid;
      if (focus) this.makeSelect?.focus({ preventScroll: true });
    }

    populateMakes() {
      const makes = uniqueMakes(this.vehicles);
      const options = makes.map((make) => ({ value: make, label: make }));
      // Always last (S3.2a): the Wix dropdown's "can't find vehicle model"
      // option, which 62 corrected orders came through on the old site.
      options.push({ value: UNLISTED_MAKE_VALUE, label: this.unlistedMakeLabel });
      setSelectOptions(this.makeSelect, [], 'Select make', options);
    }

    onMakeChange() {
      const make = this.makeSelect.value;

      if (make === UNLISTED_MAKE_VALUE) {
        this.enterUnlistedMode();
        return;
      }

      this.exitUnlistedMode();
      // Review 2 finding 22 follow-up (26 Sep 2026, T5-after-Ducato): picking
      // a new make clears whichever vehicle was previously resolved (from a
      // stored van, a deep link, or an earlier pick) - there is no complete
      // vehicle again until a generation is chosen, so the confirm button
      // must fall back to the generic label in the meantime rather than
      // keep naming the old one.
      this.selectedVehicle = null;
      const models = make ? uniqueModelsForMake(this.vehicles, make) : [];
      setSelectOptions(this.modelSelect, models, 'Select model');
      this.modelSelect.disabled = models.length === 0;
      setSelectOptions(this.generationSelect, [], 'Select generation');
      this.generationSelect.disabled = true;
      this.generationField?.setAttribute('hidden', '');
      this.resetRimSizeStep();
      this.updateConfirmState();
    }

    /**
     * Swaps the normal model/year steps for a single "tell us the make,
     * model and year" text field and relabels the confirm button "Show all
     * wheels" - there is no model/generation to pick for a van that isn't in
     * the list at all. See buildUnlistedVehicle for what gets stored.
     */
    enterUnlistedMode() {
      this.unlistedMode = true;
      this.selectedVehicle = null;
      setSelectOptions(this.modelSelect, [], 'Select model');
      this.modelSelect.disabled = true;
      setSelectOptions(this.generationSelect, [], 'Select generation');
      this.generationSelect.disabled = true;
      this.resetRimSizeStep();
      this.modelField?.setAttribute('hidden', '');
      this.generationField?.setAttribute('hidden', '');
      this.unlistedField?.removeAttribute('hidden');
      if (this.confirmButtonLabel) this.confirmButtonLabel.textContent = this.unlistedConfirmLabel;
      this.updateConfirmState();
    }

    exitUnlistedMode() {
      if (!this.unlistedMode) return;
      this.unlistedMode = false;
      this.unlistedField?.setAttribute('hidden', '');
      if (this.unlistedInput) this.unlistedInput.value = '';
      this.modelField?.removeAttribute('hidden');
      this.generationField?.removeAttribute('hidden');
      if (this.confirmButtonLabel) this.confirmButtonLabel.textContent = this.defaultConfirmLabel;
    }

    onModelChange() {
      const make = this.makeSelect.value;
      const model = this.modelSelect.value;
      // Same reasoning as onMakeChange's own reset just above: no complete
      // vehicle again until a generation is picked.
      this.selectedVehicle = null;
      const variants = model ? variantsForModel(this.vehicles, make, model) : [];
      const options = variants.map((v) => ({ value: v.slug, label: generationLabel(v) || v.slug }));
      setSelectOptions(
        this.generationSelect,
        options.map((o) => o.value),
        'Select generation',
        options
      );
      this.generationSelect.disabled = options.length === 0;
      this.generationField?.toggleAttribute('hidden', options.length < 2);
      this.resetRimSizeStep();
      if (options.length === 1) {
        this.generationSelect.value = options[0].value;
        this.onGenerationChange();
      }
      this.updateConfirmState();
    }

    onGenerationChange() {
      const vehicle = findVehicleBySlug(this.vehicles, this.generationSelect.value);
      this.selectedVehicle = vehicle;

      if (vehicle && needsRimSizeStep(vehicle) && this.rimSizeSelect) {
        const rimSizes = rimSizesForVehicle(vehicle);
        setSelectOptions(this.rimSizeSelect, rimSizes, 'Select rim size');
        this.rimSizeSelect.disabled = false;
        this.rimSizeField?.removeAttribute('hidden');
      } else {
        this.resetRimSizeStep();
      }

      this.updateConfirmState();
    }

    resetRimSizeStep() {
      this.rimSizeField?.setAttribute('hidden', '');
      if (this.rimSizeSelect) {
        setSelectOptions(this.rimSizeSelect, [], 'Select rim size');
        this.rimSizeSelect.disabled = true;
      }
    }

    /**
     * Spec 6.2 / review 2 N28: the confirm button stays enabled rather than
     * disabled while a step is missing - a disabled primary action reads as
     * 45% orange, 2.57:1 contrast, as the very first thing on the page.
     * confirm() validates on click instead ("Pick your make first") and
     * updateConfirmState() is now only the "does this look complete"
     * question the click handler asks, not a DOM-disabling side effect -
     * see isReadyToConfirm.
     */
    isReadyToConfirm() {
      if (this.unlistedMode) return (this.unlistedInput?.value.trim() || '') !== '';
      return Boolean(this.generationSelect.value);
    }

    /**
     * Bug fixed 26 Sep 2026 (repro: store a Ducato, reload, then pick a T5 -
     * the confirm button kept reading "Show wheels for your Ducato ..."):
     * spec 6.2's own refactor note below is right that the button no longer
     * disables/enables, but wrongly concluded there was nothing left for
     * this to do - it still owns keeping {@link selectorConfirmLabel}'s
     * "Show wheels for your ..." text in sync with whichever vehicle
     * `this.selectedVehicle` currently names (see {@link confirmButtonLabel},
     * the pure function this now just wires up; onMakeChange/onModelChange
     * null that field out again the moment a step is reopened, so a
     * half-finished re-pick falls back to the generic label rather than
     * keep the old van's name on screen).
     *
     * Original note (spec 6.2 / review 2 N28): the confirm button stays
     * enabled rather than disabled while a step is missing - a disabled
     * primary action reads as 45% orange, 2.57:1 contrast, as the very
     * first thing on the page. confirm() validates on click instead ("Pick
     * your make first"); this is no longer a DOM-disabling side effect.
     */
    updateConfirmState() {
      if (this.confirmButtonLabel) {
        this.confirmButtonLabel.textContent = this.labelFor(this.unlistedMode, this.selectedVehicle);
      }
    }

    /** Validated `?return=` on the home page, unless it is a van's own wheels page; never used by the inline panel, which navigates only for a first van (goesToVanWheels). */
    returnTarget() {
      if (this.inline) return null;
      return vehicleChangeReturnPath(new URLSearchParams(window.location.search).get('return'), this.vehicles);
    }

    /** Picked in a wheel's own panel, and that wheel fits this van (RogueFitmentGate.wheelFitsVehicle). */
    onFittingWheel(vehicle) {
      if (!this.inline || this.context !== 'wheel' || !vehicle) return false;
      const gate = document.querySelector('rogue-fitment-gate');
      return typeof gate?.wheelFitsVehicle === 'function' && gate.wheelFitsVehicle(vehicle);
    }

    /** The confirm button's words for the current pick - see confirmButtonLabel, inlineConfirmLabel, returnConfirmLabel. */
    labelFor(unlistedMode, vehicle) {
      const toVanWheels = !unlistedMode && goesToVanWheels(Boolean(globalStore.get()), vehicle, this.onFittingWheel(vehicle));
      if (this.inline) return inlineConfirmLabel(this.context, unlistedMode, vehicle, toVanWheels);
      if (this.returnTarget() && !toVanWheels) return returnConfirmLabel(unlistedMode, vehicle);
      return confirmButtonLabel(unlistedMode, vehicle, this.defaultConfirmLabel, this.unlistedConfirmLabel);
    }

    async confirm() {
      if (this._savingVehicle) return;
      if (!this.isReadyToConfirm()) {
        const missing = this.unlistedMode ? this.unlistedInput : !this.makeSelect.value ? this.makeSelect : !this.modelSelect.value ? this.modelSelect : this.generationSelect;
        if (this.statusMessage) this.statusMessage.textContent = this.unlistedMode ? 'Enter your make, model and year.' : missing === this.makeSelect ? 'Choose your make.' : missing === this.modelSelect ? 'Choose your model.' : 'Choose your year or version.';
        missing?.focus();
        return;
      }

      if (this.unlistedMode) {
        this.confirmUnlisted();
        return;
      }

      const vehicle = findVehicleBySlug(this.vehicles, this.generationSelect.value);
      if (!vehicle) return;

      const rimSizeChosen =
        this.rimSizeField && !this.rimSizeField.hasAttribute('hidden') ? this.rimSizeSelect?.value || '' : '';
      const vehicleToStore = rimSizeChosen ? { ...vehicle, rim_size: rimSizeChosen } : vehicle;
      const toVanWheels = goesToVanWheels(Boolean(globalStore.get()), vehicle, this.onFittingWheel(vehicleToStore));

      this._savingVehicle = true;
      // T1 (31 Oct 2026): a failed basket write never blocks the van choice.
      const { stored, saved } = await storeConfirmedVehicle(vehicleToStore, { store: globalStore });
      this._savingVehicle = false;
      document.dispatchEvent(new CustomEvent(ROGUE_VEHICLE_CHANGE_EVENT, { detail: { vehicle: stored, source: this.inline ? 'inline' : 'selector' } }));
      if (this.statusMessage) {
        this.statusMessage.textContent = vehicleSavedStatus(stored, saved);
      }
      if (this.inline && !toVanWheels) {
        this.dispatchEvent(new CustomEvent('rogue:vehicle-confirmed', { bubbles: true, detail: { vehicle: stored } }));
        return;
      }
      if (vehicle.collection_handle) {
        window.location.href = (toVanWheels ? null : this.returnTarget()) || buildVehicleCollectionUrl(collectionsRoot(), vehicle.collection_handle, rimSizeChosen);
      }
    }

    /** Stores buildUnlistedVehicle's record and sends the shopper to the unfiltered "all wheels" collection. */
    async confirmUnlisted() {
      if (this._savingVehicle) return;
      const typed = this.unlistedInput?.value.trim() || '';
      if (typed === '') return;

      const vehicle = buildUnlistedVehicle(typed);
      this._savingVehicle = true;
      // T1 (31 Oct 2026): a failed basket write never blocks the van choice.
      const { stored, saved } = await storeConfirmedVehicle(vehicle, { store: globalStore });
      this._savingVehicle = false;
      document.dispatchEvent(new CustomEvent(ROGUE_VEHICLE_CHANGE_EVENT, { detail: { vehicle: stored } }));
      if (this.statusMessage) {
        // typedVehicleText, not stored.display_name: the stored shape carries
        // the internal "Unlisted: " prefix, never shown to the shopper.
        this.statusMessage.textContent = vehicleSavedStatus(stored, saved, stored ? typedVehicleText(stored) : '');
      }
      if (this.inline) {
        this.dispatchEvent(new CustomEvent('rogue:vehicle-confirmed', { bubbles: true, detail: { vehicle: stored } }));
        return;
      }
      window.location.href = this.returnTarget() || buildVehicleCollectionUrl(collectionsRoot(), vehicle.collection_handle, null);
    }

    /**
     * Review N15: the single place a van change is announced beyond the
     * selector itself - writes the Vehicle/Typed vehicle cart attributes
     * (so an order started under the old van is not left carrying it) and
     * tells any <rogue-cart-details> on the page (assets/rogue-fitment.js,
     * RogueCartDetails) to redraw itself from the new van, in case the
     * selector and the cart panel are ever on the same page at once.
     * @param {RogueVehicleSelection|null} stored
     */
    announceVehicleChange(stored) {
      if (!stored) return;
      const saved = syncVehicleCartAttributes(stored);
      document.dispatchEvent(new CustomEvent(ROGUE_VEHICLE_CHANGE_EVENT, { detail: { vehicle: stored } }));
      return saved;
    }

    /** @returns {boolean} whether a `?vehicle=slug` deep link actually selected something */
    preselectFromQueryParam() {
      const slug = vehicleSlugFromSearch(window.location.search);
      if (!slug) return false;
      const vehicle = findVehicleBySlug(this.vehicles, slug);
      if (!vehicle) return false;

      this.makeSelect.value = vehicle.make;
      this.onMakeChange();
      this.modelSelect.value = vehicle.model;
      this.onModelChange();
      this.generationSelect.value = vehicle.slug;
      this.onGenerationChange();
      if (this.rimSizeSelect && !this.rimSizeSelect.disabled) {
        const rimSizes = rimSizesForVehicle(vehicle);
        if (rimSizes.length > 0) this.rimSizeSelect.value = rimSizes[0];
      }
      this.updateConfirmState();
      return true;
    }

    /**
     * Review 2, finding 22: with a van already stored, pre-fill make/model/
     * generation (and rim size, if one was stored) instead of leaving every
     * select reading "Select make" / "Select model" / "Select generation",
     * and rename the confirm button to name the van (see
     * selectorConfirmLabel) - every select stays fully changeable
     * afterwards, exactly as if the shopper had just picked it themselves.
     * A stored "My van isn't listed" (S3.2a) restores THAT mode instead,
     * with the shopper's own typed text back in the field - there is no
     * make/model to select for it. No-ops with nothing stored at all, or a
     * stored slug that no longer matches a vehicle in the current list
     * (removed from the catalogue since it was stored).
     */
    preselectFromStore() {
      const stored = globalStore.get();
      if (!stored) return;

      if (isUnlistedVehicle(stored)) {
        this.makeSelect.value = UNLISTED_MAKE_VALUE;
        this.enterUnlistedMode();
        if (this.unlistedInput) this.unlistedInput.value = typedVehicleText(stored);
        this.updateConfirmState();
        return;
      }

      const vehicle = findVehicleBySlug(this.vehicles, stored.slug);
      if (!vehicle) return;

      this.makeSelect.value = vehicle.make;
      this.onMakeChange();
      this.modelSelect.value = vehicle.model;
      this.onModelChange();
      this.generationSelect.value = vehicle.slug;
      this.onGenerationChange();
      if (this.rimSizeSelect && !this.rimSizeSelect.disabled) {
        // Spec 9 BUY item 8c: restoring a stored van must not invent a size
        // choice the shopper never made (walk finding - "15" showed
        // pre-selected on a restored van with no rim_size stored). Only
        // restore a size that was actually stored; otherwise leave the
        // placeholder ("Select rim size") showing, same as a fresh visit.
        const rimSizes = rimSizesForVehicle(vehicle);
        const storedRim = typeof stored.rim_size === 'string' ? stored.rim_size : '';
        this.rimSizeSelect.value = rimSizes.includes(storedRim) ? storedRim : '';
      }
      this.updateConfirmState();
    }
  }

  /**
   * The product page's inline vehicle panel (snippets/rogue-inline-vehicle.liquid,
   * 25 Oct 2026). Daniel: "Pick your vehicle" sent shoppers to the home
   * selector and on to a list of wheels, not the wheel they were looking at.
   * This opens a native modal <dialog> (a bottom sheet under 750px) holding
   * the same <rogue-vehicle-selector>, in inline mode: same vehicle data,
   * same save (basket attributes, then the shared store and the
   * vehicle-changed event), and no navigation. The buy box re-evaluates on
   * that event; this element closes the dialog and moves focus to the
   * answer. The selector sits in a <template> until first opened, so a page
   * view that never uses it builds and populates nothing.
   */
  class RogueInlineVehicle extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.template = this.querySelector('template[data-rogue-vpanel-template]');
      this.body = this.querySelector('[data-rogue-vpanel-body]');
      // No <dialog> support: every trigger keeps its home-selector href.
      if (!this.dialog || typeof this.dialog.showModal !== 'function' || !this.template || !this.body) return;
      this.querySelector('[data-rogue-vpanel-close]')?.addEventListener('click', () => this.close());
      this.dialog.addEventListener('close', () => this.onClosed());
      // A tap on the backdrop lands on the <dialog> itself, never its content.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.keepFocusInside(event));
      this.addEventListener('rogue:vehicle-confirmed', (event) => {
        this.confirmed = event.detail?.vehicle || true;
        this.close();
      });
      this.setAttribute('data-ready', '');
    }

    /** Builds the selector from its template on first use. */
    ensureSelector() {
      if (!this.selector) {
        this.body.append(this.template.content.cloneNode(true));
        this.selector = this.body.querySelector('rogue-vehicle-selector');
      }
      return this.selector;
    }

    /**
     * @param {HTMLElement|null} trigger - focus goes back here if the panel is dismissed
     * @returns {boolean} false when there is nothing to show (no vehicle data), so the link navigates instead
     */
    open(trigger) {
      if (this.dialog.open) return true;
      const selector = this.ensureSelector();
      if (!selector?.makeSelect) return false;
      this.trigger = trigger || null;
      this.confirmed = null;
      selector.restoreSelection?.();
      selector.showForm?.();
      if (selector.statusMessage) selector.statusMessage.textContent = '';
      this.dialog.showModal();
      document.documentElement.classList.add('rogue-vpanel-open');
      selector.makeSelect.focus();
      return true;
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onClosed() {
      document.documentElement.classList.remove('rogue-vpanel-open');
      const confirmed = this.confirmed;
      this.confirmed = null;
      if (confirmed) {
        if (document.querySelector('rogue-fitment-gate')?.focusFitmentResult?.()) return;
        const cta = document.querySelector('rogue-tyre-info-cta [ref="tyreInfoCta"]');
        if (cta) {
          cta.focus({ preventScroll: true });
          const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          cta.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'instant' : 'smooth' });
          return;
        }
      }
      if (this.trigger?.isConnected) this.trigger.focus?.();
    }

    /** showModal already makes the page behind inert; this also wraps Tab inside the panel. */
    keepFocusInside(event) {
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button, select, input, a[href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.disabled && !element.closest('[hidden]') && element.getClientRects().length > 0);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class RogueYourVanBar extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[ref="vanBarText"]');
      this.changeLink = this.querySelector('[ref="vanBarChange"]');
      this.stateEl = this.querySelector('[ref="vanBarStatus"]');
      this.actionEl = this.querySelector('[ref="vanBarAction"]');
      this.clearButton = this.querySelector('[ref="vanBarClear"]');
      this.statusEl = this.querySelector('[ref="vanBarMessage"]');
      this.onVehicleChange = () => this.render();
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      window.addEventListener('pageshow', this.onVehicleChange);
      window.addEventListener('storage', this.onVehicleChange);
      this.render();
      this.changeLink?.addEventListener('pointerdown', () => this.render());
      this.changeLink?.addEventListener('focus', () => this.render());

      this.changeLink?.addEventListener('click', (event) => {
        this.render();
        const plainClick = !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && !(event.button > 0);
        // A wheel or tyre page: the inline panel, so the shopper never leaves the product (25 Oct 2026).
        if (plainClick && openInlineVehicle(this.changeLink)) {
          event.preventDefault();
          document.querySelector('header-drawer')?.close?.();
          return;
        }
        const selector = document.querySelector('rogue-vehicle-selector:not([data-inline])');
        if (selector && typeof selector.showForm === 'function' && plainClick) {
          event.preventDefault();
          document.querySelector('header-drawer')?.close?.();
          selector.showForm(true);
          selector.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
          return;
        }
        // The vehicles page (also 404 and search) has its own picker: Change
        // goes to it, not round the home page and back (31 Oct 2026, sweep).
        const picker = document.querySelector('rogue-van-picker');
        if (picker && plainClick) {
          event.preventDefault();
          document.querySelector('header-drawer')?.close?.();
          picker.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          picker.querySelector('input[id^="RogueVanSearch-"]')?.focus?.({ preventScroll: true });
        }
      });
      this.clearButton?.addEventListener('click', () => this.clearVehicle());
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      window.removeEventListener('pageshow', this.onVehicleChange);
      window.removeEventListener('storage', this.onVehicleChange);
    }

    async clearVehicle() {
      if (this._savingVehicle) return;
      this._savingVehicle = true;
      this.clearButton.disabled = true;
      const saved = await syncVehicleCartAttributes(null);
      this._savingVehicle = false;
      this.clearButton.disabled = false;
      if (!saved) {
        if (this.statusEl) this.statusEl.textContent = 'Could not clear your vehicle. Please try again.';
        return;
      }
      globalStore.clear();
      rememberVehicleCleared();
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('vehicle');
      window.history?.replaceState(null, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
      document.dispatchEvent(new CustomEvent(ROGUE_VEHICLE_CHANGE_EVENT, { detail: { vehicle: null } }));
      // Re-evaluate product/collection fitment from a clean page without
      // rewriting any vehicle properties already attached to basket lines.
      if (!document.querySelector('rogue-vehicle-selector:not([data-inline])')) {
        window.location.href = vehicleReturnPath(window.location.pathname + window.location.search) || '/';
      } else {
        this.changeLink?.focus();
      }
    }

    render() {
      const stored = globalStore.get();
      const vehicle = isUnlistedVehicle(stored) ? stored : findVehicleBySlug(readVehiclesFromDom(), stored?.slug);
      const unlisted = isUnlistedVehicle(vehicle);
      // A manual-review vehicle reads "Selected" like any other (25 Oct 2026,
      // Daniel); the product page explains the free double-check.
      const needsReview = unlisted;
      if (this.textEl) {
        // An unlisted van's display_name carries the internal "Unlisted: "
        // prefix (never shown to the shopper) - show the typed text on its
        // own instead (review Blocker 4, "Store the free text without the
        // 'Unlisted:' prefix").
        this.textEl.textContent = vehicle ? (unlisted ? typedVehicleText(vehicle) : shortVanLabel(vehicle) || vehicle.display_name) : 'Choose vehicle';
      }
      if (this.stateEl) this.stateEl.textContent = vehicle ? (needsReview ? 'Fitment to confirm' : '✓ Selected') : 'Find your wheels';
      if (this.actionEl) this.actionEl.textContent = vehicle ? 'Change' : '';
      if (this.clearButton) this.clearButton.hidden = !stored;
      if (this.changeLink) {
        this.changeLink.href = window.location.pathname === '/'
          ? vanBarSelectorUrl('/', window.location.search, window.location.hash || '')
          : vehicleSelectorUrl(currentReturnPath());
        this.changeLink.setAttribute('aria-label', vehicle ? `Change vehicle: ${unlisted ? typedVehicleText(vehicle) : vehicle.display_name}` : 'Choose your vehicle');
        this.changeLink.title = vehicle ? (unlisted ? typedVehicleText(vehicle) : vehicle.display_name) : 'Choose your vehicle';
      }
      this.toggleAttribute('data-has-vehicle', Boolean(vehicle));
      // An unlisted van is not a confirmed fit (no PCD data at all - see
      // isUnlistedVehicle), so the pill must never read as "fit green"
      // (review Blocker 4). data-has-vehicle stays true for it (still shows
      // the "change" link); data-vehicle-state is the one CSS keys the
      // colour off.
      this.setAttribute('data-vehicle-state', !vehicle ? 'none' : needsReview ? 'unlisted' : 'set');
    }
  }

  /**
   * Current baskets use a hidden, vehicle-only context. The field/draft
   * methods remain compatible with older rendered carts; the context-only
   * flag below prevents their absent controls from clearing saved answers.
   *
   * The basket's "Your details" panel (sections/main-cart.liquid, spec
   * 2.8): now an editable panel, not a read-only summary - Registration,
   * Upplating and Current tyre size each write their cart attribute on
   * change through /cart/update.js (postOrderFields, reusing
   * buildOrderFieldsCartData's normalisation - registration upper-cased and
   * stripped of spaces - so the product page's old add-to-cart form and
   * this panel can never disagree about what "clean" looks like). Vehicle
   * is read only, with a "change" link to /pages/vehicles?return=/cart
   * (server-rendered, no JS needed); Tyre letter colour is read only, from
   * the tyre line. Vehicle's own value still redraws live: it listens for
   * the rogue:vehicle-changed event RogueVehicleSelector fires (which has
   * already posted the new attributes, including an actively-cleared
   * "Typed vehicle", to /cart/update.js - see syncVehicleCartAttributes)
   * and reads the same vehicleCartAttributes() the write used, so the two
   * can never disagree.
   */
  const cartDetailsDraft = {};

  class RogueCartDetails extends HTMLElement {
    connectedCallback() {
      this.restoreDraft();
      this.onVehicleChange = (event) => {
        const attributes = vehicleChangeCartAttributes(event?.detail?.vehicle || null);
        Object.assign(cartDetailsDraft, attributes);
        this.restoreDraft();
        this.updateVehicleValue(attributes);
      };
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      this.bindEditableFields();
      this.reconcileStoredVehicle();
    }

    /**
     * THEME_A finding (24 Sep 2026, Major): a stored-van write that skips
     * RogueVehicleSelector.confirm/confirmUnlisted - a `?vehicle=` deep-link
     * restore, chiefly - never called syncVehicleCartAttributes, so the
     * cart's own Vehicle/Typed vehicle attributes (and the per-line
     * mismatch warning that reads them, snippets/cart-products-row.liquid)
     * could still show a PREVIOUS van by the time the shopper opened the
     * basket. Reconciles on every basket render: whenever the client-side
     * stored van disagrees with what the server just rendered here, syncs
     * the cart attributes to match and updates this panel's own Vehicle
     * value immediately rather than waiting for the shopper's next page
     * load.
     */
    async reconcileStoredVehicle() {
      const stored = globalStore.get();
      const renderedVehicle = this.querySelector('[ref="vehicleValue"]')?.textContent?.trim() || '';
      const storedDisplayName = (stored && stored.display_name) || '';
      if (storedDisplayName === renderedVehicle) return; // already in sync

      // Clear both the draft and controls before checkout can save them under
      // the new vehicle. A section morph can otherwise restore old details.
      const attributes = vehicleChangeCartAttributes(stored);
      Object.assign(cartDetailsDraft, attributes);
      this.restoreDraft();
      this.updateVehicleValue(attributes);
      await syncVehicleCartAttributes(stored);
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    /** @param {Record<string, string>} attributes */
    updateVehicleValue(attributes) {
      const value = this.querySelector('[ref="vehicleValue"]');
      if (value) value.textContent = attributes.Vehicle || '';
    }

    bindEditableFields() {
      const registrationInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="registrationInput"]'));
      registrationInput?.addEventListener('input', () => {
        // Uppercase as the shopper types, same as the old product-page field.
        const cursor = registrationInput.selectionStart;
        registrationInput.value = registrationInput.value.toUpperCase();
        if (cursor !== null) registrationInput.setSelectionRange(cursor, cursor);
      });
      registrationInput?.addEventListener('change', () => this.postOrderFields());

      this.querySelector('[ref="currentTyreSizeInput"]')?.addEventListener('change', () => this.postOrderFields());

      this.addEventListener('change', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target?.getAttribute?.('name') === 'attributes[Upplating]') this.postOrderFields();
      });
      this.addEventListener('input', () => Object.assign(cartDetailsDraft, this.orderAttributes()));
      this.querySelector('[ref="retrySave"]')?.addEventListener('click', () => this.postOrderFields());
    }

    restoreDraft() {
      for (const [ref, key] of [['registrationInput', 'Vehicle registration'], ['currentTyreSizeInput', 'Current tyre size']]) {
        const input = this.querySelector(`[ref="${ref}"]`);
        if (input && key in cartDetailsDraft) input.value = cartDetailsDraft[key];
      }
      if ('Upplating' in cartDetailsDraft) {
        for (const radio of this.querySelectorAll('input[name="attributes[Upplating]"]')) {
          radio.checked = radio.value === cartDetailsDraft.Upplating;
        }
      }
    }

    showSaveStatus(message, failed = false) {
      const status = this.querySelector('[ref="saveStatus"]');
      if (status) status.textContent = message;
      const retry = this.querySelector('[ref="retrySave"]');
      if (retry) retry.hidden = !failed;
    }

    /**
     * Reuses buildOrderFieldsCartData's normalisation (spec 2.8: "the
     * existing normalisation") but, unlike the product-page add form, sends
     * every field explicitly - including a blank one - so clearing a field
     * here actually clears the cart attribute rather than /cart/update.js
     * silently leaving an omitted key at its old value (the same class of
     * bug fixed for Typed vehicle - see vehicleCartAttributes).
     *
     * L2 (25 Sep review, finding P10): Upplating is the one exception - it
     * is sent only once the shopper has actually picked Yes or No (neither
     * radio starts checked any more - see sections/main-cart.liquid).
     * Editing Registration or Current tyre size before then must not
     * silently write "No" on their behalf.
     */
    orderAttributes() {
      // The current basket collects no optional fitment answers. Keep its
      // vehicle reconciliation/checkout barrier without treating absent
      // controls as a request to erase answers from an existing basket.
      // The legacy field handling below supports previously rendered carts.
      if (this.hasAttribute?.('data-vehicle-context-only')) {
        return vehicleCartAttributes(globalStore.get());
      }
      const registration = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="registrationInput"]'))?.value ?? '';
      const upplating = this.querySelector('input[name="attributes[Upplating]"]:checked')?.value ?? '';
      const currentTyreSize =
        /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="currentTyreSizeInput"]'))?.value ?? '';
      const fields = buildOrderFieldsCartData({ registration, upplating, currentTyreSize, letterColour: '', tyresEnabled: false });
      const attributes = {
        ...vehicleCartAttributes(globalStore.get()),
        'Vehicle registration': fields['Vehicle registration'] || '',
        'Current tyre size': fields['Current tyre size'] || '',
      };
      if ('Upplating' in fields) attributes.Upplating = fields.Upplating;
      return attributes;
    }

    async postOrderFields({ throwOnError = false } = {}) {
      const attributes = this.orderAttributes();
      Object.assign(cartDetailsDraft, attributes);
      this.showSaveStatus('Saving your details…');
      try {
        await cartAttributeWriter.save(attributes);
        this.showSaveStatus('Details saved.');
      } catch (error) {
        this.showSaveStatus('We could not save your details. Please retry before checking out.', true);
        if (throwOnError) throw error;
      }
    }
  }

  let checkoutPending = false;
  let checkoutApprovedForm = null;
  const pendingCartChanges = cartMutationState.pending;
  let flushCartPairs = async () => {};
  import('@shopify/events').then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      const promise = event?.promise;
      if (!promise?.then) return;
      pendingCartChanges.add(promise);
      promise.then((result) => {
        if (result?.detail?.didError) cartMutationState.failed = true;
        pendingCartChanges.delete(promise);
      }, () => {
        cartMutationState.failed = true;
        pendingCartChanges.delete(promise);
      });
    });
  }).catch(() => {});

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!['cart-form', 'cart-drawer-form'].includes(form?.id) || event.submitter?.name !== 'checkout') return;
    if (checkoutApprovedForm === form) {
      checkoutApprovedForm = null;
      return;
    }
    event.preventDefault();
    if (checkoutPending) return;
    checkoutPending = true;
    event.submitter.setAttribute('aria-busy', 'true');
    let reviewBlocked = false;
    try {
      let details;
      let saved;
      let lateQuantityEdit;
      do {
        for (const items of document.querySelectorAll('cart-items-component')) items.flushPendingQuantityChange?.();
        await Promise.all([...pendingCartChanges]);
        if (cartMutationState.failed) throw new Error('A basket change needs review');
        await flushCartPairs();
        details = document.querySelector('rogue-cart-details');
        saved = JSON.stringify(details?.orderAttributes());
        await details?.postOrderFields({ throwOnError: true });
        await cartAttributeWriter.flush();
        lateQuantityEdit = false;
        for (const items of document.querySelectorAll('cart-items-component')) {
          if (items.flushPendingQuantityChange?.()) lateQuantityEdit = true;
        }
      } while (lateQuantityEdit || pendingCartChanges.size || saved !== JSON.stringify(document.querySelector('rogue-cart-details')?.orderAttributes()));
      if (cartMutationState.failed) throw new Error('A basket change needs review');
      reviewBlocked = Boolean(document.querySelector('[data-rogue-review-state="missing"]'));
      if (reviewBlocked) throw new Error('A wheel package needs a fitment review request');
      const currentForm = document.getElementById(form.id);
      const submitter = document.querySelector(`button[name="checkout"][form="${form.id}"]:not(:disabled)`);
      if (!currentForm || !submitter) throw new Error('Basket is not ready for checkout');
      checkoutApprovedForm = currentForm;
      currentForm.requestSubmit(submitter);
      checkoutApprovedForm = null;
    } catch {
      const message = reviewBlocked
        ? 'We need to double-check the fit for a wheel set in your basket. Remove that set and its tyres, then add the wheel again so we can check it for you.'
        : cartMutationState.failed
        ? 'A basket change could not be confirmed. Reload your basket and check the quantities before checkout.'
        : 'Your basket could not be confirmed. Check your connection and try checkout again.';
      const details = document.querySelector('rogue-cart-details');
      details?.showSaveStatus(message, true);
      let status = document.getElementById('rogue-checkout-error');
      if (!status) {
        status = document.createElement('p');
        status.id = 'rogue-checkout-error';
        status.setAttribute('role', 'alert');
        form.after(status);
      }
      status.textContent = message + ' ';
      if (cartMutationState.failed) {
        const link = document.createElement('a');
        link.href = '/cart';
        link.textContent = 'Reload basket';
        status.append(link);
      }
    } finally {
      event.submitter.removeAttribute('aria-busy');
      checkoutPending = false;
    }
  }, true);

  /**
   * The empty basket's primary CTA (snippets/cart-products.liquid, spec
   * 2.8). Review finding (Minor): "Pick your vehicle" made no sense to a
   * shopper who already had one stored - swapped for "See the wheels for
   * your [van short]" to its collection once a real, listed van is known
   * (whether one is stored at all only exists in the shopper's browser, so
   * this can only happen client side). No vehicle, or the unlisted
   * sentinel (nothing to send a "wheels that fit" link to): the server-
   * rendered default stays exactly as it is - also what a no-JS shopper
   * keeps.
   */
  class RogueCartEmpty extends HTMLElement {
    connectedCallback() {
      const cta = this.querySelector('[ref="emptyPrimaryCta"]');
      if (!cta) return;
      const vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      if (!vehicle || isUnlistedVehicle(vehicle) || !vehicle.collection_handle) return;
      cta.textContent = `See the wheels for your ${shortVanLabel(vehicle)}`;
      cta.setAttribute('href', `${collectionsRoot()}/${vehicle.collection_handle}`);
      // 25 Oct 2026: "Pick your vehicle and we'll show you what fits" sat over a
      // button naming the vehicle already picked.
      const note = this.querySelector('.rogue-cart-empty__note');
      if (note) note.textContent = `Your ${shortVanLabel(vehicle)} is saved, so you'll go straight to its wheels.`;
    }
  }

  /**
   * Review SHOP-012 (17 Oct 2026): the tyre information page
   * (snippets/rogue-tyre-info-buy.liquid) offered "Pick your vehicle" even with
   * a van already saved. For a saved van that is still in the list, the same
   * link reads "Choose wheels for your [van]" and goes straight to the
   * wheels-with-tyres listing the pick-your-van route returns to, which
   * filters to that van. No van, an unlisted van or a withdrawn one keeps the
   * server-rendered "Pick your vehicle". Information only: nothing is added.
   */
  /**
   * D5 (31 Oct 2026, B10): the basket's van-change note with two answers.
   * "Keep this set" hides the note for this session (by line key);
   * "Remove it and see wheels for my [van]" clicks the line's own remove
   * button (so the pairing removes its tyres too), waits for the line to
   * leave the basket, then goes to the stored van's wheels.
   */
  class RogueCartVanNote extends HTMLElement {
    connectedCallback() {
      const key = this.getAttribute('data-line-key') || '';
      if (key && readKeptSet(key)) {
        this.hidden = true;
        return;
      }
      const vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      const name = this.querySelector('[data-van-name]');
      if (name && vehicle) name.textContent = shortVanLabel(vehicle);
      this.querySelector('[data-keep-set]')?.addEventListener('click', () => {
        if (key) rememberKeptSet(key);
        this.hidden = true;
      });
      this.querySelector('[data-remove-set]')?.addEventListener('click', () => this.removeAndGo(key, vehicle));
    }

    async removeAndGo(key, vehicle) {
      if (this.busy) return;
      this.busy = true;
      const status = this.querySelector('[role="status"]');
      const row = this.closest('tr') || this.closest('.cart-items__row') || this.parentElement;
      const remove = /** @type {HTMLButtonElement|null} */ (row?.querySelector('.cart-items__remove'));
      const target = vehicleWheelsUrl(vehicle);
      if (!remove) {
        if (status) status.textContent = 'Use the bin icon to remove this set.';
        this.busy = false;
        return;
      }
      if (status) status.textContent = 'Removing this set…';
      for (const button of this.querySelectorAll('button')) button.disabled = true;
      remove.click();
      const gone = await waitForLineToLeave(key);
      if (gone) {
        window.location.href = target;
        return;
      }
      if (status) status.textContent = 'The set is still in your basket. Use the bin icon, then pick your wheels.';
      for (const button of this.querySelectorAll('button')) button.disabled = false;
      this.busy = false;
    }
  }

  function keptSetKey(lineKey) {
    return `rogue.cart.keepSet.${lineKey}`;
  }

  function readKeptSet(lineKey) {
    try {
      return window.sessionStorage.getItem(keptSetKey(lineKey)) === '1';
    } catch {
      return false;
    }
  }

  function rememberKeptSet(lineKey) {
    try {
      window.sessionStorage.setItem(keptSetKey(lineKey), '1');
    } catch {
      // Private mode: the note comes back on the next render, which is harmless.
    }
  }

  /** Where "see wheels for my [van]" goes: the stored van's collection, or every wheel. */
  function vehicleWheelsUrl(vehicle) {
    const handle = vehicle && !isUnlistedVehicle(vehicle) && vehicle.collection_handle ? vehicle.collection_handle : 'all-wheels';
    return buildVehicleCollectionUrl(collectionsRoot(), handle, null);
  }

  /**
   * Polls /cart.js until the line is gone (up to about four seconds).
   * @param {string} lineKey
   * @returns {Promise<boolean>}
   */
  async function waitForLineToLeave(lineKey) {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) continue;
        const cart = await response.json();
        if (!cart.items?.some((item) => item.key === lineKey)) return true;
      } catch {
        // Try again.
      }
    }
    return false;
  }

  class RogueTyreInfoCta extends HTMLElement {
    connectedCallback() {
      this.cta = this.querySelector('[ref="tyreInfoCta"]');
      if (!this.cta) return;
      this.defaultText = this.cta.textContent;
      this.defaultHref = this.cta.getAttribute('href');
      this.render();
      this.onVehicleChange = () => this.render();
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    disconnectedCallback() {
      if (this.onVehicleChange) document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    render() {
      const vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      const target = this.getAttribute('data-saved-van-url');
      if (vehicle && !isUnlistedVehicle(vehicle) && target) {
        this.cta.textContent = `Choose wheels for your ${shortVanLabel(vehicle)}`;
        this.cta.setAttribute('href', target);
        this.cta.removeAttribute('data-rogue-inline-vehicle');
      } else {
        this.cta.textContent = this.defaultText;
        if (this.defaultHref) this.cta.setAttribute('href', this.defaultHref);
        // "Pick your vehicle" opens the inline panel on this page (25 Oct 2026).
        this.cta.setAttribute('data-rogue-inline-vehicle', '');
      }
    }
  }

  /**
   * The buy panel from snippets/rogue-fitment-gate.liquid: the Colour/Size
   * picker, the fitment gate, the price lines, the tyres panel and the
   * add-to-cart form, all in one element so there is a single place that
   * knows what the current selection is.
   */
  class RogueFitmentGate extends HTMLElement {
    connectedCallback() {
      this.setSize = Number(this.getAttribute('data-set-size')) || ROGUE_SET_SIZE;
      this.variantMetaByVariantId = this.readJson('script[data-rogue-variant-meta]', {});
      this.tyreOptions = this.readJson('script[data-rogue-tyres]', []);
      this.tyreSizeRules = parseTyreSizeRules(this.getAttribute('data-tyre-size-rules'));
      this.whiteLetterRules = parseWhiteLetterRules(this.getAttribute('data-white-letter-rules'));
      this.optionNames = this.readJson('script[data-rogue-product-options]', []);
      this.currentVariantId = this.getAttribute('data-current-variant-id') || '';
      this.productHandle = this.getAttribute('data-product-handle') || '';
      this.positions = optionPositionsByName(Array.isArray(this.optionNames) ? this.optionNames : []);
      this.variants = Object.entries(this.variantMetaByVariantId).map(([id, meta]) => ({ id, ...meta }));
      this.resolvedFits = null;
      // Set for real by renderSizeSelector, which always runs before the
      // first render() (see the init order below) - see setBadgeText for
      // where this folds into the fit line (spec 2.1/2.3: one fitting size
      // moves into the fit line instead of its own Size step).
      this.singleFitSizeLabel = null;
      // Default tyre choice (spec 9 BUY brief / section 10 decision 2):
      // "Wheels only", one setting Rogue can flip later - see
      // snippets/rogue-fitment-gate.liquid's default_tyre_choice.
      this.tyresEnabled = this.getAttribute('data-default-tyres-enabled') === 'true';
      // M4 (25 Sep review, finding P8): a "Wheel and tyre sets" card links
      // here with `?tyres=1` (see snippets/rogue-product-card.liquid) so the
      // set price shows immediately rather than the shopper having to find
      // the tyre choice for themselves after following a "sets" link.
      // Overrides the block-setting default above; a plain `?variant=` link
      // with no `tyres` param is untouched.
      try {
        if (new URLSearchParams(window.location.search).get('tyres') === '1') this.tyresEnabled = true;
      } catch {
        // Malformed query string - keep the block-setting default.
      }
      // H1 (25 Sep review, finding P1): the tyre product handle Paul's own
      // pick - see snippets/rogue-fitment-gate.liquid's default_tyre_line
      // block setting, read by defaultTyreHandle.
      this.defaultTyreLineHandle = this.getAttribute('data-default-tyre-line') || '';
      // Set for real by updateStudPatternSection on the first render() -
      // see hasStudPatternChoice (review 2, finding 11).
      this.studPatternHasChoice = false;
      // The tyre lines actually on offer for the current rim size, sorted
      // cheapest first (spec 2.3) - recomputed by updateTyrePanel every
      // render, since it depends on both the resolved rim size and the
      // stored van.
      this.availableTyreLines = [];
      // The sizes THIS wheel offers for the stored van (renderSizeSelector's
      // own sizeSelectorState.sizes) - read by updateTyrePanel's "switch to a
      // size we DO have a tyre for" offer (tyreSizeSwitchTarget). Empty until
      // renderSizeSelector runs at least once (products with no Colour/Size
      // picker never call it - see hasRogueOptionPicker - so updateTyrePanel
      // always falls back to this default rather than assuming it is set).
      this.fittingSizes = [];
      // Whether renderSizeSelector has run at least once with a real,
      // matching vehicle stored - see its own doc comment (forcePreferred).
      this._sizeSeeded = false;
      // "Show all N tyres" (spec 2.3: five rows, then a reveal).
      this.tyresExpanded = false;
      // The tyre row currently chosen, by product handle - null until the
      // shopper picks one, or the size/choice changes to something that
      // doesn't offer it any more (see updateTyrePanel).
      this.selectedTyreHandle = null;
      // H2 (25 Sep review, finding P2): the shopper's own last pick, kept
      // separately from selectedTyreHandle and NEVER cleared just because a
      // size change temporarily hid it (a "Switch to 17 inch" round trip
      // used to lose the shopper's chosen tyre for good - see
      // applyDefaultTyreSelection/updateTyrePanel, which restore it first,
      // before any default applies).
      this.lastTyreHandle = null;
      // N2 (26 Sep review round 3): the title that went with lastTyreHandle,
      // cached at the moment of selection - once a later size change drops
      // it from availableTyreLines entirely there is nothing left to look
      // its title up in, and resolveTyreSelectionForSize needs it to name
      // the shopper's own pick in the "isn't made in this size" note.
      this.lastTyreTitle = '';
      // Lettering colour, inside whichever tyre row is selected (spec 2.3
      // default Black).
      this.letterColour = 'Black';
      // Quantity counts physical wheels; spare intent classifies one of them.
      this.setChoice = null;
      this.spareIncluded = false;
      // Whether the spare tick came from picking the set size plus one (spareTickForQuantity).
      this.spareAuto = false;
      // The post-add confirmation auto-hides after 8s, or sooner if the
      // shopper changes a choice (spec 2.3's "After add" row) - see
      // showAddConfirmation/hideAddConfirmation.
      this.confirmationTimer = null;

      this.plusSpareSize = Number(this.getAttribute('data-plus-spare-size')) || this.setSize + 1;
      this.fitNoteTemplate = this.getAttribute('data-fit-note') || 'Stud pattern, offset and centre bore are set for you.';
      this.noVehicleText = this.getAttribute('data-no-vehicle-text') || 'Which van is it for?';
      this.noVehicleNote =
        this.getAttribute('data-no-vehicle-note') || "Pick your vehicle and we'll show the sizes and finishes that fit it.";
      this.unlistedTextTemplate =
        this.getAttribute('data-unlisted-fitment-text') || "We'll check this fits your {van} before it ships";
      this.tyreNoSizeText =
        this.getAttribute('data-tyre-no-size-text') ||
        "We don't have your vehicle's tyre size on file yet. Order the wheels and we'll call you to confirm tyres before anything ships.";
      this.phone = this.getAttribute('data-phone') || '';

      // Copy defaults rendered by Liquid, kept so a state change can put them
      // back rather than leaving a half-filled line behind. The spec
      // table's two placeholders are captured from `document`, not `this`:
      // spec 2.5 moved that table outside this element (see updateSpec).
      this.spareLabelDefault = this.textOf('spareLabel') || 'Add a spare wheel';
      this.loadRatingPlaceholder = document.querySelector('[ref="specLoadRating"]')?.textContent?.trim() || '';
      this.specFitmentPlaceholder = document.querySelector('[ref="specFitment"]')?.textContent?.trim() || 'Set automatically';

      // B3 (31 Oct 2026): on a phone the finish block sits straight under
      // the photo, before the fit line and title, in the DOM as well as on
      // screen (no `order: -1`), so focus order matches what is seen.
      this.placeFinishBlock();

      this.seedQuantity();
      this.lastQuantity = this.currentQuantity();
      this.bindQuantityChoices();
      this.bindSpareCheckbox();
      this.bindTyreChoice();
      this.bindStickyBar();
      this.bindAddConfirmation();

      if (this.hasRogueOptionPicker()) {
        this.initSelectionFromCurrentVariant();
        this.renderColourSwatches();
        this.renderSizeSelector();
        this.applyResolution(); // resolves the variant + calls render()
      } else {
        // No Colour/Size option to drive - e.g. a preview/test fixture product
        // with a plain fits_vehicles-only variant. Fall back to the original
        // per-variant gating.
        this.render();
      }

      this.addEventListener('change', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const name = target?.getAttribute('name');
        if (name === 'rogue-stud-pattern' || target?.hasAttribute('data-rogue-not-sure')) {
          this.updateCartAttributesFields();
        }
      });

      this.addEventListener('click', (event) => this.handlePickerClick(event));
      this.onVehicleChange = (event) => {
        if (event?.type === 'storage' && event.key !== null && event.key !== VEHICLE_STORAGE_KEY) return;
        const current = this.vehicle;
        const next = enrichVehicle(globalStore.get(), readVehiclesFromDom());
        if (JSON.stringify(current) === JSON.stringify(next)) return;
        if (this._adding) { this.vehicleChangePending = true; return; }
        this._vehicle = next;
        this.hideAddConfirmation();
        this._sizeSeeded = false;
        if (this.hasRogueOptionPicker()) {
          // A vehicle chosen on this page (the inline panel, another tab)
          // moves the selection to a finish and size that fit it, even on a
          // `?variant=` link - preferredSelection keeps the current choice
          // whenever it already fits (25 Oct 2026).
          this._reselectForVehicle = true;
          try {
            this.initSelectionFromCurrentVariant();
          } finally {
            this._reselectForVehicle = false;
          }
          this.renderColourSwatches();
          this.renderSizeSelector();
          this.applyResolution();
        } else this.render();
        document.dispatchEvent(new CustomEvent('rogue:package-change'));
      };
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      window.addEventListener('storage', this.onVehicleChange);
      window.addEventListener('pageshow', this.onVehicleChange);
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      window.removeEventListener('storage', this.onVehicleChange);
      window.removeEventListener('pageshow', this.onVehicleChange);
    }

    /**
     * The stored van, topped up with the fields only the full record carries
     * (`offset_min` / `offset_max` / `tyre_sizes_json` / the naming fields).
     * Cached until a same-page, cross-tab or history change; in-flight adds keep
     * the vehicle captured when the customer pressed Add.
     */
    get vehicle() {
      if (this._vehicle === undefined) {
        this._vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      }
      return this._vehicle;
    }

    /**
     * The vehicle used for every PCD/fits decision - Finish swatches, the
     * Size step, variant resolution (see initSelectionFromCurrentVariant,
     * renderColourSwatches, renderSizeSelector, applyResolution). Identical
     * to `vehicle` except for the "My van isn't listed" sentinel (S3.2a),
     * which carries no PCD data and shops exactly like no vehicle stored at
     * all - the full picker open, nothing greyed out - while `vehicle` (the
     * real getter) still drives the stud-pattern hold section, the
     * Vehicle/Typed vehicle cart attributes and the badge copy. See
     * isUnlistedVehicle and computeStateFromResolution.
     */
    get matchingVehicle() {
      return isUnlistedVehicle(this.vehicle) ? null : this.vehicle;
    }

    /**
     * @param {string} selector
     * @param {*} fallback
     */
    readJson(selector, fallback) {
      const script = this.querySelector(selector);
      if (!script || !script.textContent) return fallback;
      try {
        const parsed = JSON.parse(script.textContent);
        return parsed == null ? fallback : parsed;
      } catch {
        return fallback;
      }
    }

    /** @param {string} refName */
    ref(refName) {
      return this.querySelector(`[ref="${refName}"]`);
    }

    /**
     * Moves the finish block before the header on phones and back after it
     * from 750px (decision 6: the photo and the finish on screen together;
     * B3: by markup, not CSS order). The block stays inside this element,
     * so ref() and the delegated click handling are unchanged.
     */
    placeFinishBlock() {
      const block = this.ref('colourBlock');
      const header = this.querySelector('.rogue-buy__header');
      if (!block || !header || typeof window.matchMedia !== 'function') return;
      const query = window.matchMedia('(max-width: 749px)');
      const apply = () => {
        const placement = finishBlockPlacement(query.matches);
        if (placement === 'before-header' && block.nextElementSibling !== header) header.insertAdjacentElement('beforebegin', block);
        if (placement === 'after-header' && header.nextElementSibling !== block) header.insertAdjacentElement('afterend', block);
      };
      apply();
      query.addEventListener?.('change', apply);
    }

    /** @param {string} refName */
    textOf(refName) {
      return this.ref(refName)?.textContent?.trim() || '';
    }

    /** @returns {boolean} whether this product has a Colour and/or Size option to drive as a picker */
    hasRogueOptionPicker() {
      return this.positions.colour !== -1 || this.positions.size !== -1;
    }

    /**
     * Four is the initial suggested quantity; singles, pairs and other
     * positive whole quantities are supported. `data-quantity-default` on <product-form-component> covers
     * the case where the stepper is missing entirely.
     */
    seedQuantity() {
      const input = /** @type {HTMLInputElement|null} */ (this.querySelector('input[ref="quantityInput"]'));
      if (!input) return;
      const min = Number(input.min) || 1;
      if (this.setSize >= min) input.value = String(this.setSize);
    }

    currentQuantity() {
      const input = this.querySelector('input[ref="quantityInput"]');
      return wheelQuantityValue(input ? input.value : this.setSize);
    }

    bindQuantityChoices() {
      const input = this.querySelector('input[ref="quantityInput"]');
      input?.addEventListener('input', () => this.quantityChanged());
      this.querySelectorAll('[data-rogue-wheel-quantity]').forEach((button) => {
        button.addEventListener('click', () => {
          if (input) input.value = button.getAttribute('data-rogue-wheel-quantity');
          this.quantityChanged();
        });
      });
    }

    quantityChanged() {
      this.hideAddConfirmation();
      const quantity = this.currentQuantity();
      this.applySpareForQuantity(quantity);
      this.querySelectorAll('[data-rogue-wheel-quantity]').forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.getAttribute('data-rogue-wheel-quantity')) === quantity));
      });
      if (this.hasRogueOptionPicker()) this.applyResolution();
      else this.updatePrices(this.getCurrentVariantMeta());
      this.updateTyreFields();
      this.updateCartAttributesFields();
      this.updateSummaryLine();
      this.renderTyreRows();
      document.dispatchEvent(new CustomEvent('rogue:package-change'));
    }

    quantityError() {
      const quantity = this.currentQuantity();
      const wheelError = variantQuantityError(this.getCurrentVariantMeta(), quantity);
      if (wheelError) return wheelError;
      const tyre = this.resolvedTyreLine();
      const tyreError = tyre ? variantQuantityError(tyre.variant, quantity) : '';
      return tyreError ? `Tyres: ${tyreError}` : '';
    }

    updateQuantityState() {
      const error = this.quantityError();
      const status = this.ref('quantityStatus');
      // A sold-out size and finish already says so beside "Call to reserve";
      // the quantity line no longer repeats it (the button stays disabled).
      const shown = this.getCurrentVariantMeta?.()?.available === false ? '' : error;
      if (status) { status.textContent = shown; status.hidden = !shown; }
      const button = this.ref('addToCartButton');
      if (button && !this.addNeedsReview) button.disabled = Boolean(error || this._adding || ['no-vehicle', 'not-fits'].includes(this.fitmentState));
    }

    /* ------------------------- "Add a spare wheel" (spec 2.3) ------------------------- */

    /** Classify one of the selected wheels as a spare without changing quantity. */
    bindSpareCheckbox() {
      const checkbox = /** @type {HTMLInputElement|null} */ (this.ref('spareCheckbox'));
      checkbox?.addEventListener('change', () => {
        this.hideAddConfirmation();
        this.applySpareChoice(checkbox.checked);
      });
    }

    /** Ticks or clears the spare for a new wheel count, box included; see spareTickForQuantity. */
    applySpareForQuantity(quantity) {
      const previousQuantity = this.lastQuantity ?? null;
      this.lastQuantity = quantity;
      const next = spareTickForQuantity({ quantity, previousQuantity, plusSpareSize: this.plusSpareSize, checked: this.spareIncluded, auto: this.spareAuto });
      this.spareAuto = next.auto;
      if (next.checked === this.spareIncluded) return;
      this.spareIncluded = next.checked;
      const checkbox = /** @type {HTMLInputElement|null} */ (this.ref('spareCheckbox'));
      if (checkbox) checkbox.checked = next.checked;
    }

    /** The shopper's own choice, which the wheel count never undoes. @param {boolean} checked */
    applySpareChoice(checked) {
      this.spareIncluded = Boolean(checked);
      this.spareAuto = false;
      this.quantityChanged();
    }

    /** Spare intent is independent of physical quantity and price. */
    updateSpareLabel() {
      const label = this.ref('spareLabel');
      if (!label) return;
      // 25 Oct 2026: a single wheel read "One of these wheels is a spare".
      label.textContent = this.currentQuantity() === 1 ? 'This wheel is a spare' : 'One of these wheels is a spare';
    }

    /** Seeds selectedColour/selectedSize from the server-selected variant (or the first colour). */
    initSelectionFromCurrentVariant() {
      const current = this.getCurrentVariantMeta();
      this.selectedColour = current ? variantOptionValue(current, this.positions, 'colour') : null;
      this.selectedSize = current ? variantOptionValue(current, this.positions, 'size') : null;

      if (!this.selectedColour) {
        const swatches = buildColourSwatches(this.variants, this.positions);
        this.selectedColour = swatches[0]?.value || null;
      }

      // A shared variant link names a real customer choice. Keep its finish and
      // size, explaining stock/fitment below instead of silently substituting.
      if (!this._reselectForVehicle && new URLSearchParams(window.location.search).has('variant')) return;

      // Land on something the shopper can actually buy for their van rather
      // than Shopify's first variant (see preferredSelection). Uses
      // matchingVehicle, not vehicle: an unlisted van has no PCD data to
      // filter "buyable" against, so it shops unrestricted like no vehicle
      // stored (see the matchingVehicle getter).
      const preferred = preferredSelection(this.variants, this.positions, this.matchingVehicle, {
        colourValue: this.selectedColour,
        sizeValue: this.selectedSize,
      });
      if (preferred.changed) {
        this.selectedColour = preferred.colour;
        this.selectedSize = preferred.size;
      }
    }

    /** Known vehicles show fitting finishes, with sold-out choices in a disclosure. */
    renderColourSwatches() {
      const container = this.ref('colourSwatches');
      if (!container) return;

      const states = colourSwatchStates(this.variants, this.positions, this.matchingVehicle);
      if (states.length === 0) {
        container.setAttribute('hidden', '');
        return;
      }

      container.removeAttribute('hidden');
      const fitting = this.matchingVehicle ? states.filter((state) => state.reason !== 'Not for your van') : states;
      const available = fitting.filter((state) => state.selectable);
      const soldOut = fitting.filter((state) => !state.selectable);
      const markup = (list) => list
        .map((swatch) => {
          const selected = swatch.value === this.selectedColour;
          const style = swatch.image
            ? `background-image:url('${escapeHtml(swatch.image)}');background-size:cover;background-position:center`
            : `background:${escapeHtml(swatch.background || swatch.color)}`;
          const title = swatch.reasonTitle ? ` title="${escapeHtml(swatch.reasonTitle)}" aria-label="${escapeHtml(swatch.value)}, ${escapeHtml(swatch.reasonTitle)}"` : '';
          return `
            <button
              type="button"
              class="rogue-swatch${selected ? ' is-on' : ''}"
              data-rogue-colour="${escapeHtml(swatch.value)}"
              aria-pressed="${selected}"
              ${title}
            >
              <span class="rogue-swatch__dot" style="${style}"></span>
              <span class="rogue-swatch__label">${escapeHtml(swatch.value)}</span>
              ${swatch.selectable ? '' : '<span class="rogue-swatch__label">Out of stock</span>'}
            </button>
          `;
        })
        .join('');
      const soldOutOpen = container.querySelector('details')?.open || soldOut.some((state) => state.value === this.selectedColour);
      container.innerHTML = this.matchingVehicle
        ? markup(available) + (soldOut.length ? `<details class="rogue-buy__soldout-finishes"${soldOutOpen ? ' open' : ''}><summary>Sold-out finishes for your vehicle (${soldOut.length})</summary><div class="rogue-buy__swatches">${markup(soldOut)}</div></details>` : '')
        : markup(fitting);
      const note = this.ref('colourNote');
      if (note) {
        // B12 (31 Oct 2026): no "Choose your vehicle to check which finishes
        // fit" under the swatches; the van step says so already.
        note.textContent = '';
        if (isUnlistedVehicle(this.vehicle)) note.textContent = 'Fitment must be confirmed for your unlisted vehicle.';
        note.hidden = !note.textContent;
      }
    }

    /** @param {import('./rogue-fitment.js').RogueSwatchState[]} states */
    updateFinishNote(states) {
      const note = this.ref('colourNote');
      if (!note) return;
      const text = finishNoteText(states);
      note.textContent = text;
      note.toggleAttribute('hidden', text === '');
    }

    /**
     * Renders the Size step: pills when the fitting variants for the stored
     * vehicle (or, with no vehicle stored, all variants of this colour) span
     * more than one size, otherwise a plain "{size}, fits your {van}" line
     * with no selector at all (see {@link sizeSelectorState}).
     *
     * Walk finding (Daniel, 25 Sep 2026): initSelectionFromCurrentVariant
     * seeds `this.selectedSize` from Liquid's server-rendered variant, which
     * has no idea a van is even stored - on the Transporter T5-T6.1, Rocky's
     * first available variant happened to be 16 inch, and 16 inch DOES fit
     * the van's PCD, so the `!state.sizes.includes(this.selectedSize)` guard
     * below saw its own seed as already valid and never called
     * preferredFittingSize at all - popular_rim/tyreSizeIsKnown never got a
     * say, no matter how they were ordered. `this._sizeSeeded` forces that
     * one call, once, on this element's very first render with a real,
     * matching vehicle stored - every later call (a Finish swatch clicked, a
     * Size pill clicked, "Switch to N inch" clicked) goes back to only
     * overriding the selection when it stops fitting the current Colour, so
     * a shopper's own choice is never silently reset back to popular_rim.
     *
     * H3 (25 Sep review, finding P3): that one forced call is skipped
     * outright when the page was opened on a specific `?variant=` link -
     * a shared link, an ad or a Google Shopping link names an exact size,
     * and overriding it with popular_rim showed a DIFFERENT size at the
     * same price with no hint anything had changed (measured live:
     * `?variant=` for the 16 inch Rocky opened on 17 inch instead).
     * `initSelectionFromCurrentVariant` has already seeded `this.selectedSize`
     * from that variant by the time this runs, so simply not forcing here
     * is enough to keep it - the `!state.sizes.includes(this.selectedSize)`
     * branch below still applies if that size genuinely isn't buyable for
     * the stored van at all.
     */
    renderSizeSelector() {
      const vehicle = this.matchingVehicle;
      const state = sizeSelectorState(this.variants, this.positions, this.selectedColour, vehicle);
      const openedOnVariant = new URLSearchParams(window.location.search).has('variant');
      const forcePreferred = Boolean(vehicle) && !this._sizeSeeded && !openedOnVariant;
      const variantsForSize = (size) => this.variants.filter((variant) =>
        variantOptionValue(variant, this.positions, 'colour') === this.selectedColour &&
        variantOptionValue(variant, this.positions, 'size') === size &&
        (!vehicle || variantFitsVehicle(variant, this.positions, vehicle)));
      const availableSizes = state.sizes.filter((size) => variantsForSize(size).some((variant) => variant.available !== false));
      const quantitySizes = availableSizes.filter((size) => variantsForSize(size).some((variant) => !variantQuantityError(variant, this.currentQuantity())));
      const preferredSizes = quantitySizes.length ? quantitySizes : availableSizes.length ? availableSizes : state.sizes;

      if (state.autoSelected) {
        this.selectedSize = state.autoSelected;
      } else if (state.visible && (forcePreferred || !state.sizes.includes(this.selectedSize))) {
        this.selectedSize = preferredFittingSize({ ...state, sizes: preferredSizes }, vehicle);
      } else if (!state.visible && !state.autoSelected) {
        this.selectedSize = null;
      }

      // Spec 2.1/2.3 (review finding, Major - "drop the SIZE block"): the
      // whole step only renders with a real choice (two or more fitting
      // sizes) - previously the outer block stayed on screen regardless,
      // showing just the "Size" label plus the auto-selected size as text,
      // eating vertical space the 390 button-position target (spec 2.5)
      // needed. One fitting size instead folds into the fit line itself
      // (see setBadgeText) - singleFitSizeLabel is only set from the real
      // "one fitting size for a real vehicle" branch of sizeSelectorState,
      // never the no-vehicle "only one size exists at all" branch, which
      // has nothing to do with fit.
      const sizeBlock = this.ref('sizeBlock');
      if (sizeBlock) sizeBlock.toggleAttribute('hidden', !state.visible);
      this.singleFitSizeLabel =
        vehicle && !state.visible && state.sizes.length === 1 ? formatSizeLabel(state.sizes[0]) : null;

      const pillsContainer = this.ref('sizeSelector');
      if (pillsContainer) {
        if (state.visible) {
          pillsContainer.removeAttribute('hidden');
          pillsContainer.innerHTML = state.sizes
            .map(
              (size) => `
                <button
                  type="button"
                  class="rogue-pill${size === this.selectedSize ? ' is-on' : ''}"
                  data-rogue-size="${escapeHtml(size)}"
                  aria-pressed="${size === this.selectedSize}"
                >${escapeHtml(formatSizeLabel(size))}${availableSizes.includes(size) ? '' : ' — Out of stock'}</button>
              `
            )
            .join('');
        } else {
          pillsContainer.setAttribute('hidden', '');
          pillsContainer.innerHTML = '';
        }
      }

      const textEl = this.ref('sizeText');
      if (textEl) {
        if (!state.visible && state.autoText) {
          textEl.textContent = state.autoText;
          textEl.removeAttribute('hidden');
        } else {
          textEl.setAttribute('hidden', '');
        }
      }

      const noteEl = this.ref('sizeNote');
      if (noteEl) noteEl.textContent = !availableSizes.includes(this.selectedSize) && availableSizes.length
        ? `${formatSizeLabel(this.selectedSize)} is out of stock in this finish. Choose ${availableSizes.map(formatSizeLabel).join(' or ')} to see available wheels, or give us a call.`
        : sizeNoteText(state.visible ? state.sizes.length : 0, shortVanLabel(vehicle));

      // Read by updateTyrePanel's own "switch to a size we DO have a tyre
      // for" offer (tyreSizeSwitchTarget) - the sizes THIS wheel offers for
      // the stored van, for the currently selected colour, exactly as this
      // method just rendered them (pills or not). Kept in sync here rather
      // than recomputed a second time in updateTyrePanel, so the two can
      // never disagree about what this wheel comes in.
      this.fittingSizes = state.sizes;
      this._sizeSeeded = true;

      return state;
    }

    /** @param {MouseEvent} event */
    handlePickerClick(event) {
      const target = /** @type {HTMLElement} */ (event.target);

      const keepShoppingButton = target?.closest('[ref="keepShoppingButton"]');
      if (keepShoppingButton) {
        this.hideAddConfirmation();
        return;
      }

      const showAllButton = target?.closest('[ref="tyreShowAll"]');
      if (showAllButton) {
        this.tyresExpanded = true;
        this.renderTyreRows();
        return;
      }

      // "Switch to 17 inch" (tyreSizeSwitchTarget/updateTyrePanel): the size
      // the button carries is always one of this wheel's own fitting sizes
      // (renderSizeSelector's own this.fittingSizes is exactly what
      // updateTyrePanel picked it from), so selecting it the same way a
      // Size pill click does - reselect, re-render the pills, re-resolve the
      // variant - lands on a real, buyable size with its tyre rows showing.
      const switchSizeButton = target?.closest('[ref="tyreSwitchSize"]');
      if (switchSizeButton) {
        const switchSize = switchSizeButton.getAttribute('data-rogue-switch-size');
        if (switchSize) {
          this.hideAddConfirmation();
          this.selectedSize = switchSize;
          this.renderSizeSelector();
          this.applyResolution();
        }
        return;
      }

      const tyreRow = target?.closest('[data-rogue-tyre-handle]');
      // A click on the lettering radio/label inside the row is its own
      // 'change' handler (bindTyreChoice) - only a click on the row itself
      // (or its own radio) selects the tyre line. The keyboard path (arrow
      // keys between same-named radios) goes through selectTyreLine via the
      // 'change' listener bindTyreChoice also sets up, not this click
      // handler, so both pointer and keyboard selection land on the same
      // one place.
      if (tyreRow && !target.closest('.rogue-buy__tyre-row-lettering')) {
        this.selectTyreLine(tyreRow.getAttribute('data-rogue-tyre-handle'));
        return;
      }

      const colourButton = target?.closest('[data-rogue-colour]');
      if (colourButton) {
        if (colourButton.getAttribute('aria-disabled') === 'true') return;
        this.hideAddConfirmation();
        this.selectedColour = colourButton.getAttribute('data-rogue-colour');
        this.renderColourSwatches();
        this.renderSizeSelector();
        this.applyResolution();
        return;
      }

      const sizeButton = target?.closest('[data-rogue-size]');
      if (sizeButton) {
        this.hideAddConfirmation();
        this.selectedSize = sizeButton.getAttribute('data-rogue-size');
        this.renderSizeSelector();
        this.applyResolution();
      }
    }

    /**
     * Resolves the variant for the current Colour/Size selection (Fitment
     * comes along for free - see {@link resolveVariant}), updates the
     * add-to-cart form's variant id, and re-runs the gate. Uses
     * matchingVehicle - see that getter for why an unlisted van resolves as
     * if no vehicle were stored.
     */
    applyResolution() {
      const vehicle = this.matchingVehicle;
      const { variant, fits } = resolveVariant(
        this.variants,
        this.positions,
        { colourValue: this.selectedColour, sizeValue: this.selectedSize, quantity: this.currentQuantity(), preferredVariantId: this.currentVariantId },
        vehicle
      );

      const previous = this.getCurrentVariantMeta();
      const specificationNote = this.ref('specificationNote');
      if (specificationNote) {
        // 25 Oct 2026: only a quantity-driven switch between two drillings that
        // both fit. On first load (or a vehicle change) the move to the fitting
        // drilling is the fit itself, and "updated for this quantity" misled.
        const changedDrilling = previous && variant && String(this.currentVariantId) !== String(variant.id) &&
          variantOptionValue(previous, this.positions, 'colour') === this.selectedColour &&
          variantOptionValue(previous, this.positions, 'size') === this.selectedSize &&
          (!vehicle || variantFitsVehicle(previous, this.positions, vehicle));
        specificationNote.textContent = changedDrilling ? `Available specification updated for this quantity: ${variantOptionValue(variant, this.positions, 'fitment') || 'see wheel specification'}.` : '';
        specificationNote.hidden = !specificationNote.textContent;
      }
      if (variant) this.currentVariantId = String(variant.id);
      this.resolvedFits = fits;
      this.updateVariantIdInput();
      this.render();
    }

    updateVariantIdInput() {
      const input = /** @type {HTMLInputElement|null} */ (this.querySelector('input[name="id"]'));
      if (input) {
        input.value = this.currentVariantId;
        return;
      }
      // The form is inside this element, but keep the by-id lookup as a
      // fallback for any layout that moves it out.
      const formId = this.getAttribute('data-cart-form-id') || '';
      const form = formId ? document.getElementById(formId) : null;
      const fallback = /** @type {HTMLInputElement|null} */ (form?.querySelector('input[name="id"]'));
      if (fallback) fallback.value = this.currentVariantId;
    }

    getCurrentVariantMeta() {
      return this.variantMetaByVariantId[this.currentVariantId] || null;
    }

    /**
     * Fitment state derived from {@link resolveVariant}'s `fits` result
     * (Colour/Size-driven products), rather than the older per-variant
     * `fits_vehicles` list. An unlisted van (S3.2a) short-circuits to its
     * own state before `resolvedFits` is even consulted - see
     * isUnlistedVehicle and the matchingVehicle getter.
     * @param {RogueVehicleSelection|null} vehicle
     * @returns {FitmentState}
     */
    computeStateFromResolution(vehicle) {
      if (!vehicle) return 'no-vehicle';
      if (isUnlistedVehicle(vehicle)) return 'unlisted';
      if (this.resolvedFits === false) return 'not-fits';
      return requiresManualReview(vehicle) ? 'manual-review' : 'fits';
    }

    render() {
      const vehicle = this.vehicle;
      const variantMeta = this.getCurrentVariantMeta();
      const state = this.hasRogueOptionPicker()
        ? this.computeStateFromResolution(vehicle)
        : computeFitmentState(vehicle, variantMeta);
      // Sold out (spec 2.4): the resolved variant isn't available and isn't
      // a pre-order - only meaningful once there's a real, fitting variant
      // to be out of stock in.
      const { isPreorder } = preorderBadgeData(variantMeta);
      const soldOut = (state === 'fits' || state === 'manual-review') && variantMeta?.available === false;

      this.fitmentState = state;
      this.setAttribute('data-fitment-state', state);
      // 25 Oct 2026: nothing of this wheel can be bought for this vehicle right
      // now (FH1), so the quantity, tyre and spare steps step back and the
      // "Call to reserve" line is the one way on (assets/rogue-buy.css).
      const allSoldOut = soldOut && !(this.variants || []).some((v) => v.available !== false);
      this.toggleAttribute('data-all-sold-out', allSoldOut);
      // Product-level answer (any finish, any size) - 'none' collapses the
      // whole buy box to "Sorry, this wheel won't fit your ..." plus wheels
      // that do (see updateStateCollapse).
      this.productFit = productFitSummary(this.variants, this.positions, vehicle);
      this.setAttribute('data-product-fit', this.productFit);
      this.setBadgeText(vehicle, state);
      this.updateStockLine(variantMeta);
      this.updateSpec(variantMeta, state);
      this.updateStateCollapse(vehicle, state, soldOut);
      this.updateAddToCart(state, soldOut);
      this.updateStudPatternSection(vehicle);
      this.updatePreorderBadge(variantMeta);
      this.updateFitAssurance(vehicle, state, soldOut);
      // Recomputes availableTyreLines for the (possibly just-changed) rim
      // size before updatePrices reads it for the combined total.
      this.updateTyrePanel(vehicle);
      this.updatePrices(variantMeta);
      this.updateSpareLabel();
      this.updateTyreFields();
      this.updateCartAttributesFields();
      this.updateSummaryLine();
      this.announceVariant(variantMeta);
      this.updateStickyBar(state, variantMeta, soldOut);
    }

    setBadgeText(vehicle, state) {
      const badge = this.ref('fitmentBadge');
      const text = this.ref('fitmentBadgeText') || badge;
      const note = this.ref('fitmentBadgeNote');
      if (badge) badge.setAttribute('data-state', state);

      if (text) {
        if (state === 'no-vehicle') {
          text.textContent = this.noVehicleText;
        } else if (state === 'manual-review') {
          text.textContent = manualReviewBadgeText(vehicle);
        } else if (state === 'unlisted') {
          const template = this.unlistedTextTemplate;
          text.textContent = template.includes('{van}') ? template.split('{van}').join(typedVehicleText(vehicle)) : template;
        } else if (state === 'fits') {
          // Spec 2.3: "[van short] is model plus generation... 'Ducato L1,
          // L2, L3'" - never the make (vehicle.display_name carries the
          // make too, e.g. "Fiat Ducato L1, L2, L3"). See fitsBadgeText for
          // the "one fitting size folds in here" case.
          text.textContent = fitsBadgeText(vehicle, this.singleFitSizeLabel);
        } else {
          text.textContent = `Not available for your ${shortVanLabel(vehicle)}`;
        }
      }

      // The muted second line (spec 2.3): present for 'no-vehicle' and
      // 'fits' (both have something true to say about fitment), absent for
      // 'not-fits' (its own single-line panel replaces this whole header
      // area's relevance) and 'unlisted' (no PCD data to promise anything
      // about yet).
      if (note) {
        if (state === 'no-vehicle') {
          note.textContent = this.noVehicleNote;
          note.removeAttribute('hidden');
        } else if (state === 'manual-review') {
          note.textContent = MANUAL_REVIEW_BADGE_NOTE;
          note.removeAttribute('hidden');
        } else if (state === 'fits') {
          note.textContent = this.fitNoteTemplate;
          note.removeAttribute('hidden');
        } else {
          note.setAttribute('hidden', '');
        }
      }

      // 'not-fits' gets its own, more prominent CTA in the collapsed
      // not-fits panel (see updateStateCollapse) - showing this smaller
      // header link too would just be a second "see wheels that fit"
      // (review finding 10). 'unlisted' never shows this link either -
      // spec 2.4's unlisted row is explicit ("Remove the 'See wheels that
      // fit' link"), matching section 0's own walk finding about this exact
      // link ("goes to all wheels, which is where the shopper already
      // came from") and BUY's acceptance check ("no 'See wheels that fit'
      // link"). A previous pass here kept it showing for 'unlisted' on the
      // reasoning that the buy box needs "its only way to the van's own
      // collection" - that reasoning is exactly what the spec overrides:
      // an unlisted shopper stays on the product and buys (the button is
      // enabled), they are not sent back to all wheels.
      const seeWheelsLink = this.ref('seeWheelsLink');
      if (seeWheelsLink) {
        seeWheelsLink.setAttribute('hidden', '');
      }
    }

    /**
     * Three mutually exclusive panels (spec 2.4): a confirmed non-fit
     * collapses to one line and a ghost "see wheels that fit"; no van at
     * all collapses to one "Pick your vehicle" link (Chris's rule, D3 - nothing
     * is bought before the van is known); everything else (fits, unlisted,
     * no tyre size on file, pre-order, sold-out) shows the full fitsPanel.
     * Sold-out is not a fourth panel - it swaps the button area WITHIN
     * fitsPanel (see below), since Finish/Size/Tyres/spare all stay
     * interactive so the shopper can pick another fitting finish.
     * @param {RogueVehicleSelection|null} vehicle
     * @param {FitmentState} state
     * @param {boolean} soldOut
     */
    updateStateCollapse(vehicle, state, soldOut) {
      const notFitsPanel = this.ref('notFitsPanel');
      const noVehicleCta = this.ref('noVehicleCta');
      const fitsPanel = this.ref('fitsPanel');
      if (!notFitsPanel || !noVehicleCta || !fitsPanel) return;

      // "Change vehicle" beside the fit line once any vehicle is set; the
      // product-level no-fit panel carries its own, larger one instead.
      const changeLink = this.ref('fitChangeVehicle');
      if (changeLink) changeLink.toggleAttribute('hidden', !vehicle || this.productFit === 'none');

      const noFitWholeProduct = state === 'not-fits' && vehicle && this.productFit === 'none';
      const heading = this.ref('notFitsHeading');
      const alternatives = this.ref('notFitsAlternatives');
      const notFitsChange = this.ref('notFitsChange');
      if (heading) heading.toggleAttribute('hidden', !noFitWholeProduct);
      if (notFitsChange) notFitsChange.toggleAttribute('hidden', !noFitWholeProduct);
      if (!noFitWholeProduct && alternatives) {
        alternatives.setAttribute('hidden', '');
        this._alternativesFor = null;
      }

      if (state === 'not-fits' && vehicle) {
        const message = this.ref('notFitsMessage');
        const title = this.modelTitle() || 'This wheel';
        const copy = noFitCopy(vehicle, title);
        if (noFitWholeProduct && heading) heading.textContent = copy.heading;
        // B12 (31 Oct 2026, P22): when the whole wheel does not fit, the
        // heading says so once; no second sentence under it.
        if (message) {
          message.textContent = noFitWholeProduct
            ? ''
            : `${title} in this finish and size has no confirmed fitment for your ${shortVanLabel(vehicle)}. Choose another fitting finish above or browse wheels for your vehicle.`;
          message.toggleAttribute('hidden', noFitWholeProduct);
        }
        // ... and the fit row's "Not available for your ..." eyebrow stays out of the way.
        this.ref('fitmentBadge')?.toggleAttribute('hidden', Boolean(noFitWholeProduct));
        const cta = this.ref('notFitsCta');
        if (cta && vehicle.collection_handle) {
          cta.setAttribute('href', `${collectionsRoot()}/${vehicle.collection_handle}`);
          // Spec 2.4: "See the wheels that fit your [van short]" - the
          // static Liquid fallback ("See wheels that fit your van") is only
          // what a shopper with no JavaScript sees.
          cta.textContent = noFitWholeProduct ? copy.cta : `See the wheels that fit your ${shortVanLabel(vehicle)}`;
        }
        if (noFitWholeProduct) this.loadAlternatives(vehicle);
        notFitsPanel.removeAttribute('hidden');
        noVehicleCta.setAttribute('hidden', '');
        fitsPanel.setAttribute('hidden', '');
      } else if (state === 'no-vehicle') {
        notFitsPanel.setAttribute('hidden', '');
        noVehicleCta.removeAttribute('hidden');
        fitsPanel.setAttribute('hidden', '');
        this.ref('fitmentBadge')?.removeAttribute('hidden');
      } else {
        notFitsPanel.setAttribute('hidden', '');
        noVehicleCta.setAttribute('hidden', '');
        fitsPanel.removeAttribute('hidden');
        this.ref('fitmentBadge')?.removeAttribute('hidden');
      }

      // Sold out, within fitsPanel: the real button/summary swap for a
      // ghost "Call to reserve" and one status line.
      const normalAddArea = this.ref('normalAddArea');
      const soldOutCta = this.ref('soldOutCta');
      const summary = this.ref('summaryLine');
      const note = this.ref('promises');
      if (normalAddArea) normalAddArea.toggleAttribute('hidden', soldOut);
      if (soldOutCta) soldOutCta.toggleAttribute('hidden', !soldOut);
      if (summary) summary.toggleAttribute('hidden', soldOut);
      if (note) note.toggleAttribute('hidden', soldOut);
    }

    /**
     * After the inline panel saves a vehicle: move focus (and with it the
     * screen reader and the visible viewport) to the answer - the "Fits your
     * ..." line, or the "Sorry, this wheel won't fit" heading.
     * @returns {boolean} whether something took focus
     */
    focusFitmentResult() {
      const target = this.productFit === 'none'
        ? this.ref('notFitsHeading')
        : this.fitmentState === 'not-fits'
          ? this.ref('notFitsMessage')
          : this.ref('fitmentBadge');
      if (!target || target.hasAttribute('hidden')) return false;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      // 25 Oct 2026: no browser focus scroll (it centred the fit line and lost
      // the Add button); one measured scroll instead, after layout settles.
      target.focus({ preventScroll: true });
      const reveal = () => this.revealFitResult(target);
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
      else reveal();
      return true;
    }

    /** The last thing the shopper needs after the answer: the Add button, the sold-out call, or the no-fit way on. */
    fitResultEnd() {
      const candidates = this.productFit === 'none' || this.fitmentState === 'not-fits'
        ? [this.ref('notFitsCta'), this.ref('notFitsPanel')]
        : [this.ref('addToCartButton'), this.ref('soldOutCta')];
      return candidates.find((el) => el && typeof el.getBoundingClientRect === 'function' && !el.closest?.('[hidden]') && el.getClientRects?.().length > 0) || null;
    }

    /**
     * Scrolls the page's real scroller (Horizon scrolls .page-wrapper at
     * 990px and up, the window below) just far enough - see fitResultScroll.
     * @param {HTMLElement} target - the fit line or no-fit heading
     */
    revealFitResult(target) {
      if (!target?.isConnected || typeof target.getBoundingClientRect !== 'function') return;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const header = document.querySelector('header.shopify-section-group-header-group');
      let topInset = 0;
      const headerStyle = header ? getComputedStyle(header) : null;
      if (header && ['sticky', 'fixed'].includes(headerStyle.position)) {
        // Where the header (and the van bar inside it) will sit once stuck:
        // at the top of the page the announcement bar still pushes it down.
        const box = header.getBoundingClientRect();
        const vanBar = header.querySelector('rogue-your-van-bar');
        const bottom = Math.max(box.bottom, vanBar?.getClientRects().length ? vanBar.getBoundingClientRect().bottom : 0);
        const notYetStuck = Math.max(0, box.top - (parseFloat(headerStyle.top) || 0));
        topInset = Math.min(Math.max(bottom - notYetStuck, 0), viewportHeight / 3);
      }
      const end = this.fitResultEnd();
      const { delta, bothVisible } = fitResultScroll({
        resultTop: target.getBoundingClientRect().top,
        endBottom: end ? end.getBoundingClientRect().bottom : NaN,
        viewportHeight,
        topInset,
      });
      if (delta !== 0) {
        const wrapper = document.querySelector('.page-wrapper');
        const wrapperScrolls = window.matchMedia?.('(min-width: 990px)').matches && wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1;
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        // 'instant', not 'auto': base.css sets scroll-behavior: smooth on both scrollers.
        const behavior = reduce || Math.abs(delta) > viewportHeight ? 'instant' : 'smooth';
        (wrapperScrolls ? wrapper : window).scrollBy({ top: delta, behavior });
      }
      // A phone can't show the answer and the Add button together: bring the
      // sticky Add bar up until the shopper reaches the real button.
      if (!bothVisible && this.ref('addToCartButton') === end) this.revealStickyBar();
    }

    /** Shows the 390 sticky Add bar while the real button is below the fold (see bindStickyBar). */
    revealStickyBar() {
      const stickyBar = this.ref('stickyBar');
      const narrow = window.matchMedia?.('(max-width: 749px)').matches;
      if (!stickyBar || !narrow) return;
      this.stickyRevealBelow = true;
      this.stickyRevealGuardUntil = Date.now() + 1500;
      this.updateStickyBar();
      stickyBar.removeAttribute('hidden');
    }

    /**
     * Fitting wheels for the no-fit panel, the same way the Popular row does
     * it (sections/rogue-popular-for-van.liquid): the vehicle's own
     * collection page, cards whose approved list names this exact vehicle,
     * never this wheel, up to four. A failed or empty result just leaves the
     * "See all wheels for your ..." button.
     * @param {RogueVehicleSelection} vehicle
     */
    async loadAlternatives(vehicle) {
      const wrap = this.ref('notFitsAlternatives');
      const row = this.ref('notFitsAlternativesRow');
      if (!wrap || !row || !vehicle?.slug || !vehicle.collection_handle) return;
      if (this._alternativesFor === vehicle.slug) return;
      const key = vehicle.slug;
      this._alternativesFor = key;
      wrap.removeAttribute('hidden');
      wrap.setAttribute('aria-busy', 'true');
      row.replaceChildren?.();
      try {
        const response = await fetch(`${collectionsRoot()}/${encodeURIComponent(vehicle.collection_handle)}`, { headers: { Accept: 'text/html' } });
        if (!response?.ok) throw new Error('Collection unavailable');
        const html = await response.text();
        if (this._alternativesFor !== key) return;
        const page = new DOMParser().parseFromString(html, 'text/html');
        const cards = [...page.querySelectorAll('.rogue-collection rogue-product-card')].map((card) => {
          let fits = [];
          try { fits = JSON.parse(card.getAttribute('data-rogue-fits-vehicles') || '[]'); } catch { fits = []; }
          return { card, fits, handle: productHandleFromUrl(card.querySelector('a[href]')?.getAttribute('href')) };
        });
        const picked = fittingAlternatives(cards, key, this.productHandle, 4);
        if (picked.length === 0) throw new Error('No fitting wheels');
        row.replaceChildren(...picked.map(({ card }) => document.importNode(card, true)));
      } catch {
        if (this._alternativesFor === key) wrap.setAttribute('hidden', '');
      } finally {
        if (this._alternativesFor === key) wrap.removeAttribute('aria-busy');
      }
    }

    /** Whether any finish and size of this wheel fits the van (productFitSummary), for the inline panel's first pick. */
    wheelFitsVehicle(vehicle) {
      return productFitSummary(this.variants, this.positions, vehicle) === 'fits';
    }

    /** Unit price and selected physical quantity drive PDP, button and sticky
     * totals. A resolved fitted tyre adds one tyre price per wheel. */
    updatePrices(variantMeta) {
      // Until a vehicle is chosen the price depends on a fitment the shopper
      // hasn't made yet: show the cheapest, labelled "From" (pricing audit 7).
      const noVehicle = (this.fitmentState || this.getAttribute('data-fitment-state') || 'no-vehicle') === 'no-vehicle';
      const fromCents = noVehicle ? fromPriceCents(this.variants) : null;
      const isFrom = fromCents !== null;
      if (isFrom) {
        variantMeta = { ...(variantMeta || {}), price: fromCents, price_formatted: formatGbpPence(fromCents) };
      }
      // B12 (31 Oct 2026): "From" shows once, on the set price; the unit
      // line is just the figure ("£155 a wheel").
      const unit = this.ref('priceUnit');
      if (unit && variantMeta?.price_formatted) unit.textContent = variantMeta.price_formatted;
      this.ref('priceFromLabel')?.toggleAttribute('hidden', !isFrom);

      const choice = null;
      const quantity = this.currentQuantity();
      const wheelsFormatted = quantity === null ? '' : formatGbpPence(setPriceCents(variantMeta?.price, quantity));

      const tyreLine = this.resolvedTyreLine();
      const combinedCents = tyreLine && quantity !== null
        ? combinedSetPriceCents({
            wheelUnitCents: variantMeta?.price,
            tyreUnitCents: tyreLine.variant?.price_cents,
            quantity,
            tyresEnabled: true,
          })
        : null;

      const { amount, note } = setAndTyresPriceLine({
        wheelsFormatted,
        combinedFormatted: combinedCents != null ? formatGbpPence(combinedCents) : null,
        tyresEnabled: Boolean(tyreLine),
        choice,
        setSize: this.setSize,
        quantity,
      });

      const amountEl = this.ref('priceSetAmount');
      if (amountEl) amountEl.textContent = amount;
      const noteEl = this.ref('priceSetNote');
      if (noteEl) noteEl.textContent = note + (amount && this.spareIncluded ? ' (includes 1 spare)' : '');

      this.packageAmount = amount;
      this.packageCents = quantity === null ? null : combinedCents ?? setPriceCents(variantMeta?.price, quantity);
      this.updateButtonTotal();

      this.updateQuantityState();
      this.updateStickyBar(undefined, variantMeta);
    }

    /* ------------------------- Extras in the buy box (31 Oct 2026) ------------------------- */

    /**
     * Skye, 31 Oct 2026: add-ons during the process, not after it. The
     * "Complete your set" section (assets/rogue-upsell.js, RogueCompleteSet)
     * owns the rows, their rules and the tick state, and registers here; the
     * buy box adds the ticked ones with the wheels and counts them in the
     * button total and the summary line.
     * @param {{ totalCents(): number, summary(): string, itemsForAdd(cartItems: Array<Record<string, unknown>>): { items: Array<{ id: string|number, quantity: number }>, error: string }, reset(): void } | null} provider
     */
    setExtrasProvider(provider) {
      this.extras = provider || null;
      this.extrasChanged();
    }

    /** The provider's ticks or quantities changed. */
    extrasChanged() {
      this.updateButtonTotal();
      this.updateSummaryLine();
      this.syncStickyBar();
    }

    /** The package price, plus any ticked extras once the package has a real total. */
    updateButtonTotal() {
      const buttonPrice = this.ref('addToCartPrice');
      if (!buttonPrice) return;
      const extraCents = this.extras?.totalCents?.() || 0;
      if (extraCents > 0 && typeof this.packageCents === 'number') buttonPrice.textContent = formatGbpPence(this.packageCents + extraCents);
      // Before the first updatePrices the server-rendered price stays.
      else if (this.packageAmount !== undefined) buttonPrice.textContent = this.packageAmount;
    }

    /**
     * The plain model name, whatever the H1 currently says: the basket
     * payload, the summary line and the no-fit copy want "CC1", not
     * "16" CC1 SATIN BLACK 5000KG LOAD RATING".
     * @returns {string}
     */
    modelTitle() {
      const el = this.ref('productTitle');
      return el?.getAttribute?.('data-model') || el?.textContent?.trim() || '';
    }

    /**
     * Keeps a wheel's H1 live: size, model, finish and set load rating, from
     * the chosen size and finish and the resolved variant's load rating. Only
     * the H1's text changes: the page <title> and SEO markup stay server-side.
     * @param {RogueVariantMeta|null} variantMeta
     */
    updateTitle(variantMeta) {
      const el = this.ref('productTitle');
      if (!el || el.getAttribute?.('data-dynamic-title') !== 'true') return;
      const main = this.ref('titleMain');
      const finish = this.ref('titleFinish');
      const load = this.ref('titleLoad');
      if (!main || !finish || !load) return;
      const sizeIndex = this.positions?.size;
      const sizes = sizeIndex != null && sizeIndex !== -1
        ? [...new Set((this.variants || []).map((v) => v?.options?.[sizeIndex]).filter(Boolean))]
        : [];
      const parts = wheelTitleParts({
        model: this.modelTitle(),
        size: this.selectedSize,
        sizes,
        finish: this.selectedColour || (variantMeta ? variantOptionValue(variantMeta, this.positions, 'colour') : null),
        loadRaw: variantMeta?.load_rating
      });
      main.textContent = parts.main;
      finish.textContent = parts.finish;
      load.textContent = parts.load;
    }

    updateStockLine(variantMeta) {
      const stock = this.ref('stockText');
      if (!stock) return;
      const { isPreorder, dueIn } = preorderBadgeData(variantMeta);
      if (variantMeta?.available === false) stock.textContent = 'Out of stock';
      else if (dueIn) stock.textContent = `Due ${dueIn}`;
      else if (isPreorder) stock.textContent = 'Pre-order';
      else stock.textContent = 'In stock';
    }

    /**
     * The Specification disclosure now lives outside this element (spec
     * 2.5: below the fold, full width, beside About this wheel - see
     * sections/rogue-product.liquid) - a page-level lookup rather than
     * `this.ref`, safe because there is exactly one buy box per product
     * page. "Fitment for your van" reads the resolved variant's own stud
     * pattern (see pcdLabelsForVariant - the same PCD reasoning
     * resolveVariant already used to match it) for a real, matched van;
     * for an unlisted van (S3.2a, nothing resolved yet), the same honest
     * copy the fit line uses; otherwise the server-rendered placeholder.
     * @param {RogueVariantMeta|null} variantMeta
     * @param {FitmentState} state
     */
    updateSpec(variantMeta, state) {
      this.updateTitle(variantMeta);
      const loadRating = document.querySelector('[ref="specLoadRating"]');
      if (loadRating) {
        const formatted = formatLoadRating(variantMeta?.load_rating);
        loadRating.textContent = formatted !== null ? formatted : this.loadRatingPlaceholder;
      }

      const fitmentCell = document.querySelector('[ref="specFitment"]');
      if (!fitmentCell) return;

      if (state === 'manual-review') {
        fitmentCell.textContent = 'Specification to be confirmed before dispatch';
        return;
      }
      if (state === 'unlisted') {
        const template = this.unlistedTextTemplate;
        fitmentCell.textContent = template.includes('{van}')
          ? template.split('{van}').join(typedVehicleText(this.vehicle))
          : template;
        return;
      }

      if (state === 'fits') {
        const currentVariant = this.variants.find((v) => String(v.id) === this.currentVariantId);
        const pcdLabels = currentVariant ? pcdLabelsForVariant(currentVariant, this.positions) : [];
        const fitmentValue = currentVariant ? variantOptionValue(currentVariant, this.positions, 'fitment') : null;
        const label = resolvedFitmentLabel(fitmentValue, pcdLabels);
        if (label !== '') {
          fitmentCell.textContent = label;
          return;
        }
      }

      fitmentCell.textContent = this.specFitmentPlaceholder;
    }

    updateAddToCart(state, soldOut) {
      const addToCartComponent = this.querySelector('add-to-cart-component');
      if (!addToCartComponent) return;

      const shouldDisable = this._adding || (!this.addNeedsReview && (state === 'no-vehicle' || state === 'not-fits' || soldOut));
      if (shouldDisable && typeof addToCartComponent.disable === 'function') addToCartComponent.disable();
      else if (!shouldDisable && typeof addToCartComponent.enable === 'function') addToCartComponent.enable();
      if (this.addNeedsReview && this.ref('addToCartLabel')) this.ref('addToCartLabel').textContent = 'View basket';
    }

    updateStudPatternSection(vehicle) {
      const section = this.ref('studPatternSection');
      if (!section) return;

      // Review 2, finding 11: render the question only when there is a real
      // choice - see hasStudPatternChoice. Recorded on the instance so
      // updateCartAttributesFields (which may run again later, e.g. on an
      // order-field edit, without this method running again first) knows
      // whether a visible "not sure" radio even exists to read.
      this.studPatternHasChoice = hasStudPatternChoice(vehicle);

      if (!this.studPatternHasChoice) {
        section.setAttribute('hidden', '');
        return;
      }

      section.removeAttribute('hidden');
      const list = this.ref('studPatternOptions');
      if (list && list.childElementCount === 0) {
        list.innerHTML =
          buildPcdRadioOptions(vehicle.pcd_options)
            .map(
              (option) => `
              <label class="rogue-buy__stud-option">
                <input type="radio" name="rogue-stud-pattern" value="${escapeHtml(option.value)}">
                ${escapeHtml(option.label)}
              </label>
            `
            )
            .join('') +
          `
            <label class="rogue-buy__stud-option">
              <input type="radio" name="rogue-stud-pattern" data-rogue-not-sure value="">
              Not sure? We'll check it for you
            </label>
          `;
      }
    }

    /** Read the same fitment decision for the native and wheel-plus-tyre add paths. */
    verificationCartData() {
      // Review 2, finding 11: with no real stud-pattern choice on offer (see
      // hasStudPatternChoice/updateStudPatternSection), there is no radio
      // group to read a selection from - the order still lands on the hold
      // path exactly as if "not sure" had been picked by hand, since Rogue
      // still checks fitment before anything ships either way.
      const selected = this.querySelector('input[name="rogue-stud-pattern"]:checked');
      const notSure = this.studPatternHasChoice
        ? Boolean(selected?.hasAttribute('data-rogue-not-sure'))
        : needsStudPatternVerification(this.vehicle);
      return buildVerificationCartData({
        vehicle: this.vehicle,
        studPattern: this.studPatternHasChoice && !notSure ? selected?.value ?? null : null,
        notSure,
      });
    }

    /** Write the buy panel's cart attributes and persistent wheel properties. */
    updateCartAttributesFields() {
      const verification = this.verificationCartData();

      // Registration/upplating/current tyre size moved to the basket's
      // "Your details" panel (spec 2.8, RogueCartDetails in
      // sections/main-cart.liquid). This page writes nothing for Upplating
      // any more (L2, 25 Sep review, finding P10) - it used to write "No"
      // the instant a wheel was added, before the shopper had ever seen the
      // question; buildOrderFieldsCartData now leaves the attribute out
      // entirely unless it's told an actual answer, so it stays unset until
      // the shopper answers in the basket panel. "Tyre letter colour" IS
      // still written here (L2: Paul's Google Sheet feed reads it from the
      // cart attributes - App\Support\Sheets\OrderSheetRow - and there is no
      // per-line reader for it today), but only for a real, resolved tyre
      // line.
      const tyreLine = this.resolvedTyreLine();
      const orderFields = buildOrderFieldsCartData({
        registration: '',
        upplating: '',
        currentTyreSize: '',
        // M1 (review P5): never the raw this.letterColour, which could be a
        // "White" left over from an earlier line that doesn't offer it.
        letterColour: this.effectiveLetterColour(),
        tyresEnabled: Boolean(tyreLine),
      });

      const attributes = { ...verification.attributes, ...orderFields, ...vehicleCartAttributes(this.vehicle) };

      // Choosing "Wheels and tyres" with no size on file IS the intent - no
      // separate checkbox any more (spec 2.1: "the checkbox removed").
      // H4 (25 Sep review, finding P4): this.availableTyreLines is already
      // "" whenever the size isn't known AND whenever it's known but
      // nothing is on offer at it (see updateTyrePanel), so reading its
      // length here covers both cases with one check.
      const tyresWanted = tyresWantedCartProperty({
        tyresEnabled: this.tyresEnabled,
        sizeKnown: this.availableTyreLines.length > 0,
        wanted: true,
      });

      // Review finding (Major): the wheel line's own record of which van it
      // was added for - buildAddToBasketPayload writes the same property
      // for the JS dual-line add; this is what a native/no-JS single-item
      // submit carries instead (see snippets/cart-products-row.liquid for
      // the mismatch note that reads it back).
      const vanProperty = this.vehicle && this.vehicle.display_name ? { Van: this.vehicle.display_name } : {};
      // The same "Due" line note the JS add writes (buildAddToBasketPayload).
      const dueText = String(this.getCurrentVariantMeta()?.due_in || '').trim();

      this.writeHiddenFields('verificationFields', attributes, {
        ...vanProperty,
        ...(dueText !== '' ? { Due: dueText } : {}),
        ...verification.properties,
        ...tyresWanted,
      });
    }

    /**
     * The pre-order chip beside the fit line, the button's own label (spec
     * 2.4: "Pre-order £X" instead of "Add to basket £X" - the price still
     * comes from the separate addToCartPrice span updatePrices fills), and
     * the reassurance line underneath ("You pay today and we ship when the
     * stock arrives in [Month]."). All three read the one preorderBadgeData
     * result, so they can never disagree about whether this variant is a
     * pre-order.
     */
    updatePreorderBadge(variantMeta) {
      const badge = this.ref('preorderBadge');
      const { isPreorder, dueMonth } = preorderBadgeData(variantMeta);
      if (badge) {
        if (isPreorder) {
          badge.textContent = dueMonth ? `Pre-order, due ${dueMonth}` : 'Pre-order';
          badge.removeAttribute('hidden');
        } else {
          badge.setAttribute('hidden', '');
        }
      }

      const label = this.ref('addToCartLabel');
      // 25 Oct 2026 (Daniel): a manual-review vehicle buys like any other; the
      // reassurance line under the button (updateFitAssurance) explains the check.
      if (label) label.textContent = isPreorder ? 'Pre-order' : 'Add to basket';

      const note = this.ref('preorderNote');
      if (note) {
        note.textContent = dueMonth
          ? `You pay today and we ship when the stock arrives in ${dueMonth}.`
          : 'You pay today and we ship once the stock arrives.';
        note.toggleAttribute('hidden', !isPreorder);
      }
    }

    /**
     * 25 Oct 2026 (Daniel): "We still want people to buy, but there will be a
     * verification process after that." A manual-review vehicle gets the
     * normal Add to basket button with one confident line under it. Words
     * only; the order still carries `_fitment_verification=requested`.
     */
    updateFitAssurance(vehicle, state, soldOut) {
      const line = this.ref('fitAssurance');
      if (!line) return;
      const show = state === 'manual-review' && !soldOut;
      const text = this.ref('fitAssuranceText') || line;
      text.textContent = show ? manualReviewAssuranceText(vehicle) : '';
      line.toggleAttribute('hidden', !show);
    }

    /* ------------------------------ tyres ------------------------------ */

    /** The "Wheels only / Wheels and tyres" two-segment choice (spec 2.1/2.3), replacing the old toggle. */
    bindTyreChoice() {
      const group = this.ref('tyreChoice');
      group?.addEventListener('click', (event) => {
        const button = /** @type {HTMLElement} */ (event.target)?.closest('[data-rogue-tyre-choice]');
        if (!button) return;
        this.hideAddConfirmation();
        this.tyresEnabled = button.getAttribute('data-rogue-tyre-choice') === 'wheels-and-tyres';
        for (const b of group.querySelectorAll('[data-rogue-tyre-choice]')) {
          b.setAttribute('aria-pressed', String(b === button));
        }
        const panel = this.ref('tyrePanel');
        panel?.setAttribute('data-enabled', String(this.tyresEnabled));
        panel?.toggleAttribute('hidden', !this.tyresEnabled);
        // H1 (25 Sep review, finding P1 - Daniel's "the prices weren't
        // changing" reproduction): choosing "Wheels and tyres" must price a
        // set AT ONCE, not wait for a second click on a tyre row.
        // updateTyrePanel doesn't run again here (the rim size hasn't
        // changed, so this.availableTyreLines is already correct for it) -
        // see applyDefaultTyreSelection for the order (the shopper's own
        // last pick, then Paul's default, then popularity, then cheapest).
        if (this.applyDefaultTyreSelection()) this.renderTyreRows();
        this.updateTyreFields();
        this.updateCartAttributesFields();
        this.updateSpareLabel();
        this.updatePrices(this.getCurrentVariantMeta());
        this.updateSummaryLine();
      });

      // Seed the buttons' visual state from the default set by
      // snippets/rogue-fitment-gate.liquid's default_tyre_choice setting.
      for (const b of group?.querySelectorAll('[data-rogue-tyre-choice]') || []) {
        const isOn = (b.getAttribute('data-rogue-tyre-choice') === 'wheels-and-tyres') === this.tyresEnabled;
        b.setAttribute('aria-pressed', String(isOn));
      }
      const panel = this.ref('tyrePanel');
      panel?.setAttribute('data-enabled', String(this.tyresEnabled));
      panel?.toggleAttribute('hidden', !this.tyresEnabled);

      // Lettering (Black/White), inside whichever row is selected; and the
      // keyboard path for row selection itself (arrow keys between
      // same-named radios change the checked one without necessarily
      // dispatching a click) - see selectTyreLine.
      this.ref('tyreRows')?.addEventListener('change', (event) => {
        const target = /** @type {HTMLInputElement} */ (event.target);
        const name = target?.getAttribute('name');
        // Per-row group name (see renderTyreRows) - "rogue-tyre-letter-"
        // plus the row's own handle, so this still catches it regardless
        // of which row is currently selected.
        if (typeof name === 'string' && name.startsWith('rogue-tyre-letter')) {
          this.letterColour = target.value === 'White' ? 'White' : 'Black';
          this.updateCartAttributesFields();
        } else if (name === 'rogue-tyre-line') {
          this.selectTyreLine(target.value);
        }
      });
    }

    /**
     * Selects a tyre row by product handle - the one place both the click
     * delegation (handlePickerClick) and the keyboard 'change' path
     * (bindTyreChoice) land, so pointer and keyboard selection can never
     * disagree about what just happened.
     * @param {string} handle
     */
    selectTyreLine(handle) {
      if (handle === this.selectedTyreHandle) return;
      this.hideAddConfirmation();
      this.selectedTyreHandle = handle;
      // H2 (review P2): remembered separately from selectedTyreHandle, and
      // never cleared elsewhere - see updateTyrePanel/applyDefaultTyreSelection.
      this.lastTyreHandle = handle;
      // N2 (26 Sep review round 3): cached alongside the handle - see
      // resolveTyreSelectionForSize, which needs the title to name this
      // pick in the "isn't made in this size" note once a later size
      // change drops it from availableTyreLines entirely.
      const line = (this.availableTyreLines || []).find((l) => l.handle === handle);
      if (line?.title) this.lastTyreTitle = line.title;
      // A pick the shopper just made themselves resolves any earlier swap
      // note outright - there's nothing left to explain.
      this.updateTyreSwapNote('');
      this.renderTyreRows();
      this.updateTyreFields();
      this.updateSpareLabel();
      this.updatePrices(this.getCurrentVariantMeta());
      this.updateSummaryLine();
      this.updateCartAttributesFields();
    }

    /**
     * Review 2, finding 12: a sticky bottom bar (390 and under only, see
     * assets/rogue-buy.css) that appears once the real add-to-basket button
     * has scrolled off, so the shopper is never more than one tap from
     * buying without hunting back up the page for it. With no van stored it
     * shows "Pick your vehicle" instead, always (spec 2.4's "390 sticky bar"
     * column) - its own link, not a click-through to the real button, since
     * there is no real button to click yet. Its price/label text is a plain
     * copy of the real button's own (kept in sync by updateStickyBar,
     * called from render()/updatePrices() - the two places that ever change
     * those texts).
     */
    bindStickyBar() {
      const stickyBar = this.ref('stickyBar');
      const stickyButton = /** @type {HTMLButtonElement|null} */ (this.ref('stickyBarButton'));
      const actions = this.ref('normalAddArea');
      if (!stickyBar || !stickyButton || !actions || typeof IntersectionObserver === 'undefined') return;

      stickyButton.addEventListener('click', () => {
        const realButton = /** @type {HTMLButtonElement|null} */ (this.ref('addToCartButton'));
        if (realButton && !realButton.disabled) realButton.click();
      });

      const narrowQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          const isNarrow = !narrowQuery || narrowQuery.matches;
          // No van stored: there is no real button to scroll past at all
          // (fitsPanel is hidden), so the bar is eligible to show as soon as
          // it's narrow enough - see updateStickyBar for the "Pick your
          // van" swap.
          const state = this.getAttribute('data-fitment-state') || 'no-vehicle';
          if (state === 'no-vehicle') {
            stickyBar.toggleAttribute('hidden', !isNarrow);
            return;
          }
          // B2 (31 Oct 2026, Skye): once the van fits, the bar shows whenever
          // the real button is off screen, above or below. The defaults are
          // valid, so a tap adds straight away; the bar hides as soon as the
          // real button is in view. A confirmed non-fit never shows it.
          if (entry.isIntersecting && Date.now() > (this.stickyRevealGuardUntil || 0)) this.stickyRevealBelow = false;
          const offScreen = !entry.isIntersecting && state !== 'not-fits';
          stickyBar.toggleAttribute('hidden', !(isNarrow && offScreen));
        },
        { threshold: 0 }
      );
      observer.observe(actions);

      narrowQuery?.addEventListener?.('change', () => {
        if (!narrowQuery.matches) stickyBar.setAttribute('hidden', '');
      });

      this.updateStickyBar();
    }

    /** Review SHOP-010: re-copy the main button's label and state, when this buy box has a sticky bar. */
    syncStickyBar() {
      if (this.ref('stickyBar')) this.updateStickyBar();
    }

    /**
     * @param {FitmentState} [state] - defaults to the element's own current data-fitment-state
     * @param {RogueVariantMeta} [variantMeta] - defaults to the currently resolved variant
     * @param {boolean} [soldOut]
     */
    updateStickyBar(state, variantMeta, soldOut) {
      const priceEl = this.ref('stickyBarPrice');
      const labelEl = this.ref('stickyBarLabel');
      const buttonEl = /** @type {HTMLButtonElement|null} */ (this.ref('stickyBarButton'));
      const linkEl = this.ref('stickyBarLink');
      const resolvedState = state || this.getAttribute('data-fitment-state') || 'no-vehicle';

      if (linkEl && buttonEl) {
        const isNoVehicle = resolvedState === 'no-vehicle';
        linkEl.toggleAttribute('hidden', !isNoVehicle);
        buttonEl.toggleAttribute('hidden', isNoVehicle);
      }
      if (resolvedState === 'no-vehicle') return;

      if (priceEl) priceEl.textContent = this.textOf('addToCartPrice') || this.textOf('priceSetAmount');
      if (labelEl) labelEl.textContent = soldOut ? 'Call to reserve' : this.textOf('addToCartLabel') || 'Add to basket';

      if (buttonEl) {
        const resolvedVariantMeta = variantMeta || this.getCurrentVariantMeta();
        buttonEl.disabled = resolvedState === 'not-fits' || soldOut || resolvedVariantMeta?.available === false || Boolean(this.ref('addToCartButton')?.disabled);
      }
    }

    /**
     * Review 2, finding 12: one listener, on `this`, for the same
     * CartLinesUpdateEvent(action: 'add') both add paths already dispatch -
     * Horizon's own native single-item submit (assets/product-form.js,
     * dispatched on <product-form-component>, a child of this element) and
     * this file's own wheel-and-tyre add (submitWheelAndTyre/
     * announceCartUpdate, dispatched on `this` directly - see that method).
     * Both bubble through `this`, so there is nothing extra to wire up per
     * path. `event.lines.length > 1` is what tells the two paths apart
     * (a tyre line alongside the wheel), without either path having to say
     * so itself.
     */
    bindAddConfirmation() {
      import('@shopify/events')
        .then(({ StandardEvents }) => {
          this.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
            if (/** @type {any} */ (event).action !== 'add') return;
            // T2 (31 Oct 2026): one confirmation. When the extras provider
            // (rogue-upsell.js) will open its post-add sheet, the sheet is
            // the confirmation and carries the real total, extras included.
            if (this.extras?.confirmsAdd?.()) return;
            const lines = Array.isArray(/** @type {any} */ (event).lines) ? /** @type {any} */ (event).lines : [];
            const quantity = lines[0]?.quantity;
            /** @type {any} */ (event).promise?.then(() => this.showAddConfirmation({ quantity })).catch(() => {});
          });
        })
        .catch((error) => {
          console.warn('[rogue-fitment-gate] add confirmation not wired up:', error);
        });
    }

    /**
     * "Added to your basket." plus the same summary line the button already
     * carries (spec 2.3's "After add" row) - replaces the button in place
     * for 8 seconds, or until the shopper changes a choice (hideAddConfirmation).
     * @param {{ quantity: number|null|undefined }} params
     */
    showAddConfirmation({ quantity }) {
      const container = this.ref('addToCartConfirmation');
      const textEl = this.ref('addToCartConfirmationText');
      const normalAddArea = this.ref('normalAddArea');
      if (!container || !textEl) return;

      const text = this.buildSummaryLine(quantity);
      if (text === '') return;

      // The set's real total, tyres included (25 Oct 2026, go-live P1-9).
      textEl.textContent = summaryWithSetTotal(text, this.lastAddTotalText || this.textOf('priceSetAmount'));
      container.removeAttribute('hidden');
      normalAddArea?.setAttribute('hidden', '');
      // THEME_A finding (24 Sep 2026, Minor): summaryLine is a sibling of
      // normalAddArea, not inside it, so hiding normalAddArea alone left
      // it showing - the same summary the confirmation panel's own
      // addToCartConfirmationText now carries too, so it printed twice.
      this.ref('summaryLine')?.setAttribute('hidden', '');
      if (this.confirmationTimer) clearTimeout(this.confirmationTimer);
      this.confirmationTimer = setTimeout(() => this.hideAddConfirmation(), 8000);
    }

    hideAddConfirmation() {
      if (this.confirmationTimer) {
        clearTimeout(this.confirmationTimer);
        this.confirmationTimer = null;
      }
      if (this.refusedAddShown) {
        // "Only 2 left in this size and finish" (showRefusedAdd) is about the
        // choice the shopper has just changed.
        this.refusedAddShown = false;
        this.ref('addToCartTextError')?.classList.add('hidden');
      }
      const container = this.ref('addToCartConfirmation');
      if (!container || container.hasAttribute('hidden')) return;
      container.setAttribute('hidden', '');
      this.ref('normalAddArea')?.removeAttribute('hidden');
      this.ref('summaryLine')?.removeAttribute('hidden');
    }

    /**
     * The tyre line currently selected among the radio rows, resolved
     * against `availableTyreLines` by product handle - null whenever tyres
     * are off, nothing is chosen, or the chosen handle no longer has a
     * variant in the current rim size (e.g. the shopper changed Size after
     * choosing a line) - see updateTyrePanel/renderTyreRows, which drop a
     * selection that stops being on offer.
     * @returns {{ handle: string, title: string, brand: string, type: string, variant: object }|null}
     */
    resolvedTyreLine() {
      if (!this.tyresEnabled || !this.selectedTyreHandle) return null;
      return (this.availableTyreLines || []).find((line) => line.handle === this.selectedTyreHandle) || null;
    }

    /**
     * The whole tyres panel, driven by the resolved rim size (this.selectedSize)
     * and the stored van (review Blocker 2): the size line always shows the
     * van's real tyre size or the honest no-size sentence, never a
     * placeholder stored as if it were a size. When {@link tyreSizeIsKnown}
     * is false (no vehicle, the unlisted sentinel, or a real van with
     * nothing mapped for this rim), the rows/show-all are hidden behind
     * `data-size-known="false"` (assets/rogue-buy.css) and one of two lines
     * shows instead: a "Switch to N inch" offer when THIS wheel also comes in
     * a size the van's tyre size IS on file for (see
     * {@link tyreSizeSwitchTarget} - Daniel's reproduction, 25 Sep 2026: the
     * Transporter T5-T6.1 opens Rocky at 16 inch with no tyre size on file,
     * but Rocky also comes in 17, which has one), or, only once no size this
     * wheel offers has one either, the honest no-size sentence
     * ({@link tyreNoSizeMessage}). Nothing chargeable is ever offered for a
     * size we don't actually know.
     * @param {RogueVehicleSelection|null} vehicle
     */
    updateTyrePanel(vehicle) {
      const panel = this.ref('tyrePanel');
      const sizeLineText = this.ref('tyreSize');
      const noSizeText = this.ref('tyreNoSizeText');
      const switchPanel = this.ref('tyreSwitchPanel');
      const switchMessage = this.ref('tyreSwitchMessage');
      const switchButton = this.ref('tyreSwitchSize');
      const sizeLine = this.ref('tyreSizeLine');
      const sizeCheckNote = this.ref('tyreSizeCheckNote');
      const rimSize = this.selectedSize;
      const sizeKnown = tyreSizeIsKnown(vehicle, rimSize);
      const switchTarget = sizeKnown ? null : tyreSizeSwitchTarget(this.fittingSizes || [], rimSize, vehicle);

      // H4 (25 Sep review, finding P4 - the Fiat Ducato at 15 inch): a rim
      // size the van's tyre_sizes_json DOES carry a real size for, but with
      // no tyre product that has a variant in it, is treated exactly like
      // "size not known" throughout the rest of this method -
      // this.availableTyreLines ends up empty either way, so every caller
      // that reads its length (resolvedTyreLine, updateCartAttributesFields's
      // tyresWantedCartProperty call) never has to ask "known, or known-
      // with-stock?" separately. Only the sentence differs, below.
      const resolvedSize = sizeKnown ? tyreSizeForRim(vehicle?.tyre_sizes_json, rimSize) : '';
      this.availableTyreLines = sizeKnown ? sortedTyreLines(availableTyreLines(this.tyreOptions, vehicle, rimSize, this.tyreSizeRules, this.whiteLetterRules)) : [];
      const hasTyresToSell = this.availableTyreLines.length > 0;
      this.ref('tyreChargeNote')?.toggleAttribute('hidden', hasTyresToSell);

      if (panel) panel.setAttribute('data-size-known', String(hasTyresToSell));
      sizeLine?.toggleAttribute('hidden', !hasTyresToSell);
      // M3 (25 Sep review, finding P7): one quiet line under the resolved
      // size, on every van (measured/provisional alike) - a reminder that
      // Rogue checks it again before fitting, since some sizes on file
      // today rest on order-history inference rather than a spec sheet.
      sizeCheckNote?.toggleAttribute('hidden', !hasTyresToSell);

      if (switchPanel) switchPanel.toggleAttribute('hidden', !switchTarget);
      if (switchTarget) {
        if (switchMessage) switchMessage.textContent = tyreSwitchSizeMessage(switchTarget, rimSize);
        if (switchButton) {
          switchButton.textContent = `Switch to ${switchTarget}`;
          switchButton.setAttribute('data-rogue-switch-size', switchTarget);
        }
      }

      if (noSizeText) {
        noSizeText.textContent =
          sizeKnown && !hasTyresToSell ? tyreSizeHasNoStockMessage(resolvedSize) : tyreNoSizeMessage(vehicle, this.tyreNoSizeText);
        noSizeText.toggleAttribute('hidden', hasTyresToSell || Boolean(switchTarget));
      }

      if (!hasTyresToSell) {
        this.selectedTyreHandle = null;
        this.updateTyreSwapNote('');
        this.renderTyreRows();
        return;
      }

      if (sizeLineText) sizeLineText.textContent = tyreSizedForText(vehicle);

      // H1/H2 (25 Sep review, findings P1/P2)/N2 (26 Sep review round 3):
      // resolve which line should end up selected AT THIS SIZE - the
      // shopper's own last pick restored the moment it's back on offer, or
      // Paul's default/popularity/cheapest with an honest note when it
      // genuinely isn't made in this size - see
      // resolveTyreSelectionForSize's own doc comment for the order. Only
      // meaningful with tyres actually enabled; otherwise just drop a
      // selection the panel no longer offers, same as before.
      if (this.tyresEnabled) {
        const resolved = resolveTyreSelectionForSize({
          lastHandle: this.lastTyreHandle,
          lastTitle: this.lastTyreTitle,
          size: resolvedSize,
          lines: this.availableTyreLines,
          preferredHandle: this.defaultTyreLineHandle,
        });
        this.selectedTyreHandle = resolved.handle;
        this.updateTyreSwapNote(resolved.note);
      } else if (!this.availableTyreLines.some((line) => line.handle === this.selectedTyreHandle)) {
        this.selectedTyreHandle = null;
      }
      this.renderTyreRows();
    }

    /**
     * N2 (26 Sep review round 3): the one line under the tyre rows naming a
     * swap resolveTyreSelectionForSize just made ("Michelin Agilis
     * CrossClimate isn't made in 235/65R17, so we've chosen General AT3 All
     * Terrain."). Cleared the moment the shopper picks a row themselves
     * (see selectTyreLine) - once they've acted, there's nothing left to
     * explain.
     * @param {string} text - "" hides the line
     */
    updateTyreSwapNote(text) {
      const el = this.ref('tyreSwapNote');
      if (!el) return;
      const trimmed = typeof text === 'string' ? text.trim() : '';
      el.textContent = trimmed;
      el.toggleAttribute('hidden', trimmed === '');
    }

    /**
     * H1/H2 (25 Sep review, findings P1/P2): with tyres enabled, a real size
     * resolved, and real tyre lines on offer, SOMETHING must end up
     * selected - the add button's whole job is "add tyres", and it must
     * never be able to post wheels alone with no warning (see
     * handlePickerClick's own backstop for when this still somehow doesn't
     * apply). No-op once something is already selected, with tyres off, or
     * with nothing on offer. Order:
     *
     *  1. The shopper's own last pick (`this.lastTyreHandle`, set by
     *     selectTyreLine and never cleared elsewhere - H2), when it's still
     *     on offer at this size. A size change that hides it for a moment
     *     (e.g. "Switch to 17 inch" and back) must never lose it for good.
     *  2. Failing that, {@link defaultTyreHandle} - Paul's own pick, then
     *     popularity, then cheapest.
     *
     * Called from updateTyrePanel (every render) AND bindTyreChoice's click
     * handler (switching TO "Wheels and tyres" doesn't itself call
     * updateTyrePanel, since the rim size hasn't changed).
     * @returns {boolean} whether a selection was just made (the caller then knows to re-render the rows)
     */
    applyDefaultTyreSelection() {
      if (!this.tyresEnabled || this.selectedTyreHandle || !this.availableTyreLines.length) return false;
      if (this.lastTyreHandle && this.availableTyreLines.some((line) => line.handle === this.lastTyreHandle)) {
        this.selectedTyreHandle = this.lastTyreHandle;
        return true;
      }
      const handle = defaultTyreHandle(this.availableTyreLines, this.defaultTyreLineHandle);
      if (!handle) return false;
      this.selectedTyreHandle = handle;
      return true;
    }

    /**
     * M1 (25 Sep review, finding P5): the Lettering choice the shopper made
     * on an earlier, white-letter-capable line must never carry over onto a
     * line that doesn't offer it - `this.letterColour` is one piece of
     * state shared across every row (see renderTyreRows), so reading it raw
     * would let "White" leak onto a tyre that only comes in Black. The one
     * place every caller that writes a Lettering-derived cart field
     * (updateCartAttributesFields, submitWheelAndTyre) should read from,
     * instead of `this.letterColour` directly.
     * @returns {'Black'|'White'}
     */
    effectiveLetterColour() {
      const tyreLine = this.resolvedTyreLine();
      if (!tyreLine || !tyreLineOffersWhiteLetter(tyreLine)) return 'Black';
      return this.letterColour === 'White' ? 'White' : 'Black';
    }

    /**
     * Renders up to five tyre rows (spec 2.3: "five shown, then 'Show all'"),
     * the real top-ranked line always first and labelled "Most popular"
     * (N7, 26 Sep review round 3 - see mostPopularTyreHandle/visibleTyreRows),
     * whether or not it's ticked; everyone else cheapest first (already
     * sorted by updateTyrePanel), with the ticked line swapped into the
     * collapsed five if it would otherwise fall outside it; each a radio row
     * with a set price ("£400 for four", "£500 for five" with the spare) and
     * a type chip; the lettering choice (Black/White) shows only inside
     * whichever row is selected.
     */
    renderTyreRows() {
      const container = this.ref('tyreRows');
      const showAll = this.ref('tyreShowAll');
      if (!container) return;

      const quantity = this.currentQuantity() || 0;
      const visible = visibleTyreRows(this.availableTyreLines, this.selectedTyreHandle, this.tyresExpanded);
      const mostPopularHandle = mostPopularTyreHandle(this.availableTyreLines);

      // Review finding (Minor): every row's lettering radios used the SAME
      // `name="rogue-tyre-letter"`, even though up to five rows render at
      // once (only one visible via CSS/`hidden`, the rest still in the
      // DOM). Same-named radios form ONE group regardless of which row
      // they're nested in, so the browser's own "only one checked radio
      // per group" rule silently unchecked every row's Black radio except
      // whichever rendered last in this innerHTML string - measured live,
      // the chosen row's own Black read unchecked (both Black and White
      // false) even though /cart.js correctly wrote Black (letterColour is
      // read from JS state, not the DOM, on submit - only the on-screen
      // state was wrong). Scoping the name by tyre handle below gives each
      // row its own independent group.
      container.innerHTML = visible
        .map((line) => {
          const selected = line.handle === this.selectedTyreHandle;
          const priceLine = tyreRowPriceLine(line.variant?.price_cents, line.variant?.price_formatted, quantity);
          const typeChip = line.type ? `<span class="rogue-buy__tyre-row-type">${escapeHtml(line.type)}</span>` : '';
          // N7 (26 Sep review round 3): "Most popular" only on the one line
          // mostPopularTyreHandle names - the real lowest-ranked line for
          // this rim (per-rim or global - see
          // availableTyreLines/popularityRankForRim) - never on whichever
          // row the shopper happens to have ticked. `null` (nothing ranked
          // at all) labels nothing: a defaultTyreHandle fallback with no
          // rank to draw on is just the cheapest survivor, not genuinely
          // "most popular".
          const mostPopularBadge =
            mostPopularHandle && line.handle === mostPopularHandle
              ? '<span class="rogue-buy__tyre-row-badge">Most popular</span>'
              : '';
          // M1 (25 Sep review, finding P5): only a line whose rogue.white_letter
          // metafield is true even offers the choice - see tyreLineOffersWhiteLetter's
          // own doc comment for the defensive read. Every other line has no
          // lettering markup here at all, and always sends Lettering: Black
          // (see effectiveLetterColour, which every cart write reads instead
          // of this.letterColour directly).
          const letteringBlock = tyreLineOffersWhiteLetter(line)
            ? `
              <span class="rogue-buy__tyre-row-lettering"${selected ? '' : ' hidden'}>
                <span class="rogue-label">Lettering</span>
                <span class="rogue-buy__choice-options">
                  <label class="rogue-buy__choice-option">
                    <input type="radio" name="rogue-tyre-letter-${escapeHtml(line.handle)}" value="Black" ${this.letterColour === 'White' ? '' : 'checked'}>
                    Black
                  </label>
                  <label class="rogue-buy__choice-option">
                    <input type="radio" name="rogue-tyre-letter-${escapeHtml(line.handle)}" value="White" ${this.letterColour === 'White' ? 'checked' : ''}>
                    White
                  </label>
                </span>
              </span>
            `
            : '';
          return `
            <label class="rogue-buy__tyre-row${selected ? ' is-on' : ''}" data-rogue-tyre-handle="${escapeHtml(line.handle)}">
              <input type="radio" name="rogue-tyre-line" value="${escapeHtml(line.handle)}" ${selected ? 'checked' : ''}>
              <span class="rogue-buy__tyre-row-main">
                <span class="rogue-buy__tyre-row-title">${escapeHtml(line.title)}</span>
                ${mostPopularBadge}
                ${typeChip}
              </span>
              <span class="rogue-buy__tyre-row-price">${escapeHtml(priceLine)}</span>
              ${letteringBlock}
            </label>
          `;
        })
        .join('');

      if (showAll) {
        const remaining = this.availableTyreLines.length - visible.length;
        if (!this.tyresExpanded && remaining > 0) {
          // B6 (31 Oct 2026): three rows, then "More tyres (N)" reveals the rest in place.
          showAll.textContent = moreTyresLabel(remaining);
          showAll.removeAttribute('hidden');
        } else {
          showAll.setAttribute('hidden', '');
        }
      }
    }

    updateTyreFields() {
      const tyreLine = this.resolvedTyreLine();
      const properties = buildTyreCartProperties({
        enabled: this.tyresEnabled,
        brand: tyreLine?.title || null,
        // The resolved ISO size, never the placeholder text the tyre-size
        // line may currently show - see tyreSizeIsKnown/tyreSizeForRim.
        size: tyreLine?.variant?.size || '',
        quantity: this.currentQuantity(),
      });
      this.writeHiddenFields('tyreFields', {}, properties);
    }

    /**
     * The persistent summary line under the button (spec 2.3), and the body
     * of the post-add confirmation (see showAddConfirmation) - one function
     * decides what's actually going in the basket, so the two can never say
     * different things.
     * @param {number} [quantityOverride] - defaults to the live quantity input
     * @returns {string}
     */
    buildSummaryLine(quantityOverride) {
      const quantity = quantityOverride ?? this.currentQuantity();
      const tyreLine = this.resolvedTyreLine();
      return buildBuySummaryLine({
        quantity,
        wheelTitle: this.modelTitle(),
        wheelSize: this.selectedSize,
        wheelFinish: this.selectedColour,
        tyresEnabled: this.tyresEnabled,
        // H4: same reasoning as updateCartAttributesFields's tyresWantedCartProperty
        // call - a known size with nothing on offer reads as "Tyres to
        // confirm with you", not silently as if nothing were asked for.
        sizeKnown: this.availableTyreLines.length > 0,
        tyreLine,
        // N6 (26 Sep review round 3): same source as updatePreorderBadge's
        // own read of this variant's state.
        preorder: preorderBadgeData(this.getCurrentVariantMeta()).isPreorder,
      });
    }

    updateSummaryLine() {
      const summary = this.ref('summaryLine');
      const line = this.buildSummaryLine();
      if (!summary) return;
      // A full stop between each part (31 Oct 2026): the wheels-only line has none of its own.
      const parts = line ? [line] : [];
      if (line && this.spareIncluded) parts.push('Includes 1 spare wheel.');
      const extras = line ? this.extras?.summary?.() || '' : '';
      if (extras) parts.push(`Plus ${extras}.`);
      summary.textContent = parts.map((part, index) => (index < parts.length - 1 && !/[.!?]$/.test(part) ? `${part}.` : part)).join(' ');
    }

    /** Route native click, keyboard and requestSubmit actions through the same paired add. */
    handleProductSubmit(event) {
      if (this._adding) {
        event.preventDefault();
        return true;
      }
      if (this.addNeedsReview) {
        event.preventDefault();
        window.location.href = '/cart';
        return true;
      }
      const tyreLine = this.resolvedTyreLine();
      event.preventDefault();
      if (this.ref('addToCartButton')?.disabled) return true;
      if (tyreLine || !this.tyresEnabled) {
        this.submitWheelAndTyre(tyreLine);
      } else if (this.availableTyreLines.length > 0) {
        const errorEl = this.ref('addToCartTextError');
        if (errorEl) {
          errorEl.textContent = 'Choose a tyre, or pick Wheels only';
          errorEl.classList.remove('hidden');
        }
      } else {
        // No priced tyre is available: preserve the existing held enquiry path.
        this.submitWheelAndTyre(null);
      }
      return true;
    }

    async submitWheelAndTyre(tyreLine) {
      if (this._adding || this.addNeedsReview) return;
      const quantity = this.currentQuantity();
      if (this.quantityError()) { this.updateQuantityState(); return; }

      const payload = buildAddToBasketPayload({
        wheelVariantId: this.currentVariantId,
        tyreVariantId: tyreLine?.variant?.id,
        quantity,
        wheelTitle: this.modelTitle(),
        wheelSize: this.selectedSize,
        wheelFinish: this.selectedColour,
        tyreSize: tyreLine?.variant?.size,
        // M1 (review P5): never the raw this.letterColour, which could be a
        // "White" left over from an earlier line that doesn't offer it.
        letterColour: this.effectiveLetterColour(),
        tyreLine: tyreLine?.title,
        // Review finding (Major): same source as the Vehicle cart
        // attribute (vehicleCartAttributes), so a mismatch note
        // (snippets/cart-products-row.liquid) compares like with like.
        van: this.vehicle?.display_name || '',
        vehicleSlug: this.vehicle?.slug || '',
        // Review N9: one id per add-to-basket click, not per wheel variant -
        // two separate sets of the same wheel design must pair with their
        // OWN tyres, never each other's. See cartLinePairs in
        // snippets/cart-products.liquid/assets/rogue-fitment.js.
        setId: createWheelPackageId(),
        // L1 (review P11): so the fitter isn't left guessing whether to fit
        // all five.
        spare: this.spareIncluded,
        // N6 (26 Sep review round 3): so the tyre line carries "Pre-order:
        // yes" too, and the two are packed together - see
        // buildAddToBasketPayload.
        preorder: preorderBadgeData(this.getCurrentVariantMeta()).isPreorder,
        due: this.getCurrentVariantMeta()?.due_in || '',
        // Keep the hold on this wheel line when another van is selected later.
        verificationProperties: {
          ...this.verificationCartData().properties,
          ...tyresWantedCartProperty({ tyresEnabled: this.tyresEnabled, sizeKnown: this.availableTyreLines.length > 0, wanted: true }),
        },
      });
      if (!payload) return;
      const availabilityChecks = [[this.currentVariantId, this.getCurrentVariantMeta()], [tyreLine?.variant?.id, tyreLine?.variant]];
      const wheelVariantId = String(this.currentVariantId);
      this._adding = true;
      // T2 follow-up (31 Oct 2026): the in-page confirmation (shown when the
      // post-add sheet is dismissed for the session) states the total that
      // was on the button at the moment of adding, extras included.
      this.lastAddTotalText = this.textOf('addToCartPrice') || '';
      const finishIntent = beginCartMutation();

      const button = /** @type {HTMLButtonElement|null} */ (this.ref('addToCartButton'));
      const label = this.ref('addToCartLabel');
      const originalLabel = label?.textContent || '';
      if (button) button.disabled = true;
      if (label) label.textContent = 'Adding…';
      // Review SHOP-010 (17 Oct 2026): the 390 sticky bar copies this
      // button's label and disabled state (updateStickyBar), but nothing
      // re-copied them during an add - it kept offering "Add to basket"
      // beside the main button's "Added". Re-sync at every change below.
      this.syncStickyBar();

      const errorEl = this.ref('addToCartTextError');
      errorEl?.classList.add('hidden');

      try {
        const cartCheck = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartCheck.ok) throw new Error('Could not check basket quantities');
        const existing = (await cartCheck.json()).items || [];
        for (const [id, meta] of availabilityChecks) {
          if (!id) continue;
          const already = existing.filter((item) => String(item.variant_id || item.id) === String(id)).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
          const availabilityError = variantQuantityError(meta, quantity, already);
          if (availabilityError) {
            if (label) label.textContent = originalLabel;
            if (errorEl) { errorEl.textContent = availabilityError; errorEl.classList.remove('hidden'); }
            return;
          }
        }
        const extras = this.extras?.itemsForAdd?.(existing) || { items: [], error: '' };
        if (extras.error) {
          if (label) label.textContent = originalLabel;
          if (errorEl) { errorEl.textContent = extras.error; errorEl.classList.remove('hidden'); }
          return;
        }
        // D1 (31 Oct 2026): extras go first in the POST, so Shopify (newest
        // line first) lists the set above them in the basket and at checkout.
        payload.items = orderAddItemsExtrasFirst(payload.items, extras.items);
        await this.syncCartAttributes();
        const result = await addBasketItemsOnce(payload, {
          afterAccepted: async () => {
            const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            if (!response.ok) throw new Error('Could not refresh basket');
            await this.announceCartUpdate(await response.json(), payload.items);
          },
          // Black Friday review F1 (3 Nov 2026): a set Shopify could only
          // part-fill comes straight back out of the basket.
          afterRefused: (response) => undoRefusedAdd({ items: payload.items, before: existing, status: response.status }),
        });
        if (result.state === 'refused') {
          this.addNeedsReview = !result.removed;
          this.showRefusedAdd(result, { wheelVariantId, existing, tyreLine, label, originalLabel, errorEl });
          return;
        }
        this.addNeedsReview = result.state === 'unknown' || (result.state === 'accepted' && !result.refreshed);
        if (this.addNeedsReview) {
          if (label) label.textContent = 'View basket';
          if (errorEl) {
            errorEl.textContent = result.state === 'accepted'
              ? 'Your items were added. View your basket to check them.'
              : 'We could not confirm the add. Check your basket before trying again.';
            errorEl.classList.remove('hidden');
          }
        } else {
          this.extras?.reset?.();
          if (label) label.textContent = 'Added';
          setTimeout(() => {
            if (label) label.textContent = originalLabel;
            this.syncStickyBar();
          }, 1800);
        }
      } catch {
        if (label) label.textContent = originalLabel;
        if (errorEl) {
          errorEl.textContent = 'We could not check your basket or save your details. No items were added; please try again.';
          errorEl.classList.remove('hidden');
        }
      } finally {
        finishIntent();
        this._adding = false;
        if (button) button.disabled = false;
        this.updateQuantityState();
        this.syncStickyBar();
        if (this.vehicleChangePending) {
          this.vehicleChangePending = false;
          this.onVehicleChange?.();
        }
      }
    }

    /**
     * Black Friday review F1 (3 Nov 2026): after Shopify refused an add
     * (undoRefusedAdd has already taken it back out), say plainly how many
     * are left and what to do, and put the page's own stock figure right so
     * the next try is checked here before it reaches Shopify. When taking it
     * back out failed, say what the basket holds and point to it.
     * @param {{ removed: boolean, status?: number, short?: Array<{ id: string, left: number }>, kept?: Array<Record<string, any>>, cart?: Record<string, any> }} result
     * @param {{ wheelVariantId: string, existing: Array<Record<string, any>>, tyreLine: any, label: Element|null, originalLabel: string, errorEl: Element|null }} context
     */
    showRefusedAdd(result, { wheelVariantId, existing, tyreLine, label, originalLabel, errorEl }) {
      const short = Array.isArray(result.short) ? result.short : [];
      const wheelShort = short.find((entry) => entry.id === wheelVariantId) || null;
      const tyreId = tyreLine?.variant?.id != null ? String(tyreLine.variant.id) : '';
      const tyreShort = tyreId ? short.find((entry) => entry.id === tyreId) || null : null;
      const already = (existing || [])
        .filter((item) => String(item.variant_id || item.id) === wheelVariantId)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (tyreShort && tyreLine.variant.inventory_policy === 'deny') tyreLine.variant.inventory_quantity = tyreShort.left;
      const shownBefore = String(this.currentVariantId);
      if (wheelShort) this.applyKnownStock(wheelVariantId, wheelShort.left);
      const switched = Boolean(wheelShort) && shownBefore === wheelVariantId && String(this.currentVariantId) !== wheelVariantId;
      if (label) label.textContent = result.removed ? originalLabel : 'View basket';
      if (errorEl) {
        errorEl.textContent = refusedAddMessage({
          status: result.status, wheelShort, tyreShort, already, removed: result.removed, kept: result.kept, switched, phone: this.phone,
        });
        errorEl.classList.remove('hidden');
        // Cleared by the shopper's next choice (hideAddConfirmation) once
        // the basket is right again; kept while the basket needs a look.
        this.refusedAddShown = result.removed;
      }
      if (!result.removed && result.cart) this.announceBasketChange(result.cart, result.kept);
    }

    /**
     * Set one variant's stock figure on this page to what Shopify just
     * reported, then redraw the choices the same way a size click does
     * (sold-out labels, the quantity check, "Call to reserve").
     * @param {string} variantId
     * @param {number} left
     */
    applyKnownStock(variantId, left) {
      const id = String(variantId);
      const meta = this.variantMetaByVariantId?.[id];
      if (!meta || meta.inventory_policy !== 'deny' || !Number.isFinite(Number(left))) return;
      const update = { inventory_quantity: Math.max(0, Number(left)), ...(Number(left) <= 0 ? { available: false } : {}) };
      Object.assign(meta, update);
      const variant = (this.variants || []).find((entry) => String(entry.id) === id);
      if (variant) Object.assign(variant, update);
      if (this.hasRogueOptionPicker()) {
        this.renderColourSwatches();
        this.renderSizeSelector();
        this.applyResolution();
      } else {
        this.render();
      }
    }

    /**
     * The header count and the drawer after a refused add that could not be
     * taken back out: the basket really changed, but this is no "Added"
     * moment, so it is an update on `document` (the post-add sheet and
     * bindAddConfirmation listen for 'add' only).
     * @param {Record<string, any>} cartJson
     * @param {Array<Record<string, any>>} kept
     */
    async announceBasketChange(cartJson, kept) {
      const lines = (kept || []).filter((line) => line?.key).map((line) => ({ id: String(line.key), quantity: Number(line.quantity) || 0 }));
      if (!lines.length) return;
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        document.dispatchEvent(new CartLinesUpdateEvent({
          action: 'update',
          context: 'product',
          lines,
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
            detail: { items: cartJson.items, itemCount: cartJson.item_count, source: 'rogue-fitment-gate' },
          }),
        }));
      } catch (error) {
        console.warn('[rogue-fitment-gate] cart update event skipped:', error);
      }
    }

    /**
     * Cart-level attributes (vehicle, registration, upplating, current tyre
     * size, letter colour) aren't part of `/cart/add.js`'s documented JSON
     * body for a multi-item add, so they're set with a separate
     * `/cart/update.js` call - reading back the same `attributes[...]`
     * hidden inputs updateCartAttributesFields already wrote into
     * `verificationFields`, rather than recomputing them a second time.
     */
    async syncCartAttributes() {
      const container = this.ref('verificationFields');
      if (!container) return;
      const attributes = {};
      for (const input of container.querySelectorAll('input[name^="attributes["]')) {
        const match = /** @type {HTMLInputElement} */ (input).name.match(/^attributes\[(.+)\]$/);
        if (match) attributes[match[1]] = /** @type {HTMLInputElement} */ (input).value;
      }
      if (Object.keys(attributes).length === 0) return;
      await cartAttributeWriter.save(attributes);
    }

    /**
     * @param {Record<string, unknown>} cartJson - the parsed /cart.js response
     * @param {Array<{ id: string|number, quantity: number }>} items - the payload just posted to /cart/add.js
     */
    async announceCartUpdate(cartJson, items) {
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        // The event lists the set first whatever order the POST used:
        // bindAddConfirmation reads lines[0].quantity as the wheel count.
        const lines = setLinesFirst(items);
        // Dispatched on `this`, not `document` - it still bubbles all the way
        // to document for the header bubble/cart-items-component exactly as
        // before, but now also passes through this element on the way up,
        // which is what lets bindAddConfirmation's own listener (added
        // directly on `this`) catch it too - see review 2, finding 12.
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: lines.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
              detail: {
                items: cartJson.items,
                source: 'rogue-fitment-gate',
                itemCount: cartJson.item_count,
                // The package id of this add, so the post-add sheet can price
                // exactly this set: wheels plus fitted tyres (25 Oct 2026).
                setId: lines[0]?.properties?._set_id || '',
                // The extras ticked in the buy box and added in this same
                // click (31 Oct 2026, T2), so the sheet's total includes
                // them. They carry no `_set_id` on purpose: the basket
                // pairing and the platform read that as "part of the set".
                extraVariantIds: extraVariantIdsOf(items),
              },
            }),
          })
        );
      } catch (error) {
        // '@shopify/events' failed to load (offline, CDN hiccup) - the
        // basket still holds both lines; only the live bubble/drawer
        // refresh is skipped, and the count catches up on next navigation.
        console.warn('[rogue-fitment-gate] cart update event skipped:', error);
      }
    }

    /* ---------------------------- plumbing ----------------------------- */

    /**
     * @param {string} refName
     * @param {Record<string, string>} attributes
     * @param {Record<string, string>} properties
     */
    writeHiddenFields(refName, attributes, properties) {
      const container = this.ref(refName);
      if (!container) return;
      container.innerHTML = '';

      const formId = this.getAttribute('data-cart-form-id') || '';
      for (const [key, value] of Object.entries(attributes)) {
        container.appendChild(hiddenInput(`attributes[${key}]`, value, formId));
      }
      for (const [key, value] of Object.entries(properties)) {
        container.appendChild(hiddenInput(`properties[${key}]`, value, formId));
      }
    }

    /**
     * Tells <rogue-product-gallery> which picture the current variant
     * wears, and <rogue-breadcrumb> (review finding, Minor) whether this
     * particular product actually fits the stored van - the breadcrumb has
     * no product-specific fitment data of its own, only this element
     * resolves that (see render's own `state`, mirrored onto `this.
     * fitmentState` just above the call site).
     */
    announceVariant(variantMeta) {
      const detail = {
        variantId: this.currentVariantId,
        imageId: variantMeta?.image_id ?? null,
        image: variantMeta?.image_large ?? null,
        imageAlt: variantMeta?.image_alt ?? null,
        colour: this.selectedColour,
        size: this.selectedSize,
        fitmentState: this.fitmentState,
      };
      lastVariantChangeDetail = detail;
      document.dispatchEvent(new CustomEvent(ROGUE_VARIANT_CHANGE_EVENT, { detail }));
    }
  }

  /**
   * The product gallery. Thumbnails, prev/next arrows, swipe, the keyboard
   * and a finish change in the buy panel (which has no idea this element
   * exists - it just dispatches `rogue:variant-change`) all resolve to the
   * same two calls, `showImage`/`markSelected`, so they can never disagree
   * about which photo is showing.
   *
   * Gallery-flip fix (2026-09-26): none of that ever worked. This read the
   * main image back with `this.querySelector('[ref="mainImage"]')`, but
   * Shopify's `image_tag` filter does not emit that `ref:` parameter as an
   * HTML attribute - confirmed live, on the deployed store, before this fix:
   * zero `ref="mainImage"` attributes ever reached the DOM, so `mainImage`
   * was always null and every interaction below silently did nothing to the
   * photo on screen (clicking a thumbnail still moved its own selected
   * border, which is what made this easy to miss). Found by the class
   * selector instead (`.rogue-gallery__main`, never dropped) below.
   *
   * A second bug capped the thumbnail row - and therefore every interaction
   * that reads the current photo's position from it - at the first 5
   * images (snippets/rogue-product-gallery.liquid renders one per image
   * now; the row scrolls past 5 rather than wrapping, see the CSS).
   *
   * Adds a full-screen viewer (Horizon's own <dialog-component>, assets/
   * dialog.js) that steps through the same photos with its own prev/next,
   * a close button and Escape - see openLightbox/step below.
   */
  class RogueProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.rogue-gallery__stage');
      this.mainImage = /** @type {HTMLImageElement|null} */ (this.querySelector('img.rogue-gallery__main'));
      this.thumbs = Array.from(this.querySelectorAll('[data-rogue-image-src]'));
      this.lightboxWrap = /** @type {any} */ (this.querySelector('[ref="lightboxWrap"]'));
      this.lightboxImage = /** @type {HTMLImageElement|null} */ (this.querySelector('[ref="lightboxImage"]'));
      this.lightboxCounter = this.querySelector('[ref="lightboxCounter"]');
      this.lightboxDots = this.querySelector('[ref="lightboxDots"]');
      this.lightboxReturnFocus = /** @type {HTMLElement|null} */ (null);
      this.thumbsContainer = this.querySelector('.rogue-gallery__thumbs');
      this.missingPhotoNote = this.querySelector('[ref="missingPhotoNote"]');

      this.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement|null} */ (event.target);
        if (!target) return;

        if (target.closest('[ref="zoomTrigger"]')) {
          this.openLightbox(this.currentIndex());
          return;
        }
        if (target.closest('[ref="prevButton"], [ref="lightboxPrev"]')) {
          this.step(-1);
          return;
        }
        if (target.closest('[ref="nextButton"], [ref="lightboxNext"]')) {
          this.step(1);
          return;
        }
        const dot = target.closest('[data-rogue-dot-index]');
        if (dot) {
          this.showThumbByIndex(Number(dot.getAttribute('data-rogue-dot-index')));
          return;
        }
        const thumb = target.closest('[data-rogue-image-src]');
        if (!thumb) return;
        this.showImage(thumb.getAttribute('data-rogue-image-src'), thumb.getAttribute('data-rogue-image-alt'));
        this.markSelected(thumb.getAttribute('data-rogue-image-id'));
      });

      // Arrow keys step the gallery from anywhere inside it (a focused
      // thumbnail, arrow or dot) - the theme has no text input in here to
      // steal the keys from, but the guard costs nothing and keeps this
      // correct if one is ever added.
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const target = /** @type {HTMLElement|null} */ (event.target);
        if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
        event.preventDefault();
        this.step(event.key === 'ArrowLeft' ? -1 : 1);
      });

      this.onVariantChange = (event) => {
        const { image, imageId, imageAlt, colour } = event.detail || {};
        this.reorderThumbsForColour(colour);

        if (image) {
          this.hideMissingPhotoNote();
          const thumb = this.thumbs.find((entry) => entry.getAttribute('data-rogue-image-id') === String(imageId));
          // Use the selected asset's own description, including galleries with
          // no thumbnail row. Never reuse the previous finish's description.
          this.showImage(image, thumb?.getAttribute('data-rogue-image-alt') ?? imageAlt ?? '');
          this.markSelected(imageId == null ? null : String(imageId));
          return;
        }

        // Spec item 2: no photo for this finish - keep showing whatever the
        // gallery already had (never a different finish's photo passed off
        // as this one) and say so, rather than doing nothing silently.
        // Wheel fitment and simple-product buy panels send their real
        // Color/Colour option value here. Size-only products do not send
        // a colour, so their size is never turned into a photo claim.
        if (colour) this.showMissingPhotoNote(colour);
      };
      document.addEventListener(ROGUE_VARIANT_CHANGE_EVENT, this.onVariantChange);
      // The buy panel may already have announced its first pick.
      if (lastVariantChangeDetail) this.onVariantChange({ detail: lastVariantChangeDetail });

      if (this.thumbs.length > 1) {
        this.classList.add('rogue-gallery--js-enhanced');
        this.bindSwipe(this.stage);
        this.renderLightboxDots();
      }

      // Not `[ref="dialog"]`: that name belongs to <dialog-component>'s own
      // required ref (assets/dialog.js), and Component's ref scan takes
      // exactly one name per element - see the doc comment in
      // snippets/rogue-product-gallery.liquid on why this element carries
      // that ref instead of one of this class's own names.
      const lightboxDialog = this.querySelector('.rogue-lightbox__dialog');
      if (lightboxDialog) {
        this.bindSwipe(lightboxDialog);
        lightboxDialog.addEventListener('close', () => this.onLightboxClosed());
      }
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    /** @returns {number} the index the main image currently shows (0 if none is marked yet, or there are no thumbnails at all). */
    currentIndex() {
      const index = this.thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true');
      return index === -1 ? 0 : index;
    }

    /**
     * @param {string|null} src
     * @param {string|null} alt
     */
    showImage(src, alt) {
      if (!this.mainImage || !src) return;
      // Speed (25 Oct 2026): the buy panel announces its first pick on load,
      // which used to land here with the photo already on screen, drop the
      // srcset and make every phone fetch the 1600px file (about 440 KB) on
      // top of its 750px one. The same photo is now left alone, and a real
      // finish change gets a srcset of its own, set before src.
      if (this.mainImage.getAttribute('src') !== src) {
        const srcset = galleryMainSrcset(src);
        if (srcset) {
          this.mainImage.srcset = srcset;
        } else {
          this.mainImage.removeAttribute('srcset');
          this.mainImage.removeAttribute('sizes');
        }
        this.mainImage.src = src;
      }
      if (alt != null) this.mainImage.alt = alt;
      this.syncLightboxImage();
    }

    /** @param {string|null} imageId */
    markSelected(imageId) {
      for (const thumb of this.thumbs) {
        thumb.setAttribute('aria-pressed', String(imageId != null && thumb.getAttribute('data-rogue-image-id') === imageId));
      }
      this.markLightboxDot(imageId);
    }

    /**
     * Spec item 1: brings `colour`'s own photos to the front of the
     * thumbnail row (see {@link sortThumbsForColour}), by re-appending each
     * `<button>` in its new order - `appendChild` on a node already in the
     * DOM moves it rather than cloning it, so every attribute already set
     * (`aria-pressed`, `data-rogue-image-id`, ...) comes with it untouched.
     * Re-derives `this.thumbs` from that new order afterwards so step/
     * keyboard/swipe navigation and the lightbox dots agree with what the
     * shopper now sees, and only then rebuilds the dots. A no-op with
     * nothing to sort by, or only one thumbnail to begin with.
     * @param {string|null|undefined} colour
     */
    reorderThumbsForColour(colour) {
      if (!this.thumbsContainer || this.thumbs.length < 2) return;
      if (typeof colour !== 'string' || colour.trim() === '') return;

      const entries = this.thumbs.map((thumb) => ({
        thumb,
        colours: parseImageColours(thumb.getAttribute('data-rogue-image-colour')),
      }));
      const sorted = sortThumbsForColour(entries, colour);

      for (const entry of sorted) this.thumbsContainer.appendChild(entry.thumb);
      this.thumbs = sorted.map((entry) => entry.thumb);
      this.renderLightboxDots();
    }

    /** @param {string} colour */
    showMissingPhotoNote(colour) {
      if (!this.missingPhotoNote) return;
      this.missingPhotoNote.textContent = missingFinishPhotoMessage(colour, this.getAttribute('data-product-kind') || 'wheel');
      this.missingPhotoNote.hidden = false;
    }

    hideMissingPhotoNote() {
      if (!this.missingPhotoNote) return;
      this.missingPhotoNote.hidden = true;
    }

    /** @param {number} index */
    showThumbByIndex(index) {
      if (this.thumbs.length === 0) return;
      const thumb = this.thumbs[wrapIndex(index, this.thumbs.length)];
      if (!thumb) return;
      this.showImage(thumb.getAttribute('data-rogue-image-src'), thumb.getAttribute('data-rogue-image-alt'));
      this.markSelected(thumb.getAttribute('data-rogue-image-id'));
    }

    /**
     * Steps the gallery by `delta` (±1), wrapping at either end - the one
     * method the prev/next buttons (main gallery and lightbox alike), the
     * keyboard handler and swipe all call, so they can never drift out of
     * sync with each other.
     * @param {number} delta
     */
    step(delta) {
      this.showThumbByIndex(this.currentIndex() + delta);
    }

    /**
     * Binds a left/right swipe on `element` to `step`, wrapping at either
     * end. Scoped to whichever element is passed (the main stage, or the
     * lightbox) rather than the whole gallery: binding it to the whole
     * element would fire a step every time a shopper swipes the thumbnail
     * row itself to scroll past its 5th photo.
     * @param {Element|null} element
     */
    bindSwipe(element) {
      if (!element) return;
      let startX = /** @type {number|null} */ (null);
      element.addEventListener(
        'touchstart',
        (event) => {
          const touchEvent = /** @type {TouchEvent} */ (event);
          startX = touchEvent.touches && touchEvent.touches[0] ? touchEvent.touches[0].clientX : null;
        },
        { passive: true }
      );
      element.addEventListener(
        'touchend',
        (event) => {
          if (startX == null) return;
          const touchEvent = /** @type {TouchEvent} */ (event);
          const endTouch = touchEvent.changedTouches && touchEvent.changedTouches[0];
          const endX = endTouch ? endTouch.clientX : startX;
          const direction = swipeStepDirection(endX - startX);
          startX = null;
          if (direction !== 0) this.step(direction);
        },
        { passive: true }
      );
    }

    // --- Full-screen viewer ---------------------------------------------

    /**
     * Opens the full-screen viewer on `index`. Saves the shopper's current
     * focus so closing it (Escape, the close button, or clicking the
     * backdrop - all handled by <dialog-component>, assets/dialog.js) can
     * restore it, rather than leaving focus on a control the dialog just
     * covered.
     * @param {number} index
     */
    openLightbox(index) {
      if (!this.lightboxWrap) return;
      if (typeof this.lightboxWrap.showDialog !== 'function' && 'customElements' in window) {
        customElements.upgrade(this.lightboxWrap);
      }
      if (typeof this.lightboxWrap.showDialog !== 'function') return;

      this.lightboxReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (this.thumbs.length > 0) {
        this.showThumbByIndex(index);
      } else {
        this.syncLightboxImage();
      }
      this.lightboxWrap.showDialog();
    }

    onLightboxClosed() {
      if (this.lightboxReturnFocus && document.contains(this.lightboxReturnFocus)) {
        this.lightboxReturnFocus.focus();
      }
      this.lightboxReturnFocus = null;
    }

    /**
     * Copies the main image onto the lightbox's own <img> - called on every
     * image change, whether or not the lightbox is open, so it is always
     * correct the next time it opens. (That <img> is loading="lazy", so
     * while the dialog is closed it is not rendered and never downloads.) The counter lives in markLightboxDot
     * instead, not here: showImage (which calls this) runs BEFORE
     * markSelected on every path (showThumbByIndex, the variant-change
     * listener), so `this.currentIndex()` - which reads the `aria-pressed`
     * markSelected is about to set - would still be one photo behind every
     * time, the same bug `.src` vs `.currentSrc` below was fixed for.
     */
    syncLightboxImage() {
      if (!this.lightboxImage || !this.mainImage) return;
      // `.src` (the IDL attribute), not `.currentSrc`: `showImage` just set
      // `.src` directly (no srcset), which reflects synchronously, but
      // `.currentSrc` is the result of the browser's own, asynchronous
      // resource-selection step and can still read the PREVIOUS photo for
      // a tick after - confirmed live: stepping the lightbox once landed
      // the right photo in the main gallery and on the selected thumbnail,
      // but left the lightbox's own <img> one photo behind every time.
      this.lightboxImage.src = this.mainImage.src;
      this.lightboxImage.alt = this.mainImage.alt;
    }

    /** One dot per photo, built once from the server-rendered thumbnail buttons - the lightbox's own compact position indicator (there is no thumbnail row inside it). */
    renderLightboxDots() {
      if (!this.lightboxDots) return;
      this.lightboxDots.innerHTML = this.thumbs
        .map(
          (thumb, index) => `
            <button
              type="button"
              class="rogue-lightbox__dot"
              data-rogue-dot-index="${index}"
              aria-current="${thumb.getAttribute('aria-pressed') === 'true'}"
              aria-label="${galleryPhotoLabel(index, this.thumbs.length)}"
            ></button>
          `
        )
        .join('');
    }

    /**
     * Marks the lightbox dot matching `imageId` current, and updates the
     * counter alongside it - both read the same `index`, computed from
     * `imageId` (the thumbnail's stable `data-rogue-image-id`) rather than
     * from `aria-pressed`, so this gives the right answer regardless of
     * whether markSelected has finished updating that attribute yet.
     * @param {string|null} imageId
     */
    markLightboxDot(imageId) {
      if (!this.lightboxDots && !this.lightboxCounter) return;
      const index = this.thumbs.findIndex((thumb) => thumb.getAttribute('data-rogue-image-id') === imageId);
      if (this.lightboxDots) {
        for (const dot of this.lightboxDots.querySelectorAll('[data-rogue-dot-index]')) {
          dot.setAttribute('aria-current', String(Number(dot.getAttribute('data-rogue-dot-index')) === index));
        }
      }
      if (this.lightboxCounter && index !== -1) {
        this.lightboxCounter.textContent = galleryPhotoLabel(index, this.thumbs.length);
      }
    }
  }

  /**
   * The collection page head: swaps in the stored van's name and summary line
   * when this collection is that van's, and submits the sort form on change so
   * the chip behaves like a control rather than a form.
   */
  class RogueCollectionHead extends HTMLElement {
    connectedCallback() {
      const vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      const collectionHandle = this.getAttribute('data-collection-handle') || '';
      const matchesVan = Boolean(vehicle && vehicle.collection_handle === collectionHandle);
      // A van's own page (it carries the van's name) seen with another van stored.
      const pageVan = this.getAttribute('data-metafield-short-name') || this.getAttribute('data-metafield-display-name') || '';
      const otherVanStored = Boolean(vehicle && !matchesVan && pageVan);

      const heading = this.querySelector('[ref="heading"]');
      if (heading) {
        const wanted = collectionHeading({
          collectionHandle,
          collectionTitle: this.getAttribute('data-collection-title') || heading.textContent || '',
          metafieldDisplayName: this.getAttribute('data-metafield-display-name') || '',
          vehicle,
        });
        // Liquid renders the title as two lines inside the one H1 (a <span>
        // for the part after the colon, colon dropped). Only rewrite when the
        // heading has to come from somewhere other than the title, so that
        // markup survives for the common case.
        const renderedTitle = this.getAttribute('data-collection-title') || '';
        if (normaliseHeadingText(wanted) !== normaliseHeadingText(renderedTitle)) {
          heading.textContent = wanted;
        }
      }

      const summary = this.querySelector('[ref="summary"]');
      if (summary) {
        const van =
          this.getAttribute('data-metafield-short-name') || (matchesVan ? vanShortName(vehicle) : '');
        const line = collectionSummary({
          vehicleTemplate: this.getAttribute('data-summary-vehicle') || '',
          genericTemplate: this.getAttribute('data-summary-generic') || '',
          unlistedTemplate: this.getAttribute('data-summary-unlisted') || '',
          count: this.getAttribute('data-count-word') || '',
          sizes: this.getAttribute('data-sizes-label') || '',
          van,
          isUnlisted: matchesVan && isUnlistedVehicle(vehicle),
          manualReview: requiresManualReview(vehicleByCollectionHandle(readVehiclesFromDom(), collectionHandle)) || (matchesVan && requiresManualReview(vehicle)),
        });
        if (line !== '') summary.textContent = otherVanStored ? otherVanWording(line, pageVan) : line;
      }
      if (otherVanStored) {
        const fitLine = this.closest('.rogue-collection')?.querySelector('.rogue-collection__fit-line span');
        if (fitLine) fitLine.textContent = otherVanWording(fitLine.textContent || '', pageVan);
      }

      // Spec 9 BUY item 8a / review 2 N17. `ref="eyebrow"` is opt-in: SHOP
      // adds it to the <p class="rogue-eyebrow"> in rogue-collection.liquid
      // when it wires this up, so this is a no-op until then.
      const eyebrow = this.querySelector('[ref="eyebrow"]');
      if (eyebrow) {
        const wantedEyebrow = collectionEyebrowText({
          collectionHandle,
          defaultText: eyebrow.getAttribute('data-eyebrow-default') || eyebrow.textContent || '',
        });
        if (wantedEyebrow !== '') eyebrow.textContent = wantedEyebrow;
      }

      const chip = this.querySelector('[ref="vanChip"]');
      if (chip) {
        const filled = vanChip({
          vehicle,
          collectionHandle,
          template: chip.getAttribute('data-van-chip-template') || '',
          collectionsRootPath: collectionsRoot(),
        });
        const text = this.querySelector('[ref="vanChipText"]');
        if (filled && text) {
          chip.setAttribute('href', filled.href);
          text.textContent = filled.label;
          chip.removeAttribute('hidden');
        } else {
          chip.setAttribute('hidden', '');
        }
      }

      // Sort now ends the filter row, outside this element (27 Oct 2026).
      const scope = this.closest('.rogue-collection') || document;
      const select = scope.querySelector('[ref="sortSelect"]');
      const form = scope.querySelector('[ref="sortForm"]');
      select?.addEventListener('change', () => form?.submit());
    }
  }

  /**
   * Fit-only browsing (spec items 1, 2, 4, 5, 6): the slim bar above a
   * wheel or tyre listing's grid, and the filtering it drives. One element
   * covers every listing on the site - `data-kind="tyre"` for the tyres
   * collection, `data-kind="wheel"` (the default) for everything else
   * (all-wheels, wheel-and-tyre-packages, every wheels-<pcd> stud-pattern
   * collection, a van's own collection, another van's own collection, and
   * search results) - because the decision itself
   * (wheelListingBarState/tyreListingBarState, above) is what differs
   * between them, not the DOM wiring.
   *
   * `data-grid-selector` finds the <ul role="list"> to filter from the
   * document root, so this drops into completely different section markup
   * (sections/rogue-collection.liquid's own grid, Horizon's #ResultsList on
   * templates/search.json) without either one needing to know about the
   * other. Every card in that list already carries `data-rogue-pcd-tags`
   * (wheels) or `data-rogue-tyre-sizes` (tyres) - see
   * snippets/rogue-product-card.liquid - so this never needs a second copy
   * of a card's fitment data.
   *
   * Real HTML stays complete for search engines and no-JS visitors: this
   * element starts `hidden` and every card starts visible (see the snippet
   * that renders it) - only once a van is confirmed stored does either
   * change, and never by removing a card, only by hiding it (`hidden`, not
   * a removed node), so nothing here can leave a listing incomplete for
   * anything that doesn't run this script.
   */
  class RogueFitFilterBar extends HTMLElement {
    connectedCallback() {
      this.kind = this.getAttribute('data-kind') === 'tyre' ? 'tyre' : 'wheel';
      this.collectionHandle = this.getAttribute('data-collection-handle') || '';
      this.collectionPcdLabel = this.getAttribute('data-collection-pcd-label') || '';
      this.singlePage = this.getAttribute('data-single-page') !== 'false';
      this.sessionKey = this.getAttribute('data-session-key') || `rogue.fitbar.${this.kind}.${this.collectionHandle}`;

      const gridSelector = this.getAttribute('data-grid-selector') || '';
      this.list = (gridSelector && document.querySelector(gridSelector)) || this.parentElement?.querySelector('[role="list"]') || null;

      this.textEl = this.querySelector('[ref="fitBarText"]');
      this.primaryEl = this.querySelector('[ref="fitBarPrimary"]');
      this.toggleEl = this.querySelector('[ref="fitBarToggle"]');
      this.switchEl = this.querySelector('[ref="fitBarSwitch"]');

      this.toggleEl?.addEventListener('click', () => this.setShowAll(!this.readShowAll()));
      this.switchEl?.addEventListener('click', () => this.switchToOtherVan());

      this.render();
      this.onVehicleChange = () => this.render();
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    /** Remembered for the browser session (spec item 1), never across sessions - a fresh visit defaults to filtered. */
    readShowAll() {
      try {
        return sessionStorage.getItem(this.sessionKey) === '1';
      } catch {
        return false;
      }
    }

    setShowAll(value) {
      try {
        sessionStorage.setItem(this.sessionKey, value ? '1' : '0');
      } catch {
        /* private mode/quota - degrade to the default (filtered) every render */
      }
      this.render();
    }

    render() {
      const vehicles = readVehiclesFromDom();
      const vehicle = enrichVehicle(globalStore.get(), vehicles);
      const items = this.list ? Array.from(this.list.children) : [];
      const showAll = this.readShowAll();

      let state;
      if (this.kind === 'tyre') {
        const resolvedSizes = resolvedTyreSizesForVehicle(vehicle);
        const sizeKnown = resolvedSizes.length > 0;
        let matchingCount = 0;
        const matches = items.map((item) => {
          const card = item.querySelector('[data-rogue-tyre-sizes]');
          const sizes = (card?.getAttribute('data-rogue-tyre-sizes') || '').split('|').map((s) => s.trim()).filter(Boolean);
          const ok = sizeKnown && sizes.some((size) => resolvedSizes.includes(size));
          if (ok) matchingCount += 1;
          return ok;
        });
        state = tyreListingBarState({
          vehicle,
          sizeKnown,
          totalCount: items.length,
          matchingCount,
          showAll,
          singlePage: this.singlePage,
        });
        // "Show all tyres" (spec item 4 follow-up, fit-only verifier
        // finding 25 Sep 2026): reorder the grid itself so the van's size
        // leads once every line is visible again - see tyreShowAllOrder's
        // own doc comment. Appending an existing child re-parents it (no
        // clone, no separate removal step). A no-op whenever the size
        // isn't known (nothing to put first).
        if (this.list && sizeKnown && state.applyFilter === false) {
          tyreShowAllOrder(matches).forEach((index) => this.list.append(items[index]));
        }
        this.applyVisibility(items, matches, state.applyFilter);
        this.otherVehicle = null;
      } else {
        const mode = fitFilterMode({ collectionHandle: this.collectionHandle, vehicle, vehicles });
        const otherVehicle = mode === ROGUE_FIT_MODE_OTHER_VAN ? vehicleByCollectionHandle(vehicles, this.collectionHandle) : null;
        // "all-wheels" is a real full-range listing (wheels, tyres,
        // accessories, merch - templates/collection.all.json) and search
        // results mix products, pages and articles, so only a card that
        // actually carries a pcd-* tag is a wheel-fit candidate at all
        // (isWheelFitCard) - everything else is left exactly as rendered,
        // never hidden and never counted either way (spec item 3).
        let wheelCount = 0;
        let fittingCount = 0;
        const participates = [];
        const matches = items.map((item) => {
          const card = item.querySelector('[data-rogue-pcd-tags]');
          const tags = (card?.getAttribute('data-rogue-pcd-tags') || '').split(/\s+/).filter(Boolean);
          const isWheel = isWheelFitCard(tags);
          participates.push(isWheel);
          if (!isWheel) return true;
          wheelCount += 1;
          const ok = productFitsVehicle(fitmentForCard(card), vehicle);
          if (ok) fittingCount += 1;
          return ok;
        });
        // C4: the "Pick your vehicle" row only on a listing that is not a van's own page.
        const ownsVan = Boolean(vehicleByCollectionHandle(vehicles, this.collectionHandle));
        state = wheelListingBarState({
          mode,
          vehicle,
          otherVehicle,
          totalCount: wheelCount,
          fittingCount,
          showAll,
          singlePage: this.singlePage,
          collectionPcdLabel: this.collectionPcdLabel,
          collectionsRootPath: collectionsRoot(),
          pickVehicleHref: ownsVan ? '' : pickVehicleHref(window.location.pathname),
        });
        this.applyVisibility(items, matches, state.applyFilter, participates);
        this.otherVehicle = otherVehicle;
      }

      this.paint(state);
    }

    /**
     * Hides (not removes) every wheel card the current filter choice
     * excludes - see the file header for why `hidden` matters here.
     * `participates` (wheel kind only; omitted for tyres, where every card
     * in the list is a tyre) marks which cards are even candidates: a
     * non-participating card (an accessory/tyre/merch/page/article result)
     * is always left showing, regardless of `applyFilter`.
     */
    applyVisibility(items, matches, applyFilter, participates) {
      items.forEach((item, index) => {
        const eligible = !participates || participates[index];
        if (eligible && applyFilter && !matches[index]) {
          item.setAttribute('hidden', '');
        } else {
          item.removeAttribute('hidden');
        }
      });
    }

    paint(state) {
      if (!state.visible) {
        this.setAttribute('hidden', '');
        return;
      }
      this.removeAttribute('hidden');
      if (this.textEl) this.textEl.textContent = state.message;

      if (this.primaryEl) {
        if (state.primary) {
          this.primaryEl.textContent = state.primary.label;
          this.primaryEl.setAttribute('href', state.primary.href);
          this.primaryEl.removeAttribute('hidden');
        } else {
          this.primaryEl.setAttribute('hidden', '');
        }
      }

      if (this.toggleEl) {
        if (state.toggleVisible) {
          this.toggleEl.textContent = state.toggleLabel;
          this.toggleEl.removeAttribute('hidden');
        } else {
          this.toggleEl.setAttribute('hidden', '');
        }
      }

      if (this.switchEl) {
        if (state.switchAction) {
          this.switchEl.textContent = state.switchAction.label;
          this.switchEl.removeAttribute('hidden');
        } else {
          this.switchEl.setAttribute('hidden', '');
        }
      }
    }

    /** "Switch to Transit MK8" (spec item 2): stores the OTHER van, exactly the way RogueVehicleSelector.confirm does, then re-renders - this page becomes that van's own collection, so the notice simply hides and nothing needs filtering. */
    switchToOtherVan() {
      if (!this.otherVehicle) return;
      const stored = globalStore.set(this.otherVehicle);
      if (!stored) return;
      syncVehicleCartAttributes(stored);
      document.dispatchEvent(new CustomEvent(ROGUE_VEHICLE_CHANGE_EVENT, { detail: { vehicle: stored } }));
    }
  }

  /**
   * Wraps a "you may also like"-style card grid (eyebrow + heading + <ul>,
   * sections/rogue-recommendations.liquid) and hides any wheel card that
   * does not fit the stored van (spec item 3: recommendations show fitting
   * wheels only, never a non-fitting one). `data-enabled="false"` on
   * everything that is NOT a wheel-to-wheel recommendation (a tyre,
   * accessory or merch product's own recommendations, which mix product
   * types and would otherwise be hidden outright the moment any van was
   * stored - none of them carry a pcd-* tag to match) leaves those alone.
   * A no-op with no vehicle stored: nothing here has been checked against a
   * van yet, so nothing is hidden - same rule as everywhere else in this
   * file. Hides the whole block (heading included) when every card in it
   * turns out not to fit, rather than leaving a heading over an empty grid.
   */
  class RogueFitOnlyGrid extends HTMLElement {
    connectedCallback() {
      if (this.getAttribute('data-enabled') !== 'true') return;
      this.render();
      this.onVehicleChange = () => this.render();
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    render() {
      const vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      if (!vehicle) return;

      const list = this.querySelector('[role="list"]');
      if (!list) return;

      let visibleCount = 0;
      for (const item of Array.from(list.children)) {
        const card = item.querySelector('[data-rogue-pcd-tags]');
        const tags = (card?.getAttribute('data-rogue-pcd-tags') || '').split(/\s+/).filter(Boolean);
        // A non-wheel card here (Shopify's recommendation engine mixes
        // types on the wheel product page - see rogue-recommendations.liquid's
        // own doc comment) is left showing, not judged against the van at
        // all (isWheelFitCard) - only a genuine wheel card is ever hidden.
        if (!isWheelFitCard(tags) || productFitsVehicle(fitmentForCard(card), vehicle)) {
          item.removeAttribute('hidden');
          visibleCount += 1;
        } else {
          item.setAttribute('hidden', '');
        }
      }

      if (visibleCount === 0) {
        this.setAttribute('hidden', '');
      } else {
        this.removeAttribute('hidden');
      }
    }
  }

  /**
   * A collection card: paints the finish dots from the shared map and adds the
   * "Fits your {van}" badge when the card's pcd-* tags match the stored van.
   */
  class RogueProductCard extends HTMLElement {
    connectedCallback() {
      for (const dot of this.querySelectorAll('[data-rogue-colour-name]')) {
        if (dot.style.getPropertyValue('--rogue-swatch-color') !== '') continue;
        dot.style.background = colourSwatchBackground(dot.getAttribute('data-rogue-colour-name'));
      }

      this.onVehicleChange = () => this.renderFitment();
      document.addEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
      this.renderFitment();
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VEHICLE_CHANGE_EVENT, this.onVehicleChange);
    }

    renderFitment() {
      const vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      const priceNote = this.querySelector('[ref="priceNote"]');
      if (priceNote) {
        if (priceNote.dataset.defaultText === undefined) priceNote.dataset.defaultText = priceNote.textContent;
        priceNote.textContent = cardPriceNoteText(vehicle, priceNote.dataset.defaultText);
      }
      const badge = this.querySelector('[ref="fitBadge"]');
      const badgeText = this.querySelector('[ref="fitBadgeText"]');
      if (!badge || !badgeText) return;

      const badges = collectionCardBadges(fitmentForCard(this), vehicle, cardFitsVanName(vehicle));

      // C2 (31 Oct 2026): on the stored van's own page the line above the grid says it once.
      const ownHandle = this.closest('[data-rogue-own-collection]')?.getAttribute('data-rogue-own-collection') || '';
      if (badges.fits && badges.fitsLabel !== '' && !hideBadgeOnOwnCollection(ownHandle, vehicle)) {
        badgeText.textContent = badges.fitsLabel;
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    }
  }

  /**
   * The product breadcrumb. Prefers the collection the shopper came through
   * (rendered by Liquid, and left untouched forever if so - `ownsLink`
   * below); falls back to the stored van's own collection, or "All wheels"
   * for an unlisted van or a confirmed non-fit (breadcrumbVanLabel). The
   * non-fit case is product-specific, so it isn't known on the very first
   * render (RogueBreadcrumb mounts before RogueFitmentGate resolves
   * anything) - `rogue:variant-change` is what re-renders once it is.
   */
  class RogueBreadcrumb extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[ref="crumbLink"]');
      this.separator = this.querySelector('[ref="crumbSeparator"]');
      this.template = this.getAttribute('data-crumb-template') || '';
      // A real crumb_url rendered a visible link server-side (the shopper
      // came in through /collections/x/products/y) - that stays exactly as
      // Shopify built it, on this render and every one after.
      this.ownsLink = Boolean(this.link && this.link.hasAttribute('hidden'));
      if (!this.ownsLink) return;

      this.renderVanLabel();

      this.onVariantChange = (event) => this.renderVanLabel(event.detail?.fitmentState);
      document.addEventListener(ROGUE_VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener(ROGUE_VARIANT_CHANGE_EVENT, this.onVariantChange);
    }

    /** @param {FitmentState|null} [fitmentState] */
    renderVanLabel(fitmentState) {
      // Custom elements upgrade (and run connectedCallback) in `define()`
      // call order, not DOM order - rogue-fitment-gate is defined before
      // rogue-breadcrumb, so its FIRST render (and the rogue:variant-change
      // event announceVariant fires at the end of it) already happened by
      // the time this runs for the very first time, with no listener here
      // yet to catch it. Reading the attribute render() already set
      // (data-fitment-state) directly is what makes the initial render
      // correct regardless of that ordering; the event still covers every
      // later change (a different Colour/Size after page load).
      const resolvedState =
        fitmentState != null ? fitmentState : document.querySelector('[data-fitment-state]')?.getAttribute('data-fitment-state') || null;
      const vehicle = enrichVehicle(globalStore.get(), readVehiclesFromDom());
      const result = breadcrumbVanLabel(vehicle, resolvedState, this.template);
      if (!result || !this.link) return;

      const href = result.useAllWheels
        ? `${collectionsRoot()}/${UNLISTED_VEHICLE_COLLECTION_HANDLE}`
        : `${collectionsRoot()}/${vehicle.collection_handle}`;
      this.link.setAttribute('href', href);
      this.link.textContent = result.label;
      this.link.removeAttribute('hidden');
      this.separator?.removeAttribute('hidden');
    }
  }

  /**
   * "Goes with these wheels" accessory cross-sell row (S3.5a) - rendered by
   * snippets/rogue-accessory-cross-sell.liquid on both the product page and
   * the cart page. Each card's Add button posts straight to /cart/add.js
   * with quantity 1, then fetches /cart.js and dispatches the theme's own
   * `@shopify/events` CartLinesUpdateEvent - the exact event
   * assets/product-form.js dispatches for its own add-to-cart submit - so
   * the header cart bubble (assets/cart-icon.js), the cart drawer and, on
   * the cart page, <cart-items-component> (which re-renders its section
   * when the event carries no `sections` payload - see
   * assets/component-cart-items.js's #handleCartUpdate) all refresh
   * themselves without a page reload, exactly as Horizon's own add-to-cart
   * button would. `@shopify/events` is dynamically imported here, not
   * statically at the top of the file, so this module keeps loading (and
   * `node --test`ing) with no bundler and no browser-only dependency at
   * parse time - see the file header.
   */
  class RogueAccessoryCrossSell extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => this.handleClick(event));
    }

    /** @param {MouseEvent} event */
    handleClick(event) {
      const button = /** @type {HTMLElement} */ (event.target)?.closest('[data-rogue-accessory-add]');
      if (!button || button.hasAttribute('disabled')) return;
      if (button.hasAttribute('data-check-basket')) {
        window.location.href = '/cart';
        return;
      }
      this.addAccessory(/** @type {HTMLButtonElement} */ (button));
    }

    /** @param {HTMLButtonElement} button */
    async addAccessory(button) {
      const variantId = button.getAttribute('data-rogue-variant-id');
      if (!variantId) return;

      const label = button.querySelector('[ref="addLabel"]');
      const originalText = label ? label.textContent : '';
      button.setAttribute('disabled', '');
      if (label) label.textContent = 'Adding…';

      const result = await addBasketItemsOnce({ id: variantId, quantity: 1 }, { afterAccepted: async () => {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Could not refresh basket');
        const cartJson = await cartResponse.json();
        await this.announceCartUpdate(cartJson, variantId);
      } });
      if (result.state === 'accepted' && result.refreshed) {
        if (label) label.textContent = 'Added';
        setTimeout(() => {
          button.removeAttribute('disabled');
          if (label) label.textContent = originalText;
        }, 1800);
      } else {
        if (label) label.textContent = result.state === 'accepted' ? 'Added — view basket' : 'Check basket';
        button.setAttribute('data-check-basket', '');
        button.removeAttribute('disabled');
      }
    }

    /**
     * @param {Record<string, unknown>} cartJson - the parsed /cart.js response
     * @param {string} variantId
     */
    async announceCartUpdate(cartJson, variantId) {
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
              detail: {
                items: cartJson.items,
                source: 'rogue-accessory-cross-sell',
                itemCount: cartJson.item_count,
              },
            }),
          })
        );
      } catch (error) {
        // '@shopify/events' failed to load (offline, CDN hiccup) - the
        // accessory is still in the basket; only the live bubble/drawer
        // refresh is skipped, and the count catches up on next navigation.
        console.warn('[rogue-accessory-cross-sell] cart update event skipped:', error);
      }
    }
  }

  /**
   * Review N9: keeps a paired tyre line's quantity and removal in step with
   * its wheel line, wherever the change came from (this file's own
   * add-to-basket flow, Horizon's stock quantity stepper on the wheel line,
   * or its stock remove button). Purely reactive rather than intercepting
   * any particular click: every cart mutation on this theme, whoever makes
   * it, dispatches `@shopify/events`' CartLinesUpdateEvent on `document`
   * (see RogueFitmentGate.announceCartUpdate/RogueAccessoryCrossSell for two
   * more examples) - this waits for that event's own promise to settle, then
   * re-reads the live cart and applies whatever cartPairingCorrections says
   * is needed. The visual grouping itself (the nested-line indent, "Tyres
   * for this set", the wheel's own "removing this removes the tyres" note)
   * is server-rendered by snippets/cart-products.liquid from the same
   * `_set_id` property, so it survives every section morph with no JS
   * involved at all - this only covers the two things that need a network
   * follow-up.
   */
  class RoguePackageSpare extends HTMLElement {
    connectedCallback() {
      this.querySelector('input')?.addEventListener('change', (event) => this.save({ included: event.target.checked }));
      this.querySelector('[data-remove-spare]')?.addEventListener('click', () => this.save({ remove: true }));
    }

    async save(change) {
      if (this.busy || cartMutationState.failed) return;
      this.busy = true;
      const components = [...document.querySelectorAll('cart-items-component')];
      for (const component of components) component.flushPendingQuantityChange?.();
      const preceding = [...pendingCartChanges];
      const disabled = components.map((component) => ({ component, inert: component.inert }));
      for (const { component } of disabled) component.inert = true;
      const status = this.querySelector('[role="status"]');
      if (status) status.textContent = 'Saving spare choice…';
      const packageId = this.getAttribute('data-package-id');
      const lineKey = this.getAttribute('data-line-key');
      const mutation = (async () => {
        await Promise.all(preceding);
        await flushCartPairs();
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Could not check basket');
        const cart = await response.json();
        const item = cart.items?.find((line) => line.product_type === 'Wheel' && (packageId ? line.properties?._set_id === packageId : line.key === lineKey));
        const body = spareCartChange(item, change);
        if (!body) throw new Error('Wheel package changed');
        const changed = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        if (!changed.ok) throw new Error('Spare update unconfirmed');
        const saved = await changed.json();
        if (saved.errors) throw new Error('Spare update unconfirmed');
        await flushCartPairs();
        const finalResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!finalResponse.ok) throw new Error('Could not confirm basket');
        const finalCart = await finalResponse.json();
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        document.dispatchEvent(new CartLinesUpdateEvent({ action: body.quantity ? 'update' : 'remove', context: 'cart',
          lines: [{ id: item.key, quantity: body.quantity }], promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalCart),
            detail: { items: finalCart.items, itemCount: finalCart.item_count, source: 'rogue-package-spare' },
          }) }));
        if (status) status.textContent = 'Spare choice saved.';
      })();
      pendingCartChanges.add(mutation);
      try { await mutation; }
      catch {
        cartMutationState.failed = true;
        if (status) status.textContent = 'Reload your basket to check the spare choice before continuing.';
      } finally {
        pendingCartChanges.delete(mutation);
        for (const { component, inert } of disabled) component.inert = inert;
        this.busy = false;
      }
    }
  }

  function initRogueCartPairing() {
    // Guards against a double `installDomBehaviour()` run the same way
    // `define()` below does for customElements.define - see its own comment
    // for why this file can legitimately be evaluated twice.
    if (window.__rogueCartPairingInitialized) return;
    window.__rogueCartPairingInitialized = true;

    let running = null;
    let rerun = false;

    /** @param {Record<string, unknown>[]} rawItems - a `/cart.js` response's `items` array */
    function toLiteItems(rawItems) {
      return (Array.isArray(rawItems) ? rawItems : []).map((item) => {
        const properties = /** @type {Record<string, string>|null|undefined} */ (item?.properties);
        const setId = properties && typeof properties['_set_id'] === 'string' ? properties['_set_id'] : '';
        const type = item?.product_type;
        const role = type === 'Wheel' ? 'wheel' : type === 'Tyre' ? 'tyre' : '';
        // N11: cartPairingCorrections reads a wheel line's own `Spare`
        // property back off this, so it needs the real properties object,
        // not just the `_set_id` pulled out of it above.
        return { key: String(item?.key || ''), quantity: Number(item?.quantity) || 0, setId, role, properties: properties || {} };
      });
    }

    /**
     * Review SHOP-007 (17 Oct 2026): drop a "Tyre letter colour" left behind
     * once no tyre line remains (see staleTyreLetterAttributes). Skipped while
     * any basket change is still in flight, because a fitted add writes this
     * attribute before its lines exist. Queued on the shared writer, so a later
     * fitted add's own write still lands after it, and the checkout barrier's
     * cartAttributeWriter.flush() waits for it. Sent once (`forget`), so later
     * writes from this page never re-clear a colour set since; if it fails,
     * the next pairing check (on every basket change and at checkout) sees
     * the stale value and tries again.
     */
    function clearStaleTyreLetter(cart) {
      const clear = staleTyreLetterAttributes(cart);
      if (!clear || cartMutationState.pending.size > 0) return;
      cartAttributeWriter.save(clear, { forget: true }).catch(() => {});
    }

    async function syncPairsOnce() {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Could not check wheel and tyre quantities');
      const cart = await cartResponse.json();
      const items = toLiteItems(cart.items);
      const corrections = cartPairingCorrections(items);
      if (corrections.length === 0) {
        clearStaleTyreLetter(cart);
        return;
      }

      for (const correction of corrections) {
        // N11: a Spare-property correction carries its full replacement
        // `properties` object (see cartPairingCorrections) - `/cart/change.js`
        // replaces a line's properties wholesale, so this always sends
        // everything that line already had, Spare added or removed.
        const body = { id: correction.key, quantity: correction.quantity };
        if (correction.properties) body.properties = correction.properties;
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error('Could not save wheel and tyre quantities');
      }

      const finalCartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!finalCartResponse.ok) throw new Error('Could not confirm wheel and tyre quantities');
      const finalCart = await finalCartResponse.json();
      clearStaleTyreLetter(finalCart);

      const { CartLinesUpdateEvent } = await import('@shopify/events');
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: corrections.map((correction) => ({ id: correction.key, quantity: correction.quantity })),
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalCart),
            detail: { items: finalCart.items, source: 'rogue-cart-pairing', itemCount: finalCart.item_count },
          }),
        })
      );
    }

    function syncPairsFromServer() {
      rerun = true;
      if (!running) {
        running = (async () => {
          do {
            rerun = false;
            await syncPairsOnce();
          } while (rerun);
        })().finally(() => { running = null; });
      }
      return running;
    }

    flushCartPairs = syncPairsFromServer;

    import('@shopify/events').then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        /** @type {any} */ (event)?.promise?.then((result) => {
          if (result?.detail?.source !== 'rogue-cart-pairing') return syncPairsFromServer();
        }).catch(() => {});
      });
    });

    // Review finding S1 (2026-09-26): the listener above only corrects a
    // mismatch reactively, on the NEXT cart mutation - a set already
    // mismatched when the cart page itself loads (raised past its wheel
    // quantity through a raw /cart/change.js call, say) stayed wrong until
    // something else changed the cart. sections/main-cart.liquid is the
    // cart PAGE only (not the drawer), and always renders `.cart-page`, so
    // run the same correction once, immediately, there.
    if (document.querySelector('.cart-page')) {
      syncPairsFromServer().catch(() => {});
    }
  }

  function hiddenInput(name, value, formId) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    if (formId) input.setAttribute('form', formId);
    return input;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {string[]} values
   * @param {string} placeholder
   * @param {Array<{ value: string, label: string }>} [labelled] - use explicit {value,label} pairs instead of `values` for both
   */
  function setSelectOptions(select, values, placeholder, labelled) {
    if (!select) return;
    select.innerHTML = '';
    select.appendChild(new Option(placeholder, ''));
    const entries = labelled || values.map((v) => ({ value: v, label: v }));
    for (const entry of entries) {
      select.appendChild(new Option(entry.label, entry.value));
    }
    select.value = '';
  }

  function safeLocalStorage() {
    try {
      const testKey = '__rogue_storage_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    } catch {
      return null;
    }
  }

  define('rogue-vehicle-selector', RogueVehicleSelector);
  define('rogue-your-van-bar', RogueYourVanBar);
  define('rogue-cart-details', RogueCartDetails);
  define('rogue-cart-empty', RogueCartEmpty);
  define('rogue-cart-van-note', RogueCartVanNote);
  define('rogue-tyre-info-cta', RogueTyreInfoCta);
  define('rogue-inline-vehicle', RogueInlineVehicle);
  // The gallery before the gate, so it is listening when the gate
  // announces its first pick; the replay above covers any other order.
  define('rogue-product-gallery', RogueProductGallery);
  define('rogue-fitment-gate', RogueFitmentGate);
  define('rogue-collection-head', RogueCollectionHead);
  define('rogue-fit-filter-bar', RogueFitFilterBar);
  define('rogue-fit-only-grid', RogueFitOnlyGrid);
  define('rogue-product-card', RogueProductCard);
  define('rogue-package-spare', RoguePackageSpare);
  define('rogue-breadcrumb', RogueBreadcrumb);
  define('rogue-accessory-cross-sell', RogueAccessoryCrossSell);
  initRogueCartPairing();

  /**
   * `customElements.define` throws if the name is taken, and this module can
   * legitimately be evaluated twice (two <script type="module"> tags whose
   * asset URLs differ by cache-busting query). Registering once is enough.
   * @param {string} name
   * @param {CustomElementConstructor} constructor
   */
  function define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }
}
